import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import Pagination from '../components/Pagination';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import SalaryAdvance from '../components/SalaryAdvance';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  Users, UserPlus, FileText, DollarSign, Upload, CheckCircle2, 
  XCircle, Clock, Building2, Wallet, CreditCard, Search,
  RefreshCw, Eye, Loader2, AlertCircle, AlertTriangle, FileCheck,
  Calculator, FileSpreadsheet, Settings, Edit, Download, Pencil,
  User, Briefcase, Plus, Filter, ChevronRight, Shield,
  Mail, Check, X, Calendar, FileDown, Receipt, Send, ArrowLeft, Trash2, MoreVertical,
  Phone, Building, Bike, Brain, Sparkles, Bot, Maximize2, Minimize2, ChevronDown, Banknote, Wrench, ExternalLink, FileWarning, AlertOctagon, ShieldAlert,
  Zap, PlayCircle, ShieldCheck, History
} from 'lucide-react';
import { WhatsAppIcon } from '../components/WhatsAppButton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '../components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Switch } from '../components/ui/switch';
import { FormulaBuilderDialog, PlatformCard, CreatePayrollWizard } from '../components/talent';
import PayrollWizardReadOnlyDialog from '../components/PayrollWizardReadOnlyDialog';
import { DuplicateAccountsDialog } from '../components/talent/DuplicateAccountsDialog';
import { AdjustmentHistory } from '../components/talent/AdjustmentHistory';
import { AutoSettledArrearsDialog } from '../components/talent/AutoSettledArrearsDialog';
import EmployeeLeaves from './EmployeeLeaves';
import NationalitySelect from '../components/NationalitySelect';
import { formatDateDisplay, truncateName, truncateCompanyName } from '../utils/dateUtils';
import { CurrencyDisplay } from '../components/DirhamSymbol';
import useDeviceDetection from '../hooks/useDeviceDetection';
import usePermissions from '../hooks/usePermissions';
import { userCan } from '../utils/permissionUtils';
import { formatErrorMessage } from '../utils/formatError';
import { round2, round2PreviewRows } from '../utils/roundMoney';
import { downloadExcel, downloadPDF } from '../utils/downloadHelpers';

const API = process.env.REACT_APP_BACKEND_URL ? process.env.REACT_APP_BACKEND_URL + '/api' : '/api';

// Debug log for API URL (helps diagnose production issues)
if (process.env.NODE_ENV === 'development' || !process.env.REACT_APP_BACKEND_URL) {
  console.log('Talent API URL:', API);
}

// ─── Noon Minutes detector (fuzzy) ──────────────────────────────
// Noon Minutes bakes Tips INTO gross as an EARNING. Historical rows
// store the platform under a dozen aliases ("Noon-Minutes",
// "noon_minutes", "Noon Food (Minutes)", or blank with the NM
// formula). This helper detects an NM row by (a) platform-name,
// (b) formula signature, OR (c) NM-only breakdown keys written by
// the backend NM branch.
const _isNoonMinutes = (a, b) => {
  const _get = (obj, key) => {
    if (!obj || typeof obj !== 'object') return undefined;
    if (obj[key] !== undefined) return obj[key];
    if (obj.calculation_breakdown && obj.calculation_breakdown[key] !== undefined) {
      return obj.calculation_breakdown[key];
    }
    return undefined;
  };
  const srcs = [a, b, a?.calculation_breakdown, b?.calculation_breakdown].filter(Boolean);
  for (const s of srcs) {
    const plat = String(_get(s, 'platform') || '').toLowerCase();
    if (plat.includes('noon') && plat.includes('minute')) return true;
    const formula = String(_get(s, 'formula') || '').toLowerCase();
    if (
      (formula.includes('platform_deduction') || formula.includes('platform deduction')) &&
      (formula.includes('admin_fee') || formula.includes('admin fee')) &&
      (formula.includes('fuel_charges') || formula.includes('fuel charges'))
    ) return true;
    if (
      s?.nm_platform_deduction !== undefined ||
      s?.nm_admin_fee !== undefined ||
      s?.nm_fuel_charges !== undefined ||
      s?.nm_cod !== undefined
    ) return true;
  }
  return false;
};



const Talent = ({ user }) => {
  // URL params for tab navigation
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const { isMobile, isTablet } = useDeviceDetection();
  
  // Permissions
  const isAdmin = user?.role === 'Admin' || user?.role === 'Super Admin';
  const permissions = usePermissions(user, 'talent');
  const canCreateContractLoan = permissions?.canCreateSubPage?.('contractLoanEntries') ?? user?.permissions?.talent?.subPages?.contractLoanEntries?.create ?? isAdmin;
  const canEditContractLoan = permissions?.canEditSubPage?.('contractLoanEntries') ?? user?.permissions?.talent?.subPages?.contractLoanEntries?.edit ?? isAdmin;
  const canDeleteContractLoan = permissions?.canDeleteSubPage?.('contractLoanEntries') ?? user?.permissions?.talent?.subPages?.contractLoanEntries?.delete ?? isAdmin;
  // Approved payroll batch deletion is a sensitive, destructive operation:
  // it removes all monthly_payrolls, salary_slips, revert fines/loans/advances
  // tagged to the batch. We restrict it to Super Admin only (regardless of
  // sub-page RBAC) so accidental sub-admin grants can't wipe approved data.
  const canDeletePayroll = userCan(user, 'talent', 'delete', 'payroll');
  
  // Main navigation - use URL param if available
  const [activeSubPage, setActiveSubPage] = useState(tabFromUrl || 'employees');
  const [loading, setLoading] = useState(false);
  
  // Update activeSubPage when URL changes
  useEffect(() => {
    if (tabFromUrl && ['employees', 'accounts', 'payroll', 'admin', 'recovery', 'adjustments'].includes(tabFromUrl)) {
      setActiveSubPage(tabFromUrl);
    }
  }, [tabFromUrl]);

  // Deep-link to wizard via ?wizard=<draftId> — opens the draft as a full-tab
  // wizard. Used by the "Open in New Tab" button.
  const wizardDraftFromUrl = searchParams.get('wizard');
  useEffect(() => {
    if (!wizardDraftFromUrl) return;
    setActiveSubPage('payroll');
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/talent/pms/draft/${wizardDraftFromUrl}`, { headers: getAuthHeader() });
        if (cancelled) return;
        const draft = res.data;
        setWizardData({
          month: draft.month, year: draft.year,
          payrollType: draft.payroll_type,
          platform: draft.platform || '',
          platformId: '', file: null,
          preview: round2PreviewRows(draft.payroll_data || []),
          summary: null, useAI: false, aiProcessing: false,
          unmatchedRiders: [],
          draftId: draft.id,
        });
        setWizardStep(draft.wizard_step || 4);
        setPayrollWizardOpen(true);
        setWizardFullscreen(true);  // full-page feel in the new tab
        toast.success('Draft loaded in new tab — your edits sync via the saved draft');
      } catch (e) {
        toast.error('Failed to load draft from URL — open the wizard manually and try again');
      }
    })();
    return () => { cancelled = true; };
  }, [wizardDraftFromUrl]);
  
  // Employees state
  const [employees, setEmployees] = useState([]);
  const [employeeSummary, setEmployeeSummary] = useState({ total: 0, riders: 0, staff: 0, pending_documents: 0 });
  const [employeeFilter, setEmployeeFilter] = useState('all'); // all, rider, staff, pending
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // Dialogs
  const [enrollRiderDialogOpen, setEnrollRiderDialogOpen] = useState(false);
  const [addStaffDialogOpen, setAddStaffDialogOpen] = useState(false);
  const [employeeDetailsDialogOpen, setEmployeeDetailsDialogOpen] = useState(false);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [loanDialogOpen, setLoanDialogOpen] = useState(false);
  const dupReturnRef = useRef(false);
  const [dupReopenSignal, setDupReopenSignal] = useState(0);
  useEffect(() => {
    if (!loanDialogOpen && dupReturnRef.current) {
      dupReturnRef.current = false;
      setDupReopenSignal((n) => n + 1);
    }
  }, [loanDialogOpen]);
  const [loanRequestDialogOpen, setLoanRequestDialogOpen] = useState(false);
  const [editStaffDialogOpen, setEditStaffDialogOpen] = useState(false);
  const [riderRedirectDialogOpen, setRiderRedirectDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null); // Staff employee being edited
  const [originalStaffSalary, setOriginalStaffSalary] = useState(null); // snapshot at dialog-open for diff modal
  const [salaryDiffModal, setSalaryDiffModal] = useState({ open: false, changes: [] });
  const [swapLocations, setSwapLocations] = useState([]);
  const [viewDocumentsDialogOpen, setViewDocumentsDialogOpen] = useState(false);
  const [viewDocuments, setViewDocuments] = useState([]);
  const [viewDocumentsLoading, setViewDocumentsLoading] = useState(false);
  const [viewDocumentsEmployee, setViewDocumentsEmployee] = useState(null);
  
  // Loan entry dialog state
  const [addLoanEntryDialogOpen, setAddLoanEntryDialogOpen] = useState(false);
  const [emiAdjustmentDialogOpen, setEmiAdjustmentDialogOpen] = useState(false);
  const [emiAdjustmentData, setEmiAdjustmentData] = useState({ idx: null, originalEmi: 0, newEmi: 0, skippedAmount: 0, recoveryMethod: '', installments: 2, reason: '', crossPlatformTarget: '', employeePlatforms: [] });
  // Room Rent adjustment (staff payroll wizard)
  const [roomRentAdjustmentDialogOpen, setRoomRentAdjustmentDialogOpen] = useState(false);
  const [roomRentAdjustmentData, setRoomRentAdjustmentData] = useState({ idx: null, originalRoomRent: 0, newRoomRent: 0, reason: '', employeeName: '' });
  const [miscDeductionDialog, setMiscDeductionDialog] = useState({ open: false, idx: null, amount: 0, description: '' });
  const [pastDuesDialog, setPastDuesDialog] = useState({ open: false, idx: null, amount: 0, description: '' });
  const [slipsDialog, setSlipsDialog] = useState({ open: false, batchId: null, slips: [], loading: false, actionLoading: '', search: '' });
  const [maintDetailDialog, setMaintDetailDialog] = useState({ open: false, idx: null, records: [], loading: false, employeeId: '', month: 0, year: 0 });

  // Fines detail dialog with "Split into Instalments" action
  const [finesDetailDialog, setFinesDetailDialog] = useState({
    open: false,
    idx: null,
    employeeId: '',
    employeeName: '',
    bikePlate: '',
    month: 0,
    year: 0,
    fines: [],
    loading: false,
  });
  // Instalment-split sub-dialog
  const [splitFineDialog, setSplitFineDialog] = useState({
    open: false,
    fine: null,
    numInstalments: 3,
    submitting: false,
  });
  // "Rider Pays Directly" — fine recovery outside payroll
  const [riderPayDialog, setRiderPayDialog] = useState({
    open: false,
    fine: null,
    remark: 'Rider allowed to pay by himself',
    submitting: false,
  });
  // Bike history popover (clicked from the wizard's Bike No. cell)
  const [bikeHistoryDialog, setBikeHistoryDialog] = useState({
    open: false,
    loading: false,
    riderName: '',
    month: null,
    year: null,
    history: [],
    currentBikePlate: '',
  });
  const [addRecoveryDialogOpen, setAddRecoveryDialogOpen] = useState(false);
  const [loanTypes, setLoanTypes] = useState({});
  const [loanEntryForm, setLoanEntryForm] = useState({
    loan_type: '',
    sub_type: '',
    amount: '',
    description: '',
    month: '',
    year: new Date().getFullYear(),
    date: new Date().toISOString().split('T')[0],
    receipt: null,
    recovery_method: '',
    recovery_installments: 2
  });
  const [recoveryForm, setRecoveryForm] = useState({
    amount: '',
    month: '',
    year: new Date().getFullYear(),
    description: '',
    date: new Date().toISOString().split('T')[0],
    receipt: null
  });
  const [savingLoanEntry, setSavingLoanEntry] = useState(false);
  const [savingRecovery, setSavingRecovery] = useState(false);
  const [loanBulkUploadDialogOpen, setLoanBulkUploadDialogOpen] = useState(false);
  
  // Contract Loan Entry dialog state
  const [addContractLoanDialogOpen, setAddContractLoanDialogOpen] = useState(false);
  const [contractLoanForm, setContractLoanForm] = useState({
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    recovery_method: '',
    recovery_installments: 2
  });
  const [savingContractLoan, setSavingContractLoan] = useState(false);
  
  // Edit Contract Loan Entry state
  const [editContractLoanDialogOpen, setEditContractLoanDialogOpen] = useState(false);
  const [editContractLoanForm, setEditContractLoanForm] = useState({
    id: '',
    amount: '',
    description: '',
    date: ''
  });
  const [savingEditContractLoan, setSavingEditContractLoan] = useState(false);
  
  // Expense Entry dialog state
  const [addExpenseDialogOpen, setAddExpenseDialogOpen] = useState(false);
  const [expenseForm, setExpenseForm] = useState({
    loan_type: '',
    sub_type: '',
    amount: '',
    description: '',
    month: '',
    year: new Date().getFullYear(),
    date: new Date().toISOString().split('T')[0],
    receipt: null
  });
  const [savingExpense, setSavingExpense] = useState(false);
  
  // Statement download options
  const [includeExpensesInStatement, setIncludeExpensesInStatement] = useState(true);
  
  // EMI Schedule state
  const [addEmiScheduleDialogOpen, setAddEmiScheduleDialogOpen] = useState(false);
  const [editEmiScheduleDialogOpen, setEditEmiScheduleDialogOpen] = useState(false);
  const [editingEmiEntry, setEditingEmiEntry] = useState(null);
  const [emiScheduleForm, setEmiScheduleForm] = useState({
    month: '',
    year: new Date().getFullYear(),
    amount: '',
    status: 'pending',
    paid_amount: 0,
    paid_date: '',
    notes: ''
  });
  const [savingEmiSchedule, setSavingEmiSchedule] = useState(false);
  const [emiStatusFilter, setEmiStatusFilter] = useState('all'); // Filter for EMI schedule: all, pending, paid, missed, skipped, rescheduled, partial
  const [loanBulkFile, setLoanBulkFile] = useState(null);
  const [uploadingLoanBulk, setUploadingLoanBulk] = useState(false);
  const [bulkUploadPreview, setBulkUploadPreview] = useState(null);
  const [previewingBulkUpload, setPreviewingBulkUpload] = useState(false);
  const [setEmiDialogOpen, setSetEmiDialogOpen] = useState(false);
  const [agreedEmi, setAgreedEmi] = useState('');
  
  // Loan accounts summary dashboard state
  const [loanSummary, setLoanSummary] = useState(null);
  const [loanSummaryDialogOpen, setLoanSummaryDialogOpen] = useState(false);
  const [loanSummaryDialogType, setLoanSummaryDialogType] = useState(null); // 'loans', 'recoveries', 'outstanding', 'emi'
  
  // Employee Accounts search and filter state
  const [accountsSearch, setAccountsSearch] = useState('');
  const [accountsTypeFilter, setAccountsTypeFilter] = useState('all'); // 'all', 'rider', 'staff'

  // Pagination — search runs across ALL employees / accounts (client-side)
  // and we then slice the result for the visible page so records on later
  // pages remain discoverable via the search bar.
  const EMPLOYEES_PER_PAGE = 50;
  const [employeesPage, setEmployeesPage] = useState(1);
  const [accountsPage, setAccountsPage] = useState(1);
  
  // Eligible riders for enrollment
  const [eligibleRiders, setEligibleRiders] = useState([]);
  
  // Staff form
  const [staffForm, setStaffForm] = useState({
    name: '',
    emirates_id: '',
    emirates_id_expiry: '',
    passport_number: '',
    passport_expiry: '',
    email: '',
    mobile: '',
    employer_name: '',
    joining_date: '',
    monthly_salary: '',
    designation: '',
    edenred_id: '',
    nationality: '',
    basic_salary: '',
    housing_allowance: '',
    transport_allowance: '',
    telephone_allowance: '',
    pension_amount: '',
    custom_allowances: [], // Array of {description: '', amount: ''}
    room_rent: ''
  });
  
  // Enrollment form
  const [enrollForm, setEnrollForm] = useState({
    rider_id: '',
    initial_loan_amount: 0,
    monthly_emi: 0
  });
  
  // Employee details
  const [employeeDetails, setEmployeeDetails] = useState(null);
  
  // Loan request form
  const [loanRequestForm, setLoanRequestForm] = useState({
    requested_amount: '',
    purpose: '',
    proposed_emi: ''
  });
  
  // Monthly Payroll state
  const [monthlyPayrolls, setMonthlyPayrolls] = useState([]);
  const [payrollSummary, setPayrollSummary] = useState(null);
  const [payrollFilter, setPayrollFilter] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    status: ''
  });
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importForm, setImportForm] = useState({
    file: null,
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });
  const [importLoading, setImportLoading] = useState(false);
  const [platformRates, setPlatformRates] = useState([]);
  const [ratesDialogOpen, setRatesDialogOpen] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [payrollDetailDialogOpen, setPayrollDetailDialogOpen] = useState(false);
  
  // NEW: Payroll Wizard State
  const [payrollWizardOpen, setPayrollWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardFullscreen, setWizardFullscreen] = useState(false);
  // Fines tagged "Rider Pays Directly" during THIS wizard session — must
  // be reverted if the user aborts/cancels the wizard before approving
  // the payroll. Stored as Set of fine ids. Cleared on successful submit.
  const [wizardPendingRiderPayFineIds, setWizardPendingRiderPayFineIds] = useState(() => new Set());
  // Instalment plans created during THIS wizard session — likewise
  // reverted on cancel so the Fines page stays clean until payroll
  // approval makes the change permanent.
  const [wizardPendingInstalmentPlanIds, setWizardPendingInstalmentPlanIds] = useState(() => new Set());
  // Confirmation dialog for cancelling the wizard mid-session.
  const [cancelWizardConfirm, setCancelWizardConfirm] = useState(false);
  const [calculationDialog, setCalculationDialog] = useState({ open: false, data: null, riderName: '', riderData: null });

  /** Single source of truth for closing the payroll wizard. If the user
   *  has tagged any fines as "Rider Pays Directly" OR split any fines
   *  into instalment plans during this session, prompt for confirmation
   *  and revert those changes before closing so the Fines page only
   *  reflects FINAL/approved payroll actions. */
  const requestCloseWizard = () => {
    if (wizardPendingRiderPayFineIds.size > 0 || wizardPendingInstalmentPlanIds.size > 0) {
      setCancelWizardConfirm(true);
    } else {
      setPayrollWizardOpen(false);
    }
  };

  /** Called when the user confirms cancel. Reverts every fine tagged
   *  rider_pay AND every instalment plan created during this wizard
   *  session back to their original status. */
  const confirmCancelWizard = async () => {
    const fineIds = Array.from(wizardPendingRiderPayFineIds);
    const planIds = Array.from(wizardPendingInstalmentPlanIds);
    let failed = 0;
    if (fineIds.length > 0) {
      const r = await Promise.allSettled(
        fineIds.map((fineId) =>
          axios.post(
            `${API}/fines/${fineId}/unmark-rider-pay`,
            { reason: 'Payroll wizard cancelled by user' },
            { headers: getAuthHeader() },
          ),
        ),
      );
      failed += r.filter((x) => x.status === 'rejected').length;
    }
    if (planIds.length > 0) {
      const r = await Promise.allSettled(
        planIds.map((planId) =>
          axios.delete(`${API}/fines/instalments/${planId}`, { headers: getAuthHeader() }),
        ),
      );
      failed += r.filter((x) => x.status === 'rejected').length;
    }
    if (failed > 0) {
      toast.error(`${failed} change(s) could not be reverted automatically. Please check the Fines page.`);
    } else if (fineIds.length + planIds.length > 0) {
      toast.success(`Reverted ${fineIds.length} fine(s) and ${planIds.length} instalment plan(s) to original status`);
    }
    setWizardPendingRiderPayFineIds(new Set());
    setWizardPendingInstalmentPlanIds(new Set());
    setCancelWizardConfirm(false);
    setPayrollWizardOpen(false);
  };
  const [wizardData, setWizardData] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    payrollType: '', // 'rider' or 'staff'
    platform: '',
    platformId: '', // Platform UUID for AI processing
    file: null,
    preview: [],
    summary: null,
    useAI: false, // Toggle for AI processing
    aiProcessing: false, // Loading state for AI
    unmatchedRiders: [] // Unmatched riders from AI
  });
  const [wizardLoading, setWizardLoading] = useState(false);
  const [payrollDrafts, setPayrollDrafts] = useState([]);
  const [heldSalaries, setHeldSalaries] = useState([]);

  // ── PMS Admin: Formula-driven parser toggle + Payroll Reconciliation ──
  // The runtime toggle mirrors `system_settings.payroll_formula_fallback_enabled`
  // and flips the gross-salary parser between the legacy hardcoded elif
  // chain and the new formula-driven path — no redeploy needed.
  const [formulaFallback, setFormulaFallback] = useState(null); // {enabled,source,db_value,env_value,updated_at,updated_by}
  const [togglingFormulaFallback, setTogglingFormulaFallback] = useState(false);
  const [reconMonth, setReconMonth] = useState(new Date().getMonth() || 12);
  const [reconYear, setReconYear] = useState(new Date().getFullYear());
  const [reconPlatform, setReconPlatform] = useState('__all__');
  const [reconDryRunResult, setReconDryRunResult] = useState(null);
  const [reconBusy, setReconBusy] = useState('');
  
  // AI Instructions dialog for PMS Admin
  const [aiInstructionsDialog, setAiInstructionsDialog] = useState(false);
  const [selectedPlatformForAI, setSelectedPlatformForAI] = useState(null);
  const [aiInstructions, setAiInstructions] = useState('');
  const [platformIdColumn, setPlatformIdColumn] = useState('');
  
  // Post-Approval Salary Slip Dialog State
  const [approvalSuccessDialog, setApprovalSuccessDialog] = useState(false);
  const [approvedBatchData, setApprovedBatchData] = useState(null);
  const [generatingSlips, setGeneratingSlips] = useState(false);
  const [emailingSlips, setEmailingSlips] = useState(false);
  
  // NEW: Payroll Dashboard State
  const [payrollDashboard, setPayrollDashboard] = useState({
    payrolls_generated: 0,
    last_payroll_month: null,
    last_payroll_year: null,
    total_employees: 0,
    total_platforms: 0
  });
  const [approvedPayrollLog, setApprovedPayrollLog] = useState([]);
  const [selectedMergeBatches, setSelectedMergeBatches] = useState([]);
  const [merging, setMerging] = useState(false);

  // Arrears Tracker state
  const [arrearsItems, setArrearsItems] = useState([]);
  const [arrearsTotals, setArrearsTotals] = useState({ count: 0, total_arrears: 0, total_recovered: 0, total_pending: 0 });
  const [arrearsMonth, setArrearsMonth] = useState(''); // empty = all months
  const [arrearsLoading, setArrearsLoading] = useState(false);
  const [collapsedArrearsMonths, setCollapsedArrearsMonths] = useState({});
  const [collapsedLogMonths, setCollapsedLogMonths] = useState({});
  // Per-month free-text search in Recovery Report — keyed by `${year}-${month}`
  const [monthSearchQuery, setMonthSearchQuery] = useState({});
  // Tracks in-progress export per month — { [monthKey]: 'pdf' | 'excel' | null }
  const [downloadingMonth, setDownloadingMonth] = useState({});
  // Wizard step-4 search filter for the editable preview table
  const [wizardSearch, setWizardSearch] = useState('');
  
  // NEW: Platform Configuration State
  const [platforms, setPlatforms] = useState([]);
  const [platformDialogOpen, setPlatformDialogOpen] = useState(false);
  const [platformForm, setPlatformForm] = useState({
    name: '',
    salary_type: 'per_order',
    pickup_rate: 0,
    dropoff_rate: 0,
    fixed_salary_config: null
  });
  const [formulaDialogOpen, setFormulaDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [formulaConfig, setFormulaConfig] = useState({
    rider_id_column: '',
    selected_columns: [], // Columns selected for formula
    formula: '', // e.g., "[Total Earnings] + [Tips] - [Commission]"
    detected_columns: [] // Columns detected from uploaded Excel
  });
  
  // NEW: Pending Payroll Batches
  const [pendingBatches, setPendingBatches] = useState([]);
  const [expandedBatchId, setExpandedBatchId] = useState(null);
  // Read-only Wizard dialog (used by Pending Approval Log "View" button)
  const [readOnlyWizardOpen, setReadOnlyWizardOpen] = useState(false);
  const [readOnlyWizardBatch, setReadOnlyWizardBatch] = useState(null);
  const [readOnlyWizardPayrolls, setReadOnlyWizardPayrolls] = useState([]);
  // Delete-payroll-batch (Super Admin only) typed-confirmation dialog
  const [deleteTargetBatch, setDeleteTargetBatch] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // NEW: Staff Payroll Config
  const [staffConfig, setStaffConfig] = useState({
    working_days_per_month: 26,
    overtime_rate_multiplier: 1.5,
    overtime_base_divisor: 30
  });
  const [slxConfig, setSlxConfig] = useState({ slx_amount: 80, slx_enabled: true });
  
  // Admin state
  const [pendingApprovals, setPendingApprovals] = useState({ payrolls: [], loans: [] });
  const [selectedPayrollIds, setSelectedPayrollIds] = useState([]);
  const [addRateDialogOpen, setAddRateDialogOpen] = useState(false);
  const [rateForm, setRateForm] = useState({
    platform: 'Talabat',
    rider_type: 'EV',
    pickup_rate: 2.5,
    dropoff_rate: 5.0,
    distance_rate: 0,
    incentive_rate: 0
  });
  
  // Account statement state
  const [accountStatementDialogOpen, setAccountStatementDialogOpen] = useState(false);
  // Delete-employee confirmation w/ outstanding-records warning
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [mergeCandidates, setMergeCandidates] = useState([]);
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [mergeInFlight, setMergeInFlight] = useState(false);
  const [accountStatement, setAccountStatement] = useState(null);
  
  // Salary slip state
  const [salarySlips, setSalarySlips] = useState([]);
  const [salarySlipDialogOpen, setSalarySlipDialogOpen] = useState(false);
  const [selectedSalarySlip, setSelectedSalarySlip] = useState(null);
  
  // Staff payroll state
  const [generateStaffPayrollDialogOpen, setGenerateStaffPayrollDialogOpen] = useState(false);
  const [staffPayrollLoading, setStaffPayrollLoading] = useState(false);
  
  // Lessees for dropdown
  const [lessees, setLessees] = useState([]);

  // Recovery report state
  const [recoveryData, setRecoveryData] = useState({ groups: [], totals: {} });
  const [recoveryFilter, setRecoveryFilter] = useState({ month: '', year: '', type: '', platform: '', rider: '', plate: '', ref: '' });
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryDescDialog, setRecoveryDescDialog] = useState({ open: false, text: '' });
  // Per-month UI state on the Recovery Report:
  // collapsedMonths[monthKey] = true means that month's body is collapsed
  // monthTypeFilter[monthKey] = '' | 'Fine' | 'SALIK' | 'Maintenance' | 'Loan EMI'
  const [collapsedMonths, setCollapsedMonths] = useState({});
  const [monthTypeFilter, setMonthTypeFilter] = useState({});

  // Post Payroll Adjustments state
  const [adjustmentsList, setAdjustmentsList] = useState([]);
  const [adjustmentsLoading, setAdjustmentsLoading] = useState(false);
  const [adjustmentApplyDialog, setAdjustmentApplyDialog] = useState({
    open: false,
    adjustment: null,
    payrolls: [],
    selectedPayrollId: '',
    loading: false,
    applying: false,
  });
  const [manualAdjustmentDialog, setManualAdjustmentDialog] = useState({
    open: false,
    rider_id: '',
    rider_search: '',
    rider_results: [],
    payrolls: [],          // approved payrolls for the selected rider
    payrolls_loading: false,
    selected_payroll_id: '', // month/year picker — also drives the auto-apply
    amount: '',
    direction: 'credit',
    category: 'add_to_gross',
    reason: '',
    recovered_items: [],   // list of fines/salik/maintenance items recovered in selected payroll
    recovered_loading: false,
    saving: false,
  });
  const [adjustmentCategories, setAdjustmentCategories] = useState({ credit: [], debit: [] });

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('Talent: No auth token found in localStorage');
    }
    return { Authorization: `Bearer ${token || ''}` };
  }, []);

  // Generate Loan Account Statement PDF
  const generateLoanStatementPDF = useCallback((employee, loanAccount, empDetails) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Header
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(139, 195, 74);
    doc.text('EZY MOBILITY', pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('LOAN ACCOUNT STATEMENT', pageWidth / 2, 30, { align: 'center' });
    
    // Employee Info - get passport from tap_candidate_info or full_rider_info
    const passportNo = employee.passport_number 
      || empDetails?.tap_candidate_info?.passport_number 
      || empDetails?.full_rider_info?.passport_number 
      || empDetails?.employee?.passport_number 
      || 'N/A';
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Employee: ${employee.name}`, 20, 45);
    doc.text(`Emirates ID: ${employee.emirates_id || 'N/A'}`, 20, 52);
    doc.text(`Passport: ${passportNo}`, 20, 59);
    doc.text(`Statement Date: ${new Date().toLocaleDateString()}`, pageWidth - 20, 45, { align: 'right' });
    
    // Summary Box — Contract Loan Amount + Outstanding are computed
    // net of any `recovered=true` arrears rows so the PDF math matches
    // the on-screen KPI cards.
    const entriesForKpi = loanAccount?.contract_loan_entries || [];
    const grossLoanKpi = entriesForKpi.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const recoveredArrears = entriesForKpi.reduce((s, e) => (e.recovered && e.recovered_via !== 'manual_settlement') ? s + (Number(e.amount) || 0) : s, 0);
    const totalLoan = entriesForKpi.length > 0
      ? grossLoanKpi - recoveredArrears
      : (Number(loanAccount?.initial_loan_amount) || 0);
    const totalRecovery = (loanAccount?.recoveries || []).reduce((sum, r) => sum + (r.amount || 0), 0);
    const outstanding = Math.max(0, totalLoan - totalRecovery);
    const agreedEmi = loanAccount?.agreed_emi || loanAccount?.monthly_emi || 0;
    
    doc.setFillColor(240, 240, 240);
    doc.rect(20, 68, pageWidth - 40, 25, 'F');
    
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    const summaryY = 78;
    const colWidth = (pageWidth - 40) / 4;
    
    doc.setTextColor(59, 130, 246); // Blue
    doc.text('Contract Loan Amount', 25, summaryY);
    doc.text(`AED ${totalLoan.toLocaleString()}`, 25, summaryY + 7);
    
    doc.setTextColor(34, 197, 94); // Green
    doc.text('Total Recovery', 25 + colWidth, summaryY);
    doc.text(`AED ${totalRecovery.toLocaleString()}`, 25 + colWidth, summaryY + 7);
    
    doc.setTextColor(239, 68, 68); // Red
    doc.text('Outstanding', 25 + colWidth * 2, summaryY);
    doc.text(`AED ${outstanding.toLocaleString()}`, 25 + colWidth * 2, summaryY + 7);
    
    doc.setTextColor(147, 51, 234); // Purple
    doc.text('Agreed EMI', 25 + colWidth * 3, summaryY);
    doc.text(`AED ${agreedEmi.toLocaleString()}`, 25 + colWidth * 3, summaryY + 7);
    
    let yPos = 105;

    // ---------------- Contract Loan Entries Table -----------------
    // Mirrors the "Contract Loan Entries" card in the modal. Includes
    // the original loan + all Arrears entries + stitched Salary Advances,
    // with a Status column showing Recovered / Pending / Migrated.
    const contractEntries = loanAccount?.contract_loan_entries || [];
    if (contractEntries.length > 0) {
      const badgeLabel = (e) => {
        if (e.source === 'negative_salary_auto') return 'Arrears';
        if (e.source === 'salary_advance') return 'Salary Advance';
        if (e.source === 'migrated' || String(e.description || '').toLowerCase().includes('migrated')) return 'Migrated';
        return e.type || 'Loan';
      };
      const statusLabel = (e) => {
        if (!e.recovered) return 'Pending';
        if (e.recovered_via === 'manual_settlement') return 'Recovered (manual)';
        return 'Recovered';
      };

      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Contract Loan Entries', 20, yPos);
      yPos += 5;

      doc.autoTable({
        startY: yPos,
        head: [['Date', 'Description', 'Type', 'Status', 'Amount (AED)']],
        body: contractEntries.map(entry => [
          entry.date ? new Date(entry.date).toLocaleDateString() : '-',
          entry.description || '-',
          badgeLabel(entry),
          statusLabel(entry),
          (entry.amount || 0).toLocaleString(),
        ]),
        theme: 'striped',
        headStyles: { fillColor: [99, 102, 241], fontSize: 9 },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 22 },
          2: { cellWidth: 26 },
          3: { cellWidth: 32 },
          4: { halign: 'right' },
        },
        didParseCell: function (data) {
          if (data.section === 'head' && data.column.index === 4) {
            data.cell.styles.halign = 'right';
          }
          // Color the status column: green = recovered, amber = pending
          if (data.section === 'body' && data.column.index === 3) {
            const raw = String(data.cell.raw || '').toLowerCase();
            if (raw.startsWith('recovered')) data.cell.styles.textColor = [34, 197, 94];
            else if (raw === 'pending') data.cell.styles.textColor = [234, 179, 8];
            else if (raw === 'migrated') data.cell.styles.textColor = [59, 130, 246];
          }
          // Strike-through amount when the arrears row was recovered
          if (data.section === 'body' && data.column.index === 4) {
            const row = contractEntries[data.row.index];
            if (row && row.recovered) {
              data.cell.styles.textColor = [148, 163, 184]; // slate-400
            }
          }
        },
        margin: { left: 20, right: 20 },
      });

      // Net vs Gross footnote (matches the header of the card in the UI)
      const _gross = contractEntries.reduce((s, e) => s + (Number(e.amount) || 0), 0);
      const _recArrears = contractEntries.reduce((s, e) => (e.recovered && e.recovered_via !== 'manual_settlement') ? s + (Number(e.amount) || 0) : s, 0);
      const _net = _gross - _recArrears;
      if (_recArrears > 0) {
        yPos = doc.lastAutoTable.finalY + 4;
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(
          `Net: AED ${_net.toLocaleString()}   (gross AED ${_gross.toLocaleString()} - recovered arrears AED ${_recArrears.toLocaleString()})`,
          20, yPos
        );
      }
      yPos = doc.lastAutoTable.finalY + 15;
    }

    // Expense Entries Table (conditionally included)
    const loanEntries = loanAccount?.loan_entries || [];
    if (loanEntries.length > 0 && includeExpensesInStatement) {
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Expense Entries', 20, yPos);
      yPos += 5;
      
      doc.autoTable({
        startY: yPos,
        head: [['Date', 'Type', 'Sub-Type', 'Description', 'Amount (AED)']],
        body: loanEntries.map(entry => [
          entry.date ? new Date(entry.date).toLocaleDateString() : '-',
          (entry.loan_type || '').replace('_', ' ').toUpperCase(),
          entry.sub_type || '-',
          entry.loan_type === 'accommodation' 
            ? `${entry.month || ''} ${entry.year || ''}`
            : (entry.description || '-'),
          (entry.amount || 0).toLocaleString()
        ]),
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246], fontSize: 9 },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 25 },
          4: { halign: 'right' }
        },
        margin: { left: 20, right: 20 }
      });
      
      yPos = doc.lastAutoTable.finalY + 15;
    }
    
    // Recovery Entries Table
    const recoveries = loanAccount?.recoveries || [];
    if (recoveries.length > 0) {
      // Check if we need a new page
      if (yPos > 220) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Recovery Entries', 20, yPos);
      yPos += 5;
      
      doc.autoTable({
        startY: yPos,
        head: [['Date', 'Month/Year', 'Source', 'Description', 'Amount (AED)']],
        body: recoveries.map(recovery => {
          let monthYear = `${recovery.month || ''} ${recovery.year || ''}`.trim();
          if (!monthYear && recovery.date) {
            const d = new Date(recovery.date);
            if (!isNaN(d)) {
              monthYear = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            }
          }
          const src = recovery.source || '';
          const sourceLabel = src === 'petty_cash_loan_repayment' ? 'Petty Cash'
            : src === 'salary_advance' ? 'Salary Advance'
            : src === 'salary_slip_loan_emi' ? 'Salary Slip EMI'
            : src === 'hiring_advance' ? 'Hiring Advance'
            : src === 'payroll' ? 'Payroll'
            : src === 'manual_settlement' ? 'Manual'
            : (src ? src.replace(/_/g, ' ') : 'Payroll');
          return [
            recovery.date ? new Date(recovery.date).toLocaleDateString() : '-',
            monthYear || '-',
            sourceLabel,
            recovery.description || '-',
            (recovery.amount || 0).toLocaleString()
          ];
        }),
        theme: 'striped',
        headStyles: { fillColor: [34, 197, 94], fontSize: 9, halign: 'left' },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          2: { cellWidth: 26 },
          4: { halign: 'right' }
        },
        didParseCell: function(data) {
          if (data.section === 'head' && data.column.index === 4) {
            data.cell.styles.halign = 'right';
          }
        },
        margin: { left: 20, right: 20 }
      });
      
      yPos = doc.lastAutoTable.finalY + 15;
    }
    
    // EMI Schedule Table
    const emiSchedule = loanAccount?.emi_schedule || [];
    if (emiSchedule.length > 0) {
      // Check if we need a new page
      if (yPos > 180) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('EMI Repayment Schedule', 20, yPos);
      yPos += 5;
      
      // Calculate EMI schedule summary
      const scheduledTotal = emiSchedule.reduce((sum, e) => sum + (e.amount || 0), 0);
      const paidTotal = emiSchedule.filter(e => e.status === 'paid').reduce((sum, e) => sum + (e.paid_amount || e.amount || 0), 0);
      const pendingCount = emiSchedule.filter(e => e.status === 'pending').length;
      const missedCount = emiSchedule.filter(e => e.status === 'missed' || e.status === 'skipped').length;
      
      doc.autoTable({
        startY: yPos,
        head: [['Month/Year', 'Scheduled Amount', 'Status', 'Paid Amount', 'Paid Date', 'Notes']],
        body: emiSchedule.map(emi => [
          `${emi.month} ${emi.year}`,
          (emi.amount || 0).toLocaleString(),
          (emi.status || 'pending').charAt(0).toUpperCase() + (emi.status || 'pending').slice(1),
          emi.paid_amount > 0 ? (emi.paid_amount || 0).toLocaleString() : '-',
          emi.paid_date ? new Date(emi.paid_date).toLocaleDateString() : '-',
          emi.notes || '-'
        ]),
        theme: 'striped',
        headStyles: { fillColor: [147, 51, 234], fontSize: 8 },
        bodyStyles: { fontSize: 7 },
        columnStyles: {
          1: { halign: 'right' },
          3: { halign: 'right' },
          5: { cellWidth: 35 }
        },
        margin: { left: 20, right: 20 },
        didParseCell: function(data) {
          // Color-code status column
          if (data.column.index === 2 && data.section === 'body') {
            const status = data.cell.raw.toLowerCase();
            if (status === 'paid') {
              data.cell.styles.textColor = [34, 197, 94];
            } else if (status === 'missed' || status === 'skipped') {
              data.cell.styles.textColor = [239, 68, 68];
            } else if (status === 'partial') {
              data.cell.styles.textColor = [234, 179, 8];
            } else if (status === 'rescheduled') {
              data.cell.styles.textColor = [59, 130, 246];
            }
          }
        }
      });
      
      // EMI Schedule Summary
      yPos = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Schedule Summary: Total Scheduled: AED ${scheduledTotal.toLocaleString()} | Paid: AED ${paidTotal.toLocaleString()} | Pending: ${pendingCount} | Missed: ${missedCount}`, 20, yPos);
    }
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Generated on ${new Date().toLocaleString()} | Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }
    
    // Save
    const fileName = `Loan_Statement_${employee.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    toast.success('PDF statement downloaded successfully');
  }, [includeExpensesInStatement]);

  // Generate Loan Account Statement CSV — a flat, finance-friendly
  // single-file dump of every Recovery + Contract Loan entry. Handy for
  // month-end archival where a single CSV imports cleanly into Zoho /
  // accounting tools without a multi-sheet workbook parser.
  const generateLoanStatementCSV = useCallback((employee, loanAccount) => {
    const entries = loanAccount?.contract_loan_entries || [];
    const recoveries = loanAccount?.recoveries || [];
    const grossLoan = entries.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const recoveredArrears = entries.reduce(
      (s, e) => (e.recovered && e.recovered_via !== 'manual_settlement')
        ? s + (Number(e.amount) || 0) : s, 0,
    );
    const totalLoan = entries.length > 0
      ? grossLoan - recoveredArrears
      : (Number(loanAccount?.initial_loan_amount) || 0);
    const totalRecovery = recoveries.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const outstanding = Math.max(0, totalLoan - totalRecovery);
    const srcLabel = (src) => ({
      petty_cash_loan_repayment: 'Petty Cash',
      salary_advance: 'Salary Advance',
      salary_slip_loan_emi: 'Salary Slip EMI',
      hiring_advance: 'Hiring Advance',
      payroll: 'Payroll',
      manual_settlement: 'Manual',
      negative_salary_auto: 'Arrears',
    }[src] || (src ? src.replace(/_/g, ' ') : ''));
    const csvEscape = (v) => {
      const s = (v ?? '').toString();
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [
      ['LOAN ACCOUNT STATEMENT'],
      ['Employee', employee.name],
      ['Emirates ID', employee.emirates_id || ''],
      ['Passport', employee.passport_number || ''],
      ['Statement Date', new Date().toLocaleDateString()],
      [],
      ['SUMMARY'],
      ['Contract Loan Amount', totalLoan],
      ['Total Recovery', totalRecovery],
      ['Outstanding', outstanding],
      [],
      ['CONTRACT LOAN ENTRIES'],
      ['Date', 'Description', 'Source', 'Status', 'Amount (AED)'],
      ...entries.map(e => [
        e.date ? new Date(e.date).toLocaleDateString() : '',
        e.description || '',
        srcLabel(e.source) || (e.type || 'Loan'),
        e.recovered
          ? (e.recovered_via === 'manual_settlement' ? 'Recovered (manual)' : 'Recovered')
          : 'Pending',
        Number(e.amount || 0),
      ]),
      [],
      ['RECOVERY ENTRIES'],
      ['Date', 'Source', 'Reference', 'Description', 'Amount (AED)'],
      ...recoveries.map(r => [
        r.date ? new Date(r.date).toLocaleDateString() : '',
        srcLabel(r.source) || 'Payroll',
        r.reference_number || r.transaction_ref || '',
        r.description || '',
        Number(r.amount || 0),
      ]),
    ];
    const csv = rows.map(row => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Loan_Statement_${employee.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('CSV statement downloaded');
  }, []);

  // Generate Loan Account Statement Excel
  const generateLoanStatementExcel = useCallback((employee, loanAccount) => {
    // Compute net of `recovered=true` arrears so the Excel export
    // matches the on-screen KPI cards after the arrears self-heal.
    const entriesForKpi = loanAccount?.contract_loan_entries || [];
    const grossLoanKpi = entriesForKpi.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const recoveredArrears = entriesForKpi.reduce((s, e) => (e.recovered && e.recovered_via !== 'manual_settlement') ? s + (Number(e.amount) || 0) : s, 0);
    const totalLoan = entriesForKpi.length > 0
      ? grossLoanKpi - recoveredArrears
      : (Number(loanAccount?.initial_loan_amount) || 0);
    const totalRecovery = (loanAccount?.recoveries || []).reduce((sum, r) => sum + (r.amount || 0), 0);
    const outstanding = Math.max(0, totalLoan - totalRecovery);
    const agreedEmi = loanAccount?.agreed_emi || loanAccount?.monthly_emi || 0;
    
    // EMI Schedule Summary
    const emiSchedule = loanAccount?.emi_schedule || [];
    const scheduledTotal = emiSchedule.reduce((sum, e) => sum + (e.amount || 0), 0);
    const paidTotal = emiSchedule.filter(e => e.status === 'paid').reduce((sum, e) => sum + (e.paid_amount || e.amount || 0), 0);
    const pendingCount = emiSchedule.filter(e => e.status === 'pending').length;
    const missedCount = emiSchedule.filter(e => e.status === 'missed' || e.status === 'skipped').length;
    const rescheduledCount = emiSchedule.filter(e => e.status === 'rescheduled').length;
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Summary Sheet
    const summaryData = [
      ['LOAN ACCOUNT STATEMENT'],
      [''],
      ['Employee Name:', employee.name],
      ['Emirates ID:', employee.emirates_id || 'N/A'],
      ['Passport No:', employee.passport_number || 'N/A'],
      ['Statement Date:', new Date().toLocaleDateString()],
      [''],
      ['LOAN SUMMARY'],
      ['Total Loan Issued:', totalLoan],
      ['Total Recovery:', totalRecovery],
      ['Outstanding Balance:', outstanding],
      ['Agreed EMI:', agreedEmi],
      [''],
      ['EMI SCHEDULE SUMMARY'],
      ['Total Scheduled Amount:', scheduledTotal],
      ['Total Paid:', paidTotal],
      ['Pending EMIs:', pendingCount],
      ['Missed EMIs:', missedCount],
      ['Rescheduled EMIs:', rescheduledCount]
    ];
    
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

    // Contract Loan Entries Sheet — mirrors the section on the modal.
    // Includes original loan + arrears + salary advances with badges.
    const contractEntries = loanAccount?.contract_loan_entries || [];
    if (contractEntries.length > 0) {
      const badgeLabel = (e) => {
        if (e.source === 'negative_salary_auto') return 'Arrears';
        if (e.source === 'salary_advance') return 'Salary Advance';
        if (e.source === 'migrated' || String(e.description || '').toLowerCase().includes('migrated')) return 'Migrated';
        return e.type || 'Loan';
      };
      const statusLabel = (e) => {
        if (!e.recovered) return 'Pending';
        if (e.recovered_via === 'manual_settlement') return 'Recovered (manual)';
        return 'Recovered';
      };
      const contractData = [
        ['Date', 'Description', 'Type', 'Status', 'Payroll Month', 'Recovered At', 'Amount (AED)'],
        ...contractEntries.map(entry => [
          entry.date ? new Date(entry.date).toLocaleDateString() : '',
          entry.description || '',
          badgeLabel(entry),
          statusLabel(entry),
          entry.payroll_month || '',
          entry.recovered_at ? new Date(entry.recovered_at).toLocaleDateString() : '',
          entry.amount || 0,
        ]),
      ];
      const contractSheet = XLSX.utils.aoa_to_sheet(contractData);
      XLSX.utils.book_append_sheet(wb, contractSheet, 'Contract Loan Entries');
    }

    // Expense Entries Sheet (conditionally included)
    const loanEntries = loanAccount?.loan_entries || [];
    if (loanEntries.length > 0 && includeExpensesInStatement) {
      const loanData = [
        ['Date', 'Expense Type', 'Sub-Type', 'Description', 'Amount (AED)'],
        ...loanEntries.map(entry => [
          entry.date ? new Date(entry.date).toLocaleDateString() : '',
          (entry.loan_type || '').replace('_', ' '),
          entry.sub_type || '',
          entry.loan_type === 'accommodation' 
            ? `${entry.month || ''} ${entry.year || ''}`
            : (entry.description || ''),
          entry.amount || 0
        ])
      ];
      
      const loanSheet = XLSX.utils.aoa_to_sheet(loanData);
      XLSX.utils.book_append_sheet(wb, loanSheet, 'Expense Entries');
    }
    
    // Recovery Entries Sheet
    const recoveries = loanAccount?.recoveries || [];
    if (recoveries.length > 0) {
      const sourceLabelFor = (src) => {
        if (!src) return 'Payroll';
        if (src === 'petty_cash_loan_repayment') return 'Petty Cash';
        if (src === 'salary_advance') return 'Salary Advance';
        if (src === 'salary_slip_loan_emi') return 'Salary Slip EMI';
        if (src === 'hiring_advance') return 'Hiring Advance';
        if (src === 'payroll') return 'Payroll';
        if (src === 'manual_settlement') return 'Manual';
        return src.replace(/_/g, ' ');
      };
      const recoveryData = [
        ['Date', 'Month', 'Year', 'Source', 'Description', 'Reference', 'Amount (AED)'],
        ...recoveries.map(recovery => [
          recovery.date ? new Date(recovery.date).toLocaleDateString() : '',
          recovery.month || '',
          recovery.year || '',
          sourceLabelFor(recovery.source),
          recovery.description || '',
          recovery.reference_number || recovery.transaction_ref || '',
          recovery.amount || 0
        ])
      ];
      
      const recoverySheet = XLSX.utils.aoa_to_sheet(recoveryData);
      XLSX.utils.book_append_sheet(wb, recoverySheet, 'Recovery Entries');
    }
    
    // EMI Schedule Sheet
    if (emiSchedule.length > 0) {
      const emiData = [
        ['Month', 'Year', 'Scheduled Amount (AED)', 'Status', 'Paid Amount (AED)', 'Paid Date', 'Notes'],
        ...emiSchedule.map(emi => [
          emi.month || '',
          emi.year || '',
          emi.amount || 0,
          (emi.status || 'pending').charAt(0).toUpperCase() + (emi.status || 'pending').slice(1),
          emi.paid_amount || 0,
          emi.paid_date ? new Date(emi.paid_date).toLocaleDateString() : '',
          emi.notes || ''
        ])
      ];
      
      const emiSheet = XLSX.utils.aoa_to_sheet(emiData);
      XLSX.utils.book_append_sheet(wb, emiSheet, 'EMI Schedule');
    }
    
    // Save
    const fileName = `Loan_Statement_${employee.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success('Excel statement downloaded successfully');
  }, [includeExpensesInStatement]);

  // Fetch employees
  const fetchEmployees = useCallback(async (opts = {}) => {
    // `withLoans` — when true the endpoint runs the heavy loan / petty-cash
    // stitching pass (needed only by the Employee Accounts subpage). The
    // default Employees tab uses `slim=true` to keep first-paint fast; the
    // full loan snapshot for any employee is lazy-fetched via
    // `/api/talent/employee/{id}` when the user clicks a row.
    const withLoans = opts.withLoans === true;
    setLoading(true);
    try {
      let params = new URLSearchParams();
      if (employeeFilter === 'rider') params.append('employee_type', 'rider');
      if (employeeFilter === 'staff') params.append('employee_type', 'staff');
      if (employeeFilter === 'pending') params.append('pending_docs', 'true');
      if (!withLoans) params.append('slim', 'true');

      console.log('Talent: Fetching employees from:', `${API}/talent/employees?${params}`);
      const response = await axios.get(`${API}/talent/employees?${params}`, {
        headers: getAuthHeader()
      });
      setEmployees(response.data.employees || []);
      setEmployeeSummary(response.data.summary || { total: 0, riders: 0, staff: 0, pending_documents: 0 });
      console.log('Talent: Successfully fetched', (response.data.employees || []).length, 'employees');
    } catch (error) {
      console.error('Talent: Error fetching employees:', error);
      console.error('Talent: Error response:', error.response?.status, error.response?.data);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to fetch Talent employees: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, employeeFilter]);

  const fetchSwapLocations = useCallback(async () => {
    // Split out from `fetchEmployees` so it fires in parallel with the
    // other mount-time reads instead of serialising behind them.
    try {
      const locRes = await axios.get(`${API}/swap-locations`, { headers: getAuthHeader() });
      setSwapLocations(locRes.data || []);
    } catch (e) { /* non-fatal */ }
  }, [getAuthHeader]);

  // Fetch eligible riders
  const fetchEligibleRiders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/talent/eligible-riders`, {
        headers: getAuthHeader()
      });
      setEligibleRiders(response.data.riders?.filter(r => !r.is_enrolled) || []);
    } catch (error) {
      console.error('Error fetching eligible riders:', error);
    }
  }, [getAuthHeader]);

  // Fetch lessees
  const fetchLessees = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/lessees`, {
        headers: getAuthHeader()
      });
      setLessees(response.data || []);
    } catch (error) {
      console.error('Error fetching lessees:', error);
    }
  }, [getAuthHeader]);

  // Fetch loan accounts summary for dashboard
  const fetchLoanSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/talent/loan-accounts/summary`, {
        headers: getAuthHeader()
      });
      setLoanSummary(response.data);
    } catch (error) {
      console.error('Error fetching loan summary:', error);
    }
  }, [getAuthHeader]);

  // Loading state for the Employee Details / Loan Account modal so
  // users see a spinner instead of stale data from a previously-opened
  // employee. Set to the employeeId being fetched, cleared on completion.
  const [loadingEmployeeId, setLoadingEmployeeId] = useState(null);

  // Fetch employee details
  const fetchEmployeeDetails = useCallback(async (employeeId) => {
    // Clear stale data from a previous employee so the modal doesn't
    // flash the wrong person's info for the 5-10s a large payload
    // takes on production.
    setEmployeeDetails(null);
    setLoadingEmployeeId(employeeId);
    try {
      const response = await axios.get(`${API}/talent/employee/${employeeId}`, {
        headers: getAuthHeader()
      });
      setEmployeeDetails(response.data);
    } catch (error) {
      console.error('Error fetching employee details:', error);
      toast.error('Failed to load employee details');
    } finally {
      setLoadingEmployeeId(null);
    }
  }, [getAuthHeader]);

  // Fetch monthly payrolls
  const fetchMonthlyPayrolls = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (payrollFilter.month) params.append('month', payrollFilter.month);
      if (payrollFilter.year) params.append('year', payrollFilter.year);
      if (payrollFilter.status) params.append('status', payrollFilter.status);
      
      const response = await axios.get(`${API}/talent/monthly-payrolls?${params}`, {
        headers: getAuthHeader()
      });
      setMonthlyPayrolls(response.data.payrolls || []);
      setPayrollSummary(response.data.summary);
    } catch (error) {
      console.error('Error fetching monthly payrolls:', error);
    }
  }, [getAuthHeader, payrollFilter]);

  // Fetch platform rates
  const fetchPlatformRates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/talent/platform-rates`, {
        headers: getAuthHeader()
      });
      setPlatformRates(response.data.rates || []);
    } catch (error) {
      console.error('Error fetching platform rates:', error);
    }
  }, [getAuthHeader]);

  // Fetch pending approvals
  const fetchPendingApprovals = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/talent/admin/pending-approvals`, {
        headers: getAuthHeader()
      });
      setPendingApprovals({
        payrolls: response.data.pending_payrolls || [],
        loans: response.data.pending_loans || [],
        summary: response.data.summary
      });
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
    }
  }, [getAuthHeader]);

  // Fetch account statement
  const fetchAccountStatement = useCallback(async (employeeId) => {
    try {
      const response = await axios.get(`${API}/talent/account-statement/${employeeId}`, {
        headers: getAuthHeader()
      });
      setAccountStatement(response.data);
    } catch (error) {
      console.error('Error fetching account statement:', error);
      toast.error('Failed to load account statement');
    }
  }, [getAuthHeader]);

  // Fetch salary slips
  const fetchSalarySlips = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (payrollFilter.month) params.append('month', payrollFilter.month);
      if (payrollFilter.year) params.append('year', payrollFilter.year);
      
      const response = await axios.get(`${API}/talent/salary-slips?${params}`, {
        headers: getAuthHeader()
      });
      setSalarySlips(response.data.salary_slips || []);
    } catch (error) {
      console.error('Error fetching salary slips:', error);
    }
  }, [getAuthHeader, payrollFilter]);

  // NEW: Fetch payroll dashboard stats
  const fetchPayrollDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/talent/pms/dashboard`, {
        headers: getAuthHeader()
      });
      setPayrollDashboard(response.data);
    } catch (error) {
      console.error('Error fetching payroll dashboard:', error);
    }
  }, [getAuthHeader]);

  // NEW: Fetch approved payroll log
  const fetchApprovedPayrollLog = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/talent/pms/approved-log`, {
        headers: getAuthHeader()
      });
      setApprovedPayrollLog(response.data.batches || []);
    } catch (error) {
      console.error('Error fetching approved payroll log:', error);
    }
  }, [getAuthHeader]);

  // Fetch Arrears Tracker
  const fetchArrearsTracker = useCallback(async (monthFilter = '') => {
    setArrearsLoading(true);
    try {
      const url = monthFilter
        ? `${API}/talent/pms/arrears-tracker?month=${encodeURIComponent(monthFilter)}`
        : `${API}/talent/pms/arrears-tracker`;
      const response = await axios.get(url, { headers: getAuthHeader() });
      setArrearsItems(response.data.items || []);
      setArrearsTotals(response.data.totals || { count: 0, total_arrears: 0, total_recovered: 0, total_pending: 0 });
    } catch (error) {
      console.error('Error fetching arrears tracker:', error);
    } finally {
      setArrearsLoading(false);
    }
  }, [getAuthHeader]);

  // NEW: Fetch platforms
  const fetchPlatforms = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/talent/admin/platforms`, {
        headers: getAuthHeader()
      });
      setPlatforms(response.data.platforms || []);
    } catch (error) {
      console.error('Error fetching platforms:', error);
    }
  }, [getAuthHeader]);

  // NEW: Fetch pending batches
  const fetchPendingBatches = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/talent/admin/pending-batches`, {
        headers: getAuthHeader()
      });
      setPendingBatches(response.data.batches || []);
    } catch (error) {
      console.error('Error fetching pending batches:', error);
    }
  }, [getAuthHeader]);

  // NEW: Fetch staff config
  const fetchStaffConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/talent/admin/staff-config`, {
        headers: getAuthHeader()
      });
      setStaffConfig(response.data);
    } catch (error) {
      console.error('Error fetching staff config:', error);
    }
  }, [getAuthHeader]);

  const fetchSlxConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/talent/admin/rider-deduction-config`, { headers: getAuthHeader() });
      setSlxConfig(res.data);
    } catch (e) { /* ignore */ }
  }, [getAuthHeader]);

  // Fetch recovery report data
  const fetchRecoveryReport = useCallback(async () => {
    setRecoveryLoading(true);
    try {
      const params = new URLSearchParams();
      if (recoveryFilter.month) params.append('month', recoveryFilter.month);
      if (recoveryFilter.year) params.append('year', recoveryFilter.year);
      if (recoveryFilter.type) params.append('record_type', recoveryFilter.type);
      const response = await axios.get(`${API}/talent/pms/recovery-report?${params}`, {
        headers: getAuthHeader()
      });
      setRecoveryData(response.data);
    } catch (error) {
      console.error('Error fetching recovery report:', error);
    } finally {
      setRecoveryLoading(false);
    }
  }, [getAuthHeader, recoveryFilter.month, recoveryFilter.year, recoveryFilter.type]);

  // Fetch pending Post Payroll Adjustments
  const fetchAdjustments = useCallback(async () => {
    setAdjustmentsLoading(true);
    try {
      const res = await axios.get(`${API}/talent/payroll-adjustments/pending`, { headers: getAuthHeader() });
      setAdjustmentsList(res.data.adjustments || []);
    } catch (e) {
      console.error('Error fetching pending adjustments:', e);
      toast.error('Failed to load pending adjustments');
    } finally {
      setAdjustmentsLoading(false);
    }
  }, [getAuthHeader]);

  // Open the Apply dialog for a specific adjustment — also fetches that rider's approved payrolls
  const openApplyAdjustmentDialog = useCallback(async (adj) => {
    setAdjustmentApplyDialog({
      open: true, adjustment: adj, payrolls: [], selectedPayrollId: '', loading: true, applying: false,
    });
    try {
      const res = await axios.get(`${API}/talent/payroll-adjustments/payrolls-for-rider/${adj.rider_id}`, { headers: getAuthHeader() });
      const payrolls = res.data.payrolls || [];
      // Auto-select reference payroll if present, otherwise pick the most recent one
      const refId = adj.reference_payroll_id;
      const preselect = (refId && payrolls.find(p => p.id === refId)?.id) || payrolls[0]?.id || '';
      setAdjustmentApplyDialog(prev => ({ ...prev, payrolls, selectedPayrollId: preselect, loading: false }));
    } catch (e) {
      console.error('Error loading payrolls for rider:', e);
      toast.error('Failed to load approved payrolls for this rider');
      setAdjustmentApplyDialog(prev => ({ ...prev, loading: false }));
    }
  }, [getAuthHeader]);

  // Submit Apply
  const submitApplyAdjustment = useCallback(async () => {
    const { adjustment, selectedPayrollId } = adjustmentApplyDialog;
    if (!adjustment || !selectedPayrollId) {
      toast.error('Please select a payroll to apply this adjustment to');
      return;
    }
    setAdjustmentApplyDialog(prev => ({ ...prev, applying: true }));
    try {
      const formData = new FormData();
      formData.append('payroll_id', selectedPayrollId);
      const res = await axios.post(
        `${API}/talent/payroll-adjustments/${adjustment.id}/apply`,
        formData,
        { headers: { ...getAuthHeader() } }
      );
      toast.success(res.data?.message || 'Adjustment applied & salary slip regenerated');
      setAdjustmentApplyDialog({ open: false, adjustment: null, payrolls: [], selectedPayrollId: '', loading: false, applying: false });
      fetchAdjustments();
    } catch (e) {
      console.error('Apply adjustment error:', e);
      toast.error(e?.response?.data?.detail || 'Failed to apply adjustment');
      setAdjustmentApplyDialog(prev => ({ ...prev, applying: false }));
    }
  }, [adjustmentApplyDialog, getAuthHeader, fetchAdjustments]);

  // Cancel a pending adjustment
  const cancelAdjustment = useCallback(async (adj) => {
    if (!window.confirm(`Cancel adjustment of AED ${Number(adj.amount).toFixed(2)} for ${adj.rider_name}?\nThis cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/talent/payroll-adjustments/${adj.id}`, { headers: getAuthHeader() });
      toast.success('Adjustment cancelled');
      fetchAdjustments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to cancel');
    }
  }, [getAuthHeader, fetchAdjustments]);

  // Rider search for manual adjustment — uses a sequence counter so a slow
  // response from an earlier query can't overwrite a newer (or cleared) state.
  // Also debounces keystrokes so we don't fire one API call per character.
  const riderSearchSeqRef = useRef(0);
  const riderSearchTimerRef = useRef(null);
  const searchRidersForAdjustment = useCallback((query) => {
    // Always update the visible text immediately
    setManualAdjustmentDialog(prev => ({ ...prev, rider_search: query }));
    if (!query || query.trim().length < 2) {
      if (riderSearchTimerRef.current) clearTimeout(riderSearchTimerRef.current);
      setManualAdjustmentDialog(prev => ({ ...prev, rider_results: [] }));
      return;
    }
    if (riderSearchTimerRef.current) clearTimeout(riderSearchTimerRef.current);
    riderSearchTimerRef.current = setTimeout(async () => {
      const seq = ++riderSearchSeqRef.current;
      try {
        // Fetch riders AND staff in parallel so the picker covers both realms.
        // Adjustments are keyed by employee_id downstream; the same handler
        // works whether the picked person is a rider or a staff member.
        const [ridersRes, staffRes] = await Promise.all([
          axios.get(`${API}/riders?search=${encodeURIComponent(query)}&limit=10`,
                    { headers: getAuthHeader() }).catch(() => ({ data: [] })),
          axios.get(`${API}/talent/pms/staff/search?search=${encodeURIComponent(query)}&limit=10`,
                    { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        ]);
        if (seq !== riderSearchSeqRef.current) return;
        const riders = (ridersRes.data?.riders || ridersRes.data || []).map(r => ({
          ...r, employee_type: 'rider',
        }));
        const staff = (staffRes.data?.employees || staffRes.data?.staff || staffRes.data || []).map(s => ({
          ...s, employee_type: 'staff',
        }));
        const merged = [...riders, ...staff];
        setManualAdjustmentDialog(prev => {
          if (!prev.rider_search || prev.rider_search.trim().length < 2) {
            return { ...prev, rider_results: [] };
          }
          return { ...prev, rider_results: merged };
        });
      } catch {
        if (seq === riderSearchSeqRef.current) {
          setManualAdjustmentDialog(prev => ({ ...prev, rider_results: [] }));
        }
      }
    }, 300);
  }, [getAuthHeader]);

  // Create manual adjustment
  const submitManualAdjustment = useCallback(async () => {
    const { rider_id, amount, direction, category, reason, selected_payroll_id } = manualAdjustmentDialog;
    if (!rider_id) { toast.error('Please select a rider'); return; }
    if (!selected_payroll_id) { toast.error('Please select the payroll month'); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Amount must be greater than 0'); return; }
    if (!reason || reason.trim().length < 5) { toast.error('Reason must be at least 5 characters'); return; }
    if (!category) { toast.error('Please select a category'); return; }
    setManualAdjustmentDialog(prev => ({ ...prev, saving: true }));
    try {
      const res = await axios.post(`${API}/talent/payroll-adjustments/create`, {
        rider_id,
        amount: amt,
        direction,
        category,
        reason: reason.trim(),
        type: 'manual_adjustment',
        apply_to_payroll_id: selected_payroll_id,
      }, { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } });
      if (res.data?.applied) {
        toast.success(`Adjustment applied. New net salary: AED ${Number(res.data.new_net_salary || 0).toFixed(2)}`);
      } else {
        toast.success('Manual adjustment created (queued as pending)');
      }
      setManualAdjustmentDialog({
        open: false, rider_id: '', rider_search: '', rider_results: [],
        payrolls: [], payrolls_loading: false, selected_payroll_id: '',
        amount: '', direction: 'credit', category: 'add_to_gross', reason: '',
        recovered_items: [], recovered_loading: false, saving: false,
      });
      fetchAdjustments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create adjustment');
      setManualAdjustmentDialog(prev => ({ ...prev, saving: false }));
    }
  }, [manualAdjustmentDialog, getAuthHeader, fetchAdjustments]);

  // Fetch payrolls for the selected rider — called when a rider is picked
  const fetchPayrollsForRider = useCallback(async (rider_id) => {
    if (!rider_id) return;
    setManualAdjustmentDialog(prev => ({ ...prev, payrolls_loading: true, payrolls: [], selected_payroll_id: '' }));
    try {
      const res = await axios.get(`${API}/talent/payroll-adjustments/payrolls-for-rider/${rider_id}`, { headers: getAuthHeader() });
      const payrolls = res.data?.payrolls || [];
      // Prefer the most-recent non-NPP payroll with a non-zero gross/net
      const meaningful = payrolls.find(p => !p.is_npp && (Number(p.gross_salary || 0) > 0 || Number(p.net_salary || 0) !== 0));
      const fallback = payrolls.find(p => Number(p.gross_salary || 0) > 0 || Number(p.net_salary || 0) !== 0);
      const preselect = (meaningful?.id) || (fallback?.id) || (payrolls[0]?.id) || '';
      setManualAdjustmentDialog(prev => ({
        ...prev,
        payrolls,
        payrolls_loading: false,
        selected_payroll_id: preselect,
      }));
    } catch {
      toast.error('Failed to load payrolls for this rider');
      setManualAdjustmentDialog(prev => ({ ...prev, payrolls_loading: false }));
    }
  }, [getAuthHeader]);

  // Fetch recovered items (fines/salik/maintenance) for the selected payroll & category
  const fetchRecoveredItems = useCallback(async (payroll_id, category) => {
    const item_type = (
      category === 'refund_fines' ? 'fines' :
      category === 'refund_salik' ? 'salik' :
      category === 'refund_maintenance' ? 'maintenance' :
      null
    );
    if (!payroll_id || !item_type) {
      setManualAdjustmentDialog(prev => ({ ...prev, recovered_items: [], recovered_loading: false }));
      return;
    }
    setManualAdjustmentDialog(prev => ({ ...prev, recovered_loading: true, recovered_items: [] }));
    try {
      const res = await axios.get(
        `${API}/talent/payroll-adjustments/recovered-items/${payroll_id}?item_type=${item_type}`,
        { headers: getAuthHeader() }
      );
      setManualAdjustmentDialog(prev => ({
        ...prev,
        recovered_items: res.data?.items || [],
        recovered_loading: false,
      }));
    } catch {
      setManualAdjustmentDialog(prev => ({ ...prev, recovered_loading: false, recovered_items: [] }));
    }
  }, [getAuthHeader]);

  // Fetch adjustment categories (credit & debit)
  const fetchAdjustmentCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/talent/payroll-adjustments/categories`, { headers: getAuthHeader() });
      setAdjustmentCategories(res.data || { credit: [], debit: [] });
    } catch {
      // Fallback to a minimal hardcoded set so the dropdown still works
      setAdjustmentCategories({
        credit: [{ value: 'add_to_gross', label: 'Add to Gross Salary' }],
        debit: [{ value: 'misc_deduction', label: 'Miscellaneous Deduction' }],
      });
    }
  }, [getAuthHeader]);

  // When the selected payroll OR category changes, refetch recovered items
  // (only matters for refund_fines/salik/maintenance).
  useEffect(() => {
    if (!manualAdjustmentDialog.open) return;
    fetchRecoveredItems(manualAdjustmentDialog.selected_payroll_id, manualAdjustmentDialog.category);
  }, [manualAdjustmentDialog.selected_payroll_id, manualAdjustmentDialog.category, manualAdjustmentDialog.open, fetchRecoveredItems]);

  // Client-side filter for additional fields (platform, rider, plate, ref)
  const getFilteredRecoveryRecords = useCallback(() => {
    const allRecords = (recoveryData.groups || []).flatMap(g => g.records || []);
    return allRecords.filter(r => {
      if (recoveryFilter.platform && (r.platform || '') !== recoveryFilter.platform) return false;
      if (recoveryFilter.rider && (r.rider_id || r.rider_name || '') !== recoveryFilter.rider) return false;
      if (recoveryFilter.plate && (r.plate || '') !== recoveryFilter.plate) return false;
      if (recoveryFilter.ref && (r.ref_no || '') !== recoveryFilter.ref) return false;
      return true;
    });
  }, [recoveryData, recoveryFilter]);

  // Get filtered groups for display
  const getFilteredRecoveryGroups = useCallback(() => {
    const hasClientFilter = recoveryFilter.platform || recoveryFilter.rider || recoveryFilter.plate || recoveryFilter.ref;
    if (!hasClientFilter) return recoveryData.groups || [];

    return (recoveryData.groups || []).map(g => {
      const filtered = (g.records || []).filter(r => {
        if (recoveryFilter.platform && (r.platform || '') !== recoveryFilter.platform) return false;
        if (recoveryFilter.rider && (r.rider_id || r.rider_name || '') !== recoveryFilter.rider) return false;
        if (recoveryFilter.plate && (r.plate || '') !== recoveryFilter.plate) return false;
        if (recoveryFilter.ref && (r.ref_no || '') !== recoveryFilter.ref) return false;
        return true;
      });
      return { ...g, records: filtered, total_amount: filtered.reduce((s, r) => s + (r.amount || 0), 0) };
    }).filter(g => g.records.length > 0);
  }, [recoveryData, recoveryFilter]);

  // Build unique option lists for the recovery filter dropdowns
  const getRecoveryFilterOptions = useCallback(() => {
    const allRecords = (recoveryData.groups || []).flatMap(g => g.records || []);
    const platforms = new Set();
    const ridersMap = new Map(); // key (rider_id or rider_name) -> label
    const plates = new Set();
    const refs = new Set();
    allRecords.forEach(r => {
      if (r.platform) platforms.add(r.platform);
      const riderKey = r.rider_id || r.rider_name;
      if (riderKey) {
        const label = r.rider_name && r.rider_id
          ? `${r.rider_name} (${r.rider_id})`
          : (r.rider_name || r.rider_id);
        if (!ridersMap.has(riderKey)) ridersMap.set(riderKey, label);
      }
      if (r.plate) plates.add(r.plate);
      if (r.ref_no && r.ref_no !== '-') refs.add(r.ref_no);
    });
    return {
      platforms: Array.from(platforms).sort(),
      riders: Array.from(ridersMap.entries())
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label)),
      plates: Array.from(plates).sort(),
      refs: Array.from(refs).sort(),
    };
  }, [recoveryData]);

  // Download salary calculation as Excel
  const downloadSalaryCalculationExcel = (riderName, riderData, calculationData) => {
    const wb = XLSX.utils.book_new();
    
    // Build data rows
    const data = [
      ['SALARY CALCULATION'],
      ['Employee Name', riderName],
      [''],
      ['BASE CALCULATION'],
    ];
    
    if (calculationData?.vehicle_type) {
      data.push(['Vehicle Type', calculationData.vehicle_type]);
    }
    if (calculationData?.rate_per_order !== undefined && calculationData?.rate_per_order !== null) {
      data.push(['Rate per Order', `AED ${calculationData.rate_per_order}`]);
    }
    if (calculationData?.orders !== undefined) {
      data.push(['Orders', calculationData.orders]);
    }
    if (calculationData?.orders_amount !== undefined) {
      data.push(['Orders Amount', `AED ${Number(calculationData.orders_amount || 0).toFixed(2)}`]);
    }
    
    data.push(['']);
    data.push(['ADDITIONAL COMPONENTS']);
    
    // Iterate additional_components (list) FIRST — Talabat/Noon/Keeta shape.
    // Custom AI labels like "Rider Return Trips Payment" render verbatim.
    const _toLabelXL = (k) => k
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
    const _shownLabels = new Set();
    const ac = calculationData?.additional_components;
    if (Array.isArray(ac)) {
      ac.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        const label = item.label;
        const num = parseFloat(item.value);
        if (label && !isNaN(num)) {
          const sign = item.type === 'currency_sub' ? '-' : '+';
          data.push([label, `${sign} AED ${num.toFixed(2)}`]);
          _shownLabels.add(label);
        }
      });
    }
    // Only fall back to base_components / legacy fields when
    // additional_components was empty — prevents duplicate rows.
    if (_shownLabels.size === 0) {
      const bc = calculationData?.base_components;
      if (bc && typeof bc === 'object' && !Array.isArray(bc)) {
        Object.entries(bc).forEach(([k, v]) => {
          if (k === 'orders_amount') return;
          const num = parseFloat(v);
          if (!isNaN(num) && num !== 0 && !_shownLabels.has(_toLabelXL(k))) {
            const label = _toLabelXL(k);
            data.push([label, `+ AED ${num.toFixed(2)}`]);
            _shownLabels.add(label);
          }
        });
      }
      const _legacyXL = [
        ['Deliveries Return (LC)', calculationData?.return_lc],
        ['Distance (LC)', calculationData?.distance_lc],
        ['Riders Incentives', calculationData?.incentives],
        ['Tips', calculationData?.tips],
      ];
      _legacyXL.forEach(([label, val]) => {
        const num = parseFloat(val);
        if (!isNaN(num) && num !== 0 && !_shownLabels.has(label)) {
          data.push([label, `+ AED ${num.toFixed(2)}`]);
        }
      });
    }
    
    data.push(['']);
    data.push(['FORMULA APPLIED']);
    if (calculationData?.formula) data.push(['Formula', calculationData.formula]);
    data.push(['Gross Salary', `AED ${Number(riderData?.gross_salary || calculationData?.gross_salary || 0).toFixed(2)}`]);
    
    data.push(['']);
    data.push(['DEDUCTIONS']);
    const _isCareemSplit = (calculationData?.formula_type || '') === 'careem_eligible_split';
    // Noon Minutes: Tips is an earning line, not a deduction. Zero it
    // out on the XLSX export so finance's archive agrees with the UI.
    // Oct-2026: FUZZY detection — platform may be stored as
    // "Noon-Minutes", "noon_minutes", "Noon Food (Minutes)" or blank
    // with the NM formula signature. We fall back to formula-shape and
    // NM-only breakdown keys so legacy rows self-heal.
    const _tipsForXL = _isNoonMinutes(calculationData, riderData) ? 0 : riderData?.paid_as_tips;
    const _dedXL = [
      [_isCareemSplit ? 'Advance Paid' : 'Paid in Wallet', riderData?.paid_in_wallet],
      ['Paid as Tips', _tipsForXL],
      [_isCareemSplit ? 'COD' : 'Paid as COD', riderData?.paid_as_cod],
      ['Clawback Deduction', riderData?.clawback_deduction],
      ['Platform Deduction', riderData?.platform_deduction],
      ['Food Compensation', riderData?.food_compensation],
      ['Room Rent', riderData?.room_rent],
      ['SLX Charge', riderData?.slx_deduction],
      ['Fines', riderData?.fines],
      ['SALIK', riderData?.salik],
      ['Maintenance', riderData?.maintenance],
      ['Loan EMI', riderData?.loan_emi],
      ['Previous Month Arrears', riderData?.prev_arrears],
      [riderData?.misc_deduction_desc || 'Miscellaneous', riderData?.misc_deduction],
      ['Salary Advance', riderData?.salary_advance],
      ['Other Deductions', riderData?.other_deductions],
    ];
    _dedXL.forEach(([label, val]) => {
      const num = parseFloat(val);
      if (!isNaN(num) && num > 0) {
        data.push([label, `- AED ${num.toFixed(2)}`]);
      }
    });
    // Feb-2026: sign-tagged custom deductions from AI COLUMN MAPPINGS
    // (skip labels that collide with hardcoded inline rows above so stale
    // additional_deductions items don't double-count on the Excel export).
    const _skipDedLabelsXL = new Set([
      'platform deduction', 'deduction',
      'food compensation', 'food_compensation',
      'fines', 'salik', 'maintenance', 'loan emi', 'loan_emi',
      'slx charge', 'slx',
      'paid in wallet', 'paid as tips', 'paid as cod',
      'clawback deduction',
    ]);
    const _addDedXL = calculationData?.additional_deductions;
    if (Array.isArray(_addDedXL)) {
      _addDedXL.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        const num = parseFloat(item.value);
        const lblLc = String(item.label || '').trim().toLowerCase();
        if (item.label && !isNaN(num) && num > 0 && !_skipDedLabelsXL.has(lblLc)) {
          data.push([item.label, `- AED ${num.toFixed(2)}`]);
        }
      });
    }
    data.push(['Total Deductions', `- AED ${Number(riderData?.total_deductions || 0).toFixed(2)}`]);
    
    data.push(['']);
    data.push(['NET SALARY', `AED ${Number(riderData?.net_salary || 0).toFixed(2)}`]);
    
    const ws = XLSX.utils.aoa_to_sheet(data);
    
    // Set column widths
    ws['!cols'] = [{ wch: 25 }, { wch: 20 }];
    
    XLSX.utils.book_append_sheet(wb, ws, 'Salary Calculation');
    
    // Generate filename
    const fileName = `Salary_Calculation_${riderName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success('Excel downloaded successfully');
  };

  // Download salary calculation as PDF
  const downloadSalaryCalculationPDF = (riderName, riderData, calculationData) => {
    const doc = new jsPDF();
    
    // Title
    doc.setFontSize(18);
    doc.setTextColor(30, 64, 175); // Blue
    doc.text('Salary Calculation', 105, 20, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(riderName, 105, 30, { align: 'center' });
    
    let yPos = 45;

    // Detect staff payroll (presence of attendance metadata)
    const _staffAtt = calculationData?.staff_attendance;
    const _staffFull = calculationData?.staff_full_components;
    const _isStaffCalc = !!_staffAtt;

    // ----- STAFF: Attendance & Pro-rata breakdown (replaces the order/rate
    // "BASE CALCULATION" section that's specific to riders) -----
    if (_isStaffCalc) {
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text('ATTENDANCE & PRO-RATA', 14, yPos);
      yPos += 8;

      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);

      const wd = Number(_staffAtt.working_days || 0);
      const pd = Number(_staffAtt.present_days || 0);
      const ad = Number(_staffAtt.absent_days || 0);
      const od = Number(_staffAtt.overtime_days || 0);
      const ratio = Number(_staffAtt.attendance_ratio || 0);
      const monthlySalary = Number(_staffAtt.monthly_salary || 0);
      const otDivisor = Number(_staffAtt.overtime_base_divisor || 30);
      const otMult = Number(_staffAtt.overtime_rate_multiplier || 1.5);
      const otDaily = Number(_staffAtt.overtime_daily_rate || 0);

      doc.text(`Configured Monthly Salary: AED ${monthlySalary.toFixed(2)}`, 14, yPos); yPos += 6;
      doc.text(`Working Days in Month: ${wd}`, 14, yPos); yPos += 6;
      doc.text(`Present Days: ${pd}      Absent Days: ${ad}      Overtime Days: ${od}`, 14, yPos); yPos += 6;
      doc.text(`Pro-rata Ratio: ${pd}/${wd} = ${ratio.toFixed(4)}`, 14, yPos); yPos += 6;
      doc.text(`Overtime Rate: (AED ${monthlySalary.toFixed(2)} / ${otDivisor}) × ${otMult}× = AED ${(otDaily * otMult).toFixed(2)} per OT day`, 14, yPos); yPos += 10;

      // Full-month components → pro-rated breakdown
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text('STEP-BY-STEP COMPONENTS', 14, yPos);
      yPos += 8;

      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);

      const rows = [];
      if (_staffFull) {
        const fb = Number(_staffFull.basic || 0);
        const fh = Number(_staffFull.housing || 0);
        const ft = Number(_staffFull.transport || 0);
        const fp = Number(_staffFull.telephone || 0);
        if (fb > 0) rows.push(['Basic Salary', fb, fb * ratio]);
        if (fh > 0) rows.push(['Housing Allowance', fh, fh * ratio]);
        if (ft > 0) rows.push(['Transport Allowance', ft, ft * ratio]);
        if (fp > 0) rows.push(['Telephone Allowance', fp, fp * ratio]);
        (_staffFull.custom_allowances || []).forEach((ca) => {
          const amt = Number(ca.full_amount || 0);
          if (amt > 0) rows.push([ca.description || 'Allowance', amt, amt * ratio]);
        });
      }

      // Header row
      doc.setTextColor(80, 80, 80);
      doc.text('Component', 14, yPos);
      doc.text('Full Month', 95, yPos, { align: 'right' });
      doc.text(`× ${pd}/${wd}`, 130, yPos, { align: 'right' });
      doc.text('Pro-rated', 175, yPos, { align: 'right' });
      yPos += 5;
      doc.setDrawColor(200, 200, 200);
      doc.line(14, yPos - 2, 195, yPos - 2);

      doc.setTextColor(0, 0, 0);
      let proRataSum = 0;
      rows.forEach(([label, full, pro]) => {
        proRataSum += pro;
        doc.text(label, 14, yPos);
        doc.text(`AED ${Number(full).toFixed(2)}`, 95, yPos, { align: 'right' });
        doc.text(`× ${ratio.toFixed(4)}`, 130, yPos, { align: 'right' });
        doc.text(`AED ${Number(pro).toFixed(2)}`, 175, yPos, { align: 'right' });
        yPos += 5;
      });

      // OT row (if any)
      const otPay = Number(_staffAtt.overtime_pay || 0);
      if (otPay > 0) {
        doc.text(`Overtime Pay (${od} day${od === 1 ? '' : 's'})`, 14, yPos);
        doc.text(`AED ${(otDaily * otMult).toFixed(2)}`, 95, yPos, { align: 'right' });
        doc.text(`× ${od}`, 130, yPos, { align: 'right' });
        doc.text(`AED ${otPay.toFixed(2)}`, 175, yPos, { align: 'right' });
        yPos += 5;
      }

      // Sub-total row
      doc.setDrawColor(200, 200, 200);
      doc.line(14, yPos - 1, 195, yPos - 1);
      yPos += 1;
      doc.setFontSize(10);
      doc.setTextColor(34, 197, 94);
      doc.text('Subtotal (Pro-rata + OT)', 14, yPos);
      doc.text(`AED ${(proRataSum + otPay).toFixed(2)}`, 175, yPos, { align: 'right' });
      yPos += 10;
      doc.setTextColor(0, 0, 0);
    } else {
      // ----- Rider/PMS: original BASE CALCULATION block -----
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text('BASE CALCULATION', 14, yPos);
      yPos += 8;

      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);

      if (calculationData?.vehicle_type) {
        doc.text(`Vehicle Type: ${calculationData.vehicle_type}`, 14, yPos); yPos += 6;
      }
      if (calculationData?.rate_per_order !== undefined && calculationData?.rate_per_order !== null) {
        doc.text(`Rate per Order: AED ${calculationData.rate_per_order}`, 14, yPos); yPos += 6;
      }
      if (calculationData?.orders !== undefined) {
        doc.text(`Orders: ${calculationData.orders}`, 14, yPos); yPos += 6;
      }
      if (calculationData?.orders_amount !== undefined) {
        doc.setTextColor(34, 197, 94); // Green
        doc.text(`Orders Amount: AED ${Number(calculationData.orders_amount || 0).toFixed(2)}`, 14, yPos); yPos += 10;
        doc.setTextColor(0, 0, 0);
      } else {
        yPos += 4;
      }
    }

    // Additional Components (Pro-rated totals — same section for both staff & rider)
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(12);
    doc.text(_isStaffCalc ? 'PRO-RATED EARNINGS' : 'ADDITIONAL COMPONENTS', 14, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    
    // Build a unified label-value list of earning components from
    // calculation_breakdown so platform-specific keys (Talabat, Keeta, Noon...)
    // all render. Skip the orders_amount key — already shown above.
    const _toLabel = (k) => k
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
    const _earningEntries = [];
    // Preferred: `additional_components` list (Talabat/Noon/Keeta shape) —
    // pulled BEFORE the legacy `base_components` dict so custom AI labels
    // like "Rider Return Trips Payment" / "Hurrier Rider Wallet Payment"
    // render verbatim on the salary calculation PDF. Each entry carries a
    // `type` of "currency_add" (default) or "currency_sub" — the latter
    // renders as `- AED X` so subtracted components (e.g. Rider Incentive
    // under the Feb-2026 revised Talabat formula) show a minus sign.
    const ac = calculationData?.additional_components;
    if (Array.isArray(ac)) {
      ac.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        const label = item.label;
        const val = parseFloat(item.value);
        if (label && !isNaN(val)) {
          _earningEntries.push([label, val, item.type || 'currency_add']);
        }
      });
    }
    // Only fall back to `base_components` / legacy keys when the
    // authoritative additional_components list wasn't populated — this
    // prevents duplicate lines like "Rider Return Trips Payment" and
    // "Deliveries Return (LC)" both showing on the same PDF.
    if (_earningEntries.length === 0) {
      const bc = calculationData?.base_components;
      if (bc && typeof bc === 'object' && !Array.isArray(bc)) {
        Object.entries(bc).forEach(([k, v]) => {
          if (k === 'orders_amount') return; // shown in base section
          const num = parseFloat(v);
          if (!isNaN(num) && num !== 0) {
            _earningEntries.push([_toLabel(k), num, 'currency_add']);
          }
        });
      }
      // Backward-compat: legacy Talabat fields that may sit on calculationData root
      const _legacyEarnings = [
        ['Deliveries Return (LC)', calculationData?.return_lc],
        ['Distance (LC)', calculationData?.distance_lc],
        ['Riders Incentives', calculationData?.incentives],
        ['Tips', calculationData?.tips],
      ];
      _legacyEarnings.forEach(([label, val]) => {
        const num = parseFloat(val);
        if (!isNaN(num) && num !== 0 && !_earningEntries.find(([l]) => l === label)) {
          _earningEntries.push([label, num, 'currency_add']);
        }
      });
    }
    if (_earningEntries.length === 0) {
      doc.setTextColor(150, 150, 150);
      doc.text('(none)', 14, yPos); yPos += 6;
      doc.setTextColor(0, 0, 0);
    } else {
      _earningEntries.forEach(([label, val, type]) => {
        const sign = type === 'currency_sub' ? '-' : '+';
        doc.text(`${label}: ${sign} AED ${Number(val).toFixed(2)}`, 14, yPos); yPos += 6;
      });
    }
    yPos += 4;
    
    // Formula
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(12);
    doc.text('FORMULA APPLIED', 14, yPos);
    yPos += 8;
    
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    if (calculationData?.formula) {
      doc.text(calculationData.formula, 14, yPos);
      yPos += 8;
    }
    
    doc.setFontSize(12);
    doc.setTextColor(34, 197, 94); // Green
    doc.text(`Gross Salary: AED ${Number(riderData?.gross_salary || calculationData?.gross_salary || 0).toFixed(2)}`, 14, yPos);
    yPos += 12;
    
    // Deductions
    doc.setTextColor(220, 38, 38); // Red
    doc.setFontSize(12);
    doc.text('DEDUCTIONS', 14, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    const _isCareemSplitPdf = (calculationData?.formula_type || '') === 'careem_eligible_split';
    // Noon Minutes bakes Tips into GROSS as an earning — never deduct
    // it. Force zero on the Tips line regardless of what the stored
    // row holds (handles old slips written before the backend fix).
    // Oct-2026: FUZZY detection via `_isNoonMinutes` helper.
    const _tipsForPdf = _isNoonMinutes(calculationData, riderData) ? 0 : riderData?.paid_as_tips;
    const _deductionLines = [
      [_isCareemSplitPdf ? 'Advance Paid' : 'Paid in Wallet', riderData?.paid_in_wallet],
      ['Paid as Tips', _tipsForPdf],
      [_isCareemSplitPdf ? 'COD' : 'Paid as COD', riderData?.paid_as_cod],
      ['Clawback Deduction', riderData?.clawback_deduction],
      ['Platform Deduction', riderData?.platform_deduction],
      ['Food Compensation', riderData?.food_compensation],
      ['Room Rent', riderData?.room_rent],
      ['SLX Charge', riderData?.slx_deduction],
      ['Fines', riderData?.fines],
      ['SALIK', riderData?.salik],
      ['Maintenance', riderData?.maintenance],
      ['Loan EMI', riderData?.loan_emi],
      ['Previous Month Arrears', riderData?.prev_arrears],
      [riderData?.misc_deduction_desc || 'Miscellaneous', riderData?.misc_deduction],
      ['Salary Advance', riderData?.salary_advance],
      ['Other Deductions', riderData?.other_deductions],
    ];
    _deductionLines.forEach(([label, val]) => {
      const num = parseFloat(val);
      if (!isNaN(num) && num > 0) {
        doc.text(`${label}: - AED ${num.toFixed(2)}`, 14, yPos); yPos += 6;
      }
    });

    // Feb-2026: sign-tagged custom deductions from AI COLUMN MAPPINGS —
    // Any "Coach_Fee = Coach Fee | deduction" style mapping shows up
    // here automatically alongside the hardcoded deductions.
    // Feb-2026 (revised): skip items whose label collides with a
    // hardcoded inline row above (Platform Deduction / Food Compensation
    // / Fines / SALIK / etc.) so a stale duplicate can never
    // double-count on the salary calculation PDF.
    const _skipDedLabels = new Set([
      'platform deduction', 'deduction',
      'food compensation', 'food_compensation',
      'fines', 'salik', 'maintenance', 'loan emi', 'loan_emi',
      'slx charge', 'slx',
      'paid in wallet', 'paid as tips', 'paid as cod',
      'clawback deduction',
    ]);
    const _addDed = calculationData?.additional_deductions;
    if (Array.isArray(_addDed)) {
      _addDed.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        const num = parseFloat(item.value);
        const lblLc = String(item.label || '').trim().toLowerCase();
        if (item.label && !isNaN(num) && num > 0 && !_skipDedLabels.has(lblLc)) {
          doc.text(`${item.label}: - AED ${num.toFixed(2)}`, 14, yPos); yPos += 6;
        }
      });
    }
    
    doc.setTextColor(220, 38, 38);
    doc.text(`Total Deductions: - AED ${Number(riderData?.total_deductions || 0).toFixed(2)}`, 14, yPos);
    yPos += 12;
    
    // Net Salary
    doc.setFontSize(14);
    doc.setTextColor(30, 64, 175); // Blue
    doc.text(`NET SALARY: AED ${Number(riderData?.net_salary || 0).toFixed(2)}`, 14, yPos);
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, 105, 285, { align: 'center' });
    
    // Generate filename and save
    const fileName = `Salary_Calculation_${riderName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(fileName);
    toast.success('PDF downloaded successfully');
  };

  useEffect(() => {
    fetchEmployees();
    fetchEligibleRiders();
    fetchLessees();
    fetchPlatformRates();
    fetchLoanTypes();
    fetchSwapLocations();
  }, [fetchEmployees, fetchEligibleRiders, fetchLessees, fetchPlatformRates, fetchSwapLocations]);
  
  // Fetch loan summary when accounts tab is active. Also re-fetch the
  // employees list WITH loan stitching (the mount-time fetch is `slim`)
  // so the Employee Accounts table has loan_balance / total_loan /
  // total_recovery available for the outstanding + progress columns.
  useEffect(() => {
    if (activeSubPage === 'accounts') {
      fetchLoanSummary();
      fetchEmployees({ withLoans: true });
    }
  }, [activeSubPage, fetchLoanSummary, fetchEmployees]);
  
  // Fetch loan types
  const fetchLoanTypes = async () => {
    try {
      const response = await axios.get(`${API}/talent/loan-types`, {
        headers: getAuthHeader()
      });
      setLoanTypes(response.data.loan_types || {});
    } catch (error) {
      console.error('Error fetching loan types:', error);
    }
  };

  useEffect(() => {
    if (activeSubPage === 'payroll') {
      fetchMonthlyPayrolls();
      fetchSalarySlips();
      fetchPayrollDashboard();
      fetchApprovedPayrollLog();
      fetchPlatforms();
      fetchPayrollDrafts();
      fetchArrearsTracker(arrearsMonth);
      // The Pending Approval Log card lives on this sub-page, so it needs
      // the same fetch as the admin sub-page (where the data was previously
      // only loaded).
      fetchPendingBatches();
    }
    if (activeSubPage === 'admin') {
      fetchPendingApprovals();
      fetchPlatformRates();
      fetchPlatforms();
      fetchPendingBatches();
      fetchStaffConfig();
      fetchSlxConfig();
      fetchHeldSalaries();
      // PMS Admin: load current state of the formula-driven parser toggle
      (async () => {
        try {
          const r = await axios.get(`${API}/talent/admin/payroll-settings`, { headers: getAuthHeader() });
          setFormulaFallback(r.data?.formula_fallback || null);
        } catch (_e) { /* surfaced on toggle attempt */ }
      })();
    }
  }, [activeSubPage, fetchMonthlyPayrolls, fetchSalarySlips, fetchPendingApprovals, fetchPlatformRates, fetchPayrollDashboard, fetchApprovedPayrollLog, fetchPlatforms, fetchPendingBatches, fetchStaffConfig, fetchArrearsTracker, arrearsMonth]);

  useEffect(() => {
    if (activeSubPage === 'recovery') {
      fetchRecoveryReport();
    }
  }, [activeSubPage, fetchRecoveryReport]);

  useEffect(() => {
    if (activeSubPage === 'adjustments') {
      fetchAdjustments();
      fetchAdjustmentCategories();
    }
  }, [activeSubPage, fetchAdjustments, fetchAdjustmentCategories]);

  // Fetch payroll drafts when wizard opens
  useEffect(() => {
    if (payrollWizardOpen) {
      fetchPayrollDrafts();
    }
  }, [payrollWizardOpen]);

  // Handlers
  const handleEnrollRider = async () => {
    if (!enrollForm.rider_id) {
      toast.error('Please select a rider');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('rider_id', enrollForm.rider_id);
      formData.append('initial_loan_amount', enrollForm.initial_loan_amount || 0);
      formData.append('monthly_emi', enrollForm.monthly_emi || 0);
      formData.append('opening_balance', 0);
      formData.append('opening_balance_type', 'neutral');

      await axios.post(`${API}/talent/enroll-rider`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Rider enrolled successfully');
      setEnrollRiderDialogOpen(false);
      setEnrollForm({ rider_id: '', initial_loan_amount: 0, monthly_emi: 0 });
      fetchEmployees();
      fetchEligibleRiders();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to enroll rider'));
    }
  };

  const handleAddStaff = async () => {
    if (!staffForm.name || !staffForm.emirates_id || !staffForm.email || !staffForm.employer_name || !staffForm.joining_date || !staffForm.basic_salary) {
      toast.error('Please fill all required fields including Basic Salary');
      return;
    }

    try {
      const formData = new FormData();
      Object.entries(staffForm).forEach(([key, value]) => {
        if (key === 'custom_allowances') {
          // Serialize custom_allowances array as JSON string
          formData.append(key, JSON.stringify(value || []));
        } else if (value !== '' && value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });

      await axios.post(`${API}/talent/staff`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Staff employee added successfully');
      setAddStaffDialogOpen(false);
      setStaffForm({
        name: '', emirates_id: '', emirates_id_expiry: '', passport_number: '',
        passport_expiry: '', email: '', mobile: '', employer_name: '',
        joining_date: '', monthly_salary: '', designation: '', edenred_id: '', nationality: '',
        basic_salary: '', housing_allowance: '', transport_allowance: '', telephone_allowance: '', pension_amount: '',
        custom_allowances: [], room_rent: ''
      });
      fetchEmployees();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to add staff'));
    }
  };

  // Handle Edit Employee action from dropdown
  const handleEditEmployee = async (employee) => {
    if (employee.employee_type === 'staff') {
      // The Employees list uses `slim=true` for perf — that response
      // OMITS every salary field (basic/housing/transport/telephone/
      // pension/room_rent/custom_allowances/salary_history). If we open
      // the edit dialog with just the list object, every salary input
      // renders blank and the operator has no way to see the existing
      // values. Fetch the full staff record first so the form is
      // pre-populated with the exact values on disk.
      let enriched = { ...employee };
      try {
        const detailRes = await axios.get(
          `${API}/talent/employee/${employee.id}`,
          { headers: getAuthHeader() },
        );
        if (detailRes.data?.employee) {
          // The detail endpoint wraps the raw staff doc under
          // `.employee` — merge it over the list snapshot so any list-
          // only enrichment (loan_account_id, lessee_name display name)
          // is preserved but salary fields win.
          enriched = { ...enriched, ...detailRes.data.employee };
        }
      } catch (_e) {
        // Silent — the dialog still opens with whatever we have. If the
        // fetch fails the fields will be blank, matching the old
        // behaviour. A toast would be too noisy here.
      }
      if (!enriched.joining_date) {
        try {
          const r = await axios.get(
            `${API}/talent/staff/${employee.id}/joining-date-suggestion`,
            { headers: getAuthHeader() },
          );
          if (r.data?.suggested_joining_date) {
            enriched._suggested_joining_date = r.data.suggested_joining_date;
          }
        } catch (_e) { /* silent — field just stays blank */ }
      }
      setEditingStaff(enriched);
      // Snapshot the salary fields as they were at open-time so the
      // Save action can diff them and pop a confirmation modal listing
      // every changed allowance ("Basic 15,000 → 17,500 · Housing unchanged").
      setOriginalStaffSalary({
        basic_salary: enriched.basic_salary || 0,
        housing_allowance: enriched.housing_allowance || 0,
        transport_allowance: enriched.transport_allowance || 0,
        telephone_allowance: enriched.telephone_allowance || 0,
        pension_amount: enriched.pension_amount || 0,
        room_rent: enriched.room_rent || 0,
        custom_allowances: JSON.parse(JSON.stringify(enriched.custom_allowances || [])),
      });
      setEditStaffDialogOpen(true);
    } else if (employee.tap_candidate_id) {
      // For riders enrolled from TAP, redirect to TAP for editing
      setSelectedEmployee(employee);
      setRiderRedirectDialogOpen(true);
    } else {
      // For existing riders without TAP record, allow direct editing
      setEditingStaff(employee);
      setEditStaffDialogOpen(true);
    }
  };

  // Update staff employee — thin entry point that guards salary changes
  // with a confirmation modal. Actual PUT lives in `_persistStaffUpdate`
  // so the modal's "Confirm & Save" can call the writer directly and
  // bypass the guard (avoids the stale-closure trap where re-entering
  // `handleUpdateStaff` via setTimeout would re-detect the diff and
  // re-pop the modal in a loop).
  const handleUpdateStaff = async () => {
    if (!editingStaff) return;
    if (originalStaffSalary) {
      const SALARY_FIELDS = [
        ['basic_salary', 'Basic Salary'],
        ['housing_allowance', 'Housing Allowance'],
        ['transport_allowance', 'Transport Allowance'],
        ['telephone_allowance', 'Telephone Allowance'],
        ['pension_amount', 'Pension (GPSSA)'],
        ['room_rent', 'Room Rent'],
      ];
      const changes = [];
      for (const [key, label] of SALARY_FIELDS) {
        const oldV = parseFloat(originalStaffSalary[key]) || 0;
        const newV = parseFloat(editingStaff[key]) || 0;
        if (oldV !== newV) changes.push({ label, old: oldV, new: newV });
      }
      const oldCustom = JSON.stringify(originalStaffSalary.custom_allowances || []);
      const newCustom = JSON.stringify(
        (editingStaff.custom_allowances || []).map(a => ({
          description: a.description || '',
          amount: parseFloat(a.amount) || 0,
        })),
      );
      if (oldCustom !== newCustom) {
        changes.push({
          label: 'Custom Allowances',
          old: JSON.parse(oldCustom),
          new: JSON.parse(newCustom),
          isCustom: true,
        });
      }
      if (changes.length > 0) {
        setSalaryDiffModal({ open: true, changes });
        return; // wait for user confirm — modal's onClick calls _persistStaffUpdate
      }
    }
    await _persistStaffUpdate();
  };

  const _persistStaffUpdate = async () => {
    if (!editingStaff) return;
    try {
      const formData = new FormData();
      if (editingStaff.name) formData.append('name', editingStaff.name);
      if (editingStaff.emirates_id) formData.append('emirates_id', editingStaff.emirates_id);
      if (editingStaff.emirates_id_expiry) formData.append('emirates_id_expiry', editingStaff.emirates_id_expiry);
      if (editingStaff.passport_number) formData.append('passport_number', editingStaff.passport_number);
      if (editingStaff.passport_expiry) formData.append('passport_expiry', editingStaff.passport_expiry);
      if (editingStaff.email) formData.append('email', editingStaff.email);
      if (editingStaff.mobile) formData.append('mobile', editingStaff.mobile);
      // Always recompute monthly_salary from the visible breakdown fields
      // so we never write a stale flat total when the admin edits allowances.
      const _basic = parseFloat(editingStaff.basic_salary) || 0;
      const _housing = parseFloat(editingStaff.housing_allowance) || 0;
      const _transport = parseFloat(editingStaff.transport_allowance) || 0;
      const _telephone = parseFloat(editingStaff.telephone_allowance) || 0;
      const _pension = parseFloat(editingStaff.pension_amount) || 0;
      const _custom = (editingStaff.custom_allowances || []).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
      formData.append('monthly_salary', String(_basic + _housing + _transport + _telephone + _pension + _custom));
      if (editingStaff.location) formData.append('location', editingStaff.location);
      if (editingStaff.basic_salary) formData.append('basic_salary', editingStaff.basic_salary);
      if (editingStaff.housing_allowance) formData.append('housing_allowance', editingStaff.housing_allowance);
      if (editingStaff.transport_allowance) formData.append('transport_allowance', editingStaff.transport_allowance);
      if (editingStaff.telephone_allowance) formData.append('telephone_allowance', editingStaff.telephone_allowance);
      if (editingStaff.pension_amount !== undefined && editingStaff.pension_amount !== null && editingStaff.pension_amount !== '') {
        formData.append('pension_amount', editingStaff.pension_amount);
      }
      if (editingStaff.custom_allowances) formData.append('custom_allowances', JSON.stringify(editingStaff.custom_allowances || []));
      if (editingStaff.room_rent !== undefined && editingStaff.room_rent !== null) formData.append('room_rent', editingStaff.room_rent);
      if (editingStaff.designation) formData.append('designation', editingStaff.designation);
      if (editingStaff.edenred_id) formData.append('edenred_id', editingStaff.edenred_id);
      if (editingStaff.lessee_name) formData.append('lessee_name', editingStaff.lessee_name);
      if (editingStaff.platform) formData.append('platform', editingStaff.platform);
      formData.append('joining_date', editingStaff.joining_date || '');
      formData.append('exit_date', editingStaff.exit_date || '');
      if (editingStaff.salary_history) {
        formData.append('salary_history', JSON.stringify(editingStaff.salary_history || []));
      }

      await axios.put(`${API}/talent/staff/${editingStaff.id}`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Staff employee updated successfully');
      setEditStaffDialogOpen(false);
      setEditingStaff(null);
      setOriginalStaffSalary(null);
      setSalaryDiffModal({ open: false, changes: [] });
      fetchEmployees();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to update staff'));
      // Keep modal closed so user isn't stuck; edit dialog stays open
      // so they can retry or hit Cancel.
      setSalaryDiffModal({ open: false, changes: [] });
    }
  };

  const handleUploadDocument = async (employeeId, docType, file, docName = null) => {
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('doc_type', docType);
      formData.append('file', file);
      if (docName) {
        formData.append('doc_name', docName);
      }

      await axios.post(`${API}/talent/employee/${employeeId}/document`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Document uploaded successfully');
      fetchEmployees();
      if (selectedEmployee?.id === employeeId) {
        fetchEmployeeDetails(employeeId);
        // Also refresh the selectedEmployee snapshot so the Upload modal's
        // "View" links and green-check indicators reflect the just-uploaded file.
        // /talent/employee/{id} wraps the row under `employee`.
        try {
          const fresh = await axios.get(`${API}/talent/employee/${employeeId}`, {
            headers: getAuthHeader()
          });
          const emp = fresh?.data?.employee;
          if (emp) setSelectedEmployee(prev => ({ ...(prev || {}), ...emp }));
        } catch (_) {
          /* non-fatal */
        }
      }
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to upload document'));
    }
  };

  const handleLoanRequest = async () => {
    if (!loanRequestForm.requested_amount || !selectedEmployee) {
      toast.error('Please enter loan amount');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('employee_id', selectedEmployee.id);
      formData.append('employee_type', selectedEmployee.employee_type);
      formData.append('requested_amount', loanRequestForm.requested_amount);
      formData.append('purpose', loanRequestForm.purpose || '');
      formData.append('proposed_emi', loanRequestForm.proposed_emi || '');

      await axios.post(`${API}/talent/loan-request`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Loan request submitted for approval');
      setLoanRequestDialogOpen(false);
      setLoanRequestForm({ requested_amount: '', purpose: '', proposed_emi: '' });
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to submit loan request'));
    }
  };

  const handleImportPayout = async () => {
    if (!importForm.file) {
      toast.error('Please select a file');
      return;
    }

    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', importForm.file);
      formData.append('month', importForm.month);
      formData.append('year', importForm.year);

      const response = await axios.post(`${API}/talent/import-talabat-payout`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      toast.success(response.data.message);
      setImportDialogOpen(false);
      setImportForm({ file: null, month: new Date().getMonth() + 1, year: new Date().getFullYear() });
      fetchMonthlyPayrolls();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to import payout'));
    } finally {
      setImportLoading(false);
    }
  };

  // NEW: Payroll Wizard Handlers
  const handleDownloadStaffTemplate = async () => {
    try {
      const response = await axios.get(
        `${API}/talent/pms/staff-template?month=${wizardData.month}&year=${wizardData.year}`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const monthName = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][wizardData.month];
      link.setAttribute('download', `Staff_Attendance_Template_${monthName}_${wizardData.year}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleWizardUpload = async () => {
    if (!wizardData.file) {
      toast.error('Please select a file');
      return;
    }

    setWizardLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', wizardData.file);

      let endpoint = '';
      if (wizardData.payrollType === 'staff') {
        formData.append('month', wizardData.month);
        formData.append('year', wizardData.year);
        endpoint = `${API}/talent/pms/upload-attendance`;
      } else if (wizardData.useAI && wizardData.platformId) {
        // Use AI Payroll processing
        const monthStr = `${wizardData.year}-${String(wizardData.month).padStart(2, '0')}`;
        formData.append('month', monthStr);
        formData.append('year', wizardData.year);
        formData.append('platform_id', wizardData.platformId);
        
        // Get platform's AI instructions
        const platform = platforms.find(p => p.id === wizardData.platformId);
        const instructions = platform?.ai_instructions || '';
        formData.append('ai_instructions', instructions);
        
        endpoint = `${API}/ai-payroll/process`;
        
        const response = await axios.post(endpoint, formData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
          timeout: 120000 // 2 min timeout for AI
        });
        
        // Transform AI response — only include matched riders in payroll preview
        const aiResults = response.data.payroll_results || [];
        const matchedResults = aiResults.filter(r => r.matched !== false);
        const unmatchedResults = aiResults.filter(r => r.matched === false);
        
        const preview = matchedResults.map(r => ({
          employee_name: r.rider_name,
          employee_unique_id: r.platform_id,
          gross_salary: r.gross_salary,
          paid_in_wallet: r.paid_in_wallet || 0,
          paid_as_tips: r.paid_as_tips || 0,
          paid_as_cod: r.paid_as_cod || 0,
          clawback_deduction: r.clawback_deduction || 0,
          platform_deduction: r.platform_deduction || 0,
          food_compensation: r.food_compensation || 0,
          slx_deduction: r.slx_deduction || 0,
          // Feb-2026: sign-tagged dynamic deductions (e.g. Fuel_Deduction =
          // 350 | deduction on Keeta). Backend rolls these into
          // total_deductions & renders them in additional_deductions, but
          // if we don't carry the field across, the wizard's client-side
          // recompute on ANY cell edit does `(row.dynamic_deductions_total
          // || 0)` = 0 and silently drops the fuel/etc. deduction from
          // total_deductions. Same story for dynamic_earnings_total on
          // sign-tagged earnings.
          dynamic_deductions_total: r.dynamic_deductions_total || 0,
          dynamic_earnings_total: r.dynamic_earnings_total || 0,
          fines: r.fines,
          salik: r.salik,
          maintenance: r.maintenance,
          loan_emi: r.loan_emi,
          prev_arrears: r.prev_arrears || 0,
          prev_arrears_months: r.prev_arrears_months || [],
          past_dues: r.past_dues || 0,
          past_dues_desc: r.past_dues_desc || '',
          total_deductions: r.total_deductions,
          net_salary: r.net_salary,
          rider_id: r.rider_id,
          total_orders: r.total_orders,
          bike_plate: r.bike_plate || '',
          edenred_id: r.edenred_id || '',
          matched: true,
          calculation_breakdown: r.calculation_breakdown,
          is_npp: r.is_npp || false,
          npp_primary_platform: r.npp_primary_platform || '',
          // Store originals for reverse entry tracking
          original_fines: r.fines,
          original_salik: r.salik,
          original_maintenance: r.maintenance,
          original_loan_emi: r.loan_emi
        }));
        
        const summary = response.data.summary || {};
        const totalGross = preview.reduce((s, p) => s + (p.gross_salary || 0), 0);

        // Enrich each preview row with any pending/partial salary advance the
        // rider has. Mirrors the non-AI (legacy PMS upload) path below so the
        // AI-generated payroll wizard also surfaces Sal. Advance deductions.
        let enrichedPreview = preview;
        const _saDebug = [];
        try {
          enrichedPreview = await Promise.all(
            preview.map(async (p) => {
              const empId = p.rider_id || p.employee_id;
              if (!empId) { _saDebug.push({ name: p.employee_name, empId: null, note: 'no rider_id' }); return p; }
              try {
                const saRes = await axios.get(
                  `${API}/salary-advance/pending/by-rider?rider_id=${encodeURIComponent(empId)}`,
                  { headers: getAuthHeader() },
                );
                const total = parseFloat(saRes.data?.total_outstanding || 0);
                const ids = (saRes.data?.advances || []).map((a) => a.id);
                _saDebug.push({ name: p.employee_name, empId, total, count: ids.length });
                if (total > 0) {
                  const newDed = (parseFloat(p.total_deductions || 0) + total).toFixed(2);
                  const newNet = (parseFloat(p.gross_salary || 0) + parseFloat(p.past_dues || 0) - parseFloat(newDed)).toFixed(2);
                  return { ...p, salary_advance: total, salary_advance_ids: ids, total_deductions: parseFloat(newDed), net_salary: parseFloat(newNet) };
                }
              } catch (e) { _saDebug.push({ name: p.employee_name, empId, error: e?.message || 'fetch failed' }); }
              return p;
            }),
          );
        } catch (_e) { /* fall back to un-enriched preview */ }
        console.log('=== SALARY ADVANCE ENRICHMENT (AI Payroll) ===');
        console.table(_saDebug);
        const _saHits = _saDebug.filter(x => (x.total || 0) > 0);
        console.log(`Salary Advances applied: ${_saHits.length} / ${_saDebug.length} riders — total AED ${_saHits.reduce((s,x)=>s+(x.total||0),0).toFixed(2)}`);

        const totalDeductions = enrichedPreview.reduce((s, p) => s + (p.total_deductions || 0), 0);
        setWizardData({
          ...wizardData,
          preview: enrichedPreview,
          summary: {
            total_employees: enrichedPreview.length,
            total_gross: totalGross,
            total_deductions: totalDeductions,
            total_net: totalGross - totalDeductions,
            total_matched: matchedResults.length,
            total_unmatched: unmatchedResults.length
          },
          unmatchedRiders: unmatchedResults.map(r => ({
            rider_name: r.rider_name,
            platform_id: r.platform_id,
            total_orders: r.total_orders,
            gross_salary: r.gross_salary,
            calculation_breakdown: r.calculation_breakdown
          }))
        });
        
        // Always log debug info to browser console
        const debug = response.data.debug;
        if (debug) {
          console.log('=== PAYROLL MATCHING DEBUG ===');
          console.log('ID column used:', debug.id_column_used);
          console.log('Riders from main collection:', debug.riders_from_main);
          console.log('Riders from payroll collection:', debug.riders_from_payroll);
          console.log('Total merged riders:', debug.total_riders_in_db);
          console.log('Total lookup keys:', debug.total_lookup_keys);
          console.log('ALL Lookup keys:', debug.lookup_keys_all);
          console.log('Excel PIDs:', debug.excel_pids);
          console.log('MISSING PIDs (not in lookup):', debug.missing_pids);
          console.log('Matched count:', matchedResults.length);
          console.log('Unmatched count:', unmatchedResults.length);
        }

        if (unmatchedResults.length > 0) {
          toast.warning(`${matchedResults.length} riders matched. ${unmatchedResults.length} riders not found in system and excluded from payroll.`);
        } else {
          toast.success('AI processed payroll successfully! All riders matched.');
        }
        
        // Log deduction debug for each rider
        const allResults = response.data.payroll_results || [];
        console.log('=== PER-RIDER DEDUCTION DEBUG ===');
        for (const r of allResults) {
          if (r._deduction_debug) {
            console.log(`${r.rider_name} (${r.platform_id}): fines=${r.fines}, salik=${r.salik}, maint=${r.maintenance}, loan=${r.loan_emi}, bike=${r._deduction_debug.bike_plate_used}, rider_id=${r._deduction_debug.rider_id_used}`);
            if (r._deduction_debug.fines_found?.length > 0) {
              console.log('  Fines found:', r._deduction_debug.fines_found);
            }
          }
        }

        setWizardStep(4);
        setWizardLoading(false);
        return;
      } else {
        formData.append('platform', wizardData.platform);
        endpoint = `${API}/talent/pms/upload-payout`;
      }

      const response = await axios.post(endpoint, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      // Enrich each preview row with any pending/partial salary advance the
      // rider has — this becomes a mandatory deduction column.
      let enrichedPreview = response.data.preview || [];
      try {
        enrichedPreview = await Promise.all(
          enrichedPreview.map(async (p) => {
            const empId = p.rider_id || p.employee_id;
            if (!empId) return p;
            try {
              const saRes = await axios.get(
                `${API}/salary-advance/pending/by-rider?rider_id=${encodeURIComponent(empId)}`,
                { headers: getAuthHeader() },
              );
              const total = parseFloat(saRes.data?.total_outstanding || 0);
              const ids = (saRes.data?.advances || []).map((a) => a.id);
              if (total > 0) {
                const newDed = (parseFloat(p.total_deductions || 0) + total).toFixed(2);
                const newNet = (parseFloat(p.gross_salary || 0) + parseFloat(p.past_dues || 0) - parseFloat(newDed)).toFixed(2);
                return { ...p, salary_advance: total, salary_advance_ids: ids, total_deductions: parseFloat(newDed), net_salary: parseFloat(newNet) };
              }
            } catch (_e) { /* ignore per-rider failures */ }
            return p;
          }),
        );
      } catch (_e) { /* fall back to un-enriched preview */ }

      setWizardData({
        ...wizardData,
        preview: round2PreviewRows(enrichedPreview),
        summary: response.data.summary
      });
      setWizardStep(4); // Go to review step
      toast.success('File processed successfully');
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to process file'));
    } finally {
      setWizardLoading(false);
    }
  };

  const handleWizardSubmit = async () => {
    setWizardLoading(true);
    try {
      const formData = new FormData();
      formData.append('month', wizardData.month);
      formData.append('year', wizardData.year);
      formData.append('payroll_type', wizardData.payrollType || 'rider');
      if (wizardData.platform) formData.append('platform', wizardData.platform);
      
      // Map preview data with proper field names for backend
      const payrollData = wizardData.preview.map(p => ({
        employee_id: p.rider_id || p.employee_id || '',
        employee_unique_id: p.employee_unique_id || '',
        employee_name: p.employee_name || '',
        gross_salary: p.gross_salary || 0,
        fines: p.fines || 0,
        salik: p.salik || 0,
        maintenance: p.maintenance || 0,
        loan_emi: p.loan_emi || 0,
        prev_arrears: p.prev_arrears || 0,
        prev_arrears_months: p.prev_arrears_months || [],
        past_dues: p.past_dues || 0,
        past_dues_desc: p.past_dues_desc || '',
        paid_in_wallet: p.paid_in_wallet || 0,
        paid_as_tips: p.paid_as_tips || 0,
        paid_as_cod: p.paid_as_cod || 0,
        clawback_deduction: p.clawback_deduction || 0,
        platform_deduction: p.platform_deduction || 0,
        food_compensation: p.food_compensation || 0,
        room_rent: p.room_rent || 0,
        slx_deduction: p.slx_deduction || 0,
        // Feb-2026: preserve sign-tagged dynamic deduction totals across
        // submit so backend's total_deductions math stays reconciled.
        dynamic_deductions_total: p.dynamic_deductions_total || 0,
        dynamic_earnings_total: p.dynamic_earnings_total || 0,
        salary_advance: p.salary_advance || 0,
        salary_advance_ids: p.salary_advance_ids || [],
        total_deductions: p.total_deductions || 0,
        net_salary: p.net_salary || 0,
        is_npp: p.is_npp || false,
        npp_primary_platform: p.npp_primary_platform || '',
        total_orders: p.total_orders || 0,
        edenred_id: p.edenred_id || '',
        original_fines: p.original_fines ?? p.fines ?? 0,
        original_salik: p.original_salik ?? p.salik ?? 0,
        original_maintenance: p.original_maintenance ?? p.maintenance ?? 0,
        original_loan_emi: p.original_loan_emi ?? p.loan_emi ?? 0,
        emi_recovery_method: p.emi_recovery_method || '',
        emi_recovery_installments: p.emi_recovery_installments || 0,
        emi_pause_reason: p.emi_pause_reason || '',
        emi_skipped_amount: p.emi_skipped_amount || 0,
        emi_was_adjusted: p.emi_was_adjusted || false,
        calculation_breakdown: p.calculation_breakdown || null,
        bike_plate: p.bike_plate || '',
        hold_salary: p.hold_salary || false,
        misc_deduction: p.misc_deduction || 0,
        misc_deduction_desc: p.misc_deduction_desc || ''
      }));
      formData.append('payroll_data', JSON.stringify(payrollData));

      await axios.post(`${API}/talent/pms/submit`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Payroll submitted for approval');
      // Rider-pay tags and instalment plans become permanent once the
      // payroll is submitted.
      setWizardPendingRiderPayFineIds(new Set());
      setWizardPendingInstalmentPlanIds(new Set());
      setPayrollWizardOpen(false);
      resetWizard();
      fetchPayrollDashboard();
      fetchApprovedPayrollLog();
      fetchPendingBatches();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to submit payroll'));
    } finally {
      setWizardLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    setWizardLoading(true);
    try {
      const payrollData = wizardData.preview.map(p => ({
        rider_id: p.rider_id || p.employee_id || '',
        employee_id: p.rider_id || p.employee_id || '',
        employee_unique_id: p.employee_unique_id || '',
        employee_name: p.employee_name || '',
        gross_salary: p.gross_salary || 0,
        fines: p.fines || 0,
        salik: p.salik || 0,
        maintenance: p.maintenance || 0,
        loan_emi: p.loan_emi || 0,
        prev_arrears: p.prev_arrears || 0,
        prev_arrears_months: p.prev_arrears_months || [],
        past_dues: p.past_dues || 0,
        past_dues_desc: p.past_dues_desc || '',
        paid_in_wallet: p.paid_in_wallet || 0,
        paid_as_tips: p.paid_as_tips || 0,
        paid_as_cod: p.paid_as_cod || 0,
        clawback_deduction: p.clawback_deduction || 0,
        platform_deduction: p.platform_deduction || 0,
        food_compensation: p.food_compensation || 0,
        room_rent: p.room_rent || 0,
        slx_deduction: p.slx_deduction || 0,
        // Feb-2026: preserve sign-tagged dynamic deduction totals across
        // draft save so the wizard recompute doesn't drop Fuel/etc.
        dynamic_deductions_total: p.dynamic_deductions_total || 0,
        dynamic_earnings_total: p.dynamic_earnings_total || 0,
        salary_advance: p.salary_advance || 0,
        salary_advance_ids: p.salary_advance_ids || [],
        total_deductions: p.total_deductions || 0,
        net_salary: p.net_salary || 0,
        is_npp: p.is_npp || false,
        npp_primary_platform: p.npp_primary_platform || '',
        total_orders: p.total_orders || 0,
        edenred_id: p.edenred_id || '',
        original_fines: p.original_fines ?? p.fines ?? 0,
        original_salik: p.original_salik ?? p.salik ?? 0,
        original_maintenance: p.original_maintenance ?? p.maintenance ?? 0,
        original_loan_emi: p.original_loan_emi ?? p.loan_emi ?? 0,
        emi_recovery_method: p.emi_recovery_method || '',
        emi_recovery_installments: p.emi_recovery_installments || 0,
        emi_pause_reason: p.emi_pause_reason || '',
        emi_skipped_amount: p.emi_skipped_amount || 0,
        emi_was_adjusted: p.emi_was_adjusted || false,
        calculation_breakdown: p.calculation_breakdown || null,
        bike_plate: p.bike_plate || '',
        matched: p.matched,
        misc_deduction: p.misc_deduction || 0,
        misc_deduction_desc: p.misc_deduction_desc || ''
      }));
      
      const formData = new FormData();
      formData.append('month', wizardData.month);
      formData.append('year', wizardData.year);
      formData.append('payroll_type', wizardData.payrollType || 'rider');
      if (wizardData.platform) formData.append('platform', wizardData.platform);
      formData.append('payroll_data', JSON.stringify(payrollData));
      formData.append('wizard_step', wizardStep);
      if (wizardData.draftId) formData.append('draft_id', wizardData.draftId);
      if (wizardData.platformId) formData.append('platform_id', wizardData.platformId);

      const res = await axios.post(`${API}/talent/pms/draft/save`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      
      setWizardData(prev => ({ ...prev, draftId: res.data.draft_id }));
      toast.success('Draft saved successfully');
      fetchPayrollDrafts();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to save draft'));
    } finally {
      setWizardLoading(false);
    }
  };

  const fetchPayrollDrafts = async () => {
    try {
      const res = await axios.get(`${API}/talent/pms/drafts`, { headers: getAuthHeader() });
      setPayrollDrafts(res.data.drafts || []);
    } catch (e) { /* ignore */ }
  };

  const [wizardDraftChanges, setWizardDraftChanges] = useState(null); // { changes_detected, formula_changed }
  const loadDraft = async (draftId) => {
    try {
      const res = await axios.get(`${API}/talent/pms/draft/${draftId}`, { headers: getAuthHeader() });
      const draft = res.data;
      setWizardData({
        month: draft.month,
        year: draft.year,
        payrollType: draft.payroll_type,
        platform: draft.platform || '',
        platformId: draft.platform_id || '',
        file: null,
        preview: round2PreviewRows(draft.payroll_data || []),
        summary: null,
        useAI: false,
        aiProcessing: false,
        unmatchedRiders: [],
        draftId: draft.id
      });
      setWizardStep(draft.wizard_step || 4);
      setPayrollWizardOpen(true);
      // Feb-2026: surface ops-side deduction changes + formula-hash mismatch
      const changes = Array.isArray(draft.changes_detected) ? draft.changes_detected : [];
      const formulaChanged = !!draft.formula_changed;
      if (changes.length || formulaChanged) {
        setWizardDraftChanges({ changes, formulaChanged });
        const parts = [];
        if (changes.length) {
          const n = changes.reduce((s, r) => s + (r.changes?.length || 0), 0);
          parts.push(`${n} deduction change${n === 1 ? '' : 's'} discovered across ${changes.length} rider${changes.length === 1 ? '' : 's'} — Salik / Fines / Loan / Maint / Advance updated automatically.`);
        }
        if (formulaChanged) {
          parts.push('AI formula was edited since save — please re-upload the Excel to recompute gross.');
        }
        toast.success(parts.join(' '));
      } else {
        setWizardDraftChanges(null);
        toast.success('Draft loaded');
      }
    } catch (error) {
      toast.error('Failed to load draft');
    }
  };

  const deleteDraft = async (draftId) => {
    try {
      await axios.delete(`${API}/talent/pms/draft/${draftId}`, { headers: getAuthHeader() });
      toast.success('Draft deleted');
      fetchPayrollDrafts();
    } catch (e) {
      toast.error('Failed to delete draft');
    }
  };

  const resetWizard = () => {
    setWizardStep(1);
    setWizardData({
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      payrollType: '',
      platform: '',
      platformId: '',
      file: null,
      preview: [],
      summary: null,
      useAI: false,
      aiProcessing: false,
      unmatchedRiders: []
    });
  };

  // Held salaries
  const fetchHeldSalaries = async () => {
    try {
      const res = await axios.get(`${API}/talent/pms/held-salaries`, { headers: getAuthHeader() });
      setHeldSalaries(res.data.held_salaries || []);
    } catch (e) { /* ignore */ }
  };

  const handleReleaseSalary = async (payrollId) => {
    try {
      await axios.post(`${API}/talent/pms/release-salary/${payrollId}`, {}, { headers: getAuthHeader() });
      toast.success('Salary released');
      fetchHeldSalaries();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to release salary'));
    }
  };

  // AI Instructions handlers for PMS Admin
  const handleOpenAIInstructions = (platform) => {
    setSelectedPlatformForAI(platform);
    setAiInstructions(platform.ai_instructions || getDefaultAIInstructions(platform));
    setPlatformIdColumn(platform.platform_id_column || '');
    setAiInstructionsDialog(true);
  };

  const getDefaultAIInstructions = (platform) => {
    const isTalabat = (platform.name || '').toLowerCase().includes('talabat');
    if (isTalabat) {
      return `PLATFORM: ${platform.name}

PAYOUT RATES (edit these — they drive the wizard math):
- EV: Pickup ${platform.pickup_rate || 1.2}, Dropoff ${platform.dropoff_rate || 5.3}
- Motorbike: Pickup ${platform.pickup_rate || 1.2}, Dropoff ${platform.dropoff_rate || 5.3}
- Bicyclist: Pickup 2.7, Dropoff 1.8

HOW TO CALCULATE GROSS SALARY:
1. Effective pickup orders = Pickups Count − abs(Pickup Cancellations)
2. Effective dropoff orders = Dropoffs Count − abs(Dropoffs Cancellations)
3. gross_salary = (effective_pickups × Pickup rate) + (effective_dropoffs × Dropoff rate)
   + Deliveries Return (LC) + Distance (LC) + Riders Incentives + Tips

HOW TO MATCH RIDERS:
- Match Excel rows to system riders using the "Vendor User ID" / "id_user" column
- Aggregate per rider if the Excel has per-order rows

SPECIAL CONDITIONS (if any):
- None specified`;
    }
    return `PLATFORM: ${platform.name}

PAYOUT RATES (edit these — they drive the wizard math):
- Pickup rate: ${platform.pickup_rate || 0} AED
- Dropoff rate: ${platform.dropoff_rate || 0} AED

HOW TO CALCULATE GROSS SALARY:
1. Find the "Total Delivered" or "Orders" column for each rider
2. Multiply total orders by the dropoff rate (${platform.dropoff_rate || 0})
3. Formula: gross_salary = total_orders × dropoff_rate

HOW TO MATCH RIDERS:
- Match Excel rows to system riders using "Platform ID" column
- Platform ID in Excel should match the rider's platform ID in the system

SPECIAL CONDITIONS (if any):
- None specified`;
  };

  const handleSaveAIInstructions = async () => {
    if (!selectedPlatformForAI) return;
    try {
      await axios.put(`${API}/talent/admin/platforms/${selectedPlatformForAI.id}`, {
        ai_instructions: aiInstructions,
        platform_id_column: platformIdColumn
      }, { headers: getAuthHeader() });
      toast.success('AI instructions saved successfully');
      setAiInstructionsDialog(false);
      fetchPlatforms();
    } catch (error) {
      toast.error('Failed to save AI instructions');
    }
  };

  // NEW: Platform Management Handlers
  const handleCreatePlatform = async () => {
    try {
      await axios.post(`${API}/talent/admin/platforms`, platformForm, {
        headers: getAuthHeader()
      });
      toast.success('Platform created successfully');
      setPlatformDialogOpen(false);
      setPlatformForm({ name: '', salary_type: 'per_order', pickup_rate: 0, dropoff_rate: 0, fixed_salary_config: null });
      fetchPlatforms();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to create platform'));
    }
  };

  const handleUpdatePlatform = async (platformId, updates) => {
    try {
      await axios.put(`${API}/talent/admin/platforms/${platformId}`, updates, {
        headers: getAuthHeader()
      });
      toast.success('Platform updated');
      fetchPlatforms();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to update platform'));
    }
  };

  const handleDeletePlatform = async (platformId) => {
    if (!window.confirm('Are you sure you want to delete this platform?')) return;
    
    try {
      await axios.delete(`${API}/talent/admin/platforms/${platformId}`, {
        headers: getAuthHeader()
      });
      toast.success('Platform deleted');
      fetchPlatforms();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to delete platform'));
    }
  };

  const handleSaveFormulaConfig = async () => {
    if (!selectedPlatform) return;
    
    try {
      await axios.post(
        `${API}/talent/admin/platforms/${selectedPlatform.id}/formula`,
        formulaConfig,
        { headers: getAuthHeader() }
      );
      toast.success('Formula configuration saved');
      setFormulaDialogOpen(false);
      fetchPlatforms();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to save formula'));
    }
  };

  const handleUploadSampleFormat = async (platformId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/talent/admin/platforms/${platformId}/upload-sample`,
        formData,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Sample format uploaded');
      // Update formula config with detected columns
      setFormulaConfig({
        ...formulaConfig,
        detected_columns: response.data.columns
      });
      fetchPlatforms();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to upload sample'));
    }
  };

  // NEW: Batch Approval Handlers
  const handleApproveBatch = async (batchId) => {
    try {
      // Find the batch data before approving
      const batchData = pendingBatches.find(b => b.id === batchId);
      
      await axios.post(`${API}/talent/admin/approve-batch/${batchId}`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Payroll batch approved');
      fetchPendingBatches();
      fetchApprovedPayrollLog();
      
      // Show post-approval dialog with options
      setApprovedBatchData({ ...batchData, id: batchId });
      setApprovalSuccessDialog(true);
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to approve batch'));
    }
  };

  // Generate salary slips for a batch
  const handleGenerateBatchSalarySlips = async (batchId) => {
    setGeneratingSlips(true);
    try {
      const response = await axios.post(`${API}/talent/pms/batch/${batchId}/generate-slips`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`Generated ${response.data.generated_count || 0} salary slips`);
      fetchSalarySlips();
      fetchApprovedPayrollLog(); // Refresh to update slip count
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to generate salary slips'));
    } finally {
      setGeneratingSlips(false);
    }
  };

  // Email salary slips for a batch
  const handleEmailBatchSalarySlips = async (batchId) => {
    setEmailingSlips(true);
    try {
      const response = await axios.post(`${API}/talent/pms/batch/${batchId}/email-slips`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`Sent ${response.data.sent_count || 0} salary slip emails`);
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to send salary slip emails'));
    } finally {
      setEmailingSlips(false);
    }
  };

  const handleRejectBatch = async (batchId, reason = '') => {
    try {
      await axios.post(`${API}/talent/admin/reject-batch/${batchId}?reason=${encodeURIComponent(reason)}`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Payroll batch rejected');
      fetchPendingBatches();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to reject batch'));
    }
  };

  // View salary slips for a batch
  const handleViewBatchSlips = async (batchId) => {
    setSlipsDialog({ open: true, batchId, slips: [], loading: true, actionLoading: '', search: '' });
    try {
      const res = await axios.get(`${API}/talent/pms/batch/${batchId}/salary-slips`, { headers: getAuthHeader() });
      setSlipsDialog(prev => ({ ...prev, slips: res.data.salary_slips || [], loading: false }));
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to load salary slips'));
      setSlipsDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDownloadIndividualSlip = async (slipId, empName) => {
    try {
      const res = await axios.get(`${API}/talent/pms/salary-slip/${slipId}/download`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `SalarySlip_${empName.replace(/\s/g, '_').substring(0, 20)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to download slip'));
    }
  };

  const handleEmailIndividualSlip = async (slipId) => {
    try {
      const res = await axios.post(`${API}/talent/pms/salary-slip/${slipId}/email`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message || 'Email sent');
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to email slip'));
    }
  };

  // Download a rider's loan account statement PDF (clamped to the slip's month/year).
  const handleDownloadLoanStatement = async (slip) => {
    const empId = slip?.employee_id || slip?.rider_id;
    if (!empId) {
      toast.error('Missing employee ID — cannot fetch loan statement');
      return;
    }
    try {
      const res = await axios.get(
        `${API}/talent/pms/loan-statement/${empId}/download?year=${slip.year || 0}&month=${slip.month || 0}`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const name = (slip.employee_name || 'Rider').replace(/\s/g, '_').substring(0, 25);
      a.download = `LoanStatement_${name}_${slip.month || ''}_${slip.year || ''}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to download loan statement'));
    }
  };

  // View an individual salary slip PDF inline in a new tab (does not force download).
  const handleViewIndividualSlip = async (slipId) => {
    try {
      const res = await axios.get(`${API}/talent/pms/salary-slip/${slipId}/download`, {
        headers: getAuthHeader(),
        responseType: 'blob',
        validateStatus: () => true,
      });
      if (res.status !== 200 || !res.data) {
        toast.error(`Failed to open salary slip (HTTP ${res.status})`);
        return;
      }
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: 'application/pdf' })
      );
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to open salary slip'));
    }
  };

  // View a rider's loan account statement PDF inline in a new tab.
  const handleViewLoanStatement = async (slip) => {
    const empId = slip?.employee_id || slip?.rider_id;
    if (!empId) {
      toast.error('Missing employee ID — cannot fetch loan statement');
      return;
    }
    try {
      const res = await axios.get(
        `${API}/talent/pms/loan-statement/${empId}/download?year=${slip.year || 0}&month=${slip.month || 0}`,
        { headers: getAuthHeader(), responseType: 'blob', validateStatus: () => true }
      );
      if (res.status !== 200 || !res.data) {
        toast.error(`Failed to open loan statement (HTTP ${res.status})`);
        return;
      }
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: 'application/pdf' })
      );
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to open loan statement'));
    }
  };

  // Open any uploaded loan-account file (receipt / invoice) in a new tab.
  // Historically the backend has emitted three URL flavours: (a) inline
  // `data:<mime>;base64,…` blobs from legacy TAP expenses, (b) legacy
  // absolute filesystem paths like `/app/backend/uploads/loan_receipts/…`
  // (which resolve to the frontend origin → 404 → blank page), and
  // (c) proper GridFS-served `/api/uploads/…` paths or full https URLs.
  //
  // For (a) we decode locally into a Blob. For every other flavour we
  // `axios.get(…, blob)` against the backend so we can surface real
  // errors (404 for missing files after a redeploy, etc.) as a friendly
  // toast instead of navigating the user to a raw `{"detail":"Not Found"}`
  // JSON page. The resulting Blob is opened in a new tab via a
  // `blob:` URL — this also side-steps Chrome's "returns null when
  // `noopener` is set" popup-blocked false positive.
  const openLoanEntryFile = async (rawUrl, filename) => {
    const url = (rawUrl || '').trim();
    if (!url) {
      toast.error('This entry has no attached file');
      return;
    }
    // Helper: open the given Blob in a new tab and revoke after 60s.
    const openBlob = (blob) => {
      const objUrl = window.URL.createObjectURL(blob);
      window.open(objUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(objUrl), 60000);
    };
    try {
      // Path (a): inline data URL.
      if (url.startsWith('data:')) {
        const commaIdx = url.indexOf(',');
        const header = url.substring(5, commaIdx);       // "application/pdf;base64"
        const isBase64 = header.includes(';base64');
        const mime = header.split(';')[0] || 'application/octet-stream';
        const data = url.substring(commaIdx + 1);
        let bytes;
        if (isBase64) {
          const bin = atob(data);
          bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        } else {
          bytes = new TextEncoder().encode(decodeURIComponent(data));
        }
        openBlob(new Blob([bytes], { type: mime }));
        return;
      }
      // Paths (b)+(c): normalise a legacy `/…/uploads/…` filesystem path
      // to the `/api/uploads/…` served route.
      let normalized = url;
      if (!/^https?:\/\//i.test(normalized) && !normalized.startsWith('/api/')) {
        const idx = normalized.indexOf('/uploads/');
        if (idx !== -1) normalized = `/api${normalized.substring(idx)}`;
      }
      let full = normalized;
      if (full.startsWith('/api/')) {
        full = `${process.env.REACT_APP_BACKEND_URL || ''}${full}`;
      } else if (!/^https?:\/\//i.test(full)) {
        toast.error('This attachment is stored in an unsupported format');
        return;
      }
      // Fetch as blob so a missing file becomes a toast, not a raw
      // JSON error page.
      const res = await axios.get(full, {
        headers: getAuthHeader(),
        responseType: 'blob',
        // Don't throw on 404 — we want to surface it as a toast.
        validateStatus: () => true,
      });
      if (res.status === 200 && res.data) {
        openBlob(res.data);
        return;
      }
      if (res.status === 404) {
        toast.error(
          `File not found on server${filename ? ` (${filename})` : ''} — it may need to be re-uploaded`
        );
      } else {
        toast.error(`Failed to open file (HTTP ${res.status})`);
      }
    } catch (err) {
      console.error('openLoanEntryFile failed:', err);
      toast.error('Failed to open file — the attachment may be corrupted');
    }
  };

  // Download wizard payroll preview as Excel
  const handleDownloadWizardPayroll = () => {
    if (!wizardData.preview?.length) return;
    
    // Dynamically discover all earning columns from calculation_breakdown
    // Scan all records to collect unique base_components and additional_components labels
    const baseLabelsSet = new Set();
    const additionalLabelsSet = new Set();
    const deductionLabelsSet = new Set();
    
    wizardData.preview.forEach(p => {
      const cb = p.calculation_breakdown || {};
      const bc = cb.base_components;
      if (Array.isArray(bc)) {
        bc.forEach(c => {
          if (c.label) baseLabelsSet.add(c.label);
        });
      } else if (bc && typeof bc === 'object') {
        Object.keys(bc).forEach(k => baseLabelsSet.add(k));
      }
      const ac = cb.additional_components;
      if (Array.isArray(ac)) {
        ac.forEach(c => {
          if (c.label) additionalLabelsSet.add(c.label);
        });
      }
    });
    
    const baseLabels = [...baseLabelsSet];
    const additionalLabels = [...additionalLabelsSet];
    
    // Deduction fields - always show all columns regardless of values
    const deductionFields = [
      { key: 'paid_in_wallet', label: 'Paid in Wallet (AED)', cbKey: 'paid_in_wallet' },
      { key: 'paid_as_tips', label: 'Paid as Tips (AED)', cbKey: 'paid_as_tips' },
      { key: 'paid_as_cod', label: 'Paid as COD (AED)', cbKey: 'paid_as_cod' },
      { key: 'fines', label: 'Fines (AED)' },
      { key: 'salik', label: 'Salik (AED)' },
      { key: 'maintenance', label: 'Maintenance (AED)' },
      { key: 'loan_emi', label: 'Loan EMI (AED)' },
      { key: 'salary_advance', label: 'Sal. Advance (AED)' },
      { key: 'clawback_deduction', label: 'Clawback Deduction (AED)' },
      { key: 'misc_deduction', label: 'Misc. Deduction (AED)' },
    ];
    
    const rows = wizardData.preview.map((p, idx) => {
      const cb = p.calculation_breakdown || {};
      const row = {
        '#': idx + 1,
        'Employee Name': p.employee_name,
        'Platform ID': p.employee_unique_id,
        'Edenred ID': p.edenred_id || '',
        'Bike No.': p.bike_plate || '',
      };
      
      // Dynamic base components
      baseLabels.forEach(label => {
        const bc = cb.base_components;
        if (Array.isArray(bc)) {
          const comp = bc.find(c => c.label === label);
          row[label] = comp ? comp.value : '';
        } else if (bc && typeof bc === 'object') {
          row[label] = bc[label] !== undefined ? bc[label] : '';
        } else {
          row[label] = '';
        }
      });
      
      // Dynamic additional components
      additionalLabels.forEach(label => {
        const ac = cb.additional_components;
        if (Array.isArray(ac)) {
          const comp = ac.find(c => c.label === label);
          row[`${label} (AED)`] = comp ? comp.value : 0;
        } else {
          row[`${label} (AED)`] = 0;
        }
      });
      
      // Gross
      row['Gross Salary (AED)'] = p.gross_salary || 0;
      
      // Deductions - always show all columns
      deductionFields.forEach(d => {
        const val = p[d.key] || cb[d.cbKey || d.key] || 0;
        row[d.label] = val;
      });
      
      row['Total Deductions (AED)'] = p.total_deductions || 0;
      row['Net Salary (AED)'] = p.net_salary || 0;
      if (p.misc_deduction_desc) row['Misc. Description'] = p.misc_deduction_desc;
      
      return row;
    });
    
    const ws = XLSX.utils.json_to_sheet(rows);
    
    // Auto-size columns
    const colWidths = Object.keys(rows[0] || {}).map(key => ({
      wch: Math.max(key.length + 2, 14)
    }));
    ws['!cols'] = colWidths;
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payroll');
    XLSX.writeFile(wb, `Payroll_${wizardData.platform}_${wizardData.year}-${String(wizardData.month).padStart(2,'0')}.xlsx`);
    toast.success('Payroll downloaded');
  };

  // Download wizard payroll preview as PDF
  const handleDownloadWizardPayrollPDF = () => {
    if (!wizardData.preview?.length) return;
    const doc = new jsPDF('l', 'mm', 'a4');
    const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthName = monthNames[wizardData.month] || '';
    
    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`Payroll Report - ${monthName} ${wizardData.year}`, 148.5, 15, { align: 'center' });
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(`Platform: ${wizardData.platform || 'N/A'} | Total Riders: ${wizardData.preview.length}`, 148.5, 22, { align: 'center' });
    doc.setTextColor(0);

    // Table
    const tableData = wizardData.preview.map((p, idx) => [
      idx + 1,
      p.employee_name || '',
      p.employee_unique_id || '',
      p.bike_plate || '',
      p.total_orders || 0,
      (p.gross_salary || 0).toFixed(2),
      (p.paid_in_wallet || 0).toFixed(2),
      (p.tips || 0).toFixed(2),
      (p.cod || 0).toFixed(2),
      (p.fines || 0).toFixed(2),
      (p.salik || 0).toFixed(2),
      (p.maintenance || 0).toFixed(2),
      (p.loan_emi || 0).toFixed(2),
      (p.salary_advance || 0).toFixed(2),
      (p.misc_deduction || 0).toFixed(2),
      (p.clawback_deduction || 0).toFixed(2),
      (p.total_deductions || 0).toFixed(2),
      (p.net_salary || 0).toFixed(2),
    ]);

    // Summary row
    const summary = wizardData.summary || {};
    tableData.push([
      '', 'TOTAL', '', '', '',
      (summary.total_gross || 0).toFixed(2),
      '', '', '', '', '', '', '', '', '', '',
      (summary.total_deductions || 0).toFixed(2),
      (summary.total_net || 0).toFixed(2),
    ]);

    doc.autoTable({
      startY: 28,
      head: [['#', 'Name', 'ID', 'Bike', 'Orders', 'Gross', 'Wallet', 'Tips', 'COD', 'Fines', 'Salik', 'Maint.', 'Loan', 'Sal. Adv.', 'Misc.', 'Clawback', 'Deductions', 'Net']],
      body: tableData,
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 1.5 },
      headStyles: { fillColor: [46, 125, 50], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 7 },
        1: { cellWidth: 24 },
        2: { cellWidth: 15 },
        3: { cellWidth: 13 },
        4: { cellWidth: 9, halign: 'center' },
        5: { cellWidth: 14, halign: 'right' },
        6: { cellWidth: 11, halign: 'right' },
        7: { cellWidth: 9, halign: 'right' },
        8: { cellWidth: 9, halign: 'right' },
        9: { cellWidth: 9, halign: 'right' },
        10: { cellWidth: 9, halign: 'right' },
        11: { cellWidth: 9, halign: 'right' },
        12: { cellWidth: 9, halign: 'right' },
        13: { cellWidth: 11, halign: 'right' },
        14: { cellWidth: 9, halign: 'right' },
        15: { cellWidth: 11, halign: 'right' },
        16: { cellWidth: 15, halign: 'right' },
        17: { cellWidth: 15, halign: 'right' },
      },
      foot: [],
      didParseCell: (data) => {
        // Style the last (TOTAL) row
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [232, 245, 233];
        }
      }
    });

    doc.save(`Payroll_${wizardData.platform || 'All'}_${wizardData.year}-${String(wizardData.month).padStart(2, '0')}.pdf`);
    toast.success('Payroll PDF downloaded');
  };

  // Edit a payroll preview row
  const handleEditPreviewRow = (idx, field, value) => {
    const updated = [...wizardData.preview];
    const numVal = parseFloat(value) || 0;
    
    // If loan_emi is being reduced, show recovery method dialog
    if (field === 'loan_emi') {
      const original = updated[idx].original_loan_emi || 0;
      if (numVal < original && original > 0) {
        const skipped = original - numVal;
        setEmiAdjustmentData({
          idx,
          originalEmi: original,
          newEmi: numVal,
          skippedAmount: skipped,
          recoveryMethod: '',
          installments: 2,
          reason: '',
          riderName: updated[idx].employee_name || updated[idx].rider_name || '',
          crossPlatformTarget: '',
          employeePlatforms: []
        });
        setEmiAdjustmentDialogOpen(true);
        // Fetch other platforms for this employee
        const empId = updated[idx].rider_id || updated[idx].employee_id;
        if (empId) {
          axios.get(`${API}/talent/pms/employee-platforms/${empId}?month=${wizardData.month}&year=${wizardData.year}`, { headers: getAuthHeader() })
            .then(res => {
              const otherPlatforms = (res.data.enrolled_platforms || []).filter(p => p !== wizardData.platform);
              setEmiAdjustmentData(prev => ({ ...prev, employeePlatforms: otherPlatforms }));
            })
            .catch(() => {});
        }
        // Still update the field immediately for preview
      }
    }
    
    updated[idx] = { ...updated[idx], [field]: numVal };
    // Recalculate deductions and net (rounded to 2dp to prevent FP noise)
    const row = updated[idx];
    row.total_deductions = round2((row.fines || 0) + (row.salik || 0) + (row.maintenance || 0) + (row.loan_emi || 0) + (row.prev_arrears || 0) + (row.paid_in_wallet || 0) + (row.paid_as_tips || 0) + (row.paid_as_cod || 0) + (row.misc_deduction || 0) + (row.clawback_deduction || 0) + (row.platform_deduction || 0) + (row.food_compensation || 0) + (row.room_rent || 0) + (row.slx_deduction || 0) + (row.dynamic_deductions_total || 0) + (row.salary_advance || 0));
    row.net_salary = round2((row.gross_salary || 0) + (row.past_dues || 0) - row.total_deductions);
    // Recalculate summary
    const totalGross = round2(updated.reduce((s, p) => s + (p.gross_salary || 0), 0));
    const totalPastDues = round2(updated.reduce((s, p) => s + (p.past_dues || 0), 0));
    const totalDeductions = round2(updated.reduce((s, p) => s + (p.total_deductions || 0), 0));
    setWizardData({
      ...wizardData,
      preview: updated,
      summary: {
        ...wizardData.summary,
        total_employees: updated.length,
        total_gross: totalGross,
        total_past_dues: totalPastDues,
        total_deductions: totalDeductions,
        total_net: round2(totalGross + totalPastDues - totalDeductions)
      }
    });
  };
  
  // Confirm EMI adjustment recovery method
  const handleConfirmEmiAdjustment = async () => {
    if (!emiAdjustmentData.recoveryMethod) {
      toast.error('Please select a recovery method');
      return;
    }
    
    // Handle cross-platform adjustment
    if (emiAdjustmentData.recoveryMethod === 'cross_platform') {
      if (!emiAdjustmentData.crossPlatformTarget) {
        toast.error('Please select a target platform');
        return;
      }
      const idx = emiAdjustmentData.idx;
      const empId = wizardData.preview[idx]?.rider_id || wizardData.preview[idx]?.employee_id;
      try {
        const formData = new FormData();
        formData.append('source_employee_id', empId);
        formData.append('source_platform', wizardData.platform);
        formData.append('target_platform', emiAdjustmentData.crossPlatformTarget);
        formData.append('amount', emiAdjustmentData.skippedAmount);
        formData.append('month', wizardData.month);
        formData.append('year', wizardData.year);
        formData.append('description', emiAdjustmentData.reason || `Cross-platform EMI from ${wizardData.platform}`);
        
        await axios.post(`${API}/talent/pms/cross-platform-adjustment`, formData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
        });
        
        const updated = [...wizardData.preview];
        if (idx !== null && updated[idx]) {
          updated[idx].emi_recovery_method = 'cross_platform';
          updated[idx].emi_recovery_installments = 0;
          updated[idx].emi_pause_reason = `Shifted to ${emiAdjustmentData.crossPlatformTarget}`;
          updated[idx].emi_skipped_amount = emiAdjustmentData.skippedAmount;
          updated[idx].emi_was_adjusted = true;
          setWizardData({ ...wizardData, preview: updated });
        }
        setEmiAdjustmentDialogOpen(false);
        toast.success(`EMI of AED ${emiAdjustmentData.skippedAmount.toFixed(2)} shifted to ${emiAdjustmentData.crossPlatformTarget}`);
      } catch (error) {
        toast.error(formatErrorMessage(error, 'Failed to create cross-platform adjustment'));
      }
      return;
    }
    
    const updated = [...wizardData.preview];
    const idx = emiAdjustmentData.idx;
    if (idx !== null && updated[idx]) {
      updated[idx].emi_recovery_method = emiAdjustmentData.recoveryMethod;
      updated[idx].emi_recovery_installments = emiAdjustmentData.installments;
      updated[idx].emi_pause_reason = emiAdjustmentData.reason;
      updated[idx].emi_skipped_amount = emiAdjustmentData.skippedAmount;
      updated[idx].emi_was_adjusted = true;
      setWizardData({ ...wizardData, preview: updated });
    }
    setEmiAdjustmentDialogOpen(false);
    toast.success(`EMI adjustment saved: ${emiAdjustmentData.recoveryMethod === 'next_emi' ? 'Full recovery next month' : emiAdjustmentData.recoveryMethod === 'installments' ? `${emiAdjustmentData.installments} equal installments` : 'Extended tenure'}`);
  };

  // Merge multiple payroll batches into one Excel
  const handleMergePayrolls = async () => {
    if (selectedMergeBatches.length < 1) {
      toast.error('Select at least one payroll batch to merge');
      return;
    }
    setMerging(true);
    try {
      const response = await axios.post(`${API}/talent/pms/merge-payrolls`, selectedMergeBatches, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Merged_Payroll.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Merged payroll downloaded');
      setSelectedMergeBatches([]);
    } catch (error) {
      toast.error('Failed to merge payrolls');
    } finally {
      setMerging(false);
    }
  };

  const toggleMergeBatch = (batchId) => {
    setSelectedMergeBatches(prev =>
      prev.includes(batchId) ? prev.filter(id => id !== batchId) : [...prev, batchId]
    );
  };

  // NEW: Download Handlers
  // Open the read-only Wizard dialog (used by Pending Approval Log + PMS
  // Admin "View" buttons). For pending batches we already have the per-
  // employee payroll details in memory (batch.payroll_details). For approved
  // / rejected batches we fetch them lazily.
  const handleOpenWizardReadOnly = async (batch) => {
    setReadOnlyWizardBatch(batch);
    setReadOnlyWizardOpen(true);

    // Pending batches already have payroll_details on the row — use them.
    if (batch.payroll_details && batch.payroll_details.length > 0) {
      // Normalise to monthly_payroll-style shape so the dialog can read
      // every field. The pending-batches endpoint returns "deductions_breakdown"
      // as a sub-object — flatten it.
      const flat = batch.payroll_details.map(p => {
        const d = p.deductions_breakdown || {};
        return {
          ...p,
          fines: p.fines ?? d.fines ?? 0,
          salik: p.salik ?? d.salik ?? 0,
          maintenance: p.maintenance ?? d.maintenance ?? 0,
          loan_emi: p.loan_emi ?? d.loan_emi ?? 0,
          slx_deduction: p.slx_deduction ?? d.slx_deduction ?? 0,
          clawback_deduction: p.clawback_deduction ?? d.clawback_deduction ?? 0,
          salary_advance: p.salary_advance ?? d.salary_advance ?? 0,
          prev_arrears: p.prev_arrears ?? d.prev_arrears ?? 0,
          past_dues: p.past_dues ?? d.past_dues ?? 0,
          misc_deduction: p.misc_deduction ?? d.misc_deduction ?? 0,
        };
      });
      setReadOnlyWizardPayrolls(flat);
      return;
    }
    // Fallback: fetch payrolls for the batch by id.
    try {
      const res = await axios.get(`${API}/talent/pms/batch/${batch.id}/payrolls`, {
        headers: getAuthHeader(),
      });
      setReadOnlyWizardPayrolls(res.data?.payrolls || []);
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to load payroll details'));
      setReadOnlyWizardPayrolls([]);
    }
  };

  const handleDownloadPayroll = async (batchId, format) => {
    try {
      const endpoint = format === 'edenred-excel' ? 'download-edenred-excel' :
                       format === 'edenred-pdf' ? 'download-edenred-pdf' :
                       format === 'pdf' ? 'download-pdf' : 'download-excel';
      
      const response = await axios.get(
        `${API}/talent/pms/batch/${batchId}/${endpoint}`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format.includes('pdf') ? 'pdf' : 'xlsx';
      link.setAttribute('download', `Payroll_${batchId}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download');
    }
  };

  // NEW: Update Staff Config
  const handleUpdateStaffConfig = async () => {
    try {
      const formData = new FormData();
      formData.append('working_days_per_month', staffConfig.working_days_per_month);
      formData.append('overtime_rate_multiplier', staffConfig.overtime_rate_multiplier);
      formData.append('overtime_base_divisor', staffConfig.overtime_base_divisor || 30);
      
      await axios.put(`${API}/talent/admin/staff-config`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Staff configuration updated');
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to update configuration'));
    }
  };

  // Generate staff payroll
  const handleGenerateStaffPayroll = async () => {
    setStaffPayrollLoading(true);
    try {
      const formData = new FormData();
      formData.append('month', payrollFilter.month);
      formData.append('year', payrollFilter.year);

      const response = await axios.post(`${API}/talent/staff-payroll/generate`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      toast.success(response.data.message);
      setGenerateStaffPayrollDialogOpen(false);
      fetchMonthlyPayrolls();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to generate staff payroll'));
    } finally {
      setStaffPayrollLoading(false);
    }
  };

  // Batch approve payrolls
  const handleBatchApprovePayrolls = async () => {
    if (selectedPayrollIds.length === 0) {
      toast.error('Please select payrolls to approve');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('payroll_ids', selectedPayrollIds.join(','));
      formData.append('notes', 'Batch approved');

      await axios.post(`${API}/talent/admin/batch-approve-payrolls`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Payrolls approved successfully');
      setSelectedPayrollIds([]);
      fetchPendingApprovals();
      fetchMonthlyPayrolls();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to approve payrolls'));
    }
  };

  // Batch mark paid
  const handleBatchMarkPaid = async () => {
    if (selectedPayrollIds.length === 0) {
      toast.error('Please select payrolls to mark as paid');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('payroll_ids', selectedPayrollIds.join(','));
      formData.append('payment_reference', `BATCH-${Date.now()}`);

      await axios.post(`${API}/talent/admin/batch-mark-paid`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Payrolls marked as paid');
      setSelectedPayrollIds([]);
      fetchPendingApprovals();
      fetchMonthlyPayrolls();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to mark payrolls as paid'));
    }
  };

  // Generate salary slip
  const handleGenerateSalarySlip = async (payrollId) => {
    try {
      const formData = new FormData();
      formData.append('payroll_id', payrollId);

      const response = await axios.post(`${API}/talent/salary-slip/generate`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Salary slip generated');
      fetchSalarySlips();
      return response.data;
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to generate salary slip'));
    }
  };

  // Batch generate salary slips
  const handleBatchGenerateSalarySlips = async () => {
    try {
      const formData = new FormData();
      formData.append('month', payrollFilter.month);
      formData.append('year', payrollFilter.year);
      formData.append('status', 'approved');

      const response = await axios.post(`${API}/talent/salary-slip/batch-generate`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      toast.success(response.data.message);
      fetchSalarySlips();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to generate salary slips'));
    }
  };

  // Email salary slip
  const handleEmailSalarySlip = async (slipId) => {
    try {
      await axios.post(`${API}/talent/salary-slip/${slipId}/email`, {}, {
        headers: getAuthHeader()
      });

      toast.success('Salary slip emailed successfully');
      fetchSalarySlips();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to email salary slip'));
    }
  };

  // WhatsApp salary slip
  const handleWhatsAppSalarySlip = async (slipId, employeeName, employeeId) => {
    // Preview the destination phone first so the operator sees exactly
    // which number will be messaged before confirming.
    let phone = null;
    let optedIn = false;
    try {
      if (employeeId) {
        const p = await axios.get(
          `${API}/whatsapp/preview-recipient/${employeeId}`,
          { headers: getAuthHeader() },
        );
        phone = p.data?.phone;
        optedIn = !!p.data?.opted_in;
      }
    } catch (_e) { /* fall through — confirm will still gate */ }

    if (!phone) {
      toast.error(`Cannot send — no WhatsApp number on file for ${employeeName || 'this rider'}. Add a SIM or personal mobile in Riders/TAP first.`);
      return;
    }
    const optInNote = optedIn ? '' : '\n\nℹ Number will be auto opted-in on send.';
    if (!window.confirm(
      `📱 Send SALARY SLIP via WhatsApp to ${employeeName || 'the employee'} on the mobile no. ${phone}?\n\n` +
      `The rider will receive a formal notification with their gross earnings, deductions and net salary for this month.\n\n` +
      `⚠ This is NOT a loan account statement. Confirm to proceed.${optInNote}`
    )) return;
    try {
      const res = await axios.post(`${API}/talent/salary-slip/${slipId}/whatsapp`, {}, {
        headers: getAuthHeader()
      });
      toast.success(res.data?.message || 'Salary slip queued via WhatsApp');
      fetchSalarySlips();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to send via WhatsApp'));
    }
  };

  // WhatsApp loan statement
  const handleWhatsAppLoanStatement = async (slip) => {
    const empId = slip?.employee_id || slip?.rider_id;
    if (!empId) { toast.error('Missing employee ID'); return; }

    let phone = null;
    let optedIn = false;
    try {
      const p = await axios.get(
        `${API}/whatsapp/preview-recipient/${empId}`,
        { headers: getAuthHeader() },
      );
      phone = p.data?.phone;
      optedIn = !!p.data?.opted_in;
    } catch (_e) { /* fall through */ }

    if (!phone) {
      toast.error(`Cannot send — no WhatsApp number on file for ${slip?.employee_name || 'this rider'}.`);
      return;
    }
    const optInNote = optedIn ? '' : '\n\nℹ Number will be auto opted-in on send.';
    if (!window.confirm(
      `💰 Send LOAN ACCOUNT STATEMENT via WhatsApp to ${slip?.employee_name || 'the employee'} on the mobile no. ${phone}?\n\n` +
      `The rider will receive a summary of their outstanding advances / EMI schedule as of this month.\n\n` +
      `⚠ This is NOT a salary slip. Confirm to proceed.${optInNote}`
    )) return;
    try {
      const res = await axios.post(
        `${API}/talent/pms/loan-statement/${empId}/whatsapp?year=${slip.year || 0}&month=${slip.month || 0}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(res.data?.message || 'Loan statement queued via WhatsApp');
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to send loan statement via WhatsApp'));
    }
  };

  // Add platform rate
  const handleAddPlatformRate = async () => {
    try {
      const formData = new FormData();
      Object.entries(rateForm).forEach(([key, value]) => {
        formData.append(key, value);
      });

      await axios.post(`${API}/talent/admin/platform-rate`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Platform rate saved');
      setAddRateDialogOpen(false);
      fetchPlatformRates();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to save platform rate'));
    }
  };

  // Approve loan request
  const handleApproveLoan = async (loanRequest) => {
    try {
      const formData = new FormData();
      formData.append('approved_amount', loanRequest.requested_amount);
      formData.append('approved_emi', loanRequest.proposed_emi || (loanRequest.requested_amount / 12));
      formData.append('notes', 'Approved by admin');

      await axios.post(`${API}/talent/loan-request/${loanRequest.id}/approve`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Loan request approved and added to employee account');
      fetchPendingApprovals();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to approve loan'));
    }
  };

  // Reject loan request
  const handleRejectLoan = async (loanRequest) => {
    try {
      const formData = new FormData();
      formData.append('reason', 'Rejected by admin');

      await axios.post(`${API}/talent/loan-request/${loanRequest.id}/reject`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Loan request rejected');
      fetchPendingApprovals();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to reject loan'));
    }
  };

  // Open account statement
  const openAccountStatement = (employee) => {
    setSelectedEmployee(employee);
    fetchAccountStatement(employee.id);
    setAccountStatementDialogOpen(true);
  };

  // View salary slip
  const viewSalarySlip = (slip) => {
    setSelectedSalarySlip(slip);
    setSalarySlipDialogOpen(true);
  };

  // Download salary slip as PDF
  const handleDownloadSalarySlipPDF = async (slipId) => {
    try {
      toast.info('Generating PDF...');
      const response = await axios.get(`${API}/talent/salary-slip/${slipId}/download-pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Salary_Slip_${selectedSalarySlip?.employee_name || 'Employee'}_${selectedSalarySlip?.month}_${selectedSalarySlip?.year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    }
  };

  // Download all documents for an employee
  const handleDownloadAllDocuments = async (employee) => {
    try {
      // Get employee documents
      const response = await axios.get(`${API}/talent/employee/${employee.id}/documents`, {
        headers: getAuthHeader()
      });
      
      const docs = response.data.documents || [];
      
      if (docs.length === 0) {
        toast.info('No documents available for download');
        return;
      }
      
      // Download each document
      for (const doc of docs) {
        if (doc.url) {
          const link = document.createElement('a');
          link.href = doc.url;
          link.download = `${employee.name}_${doc.type}.pdf`;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          await new Promise(resolve => setTimeout(resolve, 500)); // Small delay between downloads
        }
      }
      
      toast.success(`Downloaded ${docs.length} document(s)`);
    } catch (error) {
      toast.error('Failed to download documents');
    }
  };

  // Download a single file (handles both OneDrive URLs and base64)
  const downloadDocFile = (url, filename) => {
    if (!url) { toast.error('No file available'); return; }
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.open(url, '_blank');
      return;
    }
    if (url.startsWith('data:')) {
      const [header, base64Data] = url.split(',');
      if (!base64Data) { toast.error('Invalid file data'); return; }
      const mimeMatch = header.match(/data:([^;]+)/);
      const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
      const byteCharacters = atob(base64Data);
      const byteArray = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) byteArray[i] = byteCharacters.charCodeAt(i);
      const blob = new Blob([byteArray], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl; link.download = filename;
      document.body.appendChild(link); link.click();
      document.body.removeChild(link); URL.revokeObjectURL(blobUrl);
      return;
    }
  };

  // Open a document inline in a new browser tab (for viewing without forcing download).
  // Falls back to download for opaque/binary types the browser cannot render.
  const viewDocFile = (url) => {
    if (!url) { toast.error('No file available'); return; }
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (url.startsWith('data:')) {
      const [header, base64Data] = url.split(',');
      if (!base64Data) { toast.error('Invalid file data'); return; }
      const mimeMatch = header.match(/data:([^;]+)/);
      const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
      const byteCharacters = atob(base64Data);
      const byteArray = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) byteArray[i] = byteCharacters.charCodeAt(i);
      const blob = new Blob([byteArray], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);
      // window.open without a `download` attribute lets the browser render inline
      // for PDFs/images. The blob URL is revoked after a short delay so the new tab
      // has time to load; if it fails, users can still hit Download.
      const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!win) {
        toast.error('Popup blocked — allow popups to view inline');
        URL.revokeObjectURL(blobUrl);
        return;
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      return;
    }
  };

  const getDocFileExt = (url) => {
    if (!url) return '.pdf';
    if (url.startsWith('data:')) {
      if (url.includes('image/jpeg') || url.includes('image/jpg')) return '.jpg';
      if (url.includes('image/png')) return '.png';
      return '.pdf';
    }
    if (url.startsWith('http')) {
      const match = url.match(/\.(jpg|jpeg|png|pdf|gif|webp)(\?|$)/i);
      if (match) return `.${match[1].toLowerCase()}`;
    }
    return '.pdf';
  };

  // View Documents handler — fetches all docs (including TAP) for an employee
  const handleViewDocuments = async (employee) => {
    setViewDocumentsEmployee(employee);
    setViewDocumentsDialogOpen(true);
    setViewDocumentsLoading(true);
    try {
      const response = await axios.get(`${API}/talent/employee/${employee.id}/documents`, {
        headers: getAuthHeader()
      });
      setViewDocuments(response.data.documents || []);
    } catch (error) {
      toast.error('Failed to fetch documents');
      setViewDocuments([]);
    } finally {
      setViewDocumentsLoading(false);
    }
  };

  // Delete employee handler
  const handleDeleteEmployee = async (employee, e) => {
    e.stopPropagation(); // Prevent row click
    try {
      const [impactRes, candRes] = await Promise.all([
        axios.get(
          `${API}/talent/employees/${employee.id}/deletion-impact?employee_type=${employee.employee_type}`,
          { headers: getAuthHeader() }
        ),
        axios.get(
          `${API}/talent/employees/${employee.id}/merge-candidates?employee_type=${employee.employee_type}`,
          { headers: getAuthHeader() }
        ).catch(() => ({ data: { candidates: [] } })),
      ]);
      setDeleteImpact({ employee, impact: impactRes.data });
      setMergeCandidates(candRes.data?.candidates || []);
      setMergeTargetId('');
      setDeleteDialogOpen(true);
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to load deletion impact'));
    }
  };

  const confirmMergeEmployee = async () => {
    if (!deleteImpact || !mergeTargetId) return;
    try {
      setMergeInFlight(true);
      const res = await axios.post(
        `${API}/talent/employees/${deleteImpact.employee.id}/merge?target_id=${mergeTargetId}&employee_type=${deleteImpact.employee.employee_type}`,
        {},
        { headers: getAuthHeader() }
      );
      if (res.data?.success) {
        const totalMoved = Object.values(res.data.moves || {}).reduce((a, b) => a + (Number(b) || 0), 0);
        toast.success(`${res.data.message}. Moved ${totalMoved} linked record(s).`);
        setDeleteDialogOpen(false);
        setDeleteImpact(null);
        setMergeCandidates([]);
        setMergeTargetId('');
        fetchEmployees();
      }
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Merge failed'));
    } finally {
      setMergeInFlight(false);
    }
  };

  const confirmDeleteEmployee = async (force = false) => {
    if (!deleteImpact) return;
    try {
      const response = await axios.delete(
        `${API}/talent/employees/${deleteImpact.employee.id}?employee_type=${deleteImpact.employee.employee_type}&force=${force}`,
        { headers: getAuthHeader() }
      );
      if (response.data?.success) {
        toast.success(response.data.message || 'Employee deleted successfully');
        if (response.data.loan_account_preserved) {
          toast.info('Loan account remains attached to the other linked entry.');
        }
        setDeleteDialogOpen(false);
        setDeleteImpact(null);
        fetchEmployees();
      }
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to delete employee'));
    }
  };

  // Open Loan Dialog
  const openLoanDialog = (employee) => {
    setSelectedEmployee(employee);
    fetchEmployeeDetails(employee.id);
    setLoanDialogOpen(true);
  };

  const openEmployeeDetails = (employee) => {
    setSelectedEmployee(employee);
    fetchEmployeeDetails(employee.id);
    setEmployeeDetailsDialogOpen(true);
  };

  const filteredEmployees = useMemo(() => {
    return employees.filter(emp => {
      if (!employeeSearch) return true;
      const query = employeeSearch.toLowerCase();
      return (
        emp.name?.toLowerCase().includes(query) ||
        emp.emirates_id?.toLowerCase().includes(query) ||
        emp.employee_unique_id?.toLowerCase().includes(query) ||
        emp.edenred_id?.toLowerCase().includes(query)
      );
    }).sort((a, b) =>
      // Alphabetical by name (A-Z) — consistent with TAP / Employee Accounts.
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );
  }, [employees, employeeSearch]);

  // Reset to page 1 whenever the search term or type filter changes so
  // the user can never end up stranded on an empty higher page.
  useEffect(() => { setEmployeesPage(1); }, [employeeSearch, employeeFilter]);
  useEffect(() => { setAccountsPage(1); }, [accountsSearch, accountsTypeFilter]);

  const paginatedEmployees = useMemo(() => {
    const start = (employeesPage - 1) * EMPLOYEES_PER_PAGE;
    return filteredEmployees.slice(start, start + EMPLOYEES_PER_PAGE);
  }, [filteredEmployees, employeesPage]);

  // Employee Accounts subpage — filtered + sorted list. Search runs across
  // the FULL employees array so records on later pages are still found.
  const filteredAccounts = useMemo(() => {
    return employees
      .filter(emp => {
        if (accountsTypeFilter !== 'all' && emp.employee_type !== accountsTypeFilter) {
          return false;
        }
        if (accountsSearch) {
          const search = accountsSearch.toLowerCase();
          const name = (emp.name || emp.rider_name || '').toLowerCase();
          const uniqueId = (emp.employee_unique_id || emp.rider_unique_id || '').toLowerCase();
          const emiratesId = (emp.emirates_id || '').toLowerCase();
          const company = (emp.lessee_name || emp.employer_name || '').toLowerCase();
          return name.includes(search) || uniqueId.includes(search) || emiratesId.includes(search) || company.includes(search);
        }
        return true;
      })
      .sort((a, b) => {
        const nameA = (a.name || a.rider_name || '').toLowerCase();
        const nameB = (b.name || b.rider_name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [employees, accountsSearch, accountsTypeFilter]);

  const paginatedAccounts = useMemo(() => {
    const start = (accountsPage - 1) * EMPLOYEES_PER_PAGE;
    return filteredAccounts.slice(start, start + EMPLOYEES_PER_PAGE);
  }, [filteredAccounts, accountsPage]);

  const getStatusBadge = (status, tapStatus = null) => {
    // If it's a TAP-sourced employee, use TAP status colors
    if (tapStatus) {
      const tapStyles = {
        'In Process': 'bg-amber-100 text-amber-800',
        'in_process': 'bg-amber-100 text-amber-800',
        'Ready': 'bg-green-100 text-green-800',
        'ready': 'bg-green-100 text-green-800',
        'On Hold': 'bg-red-100 text-red-800',
        'Hold': 'bg-red-100 text-red-800',
        'hold': 'bg-red-100 text-red-800',
        'on_hold': 'bg-red-100 text-red-800',
        'Cyclist': 'bg-blue-100 text-blue-800',
        'cyclist': 'bg-blue-100 text-blue-800',
        'Rider Enrolled': 'bg-blue-100 text-blue-800',
        'Exported': 'bg-blue-100 text-blue-800',
        'active': 'bg-blue-100 text-blue-800'
      };
      return tapStyles[tapStatus] || 'bg-gray-100 text-gray-800';
    }
    
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-amber-100 text-amber-800',
      pending_approval: 'bg-amber-100 text-amber-800',
      active: 'bg-green-100 text-green-800',
      approved: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };
  
  // Helper to format TAP status for display
  const formatTapStatus = (tapStatus) => {
    const displayMap = {
      'in_process': 'In Process',
      'In Process': 'In Process',
      'ready': 'Ready',
      'Ready': 'Ready',
      'hold': 'On Hold',
      'Hold': 'On Hold',
      'on_hold': 'On Hold',
      'On Hold': 'On Hold',
      'cyclist': 'Cyclist',
      'Cyclist': 'Cyclist',
      'active': 'Cyclist',
      'Rider Enrolled': 'Cyclist',
      'Exported': 'Cyclist'
    };
    return displayMap[tapStatus] || tapStatus;
  };

  return (
    <TooltipProvider delayDuration={200}>
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800">
            {tabFromUrl === 'admin' ? 'PMS Admin' : 'Talent Management'}
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">
            {tabFromUrl === 'admin' ? 'Payroll Configuration & Approval' : 'Payroll Management System'}
          </p>
        </div>
      </div>

      {/* Sub-navigation - Hide when accessed via PMS Admin */}
      {tabFromUrl !== 'admin' && (
        <div className={`flex flex-col gap-2 border-b pb-4 ${isMobile ? 'overflow-x-auto pb-3' : ''}`}>
          {/* Row 1 — Existing modules */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={activeSubPage === 'employees' ? 'default' : 'outline'}
              onClick={() => setActiveSubPage('employees')}
              className="gap-2 whitespace-nowrap"
              size={isMobile ? 'sm' : 'default'}
            >
              <Users className="w-4 h-4" />
              {isMobile ? 'Staff' : 'Employees'}
            </Button>
            <Button
              variant={activeSubPage === 'accounts' ? 'default' : 'outline'}
              onClick={() => setActiveSubPage('accounts')}
              className="gap-2 whitespace-nowrap"
              size={isMobile ? 'sm' : 'default'}
            >
              <Wallet className="w-4 h-4" />
              {isMobile ? 'Accounts' : 'Employee Accounts'}
            </Button>
            <Button
              variant={activeSubPage === 'payroll' ? 'default' : 'outline'}
              onClick={() => setActiveSubPage('payroll')}
              className="gap-2 whitespace-nowrap"
              size={isMobile ? 'sm' : 'default'}
            >
              <Calculator className="w-4 h-4" />
              Payroll
            </Button>
            <Button
              variant={activeSubPage === 'recovery' ? 'default' : 'outline'}
              onClick={() => setActiveSubPage('recovery')}
              className="gap-2 whitespace-nowrap"
              size={isMobile ? 'sm' : 'default'}
              data-testid="recovery-tab-btn"
            >
              <Receipt className="w-4 h-4" />
              {isMobile ? 'Recovery' : 'Recovery Report'}
            </Button>
            <Button
              variant={activeSubPage === 'salary-advance' ? 'default' : 'outline'}
              onClick={() => setActiveSubPage('salary-advance')}
              className="gap-2 whitespace-nowrap"
              size={isMobile ? 'sm' : 'default'}
              data-testid="salary-advance-tab-btn"
            >
              <CreditCard className="w-4 h-4" />
              {isMobile ? 'Advance' : 'Salary Advance'}
            </Button>
            <Button
              variant={activeSubPage === 'adjustments' ? 'default' : 'outline'}
              onClick={() => setActiveSubPage('adjustments')}
              className="gap-2 whitespace-nowrap"
              size={isMobile ? 'sm' : 'default'}
              data-testid="adjustments-tab-btn"
            >
              <RefreshCw className="w-4 h-4" />
              {isMobile ? 'Adjust' : 'Post Payroll Adjustment'}
            </Button>
          </div>
          {/* Row 2 — New modules (room to grow) */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={activeSubPage === 'leaves' ? 'default' : 'outline'}
              onClick={() => setActiveSubPage('leaves')}
              className="gap-2 whitespace-nowrap"
              size={isMobile ? 'sm' : 'default'}
              data-testid="leaves-tab-btn"
            >
              <Calendar className="w-4 h-4" />
              {isMobile ? 'Leaves' : 'Employee Leaves'}
            </Button>
            <Button
              variant="outline"
              onClick={() => { window.location.href = '/talent/warnings'; }}
              className="gap-2 whitespace-nowrap"
              size={isMobile ? 'sm' : 'default'}
              data-testid="warnings-tab-btn"
            >
              <AlertCircle className="w-4 h-4" />
              Warnings
            </Button>
          </div>
        </div>
      )}

      {/* ============ EMPLOYEES SUBPAGE ============ */}
      {activeSubPage === 'employees' && tabFromUrl !== 'admin' && (
        <div className="space-y-4">
          {/* Dashboard Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${employeeFilter === 'all' ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => setEmployeeFilter('all')}
            >
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-500">Total</p>
                    <p className="text-xl sm:text-2xl font-bold">{employeeSummary.total}</p>
                  </div>
                  <Users className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${employeeFilter === 'rider' ? 'ring-2 ring-green-500' : ''}`}
              onClick={() => setEmployeeFilter('rider')}
            >
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-slate-500">Riders</p>
                    <p className="text-xl sm:text-2xl font-bold text-green-600">{employeeSummary.riders}</p>
                  </div>
                  <Briefcase className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${employeeFilter === 'staff' ? 'ring-2 ring-purple-500' : ''}`}
              onClick={() => setEmployeeFilter('staff')}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Staff</p>
                    <p className="text-2xl font-bold text-purple-600">{employeeSummary.staff}</p>
                  </div>
                  <User className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className={`cursor-pointer transition-all hover:shadow-md ${employeeFilter === 'pending' ? 'ring-2 ring-amber-500' : ''}`}
              onClick={() => setEmployeeFilter('pending')}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Pending Documents</p>
                    <p className="text-2xl font-bold text-amber-600">{employeeSummary.pending_documents}</p>
                  </div>
                  <AlertCircle className="w-8 h-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Actions Bar */}
          <div className={`flex flex-wrap items-center gap-4 ${isMobile ? 'flex-col' : 'justify-between'}`}>
            <div className={`flex items-center gap-2 ${isMobile ? 'w-full flex-col' : 'gap-4'}`}>
              <div className={`relative ${isMobile ? 'w-full' : 'w-80'}`}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name, ID, Emirates ID..."
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className={`flex gap-2 ${isMobile ? 'w-full' : ''}`}>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Excel export */}
              <Button
                variant="outline"
                size={isMobile ? "sm" : "default"}
                className="gap-2"
                data-testid="employees-export-excel-btn"
                onClick={async () => {
                  try {
                    const params = new URLSearchParams();
                    if (employeeSearch) params.set('search', employeeSearch);
                    const r = await axios.get(
                      `${API}/talent/employees/export/excel?${params.toString()}`,
                      { headers: getAuthHeader(), responseType: 'blob' },
                    );
                    const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `talent_employees_${new Date().toISOString().slice(0, 10)}.xlsx`;
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                    toast.success('Excel downloaded');
                  } catch (e) {
                    toast.error(formatErrorMessage(e, 'Excel export failed'));
                  }
                }}
              >
                <FileSpreadsheet className="w-4 h-4" />
                {!isMobile && 'Excel'}
              </Button>

              {/* PDF export */}
              <Button
                variant="outline"
                size={isMobile ? "sm" : "default"}
                className="gap-2"
                data-testid="employees-export-pdf-btn"
                onClick={async () => {
                  try {
                    const params = new URLSearchParams();
                    if (employeeSearch) params.set('search', employeeSearch);
                    const r = await axios.get(
                      `${API}/talent/employees/export/pdf?${params.toString()}`,
                      { headers: getAuthHeader(), responseType: 'blob' },
                    );
                    const blob = new Blob([r.data], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `talent_employees_${new Date().toISOString().slice(0, 10)}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                    toast.success('PDF downloaded');
                  } catch (e) {
                    toast.error(formatErrorMessage(e, 'PDF export failed'));
                  }
                }}
              >
                <FileText className="w-4 h-4" />
                {!isMobile && 'PDF'}
              </Button>

              {userCan(user, 'adminTools', 'view', 'dataAudits') && (
                <Button
                  variant="outline"
                  className="gap-2"
                  size={isMobile ? 'sm' : 'default'}
                  data-testid="download-employee-audit-btn"
                  onClick={async () => {
                    try {
                      toast.info('Running employee data audit…');
                      const res = await axios.get(`${API}/admin/audit/employees.xlsx`, {
                        headers: getAuthHeader(),
                        responseType: 'blob'
                      });
                      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `employee_audit_${new Date().toISOString().slice(0, 10)}.xlsx`;
                      document.body.appendChild(a); a.click(); document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                      toast.success('Audit report downloaded');
                    } catch (e) {
                      toast.error(formatErrorMessage(e, 'Failed to run audit'));
                    }
                  }}
                >
                  <AlertCircle className="w-4 h-4" />
                  {!isMobile && 'Data Audit'}
                </Button>
              )}

              {userCan(user, 'adminTools', 'view', 'dataAudits') && (
                <Button
                  variant="outline"
                  className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                  size={isMobile ? 'sm' : 'default'}
                  data-testid="download-document-audit-btn"
                  onClick={async () => {
                    try {
                      toast.info('Running document audit…');
                      const res = await axios.get(`${API}/admin/audit/talent-documents.xlsx`, {
                        headers: getAuthHeader(),
                        responseType: 'blob',
                        timeout: 240000,
                      });
                      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `talent_document_audit_${new Date().toISOString().slice(0, 10)}.xlsx`;
                      document.body.appendChild(a); a.click(); document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                      toast.success('Document audit downloaded');
                    } catch (e) {
                      toast.error(formatErrorMessage(e, 'Failed to run document audit'));
                    }
                  }}
                  title="Excel — per-employee document checklist with Uploaded / Pending / Storage Location."
                >
                  <FileWarning className="w-4 h-4" />
                  {!isMobile && 'Document Audit'}
                </Button>
              )}

              {/* Add Staff Dialog */}
              <Dialog open={addStaffDialogOpen} onOpenChange={setAddStaffDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2 bg-blue-600 hover:bg-blue-700" size={isMobile ? "sm" : "default"}>
                    <UserPlus className="w-4 h-4" />
                    {isMobile ? 'Staff' : 'Add Staff'}
                  </Button>
                </DialogTrigger>
                <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
                  <DialogHeader>
                    <DialogTitle>{isMobile ? 'Add Staff' : 'Add Staff Employee'}</DialogTitle>
                    <DialogDescription className="text-sm">
                      Register a new staff member in the payroll system
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                      <div className="space-y-2">
                        <Label>Full Name *</Label>
                        <Input
                          value={staffForm.name}
                          onChange={(e) => setStaffForm({...staffForm, name: e.target.value})}
                          placeholder="Enter full name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Designation</Label>
                        <Input
                          value={staffForm.designation}
                          onChange={(e) => setStaffForm({...staffForm, designation: e.target.value})}
                          placeholder="e.g., Accountant, Manager"
                        />
                      </div>
                    </div>

                    <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                      <div className="space-y-2">
                        <Label>Emirates ID *</Label>
                        <Input
                          value={staffForm.emirates_id}
                          onChange={(e) => setStaffForm({...staffForm, emirates_id: e.target.value})}
                          placeholder="784-XXXX-XXXXXXX-X"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Emirates ID Expiry</Label>
                        <Input
                          type="date"
                          value={staffForm.emirates_id_expiry}
                          onChange={(e) => setStaffForm({...staffForm, emirates_id_expiry: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                      <div className="space-y-2">
                        <Label>Passport Number</Label>
                        <Input
                          value={staffForm.passport_number}
                          onChange={(e) => setStaffForm({...staffForm, passport_number: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Passport Expiry</Label>
                        <Input
                          type="date"
                          value={staffForm.passport_expiry}
                          onChange={(e) => setStaffForm({...staffForm, passport_expiry: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                      <div className="space-y-2">
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={staffForm.email}
                          onChange={(e) => setStaffForm({...staffForm, email: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Mobile *</Label>
                        <Input
                          value={staffForm.mobile}
                          onChange={(e) => setStaffForm({...staffForm, mobile: e.target.value})}
                          placeholder="05XXXXXXXX"
                        />
                      </div>
                    </div>

                    <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                      <div className="space-y-2">
                        <Label>Employer/Company *</Label>
                        <Select 
                          value={staffForm.employer_name} 
                          onValueChange={(v) => setStaffForm({...staffForm, employer_name: v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select company" />
                          </SelectTrigger>
                          <SelectContent>
                            {lessees.filter(l => l.lessee_type === 'in-house').map(lessee => (
                              <SelectItem key={lessee.id} value={lessee.company_name}>
                                {lessee.company_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Joining Date *</Label>
                        <Input
                          type="date"
                          value={staffForm.joining_date}
                          onChange={(e) => setStaffForm({...staffForm, joining_date: e.target.value})}
                        />
                      </div>
                    </div>

                    {/* Salary Breakdown Section */}
                    <div className="border-t pt-4 mt-4">
                      <h4 className="font-semibold text-sm mb-3">Salary Breakdown</h4>
                      <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                        <div className="space-y-2">
                          <Label>Basic Salary *</Label>
                          <Input
                            type="number"
                            value={staffForm.basic_salary}
                            onChange={(e) => {
                              const basic = parseFloat(e.target.value) || 0;
                              const housing = parseFloat(staffForm.housing_allowance) || 0;
                              const transport = parseFloat(staffForm.transport_allowance) || 0;
                              const telephone = parseFloat(staffForm.telephone_allowance) || 0;
                              const pension = parseFloat(staffForm.pension_amount) || 0;
                              const customTotal = (staffForm.custom_allowances || []).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
                              setStaffForm({
                                ...staffForm,
                                basic_salary: e.target.value,
                                monthly_salary: (basic + housing + transport + telephone + pension + customTotal).toString()
                              });
                            }}
                            placeholder="e.g., 3000"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Housing Allowance</Label>
                          <Input
                            type="number"
                            value={staffForm.housing_allowance}
                            onChange={(e) => {
                              const basic = parseFloat(staffForm.basic_salary) || 0;
                              const housing = parseFloat(e.target.value) || 0;
                              const transport = parseFloat(staffForm.transport_allowance) || 0;
                              const telephone = parseFloat(staffForm.telephone_allowance) || 0;
                              const pension = parseFloat(staffForm.pension_amount) || 0;
                              const customTotal = (staffForm.custom_allowances || []).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
                              setStaffForm({
                                ...staffForm,
                                housing_allowance: e.target.value,
                                monthly_salary: (basic + housing + transport + telephone + pension + customTotal).toString()
                              });
                            }}
                            placeholder="e.g., 1000"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div className="space-y-2">
                          <Label>Transport Allowance</Label>
                          <Input
                            type="number"
                            value={staffForm.transport_allowance}
                            onChange={(e) => {
                              const basic = parseFloat(staffForm.basic_salary) || 0;
                              const housing = parseFloat(staffForm.housing_allowance) || 0;
                              const transport = parseFloat(e.target.value) || 0;
                              const telephone = parseFloat(staffForm.telephone_allowance) || 0;
                              const pension = parseFloat(staffForm.pension_amount) || 0;
                              const customTotal = (staffForm.custom_allowances || []).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
                              setStaffForm({
                                ...staffForm,
                                transport_allowance: e.target.value,
                                monthly_salary: (basic + housing + transport + telephone + pension + customTotal).toString()
                              });
                            }}
                            placeholder="e.g., 500"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Telephone Allowance</Label>
                          <Input
                            type="number"
                            value={staffForm.telephone_allowance}
                            onChange={(e) => {
                              const basic = parseFloat(staffForm.basic_salary) || 0;
                              const housing = parseFloat(staffForm.housing_allowance) || 0;
                              const transport = parseFloat(staffForm.transport_allowance) || 0;
                              const telephone = parseFloat(e.target.value) || 0;
                              const pension = parseFloat(staffForm.pension_amount) || 0;
                              const customTotal = (staffForm.custom_allowances || []).reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
                              setStaffForm({
                                ...staffForm,
                                telephone_allowance: e.target.value,
                                monthly_salary: (basic + housing + transport + telephone + pension + customTotal).toString()
                              });
                            }}
                            placeholder="e.g., 200"
                          />
                        </div>
                      </div>

                      {/* Pension (GPSSA contribution) — counts toward gross
                          but the company pays it directly to the government,
                          so it's a wash on net salary (added to gross AND to
                          deductions on the salary slip). */}
                      <div className="mt-4">
                        <Label>Pension (GPSSA)</Label>
                        <Input
                          type="number"
                          value={staffForm.pension_amount}
                          onChange={(e) => {
                            const basic = parseFloat(staffForm.basic_salary) || 0;
                            const housing = parseFloat(staffForm.housing_allowance) || 0;
                            const transport = parseFloat(staffForm.transport_allowance) || 0;
                            const telephone = parseFloat(staffForm.telephone_allowance) || 0;
                            const pension = parseFloat(e.target.value) || 0;
                            const customTotal = (staffForm.custom_allowances || []).reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
                            setStaffForm({
                              ...staffForm,
                              pension_amount: e.target.value,
                              monthly_salary: (basic + housing + transport + telephone + pension + customTotal).toString()
                            });
                          }}
                          placeholder="e.g., 750 (paid by Company to GPSSA, not to staff)"
                          data-testid="staff-pension-amount"
                        />
                        <p className="text-[11px] text-slate-500 mt-1">
                          Paid at GPSSA. Part of gross salary but auto-deducted on the salary slip — does not affect take-home.
                        </p>
                      </div>

                      {/* Custom Allowances Section */}
                      <div className="mt-4 border-t pt-4">
                        <div className="flex justify-between items-center mb-3">
                          <h5 className="font-medium text-sm text-slate-700">Additional Allowances</h5>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setStaffForm({
                                ...staffForm,
                                custom_allowances: [...(staffForm.custom_allowances || []), { description: '', amount: '' }]
                              });
                            }}
                            className="text-xs h-7"
                          >
                            <Plus className="w-3 h-3 mr-1" /> Add Allowance
                          </Button>
                        </div>
                        
                        {(staffForm.custom_allowances || []).length > 0 && (
                          <div className="space-y-2">
                            {staffForm.custom_allowances.map((allowance, index) => (
                              <div key={index} className="flex gap-2 items-center">
                                <Input
                                  placeholder="Description (e.g., Food Allowance)"
                                  value={allowance.description}
                                  onChange={(e) => {
                                    const updated = [...staffForm.custom_allowances];
                                    updated[index].description = e.target.value;
                                    setStaffForm({ ...staffForm, custom_allowances: updated });
                                  }}
                                  className="flex-1 h-9 text-sm"
                                />
                                <Input
                                  type="number"
                                  placeholder="Amount"
                                  value={allowance.amount}
                                  onChange={(e) => {
                                    const updated = [...staffForm.custom_allowances];
                                    updated[index].amount = e.target.value;
                                    const basic = parseFloat(staffForm.basic_salary) || 0;
                                    const housing = parseFloat(staffForm.housing_allowance) || 0;
                                    const transport = parseFloat(staffForm.transport_allowance) || 0;
                                    const telephone = parseFloat(staffForm.telephone_allowance) || 0;
                                    const customTotal = updated.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
                                    setStaffForm({ 
                                      ...staffForm, 
                                      custom_allowances: updated,
                                      monthly_salary: (basic + housing + transport + telephone + customTotal).toString()
                                    });
                                  }}
                                  className="w-28 h-9 text-sm"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const updated = staffForm.custom_allowances.filter((_, i) => i !== index);
                                    const basic = parseFloat(staffForm.basic_salary) || 0;
                                    const housing = parseFloat(staffForm.housing_allowance) || 0;
                                    const transport = parseFloat(staffForm.transport_allowance) || 0;
                                    const telephone = parseFloat(staffForm.telephone_allowance) || 0;
                                    const customTotal = updated.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
                                    setStaffForm({ 
                                      ...staffForm, 
                                      custom_allowances: updated,
                                      monthly_salary: (basic + housing + transport + telephone + customTotal).toString()
                                    });
                                  }}
                                  className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-blue-800">Total Monthly Salary</span>
                          <span className="text-lg font-bold text-blue-900">
                            <CurrencyDisplay amount={parseFloat(staffForm.monthly_salary || 0)} />
                          </span>
                        </div>
                      </div>
                      
                      <div className="mt-3">
                        <Label>Room Rent (Monthly Deduction)</Label>
                        <Input
                          type="number"
                          value={staffForm.room_rent || ''}
                          onChange={(e) => setStaffForm({...staffForm, room_rent: parseFloat(e.target.value) || 0})}
                          placeholder="0"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                      <div className="space-y-2">
                        <Label>Edenred ID</Label>
                        <Input
                          value={staffForm.edenred_id}
                          onChange={(e) => setStaffForm({...staffForm, edenred_id: e.target.value})}
                          placeholder="For salary processing"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Nationality</Label>
                        <NationalitySelect
                          value={staffForm.nationality}
                          onChange={(val) => setStaffForm({ ...staffForm, nationality: val })}
                          testid="staff-nationality-select"
                        />
                      </div>
                    </div>
                  </div>

                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAddStaffDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleAddStaff} className="bg-blue-600 hover:bg-blue-700">Add Staff</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Employee Log Table */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No employees found</p>
                </div>
              ) : isMobile ? (
                /* Mobile Card View */
                <div className="divide-y divide-slate-100">
                  {paginatedEmployees.map(employee => (
                    <div 
                      key={employee.id} 
                      className="p-4 hover:bg-blue-50 cursor-pointer transition-colors"
                      onClick={() => openEmployeeDetails(employee)}
                      data-testid={`employee-card-${employee.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="font-medium text-slate-900">{employee.name}</div>
                          <div className="font-mono text-xs text-blue-600">{employee.employee_unique_id || '-'}</div>
                        </div>
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <Badge 
                            variant="outline" 
                            className={employee.employee_type === 'rider' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-purple-50 text-purple-700 border-purple-200'}
                          >
                            {employee.employee_type === 'rider' ? 'Rider' : 'Staff'}
                          </Badge>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleViewDocuments(employee)}>
                                <Eye className="mr-2 h-4 w-4" />View Documents
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEmployeeDetails(employee)}>
                                <Eye className="mr-2 h-4 w-4" />View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEditEmployee(employee)}>
                                <Edit className="mr-2 h-4 w-4" />Edit Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => handleDeleteEmployee(employee, e)} className="text-red-600">
                                <Trash2 className="mr-2 h-4 w-4" />Delete Employee
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                        <div><span className="text-slate-500">Emirates ID:</span> <span className="text-slate-700">{employee.emirates_id}</span></div>
                        <div><span className="text-slate-500">Company:</span> <span className="text-slate-700">{truncateCompanyName(employee.lessee_name) || '-'}</span></div>
                        <div><span className="text-slate-500">Platform:</span> <span className="text-slate-700">{employee.platform || '-'}</span></div>
                        <div><span className="text-slate-500">Status:</span> 
                          <Badge className={`ml-1 text-xs ${getStatusBadge(employee.status, employee.tap_status)}`}>
                            {employee.tap_candidate_id ? formatTapStatus(employee.tap_status || employee.status) : (employee.tap_status ? formatTapStatus(employee.tap_status) : 'Ready')}
                          </Badge>
                        </div>
                        <div><span className="text-slate-500">Designation:</span> 
                          <Badge className={`ml-1 text-xs ${employee.designation === 'Cyclist' ? 'bg-amber-100 text-amber-800' : 'bg-cyan-100 text-cyan-800'}`}>
                            {employee.designation || 'Motorcyclist'}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
                        <span className="text-xs text-slate-500">Documents:</span>
                        {employee.documents_complete ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Complete</Badge>
                        ) : (
                          <Badge 
                            variant="secondary"
                            className="cursor-pointer bg-amber-100 text-amber-800"
                            onClick={() => {
                              setSelectedEmployee(employee);
                              setDocumentDialogOpen(true);
                            }}
                          >
                            Pending
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Desktop Table View */
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Unique ID</TableHead>
                      <TableHead className="font-semibold">Name</TableHead>
                      <TableHead className="font-semibold">Emirates ID</TableHead>
                      <TableHead className="font-semibold">Edenred ID</TableHead>
                      <TableHead className="font-semibold">Company</TableHead>
                      <TableHead className="font-semibold">Platform</TableHead>
                      <TableHead className="font-semibold whitespace-normal leading-tight" style={{maxWidth: '90px'}}>Employee Category</TableHead>
                      <TableHead className="font-semibold whitespace-normal leading-tight" style={{maxWidth: '80px'}}>TAP Status</TableHead>
                      <TableHead className="font-semibold">Visa Designation</TableHead>
                      <TableHead className="font-semibold whitespace-normal text-center" style={{maxWidth: '80px'}}>Upload Documents</TableHead>
                      <TableHead className="font-semibold text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedEmployees.map(employee => (
                      <TableRow 
                        key={employee.id} 
                        className="hover:bg-blue-50 cursor-pointer transition-colors"
                        onClick={() => openEmployeeDetails(employee)}
                      >
                        <TableCell className="font-mono text-sm text-blue-600">
                          {employee.employee_unique_id || '-'}
                        </TableCell>
                        <TableCell className="font-medium" title={employee.name}>{truncateName(employee.name)}</TableCell>
                        <TableCell className="text-sm">{employee.emirates_id}</TableCell>
                        <TableCell className="text-sm">{employee.edenred_id || '-'}</TableCell>
                        <TableCell className="text-sm" title={employee.lessee_name}>{truncateCompanyName(employee.lessee_name) || '-'}</TableCell>
                        <TableCell className="text-sm">{employee.platform || '-'}</TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={employee.employee_type === 'rider' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-purple-50 text-purple-700 border-purple-200'}
                          >
                            {employee.employee_type === 'rider' ? 'Rider' : 'Staff'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            className={getStatusBadge(employee.status, employee.tap_status)}
                          >
                            {employee.tap_candidate_id ? formatTapStatus(employee.tap_status || employee.status) : (employee.tap_status ? formatTapStatus(employee.tap_status) : 'Ready')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            className={employee.designation === 'Cyclist' ? 'bg-amber-100 text-amber-800' : 'bg-cyan-100 text-cyan-800'}
                          >
                            {employee.designation || 'Motorcyclist'}
                          </Badge>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {employee.documents_complete ? (
                            <Badge 
                              variant="outline"
                              className="bg-green-50 text-green-700 border-green-200"
                            >
                              Complete
                            </Badge>
                          ) : (
                            <Badge 
                              variant="secondary"
                              className="cursor-pointer bg-amber-100 text-amber-800"
                              onClick={() => {
                                setSelectedEmployee(employee);
                                setDocumentDialogOpen(true);
                              }}
                            >
                              Pending
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0"
                                data-testid={`actions-employee-${employee.id}`}
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleViewDocuments(employee)} className="cursor-pointer">
                                <Eye className="mr-2 h-4 w-4" />
                                View Documents
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEmployeeDetails(employee)} className="cursor-pointer">
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEditEmployee(employee)} className="cursor-pointer">
                                <Edit className="mr-2 h-4 w-4" />
                                Edit Details
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={(e) => handleDeleteEmployee(employee, e)} 
                                className="cursor-pointer text-red-600 focus:text-red-600"
                                data-testid={`delete-employee-${employee.id}`}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete Employee
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {/* Pagination — search filters across ALL employees, not just this page. */}
              {!loading && filteredEmployees.length > EMPLOYEES_PER_PAGE && (
                <div className="px-4 pb-4">
                  <Pagination
                    currentPage={employeesPage}
                    totalPages={Math.ceil(filteredEmployees.length / EMPLOYEES_PER_PAGE)}
                    totalCount={filteredEmployees.length}
                    itemsPerPage={EMPLOYEES_PER_PAGE}
                    onPageChange={setEmployeesPage}
                    loading={loading}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ============ EMPLOYEE ACCOUNTS SUBPAGE ============ */}
      {activeSubPage === 'accounts' && tabFromUrl !== 'admin' && (
        <div className="space-y-4">
          {/* Search and Filter Controls */}
          <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-4 items-start md:items-center`}>
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search by name, unique ID, Emirates ID, or company..."
                value={accountsSearch}
                onChange={(e) => setAccountsSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={accountsTypeFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAccountsTypeFilter('all')}
              >
                All
              </Button>
              <Button
                variant={accountsTypeFilter === 'rider' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAccountsTypeFilter('rider')}
                className={accountsTypeFilter === 'rider' ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                Riders
              </Button>
              <Button
                variant={accountsTypeFilter === 'staff' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setAccountsTypeFilter('staff')}
                className={accountsTypeFilter === 'staff' ? 'bg-purple-600 hover:bg-purple-700' : ''}
              >
                Staff
              </Button>
            </div>
          </div>

          {/* Loan Summary Dashboard Cards - Filtered by employee type */}
          {loanSummary && (() => {
            // Filter summary data based on selected employee type
            const filterByType = (entries, typeField = 'employee_type') => {
              if (accountsTypeFilter === 'all') return entries || [];
              return (entries || []).filter(e => e[typeField] === accountsTypeFilter);
            };
            
            const filteredLoanEntries = filterByType(loanSummary.loan_entries);
            const filteredRecoveryEntries = filterByType(loanSummary.recovery_entries);
            const filteredOutstanding = filterByType(loanSummary.employees_with_outstanding);
            const filteredEmi = filterByType(loanSummary.employees_with_emi);
            const filteredAccounts = filterByType(loanSummary.accounts);
            
            const totalLoanFiltered = filteredLoanEntries.reduce((sum, e) => sum + (e.amount || 0), 0);
            const totalRecoveryFiltered = filteredRecoveryEntries.reduce((sum, e) => sum + (e.amount || 0), 0);
            const totalOutstandingFiltered = filteredOutstanding.reduce((sum, e) => sum + (e.outstanding || 0), 0);
            const totalEmiFiltered = filteredEmi.reduce((sum, e) => sum + (e.agreed_emi || 0), 0);
            // NEW rollups sourced from the per-account list (backend `accounts`)
            const totalContractLoanFiltered = filteredAccounts.reduce((s, a) => s + (a.contract_total || 0), 0);
            const totalExpensesFiltered = filteredAccounts.reduce((s, a) => s + (a.expenses_total || 0), 0);
            const totalScheduledEmiFiltered = filteredAccounts.reduce((s, a) => s + (a.scheduled_emi_total || 0), 0);
            const contractLoanEmployeeCount = filteredAccounts.filter(a => (a.contract_total || 0) > 0).length;
            const scheduledEmiEmployeeCount = filteredAccounts.filter(a => (a.scheduled_emi_total || 0) > 0).length;
            const totalScheduledEmiCount = filteredAccounts.reduce((s, a) => s + (a.scheduled_emi_count || 0), 0);
            const totalPendingEmiCount = filteredAccounts.reduce((s, a) => s + (a.pending_emi_count || 0), 0);
            
            // Calculate breakdown by loan type for tooltip
            const loanByType = filteredLoanEntries.reduce((acc, e) => {
              const type = e.loan_type || 'other';
              acc[type] = (acc[type] || 0) + (e.amount || 0);
              return acc;
            }, {});
            
            // Get top employees with outstanding
            const topOutstanding = [...filteredOutstanding].sort((a, b) => b.outstanding - a.outstanding).slice(0, 3);
            
            // Get top employees by loan amount
            const employeeLoanTotals = filteredLoanEntries.reduce((acc, e) => {
              const name = e.employee_name || 'Unknown';
              acc[name] = (acc[name] || 0) + (e.amount || 0);
              return acc;
            }, {});
            const topLoanEmployees = Object.entries(employeeLoanTotals)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 3);
            
            return (
              <TooltipProvider delayDuration={200}>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  {/* Contract Loan Amount Card */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Card 
                        className="cursor-pointer hover:shadow-lg transition-shadow bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200"
                        onClick={() => {
                          setLoanSummaryDialogType('loans');
                          setLoanSummaryDialogOpen(true);
                        }}
                        data-testid="total-loan-card"
                      >
                        <CardContent className="pt-6">
                          <div className="text-center">
                            <p className="text-sm font-medium text-blue-600 mb-2">Contract Loan Amount</p>
                            <p className="text-2xl font-bold text-blue-700 whitespace-nowrap">
                              <CurrencyDisplay amount={totalContractLoanFiltered} />
                            </p>
                            <p className="text-xs text-blue-500 mt-2">
                              {contractLoanEmployeeCount} employees
                              {accountsTypeFilter !== 'all' && <span className="ml-1">({accountsTypeFilter}s)</span>}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="bg-slate-800 text-white p-3 max-w-xs">
                      <div className="text-xs space-y-2">
                        <div className="font-semibold border-b border-slate-600 pb-1 mb-2">Contract Loans</div>
                        <div className="text-slate-300">
                          Sum of all agreed contract loan amounts across {contractLoanEmployeeCount} employees.
                        </div>
                        {filteredAccounts.length > 0 && (
                          <>
                            <div className="font-semibold border-t border-slate-600 pt-2 mt-2">Top Employees</div>
                            {[...filteredAccounts]
                              .filter(a => (a.contract_total || 0) > 0)
                              .sort((a, b) => (b.contract_total || 0) - (a.contract_total || 0))
                              .slice(0, 3)
                              .map((emp) => (
                                <div key={emp.employee_id} className="flex justify-between">
                                  <span className="truncate max-w-[120px]">{emp.employee_name}:</span>
                                  <span>AED {(emp.contract_total || 0).toLocaleString()}</span>
                                </div>
                              ))}
                          </>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>

                  {/* Total Expenses Card */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Card
                        className="cursor-pointer hover:shadow-lg transition-shadow bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200"
                        onClick={() => {
                          setLoanSummaryDialogType('expenses');
                          setLoanSummaryDialogOpen(true);
                        }}
                        data-testid="total-expenses-card"
                      >
                        <CardContent className="pt-6">
                          <div className="text-center">
                            <p className="text-sm font-medium text-amber-600 mb-2">Total Expenses</p>
                            <p className="text-2xl font-bold text-amber-700 whitespace-nowrap">
                              <CurrencyDisplay amount={totalExpensesFiltered} />
                            </p>
                            <p className="text-xs text-amber-500 mt-2">
                              {filteredLoanEntries.length} entries
                              {accountsTypeFilter !== 'all' && <span className="ml-1">({accountsTypeFilter}s)</span>}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="bg-slate-800 text-white p-3 max-w-xs">
                      <div className="text-xs space-y-2">
                        <div className="font-semibold border-b border-slate-600 pb-1 mb-2">Expense Breakdown by Type</div>
                        {Object.entries(loanByType).length > 0 ? (
                          Object.entries(loanByType).map(([type, amount]) => (
                            <div key={type} className="flex justify-between">
                              <span className="capitalize">{type.replace('_', ' ')}:</span>
                              <span>AED {amount.toLocaleString()}</span>
                            </div>
                          ))
                        ) : (
                          <div className="text-slate-400">No expense entries</div>
                        )}
                        {topLoanEmployees.length > 0 && (
                          <>
                            <div className="font-semibold border-t border-slate-600 pt-2 mt-2">Top Employees</div>
                            {topLoanEmployees.map(([name, amount]) => (
                              <div key={name} className="flex justify-between">
                                <span className="truncate max-w-[120px]">{name}:</span>
                                <span>AED {amount.toLocaleString()}</span>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                  
                  {/* Total Recovery Card */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Card 
                        className="cursor-pointer hover:shadow-lg transition-shadow bg-gradient-to-br from-green-50 to-green-100 border-green-200"
                        onClick={() => {
                          setLoanSummaryDialogType('recoveries');
                          setLoanSummaryDialogOpen(true);
                        }}
                        data-testid="total-recovery-card"
                      >
                        <CardContent className="pt-6">
                          <div className="text-center">
                            <p className="text-sm font-medium text-green-600 mb-2">Total Recovery</p>
                            <p className="text-2xl font-bold text-green-700 whitespace-nowrap">
                              <CurrencyDisplay amount={totalRecoveryFiltered} />
                            </p>
                            <p className="text-xs text-green-500 mt-2">
                              {filteredRecoveryEntries.length} entries
                              {accountsTypeFilter !== 'all' && <span className="ml-1">({accountsTypeFilter}s)</span>}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="bg-slate-800 text-white p-3 max-w-xs">
                      <div className="text-xs space-y-2">
                        <div className="font-semibold border-b border-slate-600 pb-1 mb-2">Recovery Summary</div>
                        <div className="flex justify-between">
                          <span>Contract Loan Amount:</span>
                          <span>AED {totalContractLoanFiltered.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Total Recovered:</span>
                          <span>AED {totalRecoveryFiltered.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between font-semibold pt-1 border-t border-slate-600">
                          <span>Recovery Rate:</span>
                          <span>{totalContractLoanFiltered > 0 ? Math.round((totalRecoveryFiltered / totalContractLoanFiltered) * 100) : 0}%</span>
                        </div>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                  
                  {/* Outstanding Card */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Card 
                        className="cursor-pointer hover:shadow-lg transition-shadow bg-gradient-to-br from-red-50 to-red-100 border-red-200"
                        onClick={() => {
                          setLoanSummaryDialogType('outstanding');
                          setLoanSummaryDialogOpen(true);
                        }}
                        data-testid="outstanding-card"
                      >
                        <CardContent className="pt-6">
                          <div className="text-center">
                            <p className="text-sm font-medium text-red-600 mb-2">Outstanding</p>
                            <p className="text-2xl font-bold text-red-700 whitespace-nowrap">
                              <CurrencyDisplay amount={totalOutstandingFiltered} />
                            </p>
                            <p className="text-xs text-red-500 mt-2">
                              {filteredOutstanding.length} employees
                              {accountsTypeFilter !== 'all' && <span className="ml-1">({accountsTypeFilter}s)</span>}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="bg-slate-800 text-white p-3 max-w-xs">
                      <div className="text-xs space-y-2">
                        <div className="font-semibold border-b border-slate-600 pb-1 mb-2">Top Outstanding</div>
                        {topOutstanding.length > 0 ? (
                          topOutstanding.map((emp, idx) => (
                            <div key={idx} className="flex justify-between">
                              <span className="truncate max-w-[120px]">{emp.employee_name}:</span>
                              <span className="text-red-300">AED {emp.outstanding.toLocaleString()}</span>
                            </div>
                          ))
                        ) : (
                          <div className="text-slate-400">No outstanding balances</div>
                        )}
                        {filteredOutstanding.length > 3 && (
                          <div className="text-slate-400 pt-1 border-t border-slate-600">
                            +{filteredOutstanding.length - 3} more employees
                          </div>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                  
                  {/* Scheduled EMIs Card */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Card 
                        className="cursor-pointer hover:shadow-lg transition-shadow bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200"
                        onClick={() => {
                          setLoanSummaryDialogType('emi');
                          setLoanSummaryDialogOpen(true);
                        }}
                        data-testid="scheduled-emis-card"
                      >
                        <CardContent className="pt-6">
                          <div className="text-center">
                            <p className="text-sm font-medium text-purple-600 mb-2">Scheduled EMIs</p>
                            <p className="text-2xl font-bold text-purple-700 whitespace-nowrap">
                              <CurrencyDisplay amount={totalScheduledEmiFiltered} />
                            </p>
                            <p className="text-xs text-purple-500 mt-2">
                              {scheduledEmiEmployeeCount} employees
                              {accountsTypeFilter !== 'all' && <span className="ml-1">({accountsTypeFilter}s)</span>}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="bg-slate-800 text-white p-3 max-w-xs">
                      <div className="text-xs space-y-2">
                        <div className="font-semibold border-b border-slate-600 pb-1 mb-2">EMI Schedule</div>
                        <div className="flex justify-between">
                          <span>Total Scheduled EMIs:</span>
                          <span>AED {totalScheduledEmiFiltered.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>EMIs (total):</span>
                          <span>{totalScheduledEmiCount}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>EMIs pending:</span>
                          <span>{totalPendingEmiCount}</span>
                        </div>
                        <div className="flex justify-between pt-1 border-t border-slate-600">
                          <span>Monthly EMI Total:</span>
                          <span>AED {totalEmiFiltered.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Outstanding Balance:</span>
                          <span>AED {totalOutstandingFiltered.toLocaleString()}</span>
                        </div>
                        {totalEmiFiltered > 0 && totalOutstandingFiltered > 0 && (
                          <div className="flex justify-between font-semibold pt-1 border-t border-slate-600">
                            <span>Est. Months to Clear:</span>
                            <span>{Math.ceil(totalOutstandingFiltered / totalEmiFiltered)} months</span>
                          </div>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>
            );
          })()}
          
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Wallet className="w-5 h-5" />
                    Employee Accounts
                    {accountsTypeFilter !== 'all' && (
                      <Badge variant="outline" className={accountsTypeFilter === 'rider' ? 'bg-green-50 text-green-700' : 'bg-purple-50 text-purple-700'}>
                        {accountsTypeFilter === 'rider' ? 'Riders' : 'Staff'}
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>View loan accounts, outstanding balances, and account statements</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <DuplicateAccountsDialog
                    reopenSignal={dupReopenSignal}
                    onChanged={() => { fetchEmployees(); fetchLoanSummary(); }}
                    onOpenLoan={(r) => {
                      dupReturnRef.current = true;
                      openLoanDialog(employees.find((e) => e.id === r.payroll_rider_id) || { id: r.payroll_rider_id, name: r.name });
                    }}
                  />
                  <Button 
                    variant="outline"
                    className="gap-2"
                    onClick={() => setLoanBulkUploadDialogOpen(true)}
                  >
                    <Upload className="w-4 h-4" />
                    Bulk Upload
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="gap-2" data-testid="download-all-statements-btn">
                        <Download className="w-4 h-4" />
                        Download All Statements
                        <ChevronDown className="w-3 h-3 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        data-testid="download-excel-btn"
                        onClick={async () => {
                          try {
                            toast.info('Generating Excel report...');
                            const response = await axios.get(`${API}/talent/reports/all-account-statements?format=excel`, {
                              headers: getAuthHeader(),
                              responseType: 'blob'
                            });
                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `Employee_Account_Statements_${new Date().toISOString().slice(0,10)}.xlsx`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                            toast.success('Excel report downloaded');
                          } catch (error) {
                            toast.error('Failed to generate Excel report');
                          }
                        }}
                      >
                        <FileSpreadsheet className="mr-2 h-4 w-4 text-green-600" />
                        Download as Excel
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        data-testid="download-pdf-btn"
                        onClick={async () => {
                          try {
                            toast.info('Generating PDF report...');
                            const response = await axios.get(`${API}/talent/reports/all-account-statements?format=pdf`, {
                              headers: getAuthHeader(),
                              responseType: 'blob'
                            });
                            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `Employee_Account_Statements_${new Date().toISOString().slice(0,10)}.pdf`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                            toast.success('PDF report downloaded');
                          } catch (error) {
                            toast.error('Failed to generate PDF report');
                          }
                        }}
                      >
                        <FileText className="mr-2 h-4 w-4 text-red-600" />
                        Download as PDF
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {(() => {
                // Uses the component-level `filteredAccounts` memo above
                // (search runs across the FULL employees list). We render
                // a page-sized slice here and hang <Pagination /> below.

                if (filteredAccounts.length === 0) {
                  return (
                    <div className="text-center py-12 text-slate-500">
                      <Wallet className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>{accountsSearch || accountsTypeFilter !== 'all' ? 'No matching employee accounts found' : 'No employee accounts found'}</p>
                    </div>
                  );
                }

                if (isMobile) {
                  return (
                    /* Mobile Card View for Employee Accounts */
                    <div className="divide-y divide-slate-100">
                      {paginatedAccounts.map(employee => {
                    const loanBalance = employee.loan_balance || 0;
                    const totalLoan = employee.total_loan || 0;
                    const totalRecovery = employee.total_recovery || 0;
                    const progressPercent = totalLoan > 0 ? Math.round((totalRecovery / totalLoan) * 100) : 0;
                    return (
                      <div 
                        key={employee.id} 
                        className="p-4 hover:bg-blue-50 cursor-pointer transition-colors"
                        onClick={() => openEmployeeDetails(employee)}
                        data-testid={`account-card-${employee.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="font-medium text-slate-900">{employee.name}</div>
                            <div className="font-mono text-xs text-blue-600">{employee.employee_unique_id || '-'}</div>
                          </div>
                          <Badge variant="outline" className={employee.employee_type === 'rider' ? 'bg-green-50 text-green-700' : 'bg-purple-50 text-purple-700'}>
                            {employee.employee_type === 'rider' ? 'Rider' : 'Staff'}
                          </Badge>
                        </div>
                        <div className="text-sm text-slate-600 mb-2">{employee.lessee_name || '-'}</div>
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm text-slate-500">Outstanding:</span>
                          <span className={`font-semibold ${loanBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            <CurrencyDisplay amount={loanBalance} />
                          </span>
                        </div>
                        {employee.loan_account_id && totalLoan > 0 && (
                          <div className="mb-3">
                            <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                              <span>Recovery Progress</span>
                              <span>{progressPercent}%</span>
                            </div>
                            <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full transition-all ${progressPercent >= 100 ? 'bg-green-500' : progressPercent >= 50 ? 'bg-blue-500' : 'bg-orange-500'}`} 
                                style={{ width: `${Math.min(100, progressPercent)}%` }} 
                              />
                            </div>
                            <div className="flex justify-between text-xs text-slate-400 mt-1">
                              <span>Recovered: AED {totalRecovery.toLocaleString()}</span>
                              <span>Total: AED {totalLoan.toLocaleString()}</span>
                            </div>
                          </div>
                        )}
                        {employee.loan_account_id && totalLoan === 0 && (
                          <div className="mb-3 text-xs text-slate-400">No loan entries yet</div>
                        )}
                        <div className="flex gap-2 pt-2 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="flex-1 h-8 text-xs"
                            onClick={() => openLoanDialog(employee)}
                          >
                            <CreditCard className="w-3 h-3 mr-1" />
                            Loan Details
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="flex-1 h-8 text-xs"
                            onClick={() => openAccountStatement(employee)}
                          >
                            <FileText className="w-3 h-3 mr-1" />
                            Statement
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                  );
                }

                // Desktop Table View
                return (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-100 border-b-2 border-slate-200">
                      <TableHead className="font-bold text-slate-700">Employee ID</TableHead>
                      <TableHead className="font-bold text-slate-700">Name</TableHead>
                      <TableHead className="font-bold text-slate-700">Emirates ID</TableHead>
                      <TableHead className="font-bold text-slate-700">Company</TableHead>
                      <TableHead className="font-bold text-slate-700">Type</TableHead>
                      <TableHead className="font-bold text-slate-700 whitespace-normal leading-tight" style={{maxWidth: '100px'}}>
                        Loan<br/>Outstanding
                      </TableHead>
                      <TableHead className="font-bold text-slate-700">Recovery Progress</TableHead>
                      <TableHead className="font-bold text-slate-700 text-center">Loan Details</TableHead>
                      <TableHead className="font-bold text-slate-700 text-center">Statement</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedAccounts.map(employee => {
                      const loanBalance = employee.loan_balance || 0;
                      const totalLoan = employee.total_loan || 0;
                      const totalRecovery = employee.total_recovery || 0;
                      const progressPercent = totalLoan > 0 ? Math.round((totalRecovery / totalLoan) * 100) : 0;
                      return (
                        <TableRow 
                          key={employee.id} 
                          className="hover:bg-blue-50 cursor-pointer transition-colors border-b"
                          onClick={() => openEmployeeDetails(employee)}
                        >
                          <TableCell className="font-mono text-sm text-blue-600">
                            {employee.employee_unique_id || employee.rider_unique_id || '-'}
                          </TableCell>
                          <TableCell className="font-medium">{employee.name || employee.rider_name}</TableCell>
                          <TableCell className="font-mono text-xs">{employee.emirates_id || '-'}</TableCell>
                          <TableCell className="text-sm">{employee.lessee_name || employee.employer_name || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={employee.employee_type === 'rider' ? 'bg-green-50 text-green-700' : 'bg-purple-50 text-purple-700'}>
                              {employee.employee_type === 'rider' ? 'Rider' : 'Staff'}
                            </Badge>
                          </TableCell>
                          <TableCell className={loanBalance > 0 ? "text-red-600 font-semibold" : "text-green-600"}>
                            <CurrencyDisplay amount={loanBalance} />
                          </TableCell>
                          <TableCell>
                            {employee.loan_account_id && totalLoan > 0 ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex items-center gap-2 cursor-help">
                                      <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                                        <div 
                                          className={`h-full rounded-full transition-all ${progressPercent >= 100 ? 'bg-green-500' : progressPercent >= 50 ? 'bg-blue-500' : 'bg-orange-500'}`}
                                          style={{ width: `${Math.min(100, progressPercent)}%` }} 
                                        />
                                      </div>
                                      <span className="text-xs text-slate-500">{progressPercent}%</span>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-slate-800 text-white p-2">
                                    <div className="text-xs space-y-1">
                                      <div>Total Loan: AED {totalLoan.toLocaleString()}</div>
                                      <div>Recovered: AED {totalRecovery.toLocaleString()}</div>
                                      <div>Outstanding: AED {loanBalance.toLocaleString()}</div>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : employee.loan_account_id ? (
                              <span className="text-slate-400 text-sm">No entries</span>
                            ) : (
                              <span className="text-slate-400 text-sm">No loan</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-8 px-3 text-xs gap-1"
                              onClick={() => openLoanDialog(employee)}
                            >
                              <CreditCard className="w-3 h-3" />
                              View
                            </Button>
                          </TableCell>
                          <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-8 px-3 text-xs gap-1"
                              onClick={() => openAccountStatement(employee)}
                            >
                              <FileText className="w-3 h-3" />
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              );
              })()}
              {/* Pagination — search runs across ALL accounts, not just this page. */}
              {filteredAccounts.length > EMPLOYEES_PER_PAGE && (
                <div className="mt-2">
                  <Pagination
                    currentPage={accountsPage}
                    totalPages={Math.ceil(filteredAccounts.length / EMPLOYEES_PER_PAGE)}
                    totalCount={filteredAccounts.length}
                    itemsPerPage={EMPLOYEES_PER_PAGE}
                    onPageChange={setAccountsPage}
                    loading={loading}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ============ PAYROLL SUBPAGE ============ */}
      {activeSubPage === 'payroll' && tabFromUrl !== 'admin' && (
        <div className="space-y-4">
          {/* Dashboard Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600">Payrolls Generated</p>
                    <p className="text-2xl font-bold text-blue-800">{payrollDashboard.payrolls_generated}</p>
                  </div>
                  <FileText className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">Last Payroll</p>
                    <p className="text-2xl font-bold text-green-800">
                      {payrollDashboard.last_payroll_month ? 
                        `${['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][payrollDashboard.last_payroll_month]} ${payrollDashboard.last_payroll_year}` 
                        : '-'}
                    </p>
                  </div>
                  <Calendar className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600">Employees</p>
                    <p className="text-2xl font-bold text-purple-800">{payrollDashboard.total_employees}</p>
                  </div>
                  <Users className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-600">Platforms</p>
                    <p className="text-2xl font-bold text-amber-800">{platforms.length}</p>
                  </div>
                  <Building2 className="w-8 h-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Create Payroll Button + Drafts */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {payrollDrafts.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-amber-700 flex items-center gap-1">
                    <FileCheck className="w-4 h-4" />
                    Saved Drafts:
                  </span>
                  {payrollDrafts.map((d) => {
                    const monthName = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.month];
                    return (
                      <Button 
                        key={d.id}
                        variant="outline" 
                        size="sm" 
                        className="h-8 text-xs bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100 gap-1"
                        onClick={() => loadDraft(d.id)}
                        data-testid={`draft-btn-${d.id}`}
                      >
                        <Clock className="w-3 h-3" />
                        {monthName} {d.year} — {d.payroll_type === 'staff' ? 'Staff' : d.platform || 'Rider'}
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
            <Button 
              className="gap-2 bg-green-600 hover:bg-green-700"
              onClick={() => { resetWizard(); setPayrollWizardOpen(true); }}
            >
              <Plus className="w-4 h-4" />
              Create Payroll
            </Button>
          </div>

          {/* Pending Approval Log — read-only view of batches awaiting admin approval */}
          {pendingBatches.length > 0 && (
            <Card className="border-amber-200 mb-4">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-amber-900">
                      <Clock className="w-5 h-5 text-amber-600" />
                      Pending Approval Log
                      <Badge className="bg-amber-100 text-amber-700 ml-2">{pendingBatches.length}</Badge>
                    </CardTitle>
                    <CardDescription>Payroll batches awaiting admin approval — view-only (no edits possible)</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchPendingBatches} data-testid="refresh-pending-log">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 text-xs">
                        <TableHead>Type</TableHead>
                        <TableHead>Platform</TableHead>
                        <TableHead>Period</TableHead>
                        <TableHead className="text-center">Employees</TableHead>
                        <TableHead className="text-right text-green-700">Gross</TableHead>
                        <TableHead className="text-right text-red-600">Deductions</TableHead>
                        <TableHead className="text-right text-blue-700 font-semibold">Net</TableHead>
                        <TableHead>Submitted By</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingBatches.map((batch) => (
                        <TableRow key={batch.id} className="text-sm" data-testid={`pending-log-row-${batch.id}`}>
                          <TableCell>
                            <Badge variant="outline" className={batch.payroll_type === 'rider' ? 'bg-green-50 text-green-700' : 'bg-purple-50 text-purple-700'}>
                              {batch.payroll_type === 'rider' ? 'Rider' : 'Staff'}
                            </Badge>
                          </TableCell>
                          <TableCell>{batch.platform || '-'}</TableCell>
                          <TableCell className="text-xs">
                            {['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][batch.month]} {batch.year}
                          </TableCell>
                          <TableCell className="text-center">{batch.total_employees}</TableCell>
                          <TableCell className="text-right font-mono text-green-700"><CurrencyDisplay amount={batch.total_gross || 0} /></TableCell>
                          <TableCell className="text-right font-mono text-red-600"><CurrencyDisplay amount={batch.total_deductions || 0} /></TableCell>
                          <TableCell className="text-right font-mono font-semibold text-blue-700"><CurrencyDisplay amount={batch.total_net || 0} /></TableCell>
                          <TableCell className="text-xs text-slate-600">{batch.created_by_name || batch.created_by || '-'}</TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="sm" variant="ghost"
                                  className="h-7 w-7 p-0"
                                  data-testid={`pending-log-actions-${batch.id}`}
                                  title="Actions"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-44">
                                <DropdownMenuLabel className="text-xs">Actions</DropdownMenuLabel>
                                <DropdownMenuItem
                                  onClick={() => handleOpenWizardReadOnly(batch)}
                                  data-testid={`pending-log-view-${batch.id}`}
                                >
                                  <Eye className="w-3.5 h-3.5 mr-2" /> View Wizard
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => handleDownloadPayroll(batch.id, 'pdf')}
                                  data-testid={`pending-log-pdf-${batch.id}`}
                                >
                                  <FileDown className="w-3.5 h-3.5 mr-2 text-blue-600" /> Download PDF
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDownloadPayroll(batch.id, 'excel')}
                                  data-testid={`pending-log-excel-${batch.id}`}
                                >
                                  <FileDown className="w-3.5 h-3.5 mr-2 text-emerald-600" /> Download Excel
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {/* Inline expansion uses the same expandedBatchId state. Render the
                    per-employee breakdown below the table when expanded. */}
                {pendingBatches.filter(b => b.id === expandedBatchId).map(batch => batch.payroll_details?.length > 0 && (
                  <div key={`exp-${batch.id}`} className="mt-3 border-t pt-3">
                    <p className="text-xs font-semibold text-slate-600 mb-2">Per-Employee Breakdown — {batch.platform}, {['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][batch.month]} {batch.year}</p>
                    <div className="max-h-60 overflow-y-auto border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50 text-xs">
                            <TableHead>Employee</TableHead>
                            <TableHead className="text-right">Gross</TableHead>
                            <TableHead className="text-right">Fines</TableHead>
                            <TableHead className="text-right">Salik</TableHead>
                            <TableHead className="text-right">Maint.</TableHead>
                            <TableHead className="text-right">Loan</TableHead>
                            <TableHead className="text-right">Net</TableHead>
                            <TableHead className="text-center w-12">View</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {batch.payroll_details.map((p, i) => {
                            const ded = p.deductions_breakdown || {};
                            return (
                              <TableRow key={i} className="text-sm">
                                <TableCell>
                                  <p className="font-medium">{p.employee_name}</p>
                                  <p className="text-xs text-slate-500 font-mono">{p.employee_unique_id}</p>
                                </TableCell>
                                <TableCell className="text-right text-green-700 font-mono"><CurrencyDisplay amount={p.gross_earnings || 0} /></TableCell>
                                <TableCell className="text-right text-red-600">{ded.fines || 0}</TableCell>
                                <TableCell className="text-right text-red-600">{ded.salik || 0}</TableCell>
                                <TableCell className="text-right text-red-600">{ded.maintenance || 0}</TableCell>
                                <TableCell className="text-right text-red-600">{ded.loan_emi || 0}</TableCell>
                                <TableCell className="text-right font-semibold font-mono"><CurrencyDisplay amount={p.net_salary || 0} /></TableCell>
                                <TableCell className="text-center">
                                  <Button
                                    size="icon" variant="ghost"
                                    className="h-7 w-7 text-blue-700 hover:bg-blue-50"
                                    onClick={() => setCalculationDialog({
                                      open: true,
                                      data: p.calculation_breakdown,
                                      riderName: p.employee_name,
                                      riderData: p,
                                    })}
                                    data-testid={`pending-log-calc-${batch.id}-${i}`}
                                    title="View Salary Calculation (read-only)"
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Approved Payroll Log */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileCheck className="w-5 h-5" />
                    Approved Payroll Log
                  </CardTitle>
                  <CardDescription>Log of approved payrolls with download options</CardDescription>
                </div>
                {selectedMergeBatches.length > 0 && (
                  <Button 
                    onClick={handleMergePayrolls} 
                    disabled={merging}
                    className="gap-2 bg-blue-600 hover:bg-blue-700"
                    data-testid="merge-payrolls-btn"
                  >
                    {merging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Merge & Download ({selectedMergeBatches.length})
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {approvedPayrollLog.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No approved payrolls yet</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="approved-log-grouped">
                  {(() => {
                    // Group batches by year+month; iterate the array (already sorted DESC)
                    const groups = {};
                    const groupOrder = [];
                    approvedPayrollLog.forEach(b => {
                      const k = `${b.year}-${String(b.month).padStart(2, '0')}`;
                      if (!(k in groups)) {
                        groups[k] = { items: [], year: b.year, month: b.month,
                                       label: ['', 'January', 'February', 'March', 'April', 'May', 'June',
                                               'July', 'August', 'September', 'October', 'November', 'December'][b.month] + ' ' + b.year,
                                       total_calc: 0, total_arr: 0, total_hold: 0, total_bank: 0,
                                       total_payrolls: 0, _emp_set: new Set() };
                        groupOrder.push(k);
                      }
                      const g = groups[k];
                      g.items.push(b);
                      g.total_calc += b.total_calculated || 0;
                      g.total_arr += b.total_arrears || 0;
                      g.total_hold += b.total_on_hold || 0;
                      g.total_bank += b.total_bank_payable || 0;
                      // Total payroll line items (one per employee per platform)
                      g.total_payrolls += b.total_payrolls ?? b.total_employees ?? 0;
                      // Distinct employees across all batches in this month
                      (b.employee_ids || []).forEach(id => g._emp_set.add(id));
                    });
                    // Finalise unique employee count per month
                    Object.values(groups).forEach(g => {
                      g.unique_employees = g._emp_set.size || g.total_payrolls;
                    });
                    return groupOrder.map((mk, gi) => {
                      const grp = groups[mk];
                      // Default: latest month expanded, rest collapsed
                      const isCollapsed = collapsedLogMonths[mk] !== undefined
                        ? collapsedLogMonths[mk]
                        : gi !== 0;
                      return (
                        <Card key={mk} className="border-slate-200" data-testid={`log-month-card-${mk}`}>
                          <CardHeader
                            className="pb-2 pt-3 px-4 cursor-pointer select-none hover:bg-slate-50/60 transition-colors"
                            onClick={() => setCollapsedLogMonths(prev => ({ ...prev, [mk]: !isCollapsed }))}
                            data-testid={`log-month-header-${mk}`}
                          >
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <div className="flex items-center gap-3">
                                {isCollapsed
                                  ? <ChevronRight className="w-4 h-4 text-slate-500" />
                                  : <ChevronDown className="w-4 h-4 text-slate-500" />}
                                <div className="w-2 h-2 rounded-full bg-green-500" />
                                <CardTitle className="text-base font-semibold text-slate-800">
                                  {grp.label}
                                </CardTitle>
                                <Badge variant="outline" className="text-[10px] bg-slate-50">
                                  {grp.items.length} {grp.items.length === 1 ? 'batch' : 'batches'}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 text-right">
                                <div title="Total payroll line items (one per employee per platform)">
                                  <p className="text-[10px] text-slate-500">Payrolls</p>
                                  <p className="text-sm font-bold text-slate-800">{grp.total_payrolls}</p>
                                </div>
                                <div title="Distinct employees paid this month (a rider on Talabat + Careem counts once)">
                                  <p className="text-[10px] text-slate-500">Employees</p>
                                  <p className="text-sm font-bold text-blue-700">{grp.unique_employees}</p>
                                </div>
                                <div>
                                  <p className="text-[10px] text-slate-500">Calculated</p>
                                  <p className="text-sm font-bold text-slate-800">
                                    <CurrencyDisplay amount={grp.total_calc} />
                                  </p>
                                </div>
                                <div>
                                  <p className="text-[10px] text-red-500">Arrears</p>
                                  <p className="text-sm font-bold text-red-700">
                                    {grp.total_arr > 0
                                      ? <span className="inline-flex items-center justify-end whitespace-nowrap"><span className="mr-0.5">−</span><CurrencyDisplay amount={grp.total_arr} /></span>
                                      : <span className="text-slate-300">—</span>}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-[10px] text-amber-600">On Hold</p>
                                  <p className="text-sm font-bold text-amber-800">
                                    {grp.total_hold > 0
                                      ? <span className="inline-flex items-center justify-end whitespace-nowrap"><span className="mr-0.5">−</span><CurrencyDisplay amount={grp.total_hold} /></span>
                                      : <span className="text-slate-300">—</span>}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-[10px] text-green-600">Bank Payable</p>
                                  <p className="text-sm font-bold text-green-700">
                                    <CurrencyDisplay amount={grp.total_bank} />
                                  </p>
                                </div>
                                {/* Monthly report download buttons — hit
                                    /pms/monthly-report/download-{excel|pdf}
                                    which returns one sheet/page per
                                    platform plus a summary. */}
                                <div className="flex items-center gap-1 pl-2 border-l border-slate-200 ml-1" onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    variant="outline" size="sm"
                                    className="h-7 px-2 text-[11px] gap-1 text-green-700 border-green-200 hover:bg-green-50"
                                    title={`Download ${grp.label} full payroll (Excel — one sheet per platform + summary)`}
                                    data-testid={`download-month-excel-${mk}`}
                                    onClick={async () => {
                                      try {
                                        const res = await axios.get(
                                          `${API}/talent/pms/monthly-report/download-excel?month=${grp.month}&year=${grp.year}`,
                                          { headers: getAuthHeader(), responseType: 'blob' }
                                        );
                                        const url = window.URL.createObjectURL(new Blob([res.data]));
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = `Payroll_${grp.label.replace(' ', '_')}_All_Platforms.xlsx`;
                                        document.body.appendChild(a);
                                        a.click();
                                        a.remove();
                                        window.URL.revokeObjectURL(url);
                                      } catch (err) {
                                        toast.error('Failed to download Excel report');
                                      }
                                    }}
                                  >
                                    <FileSpreadsheet className="w-3.5 h-3.5" />
                                    Excel
                                  </Button>
                                  <Button
                                    variant="outline" size="sm"
                                    className="h-7 px-2 text-[11px] gap-1 text-red-700 border-red-200 hover:bg-red-50"
                                    title={`Download ${grp.label} full payroll (PDF — one page per platform + summary)`}
                                    data-testid={`download-month-pdf-${mk}`}
                                    onClick={async () => {
                                      try {
                                        const res = await axios.get(
                                          `${API}/talent/pms/monthly-report/download-pdf?month=${grp.month}&year=${grp.year}`,
                                          { headers: getAuthHeader(), responseType: 'blob' }
                                        );
                                        const url = window.URL.createObjectURL(new Blob([res.data]));
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = `Payroll_${grp.label.replace(' ', '_')}_All_Platforms.pdf`;
                                        document.body.appendChild(a);
                                        a.click();
                                        a.remove();
                                        window.URL.revokeObjectURL(url);
                                      } catch (err) {
                                        toast.error('Failed to download PDF report');
                                      }
                                    }}
                                  >
                                    <FileText className="w-3.5 h-3.5" />
                                    PDF
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </CardHeader>
                          {!isCollapsed && (
                            <CardContent className="px-4 pb-4 pt-1">
                              <div className="overflow-x-auto">
                                <Table data-testid={`log-month-table-${mk}`}>
                                  <TableHeader>
                                    <TableRow className="bg-slate-50">
                                      <TableHead className="w-10">
                                        <input
                                          type="checkbox"
                                          className="rounded"
                                          checked={grp.items.every(b => selectedMergeBatches.includes(b.id))}
                                          onChange={(e) => {
                                            const grpIds = grp.items.map(b => b.id);
                                            if (e.target.checked) {
                                              setSelectedMergeBatches(prev => Array.from(new Set([...prev, ...grpIds])));
                                            } else {
                                              setSelectedMergeBatches(prev => prev.filter(x => !grpIds.includes(x)));
                                            }
                                          }}
                                        />
                                      </TableHead>
                                      <TableHead className="whitespace-nowrap">Type</TableHead>
                                      <TableHead className="whitespace-nowrap">Platform</TableHead>
                                      <TableHead className="text-center whitespace-nowrap" title="Number of employees in this batch">Emp.</TableHead>
                                      <TableHead className="text-right whitespace-nowrap text-green-700" title="Total Gross Salary before deductions">Total Gross</TableHead>
                                      <TableHead className="text-right whitespace-nowrap" title="Sum of all positive net salaries (includes held)">Calculated</TableHead>
                                      <TableHead className="text-right whitespace-nowrap text-red-600" title="Sum of negative-salary auto-conversions (record-only — does not affect Bank Payable)">Arrears</TableHead>
                                      <TableHead className="text-right whitespace-nowrap text-amber-600" title="Sum of net salaries held back (will not be transferred)">On Hold</TableHead>
                                      <TableHead className="text-right whitespace-nowrap text-green-700 font-bold" title="Bank Payable = Calculated − On Hold">Bank Payable</TableHead>
                                      <TableHead className="whitespace-nowrap">Salary Slips</TableHead>
                                      <TableHead className="whitespace-nowrap w-12 text-center">Actions</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {grp.items.map(batch => (
                                      <TableRow 
                                        key={batch.id} 
                                        className={`cursor-pointer hover:bg-slate-50 ${selectedMergeBatches.includes(batch.id) ? 'bg-blue-50' : ''}`}
                                        onClick={() => {
                                          if (batch.slips_generated) {
                                            handleViewBatchSlips(batch.id);
                                          } else {
                                            handleGenerateBatchSalarySlips(batch.id);
                                          }
                                        }}
                                      >
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                          <input
                                            type="checkbox"
                                            className="rounded"
                                            checked={selectedMergeBatches.includes(batch.id)}
                                            onChange={() => toggleMergeBatch(batch.id)}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Badge variant="outline" className={batch.payroll_type === 'rider' ? 'bg-green-50 text-green-700' : 'bg-purple-50 text-purple-700'}>
                                            {batch.payroll_type === 'rider' ? 'Rider' : 'Staff'}
                                          </Badge>
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap">{batch.platform || '-'}</TableCell>
                                        <TableCell className="text-center whitespace-nowrap">{batch.total_employees}</TableCell>
                                        <TableCell className="text-right text-sm font-mono whitespace-nowrap text-green-700">
                                          <CurrencyDisplay amount={batch.total_gross ?? 0} />
                                        </TableCell>
                                        <TableCell className="text-right text-sm font-mono whitespace-nowrap">
                                          <CurrencyDisplay amount={batch.total_calculated ?? batch.total_net ?? 0} />
                                        </TableCell>
                                        <TableCell className="text-right text-sm font-mono whitespace-nowrap text-red-600">
                                          {batch.count_arrears > 0 ? (
                                            <span className="inline-flex items-center justify-end whitespace-nowrap" title={`${batch.count_arrears} rider(s) with negative net`}>
                                              <span className="mr-0.5">−</span>
                                              <CurrencyDisplay amount={batch.total_arrears || 0} />
                                            </span>
                                          ) : (
                                            <span className="text-slate-300">—</span>
                                          )}
                                        </TableCell>
                                        <TableCell className="text-right text-sm font-mono whitespace-nowrap text-amber-600">
                                          {batch.count_on_hold > 0 ? (
                                            <span className="inline-flex items-center justify-end whitespace-nowrap" title={`${batch.count_on_hold} rider(s) on hold`}>
                                              <span className="mr-0.5">−</span>
                                              <CurrencyDisplay amount={batch.total_on_hold || 0} />
                                            </span>
                                          ) : (
                                            <span className="text-slate-300">—</span>
                                          )}
                                        </TableCell>
                                        <TableCell className="text-right font-semibold whitespace-nowrap text-green-700">
                                          <CurrencyDisplay amount={batch.total_bank_payable ?? batch.total_net ?? 0} />
                                        </TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                          <div className="flex flex-col gap-1">
                                            {batch.slips_generated ? (
                                              <Button 
                                                size="sm" 
                                                variant="outline" 
                                                className="h-7 px-3 text-xs bg-green-50 text-green-700 border-green-200 gap-1"
                                                onClick={() => handleViewBatchSlips(batch.id)}
                                                data-testid={`view-slips-${batch.id}`}
                                              >
                                                <Eye className="w-3.5 h-3.5" />
                                                View ({batch.slips_count > 0 ? batch.slips_count : batch.total_employees})
                                              </Button>
                                            ) : (
                                              <Button 
                                                size="sm" 
                                                variant="outline" 
                                                className="h-7 px-2 text-xs bg-blue-50 text-blue-700 border-blue-200"
                                                onClick={() => handleGenerateBatchSalarySlips(batch.id)}
                                              >
                                                Generate
                                              </Button>
                                            )}
                                          </div>
                                        </TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                          <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                              <Button
                                                size="sm" variant="ghost"
                                                className="h-7 w-7 p-0"
                                                title="Actions"
                                                data-testid={`approved-log-actions-${batch.id}`}
                                              >
                                                <MoreVertical className="h-4 w-4" />
                                              </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="w-52">
                                              <DropdownMenuLabel className="text-xs">Download</DropdownMenuLabel>
                                              <DropdownMenuItem
                                                onClick={() => handleDownloadPayroll(batch.id, 'pdf')}
                                                data-testid={`approved-log-pdf-${batch.id}`}
                                              >
                                                <FileDown className="w-3.5 h-3.5 mr-2 text-blue-600" /> Payroll PDF
                                              </DropdownMenuItem>
                                              <DropdownMenuItem
                                                onClick={() => handleDownloadPayroll(batch.id, 'excel')}
                                                data-testid={`approved-log-excel-${batch.id}`}
                                              >
                                                <FileDown className="w-3.5 h-3.5 mr-2 text-emerald-600" /> Payroll Excel
                                              </DropdownMenuItem>
                                              <DropdownMenuSeparator />
                                              <DropdownMenuLabel className="text-xs">Edenred</DropdownMenuLabel>
                                              <DropdownMenuItem
                                                onClick={() => handleDownloadPayroll(batch.id, 'edenred-pdf')}
                                                data-testid={`approved-log-edenred-pdf-${batch.id}`}
                                              >
                                                <FileDown className="w-3.5 h-3.5 mr-2 text-orange-600" /> Edenred PDF
                                              </DropdownMenuItem>
                                              <DropdownMenuItem
                                                onClick={() => handleDownloadPayroll(batch.id, 'edenred-excel')}
                                                data-testid={`approved-log-edenred-excel-${batch.id}`}
                                              >
                                                <FileDown className="w-3.5 h-3.5 mr-2 text-orange-600" /> Edenred Excel
                                              </DropdownMenuItem>
                                              {canDeletePayroll && (
                                                <>
                                                  <DropdownMenuSeparator />
                                                  <DropdownMenuItem
                                                    onClick={() => { setDeleteTargetBatch(batch); setDeleteConfirmText(''); }}
                                                    className="text-red-600 focus:text-red-700 focus:bg-red-50"
                                                    data-testid={`delete-batch-${batch.id}`}
                                                  >
                                                    <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete Batch (Super Admin)
                                                  </DropdownMenuItem>
                                                </>
                                              )}
                                            </DropdownMenuContent>
                                          </DropdownMenu>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </CardContent>
                          )}
                        </Card>
                      );
                    });
                  })()}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Delete Payroll Batch — Super Admin only, typed confirmation */}
          <Dialog open={!!deleteTargetBatch} onOpenChange={(open) => { if (!open) { setDeleteTargetBatch(null); setDeleteConfirmText(''); } }}>
            <DialogContent className="max-w-lg" data-testid="delete-batch-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-700">
                  <AlertOctagon className="w-5 h-5" /> Delete Approved Payroll Batch
                </DialogTitle>
                <CardDescription className="text-xs text-amber-700 pt-1">
                  ⚠️ Super Admin only — destructive and irreversible.
                </CardDescription>
              </DialogHeader>
              {deleteTargetBatch && (() => {
                const monthLabel = `${['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][deleteTargetBatch.month]} ${deleteTargetBatch.year}`;
                const expected = `DELETE ${deleteTargetBatch.platform || deleteTargetBatch.payroll_type || ''} ${monthLabel}`.trim();
                return (
                  <div className="space-y-3 text-sm">
                    <div className="rounded-md bg-red-50 border border-red-200 p-3">
                      <p className="font-semibold text-red-900">{deleteTargetBatch.platform || deleteTargetBatch.payroll_type} — {monthLabel}</p>
                      <p className="text-xs text-red-700 mt-1">Employees: {deleteTargetBatch.total_employees} · Gross: AED {Number(deleteTargetBatch.total_gross || 0).toFixed(2)} · Net: AED {Number(deleteTargetBatch.total_net || 0).toFixed(2)}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-slate-600">This action will:</p>
                      <ul className="list-disc list-inside text-xs text-slate-700 space-y-0.5">
                        <li>Permanently delete the batch and all monthly_payrolls.</li>
                        <li>Delete every salary slip generated for this batch.</li>
                        <li>Revert <strong>deducted_from_payroll</strong> flags on linked fines so they can be re-processed.</li>
                        <li>Reverse loan EMI payments, salary advance recoveries, and arrears tied to this batch.</li>
                        <li>Log the action to the audit log (your user, batch ID, stats).</li>
                      </ul>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Type the phrase below exactly to confirm (case-sensitive):</Label>
                      <code className="block text-xs bg-slate-100 px-2 py-1 rounded select-all font-mono">{expected}</code>
                      <Input
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder={expected}
                        data-testid="delete-batch-confirm-input"
                        className={deleteConfirmText && deleteConfirmText !== expected ? 'border-red-300' : ''}
                      />
                      {deleteConfirmText && deleteConfirmText !== expected && (
                        <p className="text-[11px] text-red-600">Doesn't match — copy the phrase above exactly.</p>
                      )}
                    </div>
                  </div>
                );
              })()}
              <DialogFooter>
                <Button variant="outline" onClick={() => { setDeleteTargetBatch(null); setDeleteConfirmText(''); }} disabled={deleting}>Cancel</Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={
                    deleting ||
                    !deleteTargetBatch ||
                    deleteConfirmText !==
                      `DELETE ${deleteTargetBatch.platform || deleteTargetBatch.payroll_type || ''} ${['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][deleteTargetBatch.month]} ${deleteTargetBatch.year}`.trim()
                  }
                  onClick={async () => {
                    if (!deleteTargetBatch) return;
                    setDeleting(true);
                    try {
                      const res = await axios.delete(`${API}/talent/admin/delete-batch/${deleteTargetBatch.id}`, { headers: getAuthHeader() });
                      toast.success(res.data.message || 'Batch deleted');
                      setDeleteTargetBatch(null);
                      setDeleteConfirmText('');
                      fetchApprovedPayrollLog();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to delete batch');
                    } finally {
                      setDeleting(false);
                    }
                  }}
                  data-testid="delete-batch-confirm-btn"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Permanently Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Arrears Tracker — negative-salary history & recovery status */}
          <Card data-testid="arrears-tracker-card">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    Arrears Tracker
                  </CardTitle>
                  <CardDescription>
                    Riders with negative net salary — original event and recovery status
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="month"
                    value={arrearsMonth}
                    onChange={(e) => setArrearsMonth(e.target.value)}
                    className="w-[160px] h-9"
                    data-testid="arrears-month-filter"
                  />
                  {arrearsMonth && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setArrearsMonth('')}
                      data-testid="arrears-clear-filter"
                    >
                      All
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchArrearsTracker(arrearsMonth)}
                    disabled={arrearsLoading}
                    data-testid="arrears-refresh"
                  >
                    {arrearsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const url = arrearsMonth
                          ? `${API}/talent/pms/arrears-tracker/download-excel?month=${encodeURIComponent(arrearsMonth)}`
                          : `${API}/talent/pms/arrears-tracker/download-excel`;
                        const res = await axios.get(url, { headers: getAuthHeader(), responseType: 'blob' });
                        const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                        const link = document.createElement('a');
                        link.href = URL.createObjectURL(blob);
                        link.download = `arrears_tracker_${arrearsMonth || 'all'}.xlsx`;
                        link.click();
                      } catch (e) {
                        toast.error('Failed to download Excel');
                      }
                    }}
                    className="gap-1"
                    data-testid="arrears-download-excel"
                  >
                    <Download className="w-4 h-4" /> Excel
                  </Button>
                  <AutoSettledArrearsDialog onDone={() => fetchArrearsTracker(arrearsMonth)} />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {arrearsLoading ? (
                <div className="text-center py-8 text-slate-500">
                  <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-slate-400" />
                  <p className="text-sm">Loading arrears history…</p>
                </div>
              ) : arrearsItems.length === 0 ? (
                <div className="text-center py-8 text-slate-500" data-testid="arrears-empty-state">
                  <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p className="text-sm">
                    {arrearsMonth
                      ? `No negative-salary records for ${arrearsMonth}.`
                      : 'No negative-salary records found.'}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Records appear here once a payroll batch with a negative net salary is approved.
                  </p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="arrears-tracker-grouped">
                  {(() => {
                    // Group by source_month preserving descending month order
                    const groups = {};
                    const groupOrder = [];
                    arrearsItems.forEach(it => {
                      const k = it.source_month;
                      if (!(k in groups)) {
                        groups[k] = { items: [], total: 0, recovered: 0, pending: 0, label: it.source_month_label };
                        groupOrder.push(k);
                      }
                      groups[k].items.push(it);
                      groups[k].total += it.arrears_amount || 0;
                      if (it.status === 'recovered') groups[k].recovered += it.arrears_amount || 0;
                      else groups[k].pending += it.arrears_amount || 0;
                    });
                    return groupOrder.map((monthKey, gi) => {
                      const grp = groups[monthKey];
                      // Default: latest month (gi===0) expanded, others collapsed
                      const isCollapsed = collapsedArrearsMonths[monthKey] !== undefined
                        ? collapsedArrearsMonths[monthKey]
                        : gi !== 0;
                      const allRecovered = grp.pending <= 0.005 && grp.recovered > 0;
                      return (
                        <Card key={monthKey} className="border-slate-200" data-testid={`arrears-month-card-${monthKey}`}>
                          <CardHeader
                            className="pb-2 pt-3 px-4 cursor-pointer select-none hover:bg-slate-50/60 transition-colors"
                            onClick={() => setCollapsedArrearsMonths(prev => ({ ...prev, [monthKey]: !isCollapsed }))}
                            data-testid={`arrears-month-header-${monthKey}`}
                          >
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <div className="flex items-center gap-3">
                                {isCollapsed
                                  ? <ChevronRight className="w-4 h-4 text-slate-500" />
                                  : <ChevronDown className="w-4 h-4 text-slate-500" />}
                                <div className={`w-2 h-2 rounded-full ${allRecovered ? 'bg-green-500' : 'bg-red-500'}`} />
                                <CardTitle className="text-base font-semibold text-slate-800">
                                  Arrears for {grp.label}
                                </CardTitle>
                              </div>
                              <div className="flex items-center gap-4 text-right">
                                <div>
                                  <p className="text-[10px] text-slate-500">Riders</p>
                                  <p className="text-sm font-bold text-slate-800">{grp.items.length}</p>
                                </div>
                                <div>
                                  <p className="text-[10px] text-red-500">Total Arrears</p>
                                  <p className="text-sm font-bold text-red-700">
                                    <CurrencyDisplay amount={grp.total} />
                                  </p>
                                </div>
                                <div>
                                  <p className="text-[10px] text-green-600">Recovered</p>
                                  <p className="text-sm font-bold text-green-700">
                                    <CurrencyDisplay amount={grp.recovered} />
                                  </p>
                                </div>
                                <div>
                                  <p className="text-[10px] text-amber-600">Pending</p>
                                  <p className="text-sm font-bold text-amber-800">
                                    <CurrencyDisplay amount={grp.pending} />
                                  </p>
                                </div>
                              </div>
                            </div>
                          </CardHeader>
                          {!isCollapsed && (
                            <CardContent className="px-4 pb-4 pt-1">
                              <div className="overflow-x-auto">
                                <Table data-testid={`arrears-tracker-table-${monthKey}`}>
                                  <TableHeader>
                                    <TableRow className="bg-slate-50">
                                      <TableHead>Rider</TableHead>
                                      <TableHead>Platform</TableHead>
                                      <TableHead className="hidden md:table-cell">Bike</TableHead>
                                      <TableHead className="text-right">Original Net</TableHead>
                                      <TableHead className="text-right">Arrears</TableHead>
                                      <TableHead>Status</TableHead>
                                      <TableHead>Recovered In</TableHead>
                                      <TableHead className="text-center">Slip</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {grp.items.map((it, idx) => (
                                      <TableRow key={`${it.payroll_id || idx}`} data-testid={`arrears-row-${monthKey}-${idx}`}>
                                        <TableCell className="text-sm">
                                          <p className="font-medium">{it.rider_name || '-'}</p>
                                          <p className="text-[10px] text-slate-400">{it.rider_id}</p>
                                        </TableCell>
                                        <TableCell>
                                          <Badge variant="outline" className="text-xs">{it.platform || '-'}</Badge>
                                        </TableCell>
                                        <TableCell className="hidden md:table-cell text-xs font-mono text-slate-600">
                                          {it.bike_plate || '-'}
                                        </TableCell>
                                        <TableCell className="text-right text-sm text-red-600 font-mono">
                                          <CurrencyDisplay amount={it.original_net_salary || 0} />
                                        </TableCell>
                                        <TableCell className="text-right text-sm font-semibold text-red-700 font-mono">
                                          <CurrencyDisplay amount={it.arrears_amount || 0} />
                                        </TableCell>
                                        <TableCell>
                                          {it.status === 'recovered' ? (
                                            <Badge className="bg-green-100 text-green-800 border border-green-200 text-[11px]">Recovered</Badge>
                                          ) : (
                                            <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[11px]">Pending</Badge>
                                          )}
                                        </TableCell>
                                        <TableCell className="text-xs text-slate-600">
                                          {it.recovered_in_label ? (
                                            <div className="leading-tight">
                                              <p className="font-mono">{it.recovered_in_label}</p>
                                              {it.recovered_at && (
                                                <p className="text-[10px] text-slate-400">
                                                  {it.recovered_at.replace('T', ' ').substring(0, 16)}
                                                </p>
                                              )}
                                            </div>
                                          ) : (
                                            <span className="text-slate-400 italic text-[11px]">awaiting next payroll</span>
                                          )}
                                        </TableCell>
                                        <TableCell className="text-center">
                                          {it.salary_slip_id ? (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-7 w-7 p-0 hover:bg-blue-50"
                                              onClick={() => handleDownloadIndividualSlip(it.salary_slip_id, it.rider_name || 'Rider')}
                                              title={`Download salary slip for ${it.source_month_label} — shows how the negative net was calculated`}
                                              data-testid={`arrears-view-slip-${monthKey}-${idx}`}
                                            >
                                              <FileText className="w-4 h-4 text-blue-600" />
                                            </Button>
                                          ) : (
                                            <span
                                              className="text-slate-300 text-[10px] italic"
                                              title="Salary slip not found for this month — may not have been generated"
                                            >
                                              n/a
                                            </span>
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </CardContent>
                          )}
                        </Card>
                      );
                    });
                  })()}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Create Payroll Wizard Dialog */}

          <Dialog open={payrollWizardOpen} onOpenChange={(open) => { if (!open) requestCloseWizard(); else setPayrollWizardOpen(true); }}>
            <DialogContent className={`${wizardFullscreen ? 'max-w-[95vw] w-[95vw] h-[95vh]' : wizardStep === 4 ? 'max-w-4xl' : 'max-w-2xl'} max-h-[95vh] overflow-y-auto`}>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle>Create Payroll{wizardData.draftId ? <Badge className="ml-2 bg-amber-100 text-amber-800 text-xs">Draft</Badge> : ''}</DialogTitle>
                    <DialogDescription>
                      Step {wizardStep} of 4: {
                        wizardStep === 1 ? 'Select Period' :
                        wizardStep === 2 ? 'Select Type' :
                        wizardStep === 3 ? 'Upload Data' :
                        'Review & Submit'
                      }
                    </DialogDescription>
                  </div>
                  {wizardStep === 4 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setWizardFullscreen(!wizardFullscreen)}
                      className="ml-4"
                      data-testid="fullscreen-toggle"
                    >
                      {wizardFullscreen ? (
                        <><Minimize2 className="w-4 h-4 mr-1" /> Exit Fullscreen</>
                      ) : (
                        <><Maximize2 className="w-4 h-4 mr-1" /> Fullscreen</>
                      )}
                    </Button>
                  )}
                </div>
              </DialogHeader>

              {/* Step 1: Select Month and Year */}
              {wizardStep === 1 && (
                <div className="space-y-4 py-4">
                  <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                    <div className="space-y-2">
                      <Label>Month</Label>
                      <Select 
                        value={wizardData.month.toString()} 
                        onValueChange={(v) => setWizardData({...wizardData, month: parseInt(v)})}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[...Array(12)].map((_, i) => (
                            <SelectItem key={i+1} value={(i+1).toString()}>
                              {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Year</Label>
                      <Select 
                        value={wizardData.year.toString()} 
                        onValueChange={(v) => setWizardData({...wizardData, year: parseInt(v)})}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[2024, 2025, 2026].map(y => (
                            <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Saved Drafts */}
                  {payrollDrafts.length > 0 && (
                    <div className="border rounded-lg p-3 bg-amber-50/50">
                      <p className="text-sm font-medium text-amber-800 mb-2">Saved Drafts</p>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {payrollDrafts.map((d) => {
                          const monthName = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.month];
                          return (
                            <div key={d.id} className="flex items-center justify-between bg-white rounded p-2 border text-sm">
                              <div>
                                <span className="font-medium">{monthName} {d.year}</span>
                                <span className="text-slate-500 mx-2">|</span>
                                <span className="text-slate-600">{d.payroll_type === 'staff' ? 'Staff' : d.platform || 'Rider'}</span>
                                <span className="text-xs text-slate-400 ml-2">
                                  {d.updated_at ? new Date(d.updated_at).toLocaleDateString() : ''}
                                </span>
                              </div>
                              <div className="flex gap-1">
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => loadDraft(d.id)} data-testid={`load-draft-${d.id}`}>
                                  Load
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500 hover:text-red-700" onClick={() => deleteDraft(d.id)} data-testid={`delete-draft-${d.id}`}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <DialogFooter>
                    <Button variant="outline" onClick={requestCloseWizard}>Cancel</Button>
                    <Button onClick={() => setWizardStep(2)}>Next</Button>
                  </DialogFooter>
                </div>
              )}

              {/* Step 2: Select Rider or Staff */}
              {wizardStep === 2 && (
                <div className="space-y-4 py-4">
                  <Label>Select Payroll Type</Label>
                  <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                    <Card 
                      className={`cursor-pointer transition-all ${wizardData.payrollType === 'rider' ? 'ring-2 ring-green-500 bg-green-50' : 'hover:bg-slate-50'}`}
                      onClick={() => setWizardData({...wizardData, payrollType: 'rider'})}
                    >
                      <CardContent className="p-6 text-center">
                        <Users className="w-12 h-12 mx-auto mb-3 text-green-600" />
                        <h3 className="font-semibold text-lg">Rider Payroll</h3>
                        <p className="text-sm text-slate-500 mt-1">Process platform payout sheets</p>
                      </CardContent>
                    </Card>
                    <Card 
                      className={`cursor-pointer transition-all ${wizardData.payrollType === 'staff' ? 'ring-2 ring-purple-500 bg-purple-50' : 'hover:bg-slate-50'}`}
                      onClick={() => setWizardData({...wizardData, payrollType: 'staff'})}
                    >
                      <CardContent className="p-6 text-center">
                        <Briefcase className="w-12 h-12 mx-auto mb-3 text-purple-600" />
                        <h3 className="font-semibold text-lg">Staff Payroll</h3>
                        <p className="text-sm text-slate-500 mt-1">Pro-rata salary based on attendance</p>
                      </CardContent>
                    </Card>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setWizardStep(1)}>Back</Button>
                    <Button onClick={() => setWizardStep(3)} disabled={!wizardData.payrollType}>Next</Button>
                  </DialogFooter>
                </div>
              )}

              {/* Step 3: Upload Data */}
              {wizardStep === 3 && (
                <div className="space-y-4 py-4">
                  {wizardData.payrollType === 'staff' ? (
                    <>
                      <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                        <h4 className="font-medium text-purple-800 mb-2">Staff Attendance Upload</h4>
                        <p className="text-sm text-purple-600 mb-3">
                          Download the template pre-filled with employee list, fill in absent days and overtime days, then upload.
                        </p>
                        <Button variant="outline" onClick={handleDownloadStaffTemplate} className="gap-2">
                          <Download className="w-4 h-4" />
                          Download Template
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <Label>Upload Completed Attendance Sheet</Label>
                        <Input
                          type="file"
                          accept=".xlsx,.xls,.csv,.xlsm,.xlsb"
                          onChange={(e) => setWizardData({...wizardData, file: e.target.files[0]})}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label>Select Platform</Label>
                        <Select 
                          value={wizardData.platformId} 
                          onValueChange={(v) => {
                            const platform = platforms.find(p => p.id === v);
                            setWizardData({...wizardData, platformId: v, platform: platform?.name || '', useAI: true});
                          }}
                        >
                          <SelectTrigger data-testid="platform-select"><SelectValue placeholder="Choose platform" /></SelectTrigger>
                          <SelectContent>
                            {platforms.filter(p => p.is_active).map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {platforms.length === 0 && (
                          <p className="text-sm text-amber-600">No platforms configured. Please add platforms in Admin settings.</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>Upload Platform Payout Sheet</Label>
                        <Input
                          data-testid="file-upload-input"
                          type="file"
                          accept=".xlsx,.xls,.csv,.xlsm,.xlsb"
                          onChange={(e) => setWizardData({...wizardData, file: e.target.files[0], useAI: true})}
                        />
                      </div>
                    </>
                  )}
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setWizardStep(2)}>Back</Button>
                    <Button 
                      data-testid="process-btn"
                      onClick={handleWizardUpload} 
                      disabled={!wizardData.file || (wizardData.payrollType === 'rider' && !wizardData.platformId) || wizardLoading}
                    >
                      {wizardLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Process
                    </Button>
                  </DialogFooter>
                </div>
              )}

              {/* Step 4: Review & Submit */}
              {wizardStep === 4 && (
                <div className={`${wizardFullscreen ? 'space-y-2 py-1' : 'space-y-4 py-4'}`}>
                  {/* Feb-2026: banner surfacing ops-side deduction changes
                      and/or an AI-formula change detected on draft load. */}
                  {wizardDraftChanges && (wizardDraftChanges.changes.length > 0 || wizardDraftChanges.formulaChanged) && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="wizard-draft-changes-banner">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-amber-900">
                            {wizardDraftChanges.formulaChanged
                              ? 'Draft refreshed — AI formula changed since save'
                              : 'Draft refreshed with the latest operational changes'}
                          </p>
                          <p className="text-xs text-amber-800 mt-0.5">
                            {wizardDraftChanges.formulaChanged
                              ? 'The AI Formula for this platform was edited after you saved. Gross salary cannot be recomputed without the source Excel — please re-upload it to refresh gross. Ops deductions have already been refreshed below.'
                              : 'Salik / Fines / Loan / Maintenance / Salary Advance / Dynamic Deductions were re-queried and totals updated automatically.'}
                          </p>
                        </div>
                        <button
                          onClick={() => setWizardDraftChanges(null)}
                          className="text-amber-700 hover:text-amber-900 text-xs font-medium underline"
                          data-testid="dismiss-draft-changes"
                        >
                          Dismiss
                        </button>
                      </div>
                      {wizardDraftChanges.changes.length > 0 && (
                        <div className="max-h-40 overflow-y-auto border-t border-amber-200 pt-2">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-amber-800">
                                <th className="text-left pb-1">Rider</th>
                                <th className="text-left pb-1">Field</th>
                                <th className="text-right pb-1">Was</th>
                                <th className="text-right pb-1">Now</th>
                              </tr>
                            </thead>
                            <tbody>
                              {wizardDraftChanges.changes.flatMap((r) =>
                                (r.changes || []).map((c, i) => (
                                  <tr key={`${r.rider_id || r.rider_name}-${c.field}-${i}`} className="border-t border-amber-100">
                                    <td className="py-1 text-slate-700">{r.rider_name}</td>
                                    <td className="py-1 text-slate-700">{c.label}</td>
                                    <td className="text-right py-1 font-mono text-slate-500">AED {Number(c.old || 0).toFixed(2)}</td>
                                    <td className="text-right py-1 font-mono text-amber-800 font-medium">AED {Number(c.new || 0).toFixed(2)}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Summary - hidden in fullscreen for max table area */}
                  {!wizardFullscreen && wizardData.summary && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      <Card className="bg-slate-50 flex-1 min-w-[90px] max-w-[120px]">
                        <CardContent className="p-2">
                          <p className="text-[10px] text-slate-500">Employees</p>
                          <p className="text-sm font-bold">{wizardData.summary.total_employees}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-indigo-50 flex-1 min-w-[90px] max-w-[160px]">
                        <CardContent className="p-2">
                          {(() => {
                            const hasSplit = wizardData.preview.some(p => p?.calculation_breakdown?.total_pickup_orders != null && p?.calculation_breakdown?.total_dropoff_orders != null);
                            if (hasSplit) {
                              const totalPickup = wizardData.preview.reduce((s, p) => s + (parseInt(p?.calculation_breakdown?.total_pickup_orders) || 0), 0);
                              const totalDropoff = wizardData.preview.reduce((s, p) => s + (parseInt(p?.calculation_breakdown?.total_dropoff_orders) || 0), 0);
                              return (
                                <>
                                  <p className="text-[10px] text-indigo-600">Orders (Pickup / Dropoff)</p>
                                  <p className="text-sm font-bold text-indigo-800" data-testid="orders-summary-split">
                                    <span className="mr-1">P {totalPickup.toLocaleString()}</span>
                                    <span className="text-indigo-400">/</span>
                                    <span className="ml-1">D {totalDropoff.toLocaleString()}</span>
                                  </p>
                                </>
                              );
                            }
                            const total = wizardData.preview.reduce((sum, p) => sum + (parseInt(p.total_orders) || 0), 0);
                            return (
                              <>
                                <p className="text-[10px] text-indigo-600">Orders</p>
                                <p className="text-sm font-bold text-indigo-800">{total.toLocaleString()}</p>
                              </>
                            );
                          })()}
                        </CardContent>
                      </Card>
                      <Card className="bg-green-50 flex-1 min-w-[90px] max-w-[120px]">
                        <CardContent className="p-2">
                          <p className="text-[10px] text-green-600">Gross</p>
                          <p className="text-sm font-bold text-green-800"><CurrencyDisplay amount={wizardData.summary.total_gross || 0} /></p>
                        </CardContent>
                      </Card>
                      <Card className="bg-cyan-50 flex-1 min-w-[90px] max-w-[120px]">
                        <CardContent className="p-2">
                          <p className="text-[10px] text-cyan-600">Paid in Wallet</p>
                          <p className="text-sm font-bold text-cyan-800"><CurrencyDisplay amount={wizardData.preview.reduce((sum, p) => sum + (parseFloat(p.paid_in_wallet) || 0), 0)} /></p>
                        </CardContent>
                      </Card>
                      <Card className="bg-teal-50 flex-1 min-w-[90px] max-w-[120px]">
                        <CardContent className="p-2">
                          <p className="text-[10px] text-teal-600">Tips</p>
                          <p className="text-sm font-bold text-teal-800"><CurrencyDisplay amount={wizardData.preview.reduce((sum, p) => sum + (parseFloat(p.paid_as_tips) || 0), 0)} /></p>
                        </CardContent>
                      </Card>
                      <Card className="bg-indigo-50 flex-1 min-w-[90px] max-w-[120px]">
                        <CardContent className="p-2">
                          <p className="text-[10px] text-indigo-600">COD</p>
                          <p className="text-sm font-bold text-indigo-800"><CurrencyDisplay amount={wizardData.preview.reduce((sum, p) => sum + (parseFloat(p.paid_as_cod) || 0), 0)} /></p>
                        </CardContent>
                      </Card>
                      {wizardData.preview.some(p => (parseFloat(p.clawback_deduction) || 0) > 0) && (
                        <Card className="bg-orange-50 flex-1 min-w-[90px] max-w-[120px]">
                          <CardContent className="p-2">
                            <p className="text-[10px] text-orange-700">Clawback</p>
                            <p className="text-sm font-bold text-orange-800"><CurrencyDisplay amount={wizardData.preview.reduce((sum, p) => sum + (parseFloat(p.clawback_deduction) || 0), 0)} /></p>
                          </CardContent>
                        </Card>
                      )}
                      <Card className="bg-red-50 flex-1 min-w-[90px] max-w-[120px]">
                        <CardContent className="p-2">
                          <p className="text-[10px] text-red-600">Fines</p>
                          <p className="text-sm font-bold text-red-800"><CurrencyDisplay amount={wizardData.preview.reduce((sum, p) => sum + (parseFloat(p.fines) || 0), 0)} /></p>
                        </CardContent>
                      </Card>
                      <Card className="bg-orange-50 flex-1 min-w-[90px] max-w-[120px]">
                        <CardContent className="p-2">
                          <p className="text-[10px] text-orange-600">SALIK</p>
                          <p className="text-sm font-bold text-orange-800"><CurrencyDisplay amount={wizardData.preview.reduce((sum, p) => sum + (parseFloat(p.salik) || 0), 0)} /></p>
                        </CardContent>
                      </Card>
                      <Card className="bg-amber-50 flex-1 min-w-[90px] max-w-[120px]">
                        <CardContent className="p-2">
                          <p className="text-[10px] text-amber-600">Maint.</p>
                          <p className="text-sm font-bold text-amber-800"><CurrencyDisplay amount={wizardData.preview.reduce((sum, p) => sum + (parseFloat(p.maintenance) || 0), 0)} /></p>
                        </CardContent>
                      </Card>
                      <Card className="bg-purple-50 flex-1 min-w-[90px] max-w-[120px]">
                        <CardContent className="p-2">
                          <p className="text-[10px] text-purple-600">Loan</p>
                          <p className="text-sm font-bold text-purple-800"><CurrencyDisplay amount={wizardData.preview.reduce((sum, p) => sum + (parseFloat(p.loan_emi) || 0), 0)} /></p>
                        </CardContent>
                      </Card>
                      <Card className="bg-pink-50 flex-1 min-w-[90px] max-w-[120px]">
                        <CardContent className="p-2">
                          <p className="text-[10px] text-pink-600">Misc.</p>
                          <p className="text-sm font-bold text-pink-800"><CurrencyDisplay amount={wizardData.preview.reduce((sum, p) => sum + (parseFloat(p.misc_deduction) || 0), 0)} /></p>
                        </CardContent>
                      </Card>
                      <Card className="bg-rose-50 flex-1 min-w-[90px] max-w-[120px]">
                        <CardContent className="p-2">
                          <p className="text-[10px] text-rose-600">Total Ded.</p>
                          <p className="text-sm font-bold text-rose-800"><CurrencyDisplay amount={wizardData.summary.total_deductions || 0} /></p>
                        </CardContent>
                      </Card>
                      <Card className="bg-blue-50 flex-1 min-w-[90px] max-w-[120px]">
                        <CardContent className="p-2">
                          <p className="text-[10px] text-blue-600">Net</p>
                          <p className="text-sm font-bold text-blue-800"><CurrencyDisplay amount={wizardData.summary.total_net || 0} /></p>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Action bar */}
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-[280px]">
                      <Search className="w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search rider name, ID, or bike plate…"
                        value={wizardSearch}
                        onChange={(e) => setWizardSearch(e.target.value)}
                        className="h-8 text-xs max-w-xs"
                        data-testid="wizard-search-input"
                      />
                      {wizardSearch && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setWizardSearch('')}>Clear</Button>
                      )}
                      <p className="text-xs text-slate-400 ml-2 hidden lg:block">Tip: ↑↓←→ navigate cells · Enter commits · Esc cancels</p>
                    </div>
                    <div className="flex gap-2">
                      {wizardStep === 4 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            let did = wizardData.draftId;
                            if (!did) {
                              // Auto-save a draft so the new tab can load it
                              try {
                                const payrollData = wizardData.preview.map(p => ({ ...p }));
                                const formData = new FormData();
                                formData.append('month', wizardData.month);
                                formData.append('year', wizardData.year);
                                formData.append('payroll_type', wizardData.payrollType || 'rider');
                                if (wizardData.platform) formData.append('platform', wizardData.platform);
                                formData.append('payroll_data', JSON.stringify(payrollData));
                                formData.append('wizard_step', 4);
                                const res = await axios.post(`${API}/talent/pms/draft/save`, formData, {
                                  headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
                                });
                                did = res.data.draft_id;
                                setWizardData(prev => ({ ...prev, draftId: did }));
                                toast.success('Draft saved — opening in new tab');
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'Failed to save draft before opening in new tab');
                                return;
                              }
                            }
                            const url = `${window.location.origin}/talent?tab=payroll&wizard=${encodeURIComponent(did)}`;
                            window.open(url, '_blank', 'noopener');
                          }}
                          title="Open this wizard in a new tab — edits are saved on each Save Draft click and sync between tabs"
                          data-testid="open-wizard-new-tab"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" /> Open in New Tab
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={handleDownloadWizardPayrollPDF} data-testid="download-payroll-pdf">
                        <FileText className="w-4 h-4 mr-1" /> Download PDF
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleDownloadWizardPayroll} data-testid="download-payroll-excel">
                        <Download className="w-4 h-4 mr-1" /> Download Excel
                      </Button>
                    </div>
                  </div>

                  {/* Editable Preview Table */}
                  {(() => {
                    const isStaff = wizardData.payrollType === 'staff';
                    
                    // Staff earning breakdown columns (read-only)
                    const staffEarningCols = isStaff ? [
                      { key: 'basic_salary', label: 'Basic' },
                      { key: 'housing_allowance', label: 'Housing' },
                      { key: 'transport_allowance', label: 'Transport' },
                      { key: 'telephone_allowance', label: 'Telephone' },
                      { key: 'custom_allowances_total', label: 'Other Allow.' },
                      { key: 'overtime_pay', label: 'Overtime' },
                    ].filter(col => wizardData.preview.some(p => parseFloat(p[col.key] || 0) !== 0)) : [];
                    
                    // Detect Careem-style payroll so we can relabel
                    // "Wallet" → "Advance" and "COD" stays as "COD".
                    const isCareemSplit = wizardData.preview.some(p =>
                      (p?.calculation_breakdown?.formula_type || '') === 'careem_eligible_split'
                    );
                    // Detect the new Careem App-Earnings formula so we can
                    // surface an "App Earnings" income column and a
                    // "Vendor Margin" deduction column dedicated to it.
                    const isCareemAppEarnings = wizardData.preview.some(p =>
                      (p?.calculation_breakdown?.formula_type || '') === 'careem_app_earnings'
                    );
                    // Dynamically determine which deduction columns to show based on data
                    const dedColumns = [
                      { key: 'paid_in_wallet', label: isCareemSplit ? 'Advance' : 'Wallet' },
                      { key: 'paid_as_tips', label: 'Tips' },
                      { key: 'paid_as_cod', label: isCareemAppEarnings ? 'COD' : 'COD' },
                      { key: 'vendor_margin_deduction', label: 'Vendor Margin' },
                      { key: 'clawback_deduction', label: 'Clawback' },
                      { key: 'platform_deduction', label: 'Deduction' },
                      { key: 'food_compensation', label: 'Food Comp.' },
                      { key: 'slx_deduction', label: 'SLX' },
                      { key: 'room_rent', label: 'Room Rent' },
                      { key: 'fines', label: 'Fines' },
                      { key: 'fine_arrears', label: 'Fine Arrears' },
                      { key: 'salik', label: 'Salik' },
                      { key: 'maintenance', label: 'Maint.' },
                      { key: 'loan_emi', label: 'Loan' },
                      { key: 'salary_advance', label: 'Sal. Advance' },
                      { key: 'prev_arrears', label: 'Arrears' },
                      { key: 'misc_deduction', label: 'Misc.' },
                    ];
                    // For staff: always show room_rent + loan + advance + misc; for riders: fines/salik/maint/loan/advance/misc
                    const alwaysShow = isStaff 
                      ? ['room_rent', 'loan_emi', 'salary_advance', 'misc_deduction']
                      : ['fines', 'salik', 'maintenance', 'loan_emi', 'salary_advance', 'misc_deduction'];
                    // Helper: read a value from either the row's top-level field
                    // OR from the calculation_breakdown (for Careem AE fields).
                    const readVal = (p, k) => {
                      const top = parseFloat(p[k] || 0);
                      const cb = p.calculation_breakdown || {};
                      // Noon Minutes bakes Tips INTO gross as an
                      // earning line — forcing the Tips deduction cell
                      // to 0 keeps the UI honest and prevents the
                      // operator from seeing a stale value even if an
                      // older payroll row carries a non-zero
                      // `paid_as_tips` from before the backend fix.
                      if (k === 'paid_as_tips' && _isNoonMinutes(cb, p)) {
                        return 0;
                      }
                      if (top) return top;
                      if (k === 'vendor_margin_deduction') return parseFloat(cb.vendor_margin || 0);
                      if (k === 'paid_as_cod' && isCareemAppEarnings) return parseFloat(cb.cod || 0);
                      return 0;
                    };
                    const visibleDedColumns = dedColumns.filter(col => 
                      alwaysShow.includes(col.key) || wizardData.preview.some(p => readVal(p, col.key) !== 0)
                    );
                    // Excel-like layout: width spans for the 3 column groups (Income / Deductions / Total)
                    // Used in the upper "group header" row.
                    // Income span = staffEarningCols + Gross + Past Dues (+App Earnings when Careem AE)
                    // Columns listed under "PAYROLL TABLE OUTPUT" in the platform's AI formula.
                    const formulaCols = [];
                    wizardData.preview.forEach(p => (p?.calculation_breakdown?.table_columns || []).forEach(c => {
                      if (c?.key && !formulaCols.some(f => f.key === c.key)) formulaCols.push({ key: c.key, label: c.label });
                    }));
                    const readFormulaCol = (p, k) => (p?.calculation_breakdown?.table_columns || []).find(c => c.key === k)?.value;
                    const incomeColSpan = staffEarningCols.length + 2 + (isCareemAppEarnings ? 1 : 0);
                    const deductionColSpan = visibleDedColumns.length;
                    return (
                  <>
                  <div
                    className={`${wizardFullscreen ? 'max-h-[calc(95vh-180px)]' : 'max-h-80'} overflow-auto max-w-full min-w-0 border rounded-lg`}
                    onKeyDown={(e) => {
                      // Excel-like navigation: ↑↓ same column, ←→ adjacent cell,
                      // Enter commits (blur + move down), Esc cancels.
                      const tgt = e.target;
                      if (!(tgt instanceof HTMLInputElement) && !(tgt instanceof HTMLSelectElement)) return;
                      const tr = tgt.closest('tr');
                      if (!tr) return;
                      const allRows = Array.from(tr.parentElement?.querySelectorAll('tr') || []);
                      const rowIdx = allRows.indexOf(tr);
                      const cell = tgt.closest('td');
                      const cellIdx = cell ? Array.from(cell.parentElement.children).indexOf(cell) : -1;
                      const focusCellAt = (r, c) => {
                        const targetRow = allRows[r];
                        if (!targetRow) return;
                        const cells = targetRow.children;
                        if (!cells || c < 0 || c >= cells.length) return;
                        const inp = cells[c].querySelector('input, select');
                        if (inp) { inp.focus(); if (inp.select) inp.select(); }
                      };
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        focusCellAt(rowIdx + 1, cellIdx);
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        focusCellAt(rowIdx - 1, cellIdx);
                      } else if (e.key === 'Enter') {
                        e.preventDefault();
                        tgt.blur();
                        focusCellAt(rowIdx + 1, cellIdx);
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        // Reset value to last-committed (the controlled value from React state)
                        // by simply blurring without firing change handler chain again.
                        tgt.blur();
                      } else if (e.key === 'ArrowLeft' && tgt.selectionStart === 0) {
                        // Only jump if caret is at start (don't hijack normal text navigation)
                        const inputs = Array.from(tr.querySelectorAll('input, select'));
                        const myIdx = inputs.indexOf(tgt);
                        if (myIdx > 0) { e.preventDefault(); inputs[myIdx - 1].focus(); inputs[myIdx - 1].select?.(); }
                      } else if (e.key === 'ArrowRight' && tgt.selectionEnd === (tgt.value || '').length) {
                        const inputs = Array.from(tr.querySelectorAll('input, select'));
                        const myIdx = inputs.indexOf(tgt);
                        if (myIdx >= 0 && myIdx < inputs.length - 1) { e.preventDefault(); inputs[myIdx + 1].focus(); inputs[myIdx + 1].select?.(); }
                      }
                    }}
                  >
                    <Table className="text-xs">
                      <TableHeader className="sticky top-0 z-10">
                        {/* Group header row: Identity / Income / Deductions / Net */}
                        <TableRow className="bg-slate-100 border-b border-slate-300">
                          <TableHead colSpan={3 + (isStaff ? 0 : 1) + (isStaff ? 0 : 1)} className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-r-2 border-slate-300">
                            Rider / Employee
                          </TableHead>
                          {formulaCols.length > 0 && (
                            <TableHead colSpan={formulaCols.length} className="text-center text-[11px] font-semibold uppercase tracking-wider bg-slate-200/80 text-slate-700 border-r-2 border-slate-300" title="Figures read from the uploaded file, as listed in the platform formula (for reference)">
                              From platform file
                            </TableHead>
                          )}
                          <TableHead colSpan={incomeColSpan} className="text-center text-[11px] font-semibold uppercase tracking-wider bg-green-100/80 text-green-800 border-r-2 border-slate-300">
                            Income (Earnings)
                          </TableHead>
                          <TableHead colSpan={deductionColSpan} className="text-center text-[11px] font-semibold uppercase tracking-wider bg-red-100/70 text-red-800 border-r-2 border-slate-300">
                            Deductions
                          </TableHead>
                          <TableHead colSpan={2} className="text-center text-[11px] font-semibold uppercase tracking-wider bg-blue-100/70 text-blue-800">
                            Net / Action
                          </TableHead>
                        </TableRow>
                        <TableRow className="bg-slate-50 border-b border-slate-300">
                          <TableHead className="w-[180px] font-semibold">Employee</TableHead>
                          <TableHead className="w-[50px] text-center font-semibold">View</TableHead>
                          {!isStaff && <TableHead className="w-[80px] font-semibold">Bike No.</TableHead>}
                          <TableHead className={`w-[80px] font-semibold ${isStaff ? 'border-r-2 border-slate-300' : ''}`}>Edenred ID</TableHead>
                          {!isStaff && (
                            <TableHead
                              className="text-right w-[90px] font-semibold border-r-2 border-slate-300"
                              title="Pickup orders / Dropoff orders (when platform splits them); otherwise total orders"
                            >
                              Orders
                            </TableHead>
                          )}
                          {formulaCols.map((col, ci) => (
                            <TableHead key={`fx-${col.key}`} className={`text-right w-[72px] text-slate-700 font-semibold bg-slate-100 ${ci === formulaCols.length - 1 ? 'border-r-2 border-slate-300' : ''}`} title="From the uploaded file, as listed in the platform formula" data-testid={`formula-col-head-${col.key}`}>{col.label}</TableHead>
                          ))}
                          {staffEarningCols.map(col => (
                            <TableHead key={col.key} className="text-right w-[64px] text-green-800 font-semibold bg-green-50/40">{col.label}</TableHead>
                          ))}
                          {isCareemAppEarnings && (
                            <TableHead
                              className="text-right w-[92px] text-green-800 font-semibold bg-green-50/40"
                              title="App Earnings from Careem Excel (Gross = App Earnings − Vendor Margin)"
                            >
                              App Earnings
                            </TableHead>
                          )}
                          <TableHead className="text-right w-[80px] text-green-800 font-semibold bg-green-50/40">Gross</TableHead>
                          <TableHead className="text-right w-[80px] text-green-800 font-semibold bg-green-50/40 border-r-2 border-slate-300" title="Past Dues — manual income entry for amounts owed from previous payrolls">
                            Past Dues
                          </TableHead>
                          {visibleDedColumns.map((col, ci) => (
                            <TableHead
                              key={col.key}
                              className={`text-right w-[64px] text-red-800 font-semibold bg-red-50/40 ${ci === visibleDedColumns.length - 1 ? 'border-r-2 border-slate-300' : ''}`}
                            >
                              {col.label}
                            </TableHead>
                          ))}
                          <TableHead className="text-right w-[88px] font-bold bg-blue-50/40 text-blue-800">Net</TableHead>
                          <TableHead className="text-center w-[50px] font-semibold">Hold</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {wizardData.preview
                          .map((p, idx) => ({ p, idx }))
                          .filter(({ p }) => {
                            if (!wizardSearch.trim()) return true;
                            const q = wizardSearch.trim().toLowerCase();
                            return (
                              String(p.employee_name || '').toLowerCase().includes(q) ||
                              String(p.employee_unique_id || '').toLowerCase().includes(q) ||
                              String(p.bike_plate || '').toLowerCase().includes(q) ||
                              String(p.edenred_id || '').toLowerCase().includes(q)
                            );
                          })
                          .map(({ p, idx }) => (
                          <TableRow key={idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'} hover:bg-yellow-50/60 border-b border-slate-200`}>
                            <TableCell>
                              <div>
                                <p className="font-medium text-sm">{p.employee_name}</p>
                                <p className="text-xs text-slate-500">{p.employee_unique_id}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-center p-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => setCalculationDialog({ 
                                  open: true, 
                                  data: p.calculation_breakdown,
                                  riderName: p.employee_name,
                                  riderData: p
                                })}
                                data-testid={`view-calc-${idx}`}
                              >
                                <Eye className="w-4 h-4 text-blue-500" />
                              </Button>
                            </TableCell>
                            {!isStaff && (
                              <TableCell className="p-1">
                                <button
                                  type="button"
                                  className="text-xs font-mono text-blue-600 hover:text-blue-800 hover:underline px-1 bg-transparent border-0 cursor-pointer"
                                  title="Click to view bike history for this payroll month"
                                  onClick={async () => {
                                    const empId = p.rider_id || p.employee_id;
                                    if (!empId) return;
                                    setBikeHistoryDialog({
                                      open: true,
                                      loading: true,
                                      riderName: p.rider_name || p.employee_name || '',
                                      month: wizardData.month,
                                      year: wizardData.year,
                                      history: [],
                                    });
                                    try {
                                      const res = await axios.get(`${API}/motorcycles/rider-bike-history`, {
                                        headers: getAuthHeader(),
                                        params: { rider_id: empId, month: wizardData.month, year: wizardData.year },
                                      });
                                      setBikeHistoryDialog((d) => ({
                                        ...d,
                                        loading: false,
                                        history: res.data?.history || [],
                                        currentBikePlate: res.data?.current_bike_plate || '',
                                      }));
                                    } catch (err) {
                                      setBikeHistoryDialog((d) => ({ ...d, loading: false }));
                                      toast.error(formatErrorMessage(err, 'Failed to load bike history'));
                                    }
                                  }}
                                  data-testid={`bike-history-btn-${idx}`}
                                >
                                  {p.bike_plate || '-'}
                                </button>
                              </TableCell>
                            )}
                            <TableCell className={`text-xs text-slate-600 font-mono ${isStaff ? 'border-r-2 border-slate-200' : ''}`}>{p.edenred_id || '-'}</TableCell>
                            {!isStaff && (
                              <TableCell className="text-right text-sm border-r-2 border-slate-200" data-testid={`orders-cell-${idx}`}>
                                {(() => {
                                  const cb = p.calculation_breakdown || {};
                                  const pu = cb.total_pickup_orders;
                                  const dr = cb.total_dropoff_orders;
                                  if (pu != null && dr != null) {
                                    return (
                                      <span className="text-[11px] text-slate-700 font-mono whitespace-nowrap">
                                        <span>P {parseInt(pu) || 0}</span>
                                        <span className="mx-1 text-slate-300">/</span>
                                        <span>D {parseInt(dr) || 0}</span>
                                      </span>
                                    );
                                  }
                                  return <span>{p.total_orders || 0}</span>;
                                })()}
                              </TableCell>
                            )}
                            {formulaCols.map((col, ci) => {
                              const v = readFormulaCol(p, col.key);
                              return (
                                <TableCell key={`fx-${col.key}`} className={`text-right text-xs text-slate-700 bg-slate-50 p-1 font-mono ${ci === formulaCols.length - 1 ? 'border-r-2 border-slate-300' : ''}`} data-testid={`formula-col-${col.key}-${idx}`}>
                                  {v === undefined ? '-' : (Number.isInteger(v) ? v : parseFloat(v).toFixed(2))}
                                </TableCell>
                              );
                            })}
                            {staffEarningCols.map(col => (
                              <TableCell key={col.key} className="text-right text-xs text-green-700 bg-green-50/30 p-1">
                                {parseFloat(p[col.key] || 0).toFixed(2)}
                              </TableCell>
                            ))}
                            {isCareemAppEarnings && (
                              <TableCell
                                className="text-right text-xs text-green-700 bg-green-50/30 p-1 font-mono"
                                data-testid={`app-earnings-cell-${idx}`}
                                title={`Careem App Earnings for ${p.rider_name || 'rider'}`}
                              >
                                {parseFloat(p?.calculation_breakdown?.app_earnings || 0).toFixed(2)}
                              </TableCell>
                            )}
                            <TableCell className="text-right p-1 bg-green-50/30">
                              <input
                                type="number"
                                className="w-20 text-right text-sm border rounded px-1 py-0.5 text-green-800 font-semibold bg-transparent hover:bg-green-50 focus:bg-white focus:ring-1 focus:ring-green-400"
                                value={p.gross_salary || 0}
                                onChange={(e) => handleEditPreviewRow(idx, 'gross_salary', e.target.value)}
                                data-testid={`edit-gross-${idx}`}
                              />
                            </TableCell>
                            <TableCell className="text-right p-1 bg-green-50/30 border-r-2 border-slate-300" data-testid={`past-dues-cell-${idx}`}>
                              <input
                                type="number"
                                className={`w-20 text-right text-sm border rounded px-1 py-0.5 bg-transparent hover:bg-green-50 focus:bg-white focus:ring-1 focus:ring-green-400 ${(p.past_dues || 0) > 0 ? 'text-green-700 border-green-300 bg-green-50 font-semibold' : 'text-green-700'}`}
                                value={p.past_dues || 0}
                                onChange={(e) => handleEditPreviewRow(idx, 'past_dues', e.target.value)}
                                onBlur={(e) => {
                                  const val = parseFloat(e.target.value) || 0;
                                  if (val > 0) {
                                    setPastDuesDialog({
                                      open: true,
                                      idx,
                                      amount: val,
                                      description: wizardData.preview[idx]?.past_dues_desc || ''
                                    });
                                  }
                                }}
                                data-testid={`edit-past-dues-${idx}`}
                              />
                              {p.past_dues_desc && (p.past_dues || 0) > 0 && (
                                <div
                                  className="text-[9px] text-green-700 mt-0.5 truncate max-w-[80px] cursor-pointer italic"
                                  title={p.past_dues_desc}
                                  onClick={() => setPastDuesDialog({ open: true, idx, amount: p.past_dues || 0, description: p.past_dues_desc || '' })}
                                >
                                  {p.past_dues_desc}
                                </div>
                              )}
                            </TableCell>
                            {visibleDedColumns.map((col, ci) => (
                              <TableCell
                                key={col.key}
                                className={`text-right p-1 bg-red-50/20 ${ci === visibleDedColumns.length - 1 ? 'border-r-2 border-slate-300' : ''}`}
                              >
                                {p.is_npp && ['fines', 'salik', 'maintenance', 'loan_emi', 'slx_deduction', 'prev_arrears'].includes(col.key) ? (
                                  <span
                                    className="inline-flex items-center justify-center px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-100 text-slate-600 border border-slate-300 cursor-help"
                                    title={`NPP - Non Primary Platform, hence not deducted. Recovery happens on ${p.npp_primary_platform || 'Primary'} platform payroll.`}
                                    data-testid={`npp-cell-${col.key}-${idx}`}
                                  >
                                    NPP
                                  </span>
                                ) : col.key === 'misc_deduction' ? (
                                  <>
                                    <input
                                      type="number"
                                      className={`w-full text-right text-sm border rounded px-1 py-0.5 bg-transparent hover:bg-red-50 focus:bg-white focus:ring-1 focus:ring-red-400 ${(p.misc_deduction || 0) > 0 ? 'text-pink-700 border-pink-300 bg-pink-50' : 'text-red-600'}`}
                                      value={p[col.key] || 0}
                                      onChange={(e) => handleEditPreviewRow(idx, col.key, e.target.value)}
                                      onBlur={(e) => {
                                        const val = parseFloat(e.target.value) || 0;
                                        if (val > 0) {
                                          setMiscDeductionDialog({ open: true, idx, amount: val, description: wizardData.preview[idx]?.misc_deduction_desc || '' });
                                        }
                                      }}
                                      data-testid={`edit-${col.key}-${idx}`}
                                    />
                                    {p.misc_deduction_desc && (p.misc_deduction || 0) > 0 && (
                                      <div className="text-[9px] text-pink-600 mt-0.5 truncate max-w-[60px] cursor-pointer" title={p.misc_deduction_desc}
                                        onClick={() => setMiscDeductionDialog({ open: true, idx, amount: p.misc_deduction || 0, description: p.misc_deduction_desc || '' })}>
                                        {p.misc_deduction_desc}
                                      </div>
                                    )}
                                  </>
                                ) : col.key === 'loan_emi' ? (
                                  <>
                                    <input
                                      type="number"
                                      className={`w-full text-right text-sm border rounded px-1 py-0.5 bg-transparent hover:bg-red-50 focus:bg-white focus:ring-1 focus:ring-red-400 ${p.emi_was_adjusted ? 'text-orange-600 border-orange-300 bg-orange-50' : 'text-red-600'}`}
                                      value={p[col.key] || 0}
                                      onChange={(e) => handleEditPreviewRow(idx, col.key, e.target.value)}
                                      data-testid={`edit-${col.key}-${idx}`}
                                    />
                                    {p.emi_was_adjusted && (
                                      <div className="text-[10px] text-orange-600 mt-0.5">
                                        {p.emi_recovery_method === 'next_emi' ? 'Next EMI' : p.emi_recovery_method === 'installments' ? `${p.emi_recovery_installments} inst.` : p.emi_recovery_method === 'cross_platform' ? `→ ${p.emi_pause_reason}` : 'Extended'}
                                      </div>
                                    )}
                                  </>
                                ) : col.key === 'maintenance' ? (
                                  <input
                                    type="number"
                                    className="w-full text-right text-sm border rounded px-1 py-0.5 text-red-600 bg-transparent hover:bg-red-50 focus:bg-white focus:ring-1 focus:ring-red-400 cursor-pointer"
                                    value={p[col.key] || 0}
                                    onChange={(e) => handleEditPreviewRow(idx, col.key, e.target.value)}
                                    onFocus={() => {
                                      const empId = p.rider_id || p.employee_id;
                                      if (empId) {
                                        setMaintDetailDialog({ open: true, idx, records: [], loading: true, employeeId: empId, month: wizardData.month, year: wizardData.year });
                                        axios.get(`${API}/talent/pms/rider-maintenance-details/${empId}?month=${wizardData.month}&year=${wizardData.year}`, { headers: getAuthHeader() })
                                          .then(res => setMaintDetailDialog(prev => ({ ...prev, records: res.data.records || [], loading: false })))
                                          .catch(() => setMaintDetailDialog(prev => ({ ...prev, loading: false })));
                                      }
                                    }}
                                    data-testid={`edit-${col.key}-${idx}`}
                                  />
                                ) : col.key === 'prev_arrears' ? (
                                  <div className="flex flex-col items-end leading-tight" data-testid={`prev-arrears-cell-${idx}`}>
                                    <span className="text-sm text-red-600 px-1 font-medium">
                                      {parseFloat(p.prev_arrears || 0).toFixed(2)}
                                    </span>
                                    {Array.isArray(p.prev_arrears_months) && p.prev_arrears_months.length > 0 && (
                                      <span
                                        className="text-[9px] text-slate-500 italic"
                                        title={`Recovered from previous month(s) negative net salary: ${p.prev_arrears_months.join(', ')}`}
                                      >
                                        ({p.prev_arrears_months.join(', ')})
                                      </span>
                                    )}
                                  </div>
                                ) : col.key === 'fines' ? (
                                  <input
                                    type="number"
                                    className="w-full text-right text-sm border rounded px-1 py-0.5 text-red-600 bg-transparent hover:bg-red-50 focus:bg-white focus:ring-1 focus:ring-red-400 cursor-pointer"
                                    value={p[col.key] || 0}
                                    readOnly
                                    onClick={() => {
                                      const empId = p.rider_id || p.employee_id;
                                      if (!empId) return;
                                      setFinesDetailDialog({
                                        open: true, idx, employeeId: empId,
                                        employeeName: p.employee_name || p.rider_name || '',
                                        bikePlate: p.bike_plate || '',
                                        month: wizardData.month, year: wizardData.year,
                                        fines: [], loading: true,
                                      });
                                      axios.get(`${API}/fines/by-rider-month`, {
                                        headers: getAuthHeader(),
                                        params: { rider_id: empId, month: wizardData.month, year: wizardData.year, bike_plate: p.bike_plate || '' },
                                      })
                                        .then(res => setFinesDetailDialog(prev => ({ ...prev, fines: res.data.fines || [], loading: false })))
                                        .catch(err => {
                                          console.error('fines fetch failed', err);
                                          setFinesDetailDialog(prev => ({ ...prev, fines: [], loading: false }));
                                          toast.error('Could not fetch fines for this rider');
                                        });
                                    }}
                                    data-testid={`fines-cell-${idx}`}
                                    title="Click to view fine details and split into instalments"
                                  />
                                ) : col.key === 'fine_arrears' ? (
                                  <span className="text-sm text-red-600 px-1" title="Instalment recovery from previously split fines">{parseFloat(p[col.key] || 0).toFixed(2)}</span>
                                ) : col.key === 'room_rent' && isStaff ? (
                                  <input
                                    type="number"
                                    step="0.01"
                                    className={`w-full text-right text-sm border rounded px-1 py-0.5 bg-transparent hover:bg-orange-50 focus:bg-white focus:ring-1 ${p.room_rent_was_adjusted ? 'text-orange-700 font-semibold ring-1 ring-orange-300' : 'text-red-600 focus:ring-red-400'}`}
                                    value={p.room_rent ?? 0}
                                    title={p.room_rent_was_adjusted ? `Adjusted from AED ${(p.original_room_rent || 0).toFixed(2)} · Reason: ${p.room_rent_adjustment_reason || '(no reason)'}` : 'Edit room rent for this month — a reason is required'}
                                    onChange={(e) => {
                                      const updated = [...wizardData.preview];
                                      const newVal = round2(parseFloat(e.target.value) || 0);
                                      updated[idx] = { ...updated[idx], room_rent: newVal };
                                      // Recalculate net inline (no commit yet — wait for blur)
                                      const row = updated[idx];
                                      row.total_deductions = round2((row.fines || 0) + (row.salik || 0) + (row.maintenance || 0) + (row.loan_emi || 0) + (row.prev_arrears || 0) + (row.paid_in_wallet || 0) + (row.paid_as_tips || 0) + (row.paid_as_cod || 0) + (row.misc_deduction || 0) + (row.clawback_deduction || 0) + (row.platform_deduction || 0) + (row.food_compensation || 0) + (row.room_rent || 0) + (row.slx_deduction || 0) + (row.dynamic_deductions_total || 0) + (row.salary_advance || 0));
                                      row.net_salary = round2((row.gross_salary || 0) + (row.past_dues || 0) - row.total_deductions);
                                      setWizardData({ ...wizardData, preview: updated });
                                    }}
                                    onBlur={(e) => {
                                      const newVal = parseFloat(e.target.value) || 0;
                                      const original = parseFloat(p.original_room_rent ?? p.room_rent ?? 0);
                                      // If value differs from the original and no reason captured yet, open dialog
                                      if (Math.abs(newVal - original) > 0.005 && !p.room_rent_was_adjusted) {
                                        setRoomRentAdjustmentData({
                                          idx,
                                          originalRoomRent: original,
                                          newRoomRent: newVal,
                                          reason: '',
                                          employeeName: p.employee_name || p.rider_name || '',
                                        });
                                        setRoomRentAdjustmentDialogOpen(true);
                                      } else if (Math.abs(newVal - original) > 0.005 && p.room_rent_was_adjusted) {
                                        // User is editing an already-adjusted value — re-prompt for an updated reason
                                        setRoomRentAdjustmentData({
                                          idx,
                                          originalRoomRent: original,
                                          newRoomRent: newVal,
                                          reason: p.room_rent_adjustment_reason || '',
                                          employeeName: p.employee_name || p.rider_name || '',
                                        });
                                        setRoomRentAdjustmentDialogOpen(true);
                                      }
                                    }}
                                    data-testid={`edit-room-rent-${idx}`}
                                  />
                                ) : col.key === 'vendor_margin_deduction' ? (
                                  <span
                                    className="text-sm text-red-600 px-1 font-mono"
                                    title="Vendor Margin from Careem Excel (deducted from App Earnings)"
                                    data-testid={`vendor-margin-cell-${idx}`}
                                  >
                                    {parseFloat(p?.calculation_breakdown?.vendor_margin || 0).toFixed(2)}
                                  </span>
                                ) : col.key === 'paid_as_cod' && isCareemAppEarnings ? (
                                  <span
                                    className="text-sm text-red-600 px-1 font-mono"
                                    title="COD from Careem Excel"
                                    data-testid={`cod-cell-${idx}`}
                                  >
                                    {parseFloat(p.paid_as_cod || p?.calculation_breakdown?.cod || 0).toFixed(2)}
                                  </span>
                                ) : ['platform_deduction', 'food_compensation', 'paid_in_wallet', 'paid_as_tips', 'paid_as_cod', 'clawback_deduction', 'slx_deduction'].includes(col.key) ? (
                                  <span className="text-sm text-red-600 px-1">{parseFloat(p[col.key] || 0).toFixed(2)}</span>
                                ) : (
                                  <input
                                    type="number"
                                    className="w-full text-right text-sm border rounded px-1 py-0.5 text-red-600 bg-transparent hover:bg-red-50 focus:bg-white focus:ring-1 focus:ring-red-400"
                                    value={p[col.key] || 0}
                                    onChange={(e) => handleEditPreviewRow(idx, col.key, e.target.value)}
                                    data-testid={`edit-${col.key}-${idx}`}
                                  />
                                )}
                              </TableCell>
                            ))}
                            <TableCell className="text-right font-bold text-sm bg-blue-50/40">
                              <span className={parseFloat(p.net_salary || 0) < 0 ? 'text-red-600' : 'text-blue-800'}>
                                <CurrencyDisplay amount={p.net_salary || 0} />
                              </span>
                              {parseFloat(p.net_salary || 0) < 0 && (
                                <div className="text-[9px] text-red-500 font-normal">Auto-loan on approve</div>
                              )}
                            </TableCell>
                            <TableCell className="text-center p-1">
                              <input
                                type="checkbox"
                                className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                                checked={p.hold_salary || false}
                                onChange={(e) => {
                                  const updated = [...wizardData.preview];
                                  updated[idx] = { ...updated[idx], hold_salary: e.target.checked };
                                  setWizardData({ ...wizardData, preview: updated });
                                }}
                                data-testid={`hold-salary-${idx}`}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Unmatched Riders Info */}
                  {wizardData.unmatchedRiders?.length > 0 && (
                    <div className="border border-amber-200 rounded-lg bg-amber-50/50 p-3">
                      <p className="text-sm font-medium text-amber-700 mb-2">
                        {wizardData.unmatchedRiders.length} rider(s) from the file not found in system (excluded from payroll):
                      </p>
                      <div className="max-h-24 overflow-y-auto">
                        <div className="flex flex-wrap gap-2">
                          {wizardData.unmatchedRiders.map((u, i) => (
                            <Badge key={i} variant="outline" className="border-amber-300 text-amber-700 text-xs">
                              {u.rider_name} ({u.platform_id})
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}


                  {/* NPP (Non-Primary Platform) Note */}
                  {wizardData.preview.some(p => p.is_npp) && (
                    <div className="border border-slate-200 rounded-lg bg-slate-50 p-3" data-testid="npp-note">
                      <p className="text-sm font-medium text-slate-700 flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4 text-slate-500" />
                        {wizardData.preview.filter(p => p.is_npp).length} rider(s) marked as <span className="font-mono px-1.5 py-0.5 bg-slate-200 rounded text-[11px]">NPP</span>
                      </p>
                      <p className="text-xs text-slate-600 mt-1">
                        <strong>NPP = Non Primary Platform, hence not deducted.</strong> These riders' Fines / SALIK / Maintenance / Loan EMI / SLX will be deducted from their <strong>Primary Platform</strong> payroll instead. To change a rider's primary platform, edit their Platform History on the Riders page.
                      </p>
                    </div>
                  )}

                  {/* Negative Salary Warning */}
                  {wizardData.preview.some(p => parseFloat(p.net_salary || 0) < 0) && (
                    <div className="border border-red-200 rounded-lg bg-red-50/50 p-3" data-testid="negative-salary-warning">
                      <p className="text-sm font-medium text-red-700 flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" />
                        {wizardData.preview.filter(p => parseFloat(p.net_salary || 0) < 0).length} employee(s) have negative net salary
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        Upon approval, negative amounts will be automatically converted to loans and recovered in full from next month's payroll.
                      </p>
                    </div>
                  )}
                  </>
                  );
                  })()}

                  <DialogFooter className="flex justify-between sm:justify-between">
                    <Button variant="outline" onClick={() => setWizardStep(3)} data-testid="wizard-back-btn">Back</Button>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline"
                        onClick={handleSaveDraft} 
                        disabled={wizardLoading}
                        data-testid="save-draft-btn"
                      >
                        {wizardLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileCheck className="w-4 h-4 mr-1" />}
                        Save as Draft
                      </Button>
                      <Button 
                        onClick={handleWizardSubmit} 
                        disabled={wizardLoading}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="submit-payroll-btn"
                      >
                        {wizardLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Submit for Approval
                      </Button>
                    </div>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>

        </div>
      )}


      {/* ============ SALARY ADVANCE SUBPAGE ============ */}
      {activeSubPage === 'salary-advance' && tabFromUrl !== 'admin' && (
        <SalaryAdvance
          user={user}
          canCreate={
            isAdmin
              || user?.permissions?.talent?.subPages?.salaryAdvance?.create === true
              || user?.permissions?.talent?.create === true
          }
          canEdit={
            isAdmin
              || user?.permissions?.talent?.subPages?.salaryAdvance?.edit === true
              || user?.permissions?.talent?.edit === true
          }
          canDelete={
            isAdmin
              || user?.permissions?.talent?.subPages?.salaryAdvance?.delete === true
              || user?.permissions?.talent?.delete === true
          }
        />
      )}


      {/* ============ RECOVERY REPORT SUBPAGE ============ */}
      {activeSubPage === 'recovery' && tabFromUrl !== 'admin' && (
        <div className="space-y-4" data-testid="recovery-report-section">
          {/* Filters & Download */}
          <Card>
            <CardContent className="p-4 space-y-3">
              {/* Row 1: Filter dropdowns (all 7 filters) */}
              <div className="flex flex-wrap items-center gap-2">
                <Select value={recoveryFilter.month || 'all'} onValueChange={(v) => setRecoveryFilter(prev => ({ ...prev, month: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="w-28 h-9 text-xs" data-testid="recovery-month-filter">
                    <SelectValue placeholder="All Months" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Months</SelectItem>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                      <SelectItem key={m} value={String(m)}>
                        {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m-1]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={recoveryFilter.year || 'all'} onValueChange={(v) => setRecoveryFilter(prev => ({ ...prev, year: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="w-24 h-9 text-xs" data-testid="recovery-year-filter">
                    <SelectValue placeholder="All Years" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Years</SelectItem>
                    {[2024, 2025, 2026].map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={recoveryFilter.type || 'all'} onValueChange={(v) => setRecoveryFilter(prev => ({ ...prev, type: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="w-32 h-9 text-xs" data-testid="recovery-type-filter">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="fine">Fines</SelectItem>
                    <SelectItem value="salik">SALIK</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="loan">Loan EMI</SelectItem>
                    <SelectItem value="room_rent">Room Rent</SelectItem>
                    <SelectItem value="slx">SLX Charge</SelectItem>
                  </SelectContent>
                </Select>
                {(() => {
                  const opts = getRecoveryFilterOptions();
                  return (
                    <>
                      <Select value={recoveryFilter.platform || 'all'} onValueChange={(v) => setRecoveryFilter(prev => ({ ...prev, platform: v === 'all' ? '' : v }))}>
                        <SelectTrigger className="w-32 h-9 text-xs" data-testid="recovery-platform-filter">
                          <SelectValue placeholder="All Platforms" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Platforms</SelectItem>
                          {opts.platforms.map(p => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      <Select value={recoveryFilter.rider || 'all'} onValueChange={(v) => setRecoveryFilter(prev => ({ ...prev, rider: v === 'all' ? '' : v }))}>
                        <SelectTrigger className="w-48 h-9 text-xs" data-testid="recovery-rider-filter">
                          <SelectValue placeholder="All Riders / Staff" />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                          <SelectItem value="all">All Riders / Staff</SelectItem>
                          {opts.riders.map(r => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      <Select value={recoveryFilter.plate || 'all'} onValueChange={(v) => setRecoveryFilter(prev => ({ ...prev, plate: v === 'all' ? '' : v }))}>
                        <SelectTrigger className="w-32 h-9 text-xs" data-testid="recovery-plate-filter">
                          <SelectValue placeholder="All Plates" />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                          <SelectItem value="all">All Plates</SelectItem>
                          {opts.plates.map(p => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      <Select value={recoveryFilter.ref || 'all'} onValueChange={(v) => setRecoveryFilter(prev => ({ ...prev, ref: v === 'all' ? '' : v }))}>
                        <SelectTrigger className="w-36 h-9 text-xs" data-testid="recovery-ref-filter">
                          <SelectValue placeholder="All Ref Nos." />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                          <SelectItem value="all">All Ref Nos.</SelectItem>
                          {opts.refs.map(r => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </>
                  );
                })()}
              </div>

              {/* Row 2: Global refresh only (PDF/Excel moved into each month card) */}
              <div className="flex items-center justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchRecoveryReport}
                  data-testid="recovery-refresh-btn"
                  title="Refresh"
                  className="gap-1.5 text-xs"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${recoveryLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Grouped Records */}
          {recoveryLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : recoveryData.groups.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Receipt className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500 text-lg">No recovered records found</p>
                <p className="text-slate-400 text-sm mt-1">Records will appear here after payroll batches are approved with deductions.</p>
              </CardContent>
            </Card>
          ) : (
            getFilteredRecoveryGroups().length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Receipt className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500 text-lg">No records match current filters</p>
              </CardContent>
            </Card>
            ) :
            getFilteredRecoveryGroups().map((group) => {
              const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
              const monthLabel = group.month && group.year
                ? `${monthNames[group.month] || 'Unknown'} ${group.year}`
                : 'Unknown Period';
              const monthKey = `${group.year}-${group.month}`;
              const isCollapsed = !!collapsedMonths[monthKey];
              const activeTypeFilter = monthTypeFilter[monthKey] || ''; // '' = all

              // Per-month summary numbers
              const monthRecords = group.records || [];
              const sumByType = (typeName) => monthRecords
                .filter(r => r.type === typeName)
                .reduce((s, r) => s + (r.amount || 0), 0);
              const countByType = (typeName) => monthRecords.filter(r => r.type === typeName).length;
              const finesAmt = sumByType('Fine');
              const salikAmt = sumByType('SALIK');
              const maintAmt = sumByType('Maintenance');
              const loanAmt = sumByType('Loan EMI');
              const roomRentAmt = sumByType('Room Rent');
              const slxAmt = sumByType('SLX Charge');

              const searchQuery = (monthSearchQuery[monthKey] || '').trim().toLowerCase();
              const visibleRecords = (() => {
                let recs = activeTypeFilter
                  ? monthRecords.filter(r => r.type === activeTypeFilter)
                  : monthRecords;
                if (searchQuery) {
                  recs = recs.filter(r => (
                    String(r.rider_name || '').toLowerCase().includes(searchQuery)
                    || String(r.rider_id || '').toLowerCase().includes(searchQuery)
                    || String(r.plate || '').toLowerCase().includes(searchQuery)
                    || String(r.ref_no || '').toLowerCase().includes(searchQuery)
                    || String(r.platform || '').toLowerCase().includes(searchQuery)
                    || String(r.description || '').toLowerCase().includes(searchQuery)
                  ));
                }
                return recs;
              })();

              const summaryCards = [
                { key: '', label: 'Total Recovered', amount: group.total_amount, count: monthRecords.length, suffix: 'records', bg: 'bg-blue-50', border: 'border-blue-200', textTitle: 'text-blue-600', textAmt: 'text-blue-800', textSub: 'text-blue-500', icon: <DollarSign className="w-5 h-5 text-blue-400" /> },
                { key: 'Fine', label: 'Fines Recovered', amount: finesAmt, count: countByType('Fine'), suffix: 'fines', bg: 'bg-red-50', border: 'border-red-200', textTitle: 'text-red-600', textAmt: 'text-red-800', textSub: 'text-red-500', icon: <AlertTriangle className="w-5 h-5 text-red-400" /> },
                { key: 'SALIK', label: 'SALIK Recovered', amount: salikAmt, count: countByType('SALIK'), suffix: 'tolls', bg: 'bg-amber-50', border: 'border-amber-200', textTitle: 'text-amber-600', textAmt: 'text-amber-800', textSub: 'text-amber-500', icon: <CreditCard className="w-5 h-5 text-amber-400" /> },
                { key: 'Maintenance', label: 'Maintenance', amount: maintAmt, count: countByType('Maintenance'), suffix: 'repairs', bg: 'bg-green-50', border: 'border-green-200', textTitle: 'text-green-600', textAmt: 'text-green-800', textSub: 'text-green-500', icon: <Settings className="w-5 h-5 text-green-400" /> },
                { key: 'Loan EMI', label: 'Loan EMI', amount: loanAmt, count: countByType('Loan EMI'), suffix: 'deductions', bg: 'bg-purple-50', border: 'border-purple-200', textTitle: 'text-purple-600', textAmt: 'text-purple-800', textSub: 'text-purple-500', icon: <CreditCard className="w-5 h-5 text-purple-400" /> },
                { key: 'Room Rent', label: 'Room Rent', amount: roomRentAmt, count: countByType('Room Rent'), suffix: 'staff', bg: 'bg-orange-50', border: 'border-orange-200', textTitle: 'text-orange-600', textAmt: 'text-orange-800', textSub: 'text-orange-500', icon: <Building className="w-5 h-5 text-orange-400" /> },
                { key: 'SLX Charge', label: 'SLX Recovery', amount: slxAmt, count: countByType('SLX Charge'), suffix: 'charges', bg: 'bg-teal-50', border: 'border-teal-200', textTitle: 'text-teal-600', textAmt: 'text-teal-800', textSub: 'text-teal-500', icon: <Receipt className="w-5 h-5 text-teal-400" /> },
              ];

              return (
                <Card key={monthKey} className="border-slate-200">
                  <CardHeader
                    className="pb-2 pt-4 px-4 cursor-pointer select-none hover:bg-slate-50/60 transition-colors"
                    onClick={() => setCollapsedMonths(prev => ({ ...prev, [monthKey]: !prev[monthKey] }))}
                    data-testid={`recovery-month-header-${monthKey}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {isCollapsed
                          ? <ChevronRight className="w-4 h-4 text-slate-500" />
                          : <ChevronDown className="w-4 h-4 text-slate-500" />}
                        <div className="w-2 h-2 rounded-full bg-blue-500" />
                        <CardTitle className="text-base font-semibold text-slate-800">Recovery for {monthLabel}</CardTitle>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-blue-700">{group.total_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} AED</p>
                        <p className="text-xs text-slate-500">{monthRecords.length} records</p>
                      </div>
                    </div>
                  </CardHeader>
                  {!isCollapsed && (
                    <CardContent className="px-4 pb-4 space-y-4">
                      {/* Per-month summary cards (clickable to filter records below) */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                        {summaryCards.map(sc => {
                          const active = activeTypeFilter === sc.key;
                          return (
                            <button
                              type="button"
                              key={`${monthKey}-${sc.key || 'all'}`}
                              onClick={() => setMonthTypeFilter(prev => ({
                                ...prev,
                                [monthKey]: prev[monthKey] === sc.key ? '' : sc.key
                              }))}
                              className={`text-left rounded-lg border ${sc.bg} ${sc.border} px-2.5 py-2 transition-all ${active ? 'ring-2 ring-offset-1 ring-blue-500 shadow-md' : 'hover:shadow-sm hover:-translate-y-px'}`}
                              data-testid={`recovery-summary-${monthKey}-${sc.key || 'total'}`}
                            >
                              <div className="flex items-center justify-between gap-1">
                                <div className="min-w-0">
                                  <p className={`text-[9px] font-medium uppercase tracking-tight ${sc.textTitle} truncate`}>{sc.label}</p>
                                  <p className={`text-base font-bold ${sc.textAmt} leading-tight`}>
                                    {sc.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                    {sc.key === '' && <span className="text-[9px] font-normal ml-1">AED</span>}
                                  </p>
                                  <p className={`text-[9px] ${sc.textSub}`}>{sc.count} {sc.suffix}</p>
                                </div>
                                <div className="shrink-0">{sc.icon}</div>
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      {/* Per-month search + download bar */}
                      <div className="flex flex-wrap items-center gap-2 bg-slate-50/70 border border-slate-200 rounded-md px-3 py-2">
                        <div className="flex items-center gap-1.5 flex-1 min-w-[240px]">
                          <Search className="w-3.5 h-3.5 text-slate-400" />
                          <Input
                            value={monthSearchQuery[monthKey] || ''}
                            onChange={(e) => setMonthSearchQuery(prev => ({ ...prev, [monthKey]: e.target.value }))}
                            placeholder="Search name, rider ID, plate, ref no., platform…"
                            className="h-8 text-xs border-0 bg-transparent focus-visible:ring-0 px-1"
                            data-testid={`recovery-search-${monthKey}`}
                          />
                          {monthSearchQuery[monthKey] && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => setMonthSearchQuery(prev => ({ ...prev, [monthKey]: '' }))}
                            >Clear</Button>
                          )}
                          <span className="text-[10px] text-slate-400 ml-1 whitespace-nowrap">{visibleRecords.length} of {monthRecords.length}</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 gap-1 text-xs"
                          disabled={!!downloadingMonth[monthKey]}
                          data-testid={`recovery-pdf-${monthKey}`}
                          onClick={async () => {
                            if (visibleRecords.length === 0) { toast.error('No records to download'); return; }
                            setDownloadingMonth(prev => ({ ...prev, [monthKey]: 'pdf' }));
                            try {
                              const cols = [
                                { header: 'Rider ID', key: 'rider_id', width: 22 },
                                { header: 'Rider Name', key: 'rider_name', width: 38 },
                                { header: 'Platform', key: 'platform', width: 18 },
                                { header: 'Type', key: 'type', width: 16 },
                                { header: 'Ref No.', key: 'ref_no', width: 18 },
                                { header: 'Date', key: 'date', width: 16, format: (v) => (v || '').slice(0, 10) },
                                { header: 'Description', key: 'description', width: 30 },
                                { header: 'Plate', key: 'plate', width: 16 },
                                { header: 'Amount (AED)', key: 'amount', width: 18, format: (v) => Number(v || 0).toFixed(2) },
                              ];
                              await new Promise(r => setTimeout(r, 50));
                              downloadPDF(visibleRecords, cols, `Recovery_${monthLabel.replace(/\s+/g, '_')}`, `Recovery Report — ${monthLabel}`, `${visibleRecords.length} record(s)`);
                              toast.success('PDF downloaded');
                            } catch (e) {
                              toast.error('Failed to download PDF: ' + (e.message || e));
                            } finally {
                              setDownloadingMonth(prev => ({ ...prev, [monthKey]: null }));
                            }
                          }}
                        >
                          {downloadingMonth[monthKey] === 'pdf' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                          PDF
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 gap-1 text-xs"
                          disabled={!!downloadingMonth[monthKey]}
                          data-testid={`recovery-excel-${monthKey}`}
                          onClick={async () => {
                            if (visibleRecords.length === 0) { toast.error('No records to download'); return; }
                            setDownloadingMonth(prev => ({ ...prev, [monthKey]: 'excel' }));
                            try {
                              const cols = [
                                { header: 'Rider ID', key: 'rider_id', width: 18 },
                                { header: 'Rider Name', key: 'rider_name', width: 30 },
                                { header: 'Platform', key: 'platform', width: 14 },
                                { header: 'Type', key: 'type', width: 14 },
                                { header: 'Ref No.', key: 'ref_no', width: 16 },
                                { header: 'Date', key: 'date', width: 12, format: (v) => (v || '').slice(0, 10) },
                                { header: 'Description', key: 'description', width: 28 },
                                { header: 'Plate No.', key: 'plate', width: 14 },
                                { header: 'Amount (AED)', key: 'amount', width: 14, format: (v) => Number(v || 0).toFixed(2) },
                              ];
                              await new Promise(r => setTimeout(r, 50));
                              downloadExcel(visibleRecords, cols, `Recovery_${monthLabel.replace(/\s+/g, '_')}`, monthLabel);
                              toast.success('Excel downloaded');
                            } catch (e) {
                              toast.error('Failed to download Excel: ' + (e.message || e));
                            } finally {
                              setDownloadingMonth(prev => ({ ...prev, [monthKey]: null }));
                            }
                          }}
                        >
                          {downloadingMonth[monthKey] === 'excel' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
                          Excel
                        </Button>
                      </div>

                      {activeTypeFilter && (
                        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-md px-3 py-2 text-xs text-blue-800">
                          <span>Showing only <strong>{activeTypeFilter === 'Fine' ? 'Fines' : activeTypeFilter}</strong> records ({visibleRecords.length})</span>
                          <button
                            type="button"
                            className="text-blue-600 hover:text-blue-800 font-medium"
                            onClick={() => setMonthTypeFilter(prev => ({ ...prev, [monthKey]: '' }))}
                            data-testid={`recovery-summary-clear-${monthKey}`}
                          >
                            Clear
                          </button>
                        </div>
                      )}

                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50 border-b">
                            <TableHead className="font-semibold text-xs">RIDER ID</TableHead>
                            <TableHead className="font-semibold text-xs">RIDER NAME</TableHead>
                            <TableHead className="font-semibold text-xs">PLATFORM</TableHead>
                            <TableHead className="font-semibold text-xs">TYPE</TableHead>
                            <TableHead className="font-semibold text-xs">REF NO.</TableHead>
                            <TableHead className="font-semibold text-xs">DATE</TableHead>
                            <TableHead className="font-semibold text-xs">DESCRIPTION</TableHead>
                            <TableHead className="font-semibold text-xs">PLATE</TableHead>
                            <TableHead className="font-semibold text-xs text-right">AMOUNT</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {visibleRecords.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={9} className="text-center text-sm text-slate-500 py-6">
                                No records for this category
                              </TableCell>
                            </TableRow>
                          ) : visibleRecords.map((rec, idx) => (
                            <TableRow key={rec.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                              <TableCell className="text-xs text-slate-600 font-mono">{rec.rider_id || '-'}</TableCell>
                              <TableCell className="text-xs font-medium text-slate-800">{rec.rider_name || '-'}</TableCell>
                              <TableCell className="text-xs text-slate-600">{rec.platform || '-'}</TableCell>
                              <TableCell className="text-xs">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  rec.type === 'Fine' ? 'bg-red-100 text-red-800' :
                                  rec.type === 'SALIK' ? 'bg-amber-100 text-amber-800' :
                                  rec.type === 'Loan EMI' ? 'bg-purple-100 text-purple-800' :
                                  rec.type === 'Room Rent' ? 'bg-orange-100 text-orange-800' :
                                  rec.type === 'SLX Charge' ? 'bg-teal-100 text-teal-800' :
                                  'bg-green-100 text-green-800'
                                }`}>
                                  {rec.type}
                                </span>
                              </TableCell>
                              <TableCell className="text-xs text-slate-600 font-mono">{rec.ref_no || '-'}</TableCell>
                              <TableCell className="text-xs text-slate-700">{(rec.date || '').slice(0, 10)}</TableCell>
                              <TableCell className="text-xs text-slate-600 max-w-[220px]">
                                {rec.description ? (
                                  <button
                                    type="button"
                                    className="text-left w-full line-clamp-2 hover:text-blue-600 hover:underline cursor-pointer"
                                    title="Click to view full description"
                                    onClick={() => setRecoveryDescDialog({ open: true, text: rec.description })}
                                    data-testid="recovery-desc-cell"
                                  >
                                    {rec.description}
                                  </button>
                                ) : '-'}
                              </TableCell>
                              <TableCell className="text-xs text-slate-600 font-mono">{rec.plate || '-'}</TableCell>
                              <TableCell className="text-xs font-bold text-right text-slate-800">{rec.amount.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  )}
                </Card>
              );
            })
          )}

          {/* Description full-text dialog */}
          <Dialog open={recoveryDescDialog.open} onOpenChange={(open) => setRecoveryDescDialog(prev => ({ ...prev, open }))}>
            <DialogContent className="max-w-2xl" data-testid="recovery-desc-dialog">
              <DialogHeader>
                <DialogTitle>Description</DialogTitle>
              </DialogHeader>
              <div className="text-sm text-slate-700 whitespace-pre-wrap break-words leading-relaxed max-h-[60vh] overflow-y-auto" dir="auto">
                {recoveryDescDialog.text}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}


      {/* ============ POST PAYROLL ADJUSTMENT SUBPAGE ============ */}
      {activeSubPage === 'leaves' && tabFromUrl !== 'admin' && (
        <EmployeeLeaves />
      )}

      {activeSubPage === 'adjustments' && tabFromUrl !== 'admin' && (
        <div className="space-y-4" data-testid="post-payroll-adjustment-section">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 text-emerald-600" />
                    Post Payroll Adjustment
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Apply refunds from reversed fines or other corrections directly to a rider's finalized payroll.
                    Each applied adjustment regenerates the rider's salary slip.
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchAdjustments}
                    disabled={adjustmentsLoading}
                    data-testid="refresh-adjustments-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${adjustmentsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setManualAdjustmentDialog({
                      open: true, rider_id: '', rider_search: '', rider_results: [],
                      payrolls: [], payrolls_loading: false, selected_payroll_id: '',
                      amount: '', direction: 'credit', category: 'add_to_gross', reason: '',
                      recovered_items: [], recovered_loading: false, saving: false,
                    })}
                    data-testid="new-manual-adjustment-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Manual Adjustment
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <AdjustmentHistory refreshKey={adjustmentsList}>
              {(pendingIds) => { const visiblePending = pendingIds ? adjustmentsList.filter(a => pendingIds.has(a.id)) : adjustmentsList; return (<>
              {/* Table */}
              {adjustmentsLoading ? (
                <div className="flex items-center justify-center py-12 text-slate-500">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                  Loading pending adjustments…
                </div>
              ) : visiblePending.length === 0 ? (
                <div className="text-center py-12 text-slate-500 border-2 border-dashed rounded-lg" data-testid="no-adjustments-msg">
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
                  <div className="font-medium">No pending adjustments</div>
                  <div className="text-xs mt-1">Refunds from reversed fines and manual entries will appear here.</div>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs">Created</TableHead>
                        <TableHead className="text-xs">Type</TableHead>
                        <TableHead className="text-xs">Rider</TableHead>
                        <TableHead className="text-xs">Direction</TableHead>
                        <TableHead className="text-xs text-right">Amount (AED)</TableHead>
                        <TableHead className="text-xs">Reason / Ref</TableHead>
                        <TableHead className="text-xs text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visiblePending.map((adj) => (
                        <TableRow key={adj.id} data-testid={`adjustment-row-${adj.id}`}>
                          <TableCell className="text-xs text-slate-600 whitespace-nowrap">
                            {adj.created_at ? formatDateDisplay(adj.created_at) : '-'}
                          </TableCell>
                          <TableCell className="text-xs">
                            <Badge variant="outline" className="text-[10px]">
                              {(adj.type || 'manual').replace(/_/g, ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs font-medium text-slate-800">
                            {adj.rider_name || adj.rider_id}
                          </TableCell>
                          <TableCell>
                            {adj.direction === 'credit' ? (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Credit (refund)</Badge>
                            ) : (
                              <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Debit (deduct)</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-right font-mono font-bold">
                            {Number(adj.amount || 0).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-xs max-w-[300px]">
                            <div className="truncate" title={adj.reason || ''}>{adj.reason || '-'}</div>
                            {adj.category_label && (
                              <div className="text-[10px] text-emerald-700 mt-0.5 font-medium">
                                {adj.category_label}
                              </div>
                            )}
                            {adj.reference_fine_number && (
                              <div className="text-[10px] text-slate-500 mt-0.5">
                                Fine #{adj.reference_fine_number}
                              </div>
                            )}
                            {adj.proof_url && (
                              <a
                                href={`${process.env.REACT_APP_BACKEND_URL}${adj.proof_url}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[10px] text-blue-600 hover:underline inline-flex items-center gap-1 mt-0.5"
                              >
                                <ExternalLink className="w-3 h-3" /> View proof
                              </a>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => openApplyAdjustmentDialog(adj)}
                                data-testid={`apply-adjustment-btn-${adj.id}`}
                              >
                                <Check className="w-3.5 h-3.5 mr-1" />
                                Apply
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => cancelAdjustment(adj)}
                                data-testid={`cancel-adjustment-btn-${adj.id}`}
                              >
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              </>); }}
              </AdjustmentHistory>
            </CardContent>
          </Card>

          {/* APPLY DIALOG */}
          <Dialog
            open={adjustmentApplyDialog.open}
            onOpenChange={(open) => !open && setAdjustmentApplyDialog({
              open: false, adjustment: null, payrolls: [], selectedPayrollId: '', loading: false, applying: false,
            })}
          >
            <DialogContent className="max-w-2xl" data-testid="apply-adjustment-dialog">
              <DialogHeader>
                <DialogTitle>Apply Adjustment to Payroll</DialogTitle>
                <DialogDescription>
                  Choose an approved payroll for this rider. The credit/debit will be applied and the salary slip regenerated.
                </DialogDescription>
              </DialogHeader>
              {adjustmentApplyDialog.adjustment && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 p-3 rounded-lg bg-slate-50 border text-sm">
                    <div>
                      <div className="text-xs text-slate-500">Rider</div>
                      <div className="font-semibold">{adjustmentApplyDialog.adjustment.rider_name}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Amount</div>
                      <div className={`font-bold ${adjustmentApplyDialog.adjustment.direction === 'credit' ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {adjustmentApplyDialog.adjustment.direction === 'credit' ? '+' : '−'} AED {Number(adjustmentApplyDialog.adjustment.amount || 0).toFixed(2)}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-xs text-slate-500">Reason</div>
                      <div className="text-sm">{adjustmentApplyDialog.adjustment.reason}</div>
                    </div>
                    {adjustmentApplyDialog.adjustment.reference_fine_number && (
                      <div className="col-span-2">
                        <div className="text-xs text-slate-500">Reference Fine</div>
                        <div className="text-sm font-mono">#{adjustmentApplyDialog.adjustment.reference_fine_number}</div>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className="text-sm font-medium mb-2 block">Select Approved Payroll</Label>
                    {adjustmentApplyDialog.loading ? (
                      <div className="flex items-center text-sm text-slate-500 py-4">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Loading payrolls…
                      </div>
                    ) : adjustmentApplyDialog.payrolls.length === 0 ? (
                      <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                        <AlertCircle className="w-4 h-4 inline mr-2" />
                        No approved payrolls found for this rider. The adjustment can be applied once a payroll batch is approved.
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                        {adjustmentApplyDialog.payrolls.map((p) => {
                          const monthName = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][(p.month || 1) - 1];
                          const isSelected = adjustmentApplyDialog.selectedPayrollId === p.id;
                          const status = p.negative_salary_converted ? 'Arrears' :
                                         (p.salary_held || p.hold_salary) ? 'Hold' : 'Bank';
                          return (
                            <label
                              key={p.id}
                              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${isSelected ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:bg-slate-50'}`}
                              data-testid={`payroll-option-${p.id}`}
                            >
                              <input
                                type="radio"
                                name="payrollPicker"
                                checked={isSelected}
                                onChange={() => setAdjustmentApplyDialog(prev => ({ ...prev, selectedPayrollId: p.id }))}
                                className="w-4 h-4"
                              />
                              <div className="flex-1 grid grid-cols-4 gap-2 text-xs">
                                <div>
                                  <div className="text-slate-500">Period</div>
                                  <div className="font-semibold">{monthName} {p.year}</div>
                                </div>
                                <div>
                                  <div className="text-slate-500">Platform</div>
                                  <div className="font-medium">{p.platform || '-'}</div>
                                </div>
                                <div>
                                  <div className="text-slate-500">Net Salary</div>
                                  <div className="font-mono font-bold">
                                    AED {Number(p.net_salary || 0).toFixed(2)}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-slate-500">Status</div>
                                  <Badge
                                    className={`text-[10px] ${
                                      status === 'Arrears' ? 'bg-rose-100 text-rose-700' :
                                      status === 'Hold' ? 'bg-amber-100 text-amber-700' :
                                      'bg-emerald-100 text-emerald-700'
                                    }`}
                                  >
                                    {status}
                                  </Badge>
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setAdjustmentApplyDialog({
                    open: false, adjustment: null, payrolls: [], selectedPayrollId: '', loading: false, applying: false,
                  })}
                  disabled={adjustmentApplyDialog.applying}
                >
                  Cancel
                </Button>
                <Button
                  onClick={submitApplyAdjustment}
                  disabled={
                    adjustmentApplyDialog.applying ||
                    adjustmentApplyDialog.loading ||
                    !adjustmentApplyDialog.selectedPayrollId
                  }
                  data-testid="confirm-apply-adjustment-btn"
                >
                  {adjustmentApplyDialog.applying ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying…</>
                  ) : (
                    <><Check className="w-4 h-4 mr-2" /> Apply & Regenerate Slip</>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* MANUAL ADJUSTMENT DIALOG */}
          <Dialog
            open={manualAdjustmentDialog.open}
            onOpenChange={(open) => !open && setManualAdjustmentDialog({
              open: false, rider_id: '', rider_search: '', rider_results: [],
              payrolls: [], payrolls_loading: false, selected_payroll_id: '',
              amount: '', direction: 'credit', category: 'add_to_gross', reason: '',
              recovered_items: [], recovered_loading: false, saving: false,
            })}
          >
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manual-adjustment-dialog">
              <DialogHeader>
                <DialogTitle>New Manual Adjustment</DialogTitle>
                <DialogDescription>
                  Select an employee (rider or staff) and the payroll month. For Refund — Fines / SALIK / Maintenance, the current deductions and a live revised salary preview will appear below.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {/* --- Employee search (riders + staff) --- */}
                <div>
                  <Label className="text-sm">Employee</Label>
                  <Input
                    placeholder="Search employee by name, Emirates ID, platform, or bike plate…"
                    value={manualAdjustmentDialog.rider_search}
                    onChange={(e) => {
                      const v = e.target.value;
                      setManualAdjustmentDialog(prev => ({
                        ...prev, rider_id: '', payrolls: [], selected_payroll_id: '', recovered_items: [],
                      }));
                      searchRidersForAdjustment(v);
                    }}
                    data-testid="manual-adj-rider-search"
                  />
                  {manualAdjustmentDialog.rider_results.length > 0 && !manualAdjustmentDialog.rider_id && (
                    <div className="border rounded-lg mt-1 max-h-56 overflow-y-auto bg-white shadow-sm">
                      {manualAdjustmentDialog.rider_results.slice(0, 10).map((r) => {
                        const subParts = [
                          r.emirates_id || null,
                          r.platform || r.department || null,
                          r.bike_plate_number || null,
                        ].filter(Boolean);
                        const typeLabel = r.employee_type === 'staff' ? 'Staff' : 'Rider';
                        const typeChipClass = r.employee_type === 'staff'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-emerald-100 text-emerald-700';
                        return (
                          <button
                            key={`${r.employee_type || 'rider'}-${r.id}`}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b last:border-b-0"
                            onClick={() => {
                              setManualAdjustmentDialog(prev => ({
                                ...prev,
                                rider_id: r.id,
                                rider_search: r.name || r.rider_unique_id || r.employee_unique_id || r.id,
                                rider_results: [],
                                employee_type: r.employee_type || 'rider',
                              }));
                              fetchPayrollsForRider(r.id);
                            }}
                            data-testid={`manual-adj-rider-option-${r.id}`}
                          >
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-slate-800">{r.name || '(no name)'}</div>
                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${typeChipClass}`}>{typeLabel}</span>
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {subParts.length > 0 ? subParts.join(' • ') : (r.rider_unique_id || r.employee_unique_id || '—')}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* --- Month/Payroll picker --- */}
                {manualAdjustmentDialog.rider_id && (
                  <div>
                    <Label className="text-sm">Payroll Month <span className="text-rose-600">*</span></Label>
                    {manualAdjustmentDialog.payrolls_loading ? (
                      <div className="text-xs text-slate-500 py-2 flex items-center">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Loading approved payrolls…
                      </div>
                    ) : manualAdjustmentDialog.payrolls.length === 0 ? (
                      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-1">
                        <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
                        No approved payrolls for this employee yet.
                      </div>
                    ) : (
                      <Select
                        value={manualAdjustmentDialog.selected_payroll_id}
                        onValueChange={(v) => setManualAdjustmentDialog(prev => ({ ...prev, selected_payroll_id: v }))}
                      >
                        <SelectTrigger data-testid="manual-adj-payroll-picker">
                          <SelectValue placeholder="Select payroll month" />
                        </SelectTrigger>
                        <SelectContent>
                          {manualAdjustmentDialog.payrolls.map((p) => {
                            const monthName = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][(p.month || 1) - 1];
                            const nppTag = p.is_npp ? ` [NPP → ${p.npp_primary_platform || 'Primary'}]` : '';
                            return (
                              <SelectItem key={p.id} value={p.id} data-testid={`manual-adj-payroll-${p.id}`}>
                                {monthName} {p.year} — {p.platform || '-'}{nppTag} — Net AED {Number(p.net_salary || 0).toFixed(2)}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Direction</Label>
                    <Select
                      value={manualAdjustmentDialog.direction}
                      onValueChange={(v) => setManualAdjustmentDialog(prev => ({
                        ...prev,
                        direction: v,
                        category: v === 'credit' ? 'add_to_gross' : 'fines',
                      }))}
                    >
                      <SelectTrigger data-testid="manual-adj-direction">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="credit">Credit (pay Employee)</SelectItem>
                        <SelectItem value="debit">Debit (deduct from Employee)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Amount (AED)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={manualAdjustmentDialog.amount}
                      onChange={(e) => setManualAdjustmentDialog(prev => ({ ...prev, amount: e.target.value }))}
                      data-testid="manual-adj-amount"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm">
                    Category{' '}
                    <span className="text-xs text-slate-500 font-normal">
                      ({manualAdjustmentDialog.direction === 'credit' ? 'gross earning line item' : 'deduction line item'})
                    </span>
                  </Label>
                  <Select
                    value={manualAdjustmentDialog.category}
                    onValueChange={(v) => setManualAdjustmentDialog(prev => ({ ...prev, category: v }))}
                  >
                    <SelectTrigger data-testid="manual-adj-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {(manualAdjustmentDialog.direction === 'credit'
                        ? adjustmentCategories.credit
                        : adjustmentCategories.debit
                      ).map((c) => (
                        <SelectItem key={c.value} value={c.value} data-testid={`manual-adj-category-option-${c.value}`}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* --- Existing deduction details + revised preview --- */}
                {(() => {
                  const selPayroll = manualAdjustmentDialog.payrolls.find(p => p.id === manualAdjustmentDialog.selected_payroll_id);
                  const cat = manualAdjustmentDialog.category;
                  const isRefundCat = cat === 'refund_fines' || cat === 'refund_salik' || cat === 'refund_maintenance';
                  if (!selPayroll) return null;
                  const fieldName = (
                    cat === 'refund_fines' ? 'fines' :
                    cat === 'refund_salik' ? 'salik' :
                    cat === 'refund_maintenance' ? 'maintenance' : null
                  );
                  const currentValue = fieldName ? Number(selPayroll[fieldName] || 0) : 0;
                  const amt = parseFloat(manualAdjustmentDialog.amount) || 0;
                  const dir = manualAdjustmentDialog.direction;

                  // Compute revised net based on direction/category rules (mirrors backend)
                  let newNet = Number(selPayroll.net_salary || 0);
                  let newLineValue = currentValue;
                  if (amt > 0) {
                    if (dir === 'credit') {
                      newNet += amt;
                      if (isRefundCat) newLineValue = Math.max(0, currentValue - amt);
                    } else {
                      newNet -= amt;
                      newLineValue = currentValue + amt;
                    }
                  }
                  return (
                    <div className="rounded-lg border bg-slate-50 p-3 space-y-2" data-testid="manual-adj-preview">
                      <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                        Selected Payroll
                      </div>
                      {selPayroll.is_npp && (
                        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 -mt-1">
                          <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
                          This payroll is on a <strong>Non-Primary Platform (NPP)</strong>. All deductions for this rider in this month were applied to the <strong>{selPayroll.npp_primary_platform || 'Primary Platform'}</strong> payroll instead. Switch the Payroll Month dropdown to the {selPayroll.npp_primary_platform || 'primary platform'} record to refund a real deduction.
                        </div>
                      )}
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <div className="text-slate-500">Gross</div>
                          <div className="font-mono font-semibold">AED {Number(selPayroll.gross_salary || 0).toFixed(2)}</div>
                        </div>
                        <div>
                          <div className="text-slate-500">Total Deductions</div>
                          <div className="font-mono font-semibold">AED {Number(selPayroll.total_deductions || 0).toFixed(2)}</div>
                        </div>
                        <div>
                          <div className="text-slate-500">Net Salary</div>
                          <div className="font-mono font-bold text-emerald-700">AED {Number(selPayroll.net_salary || 0).toFixed(2)}</div>
                        </div>
                      </div>

                      {isRefundCat && (
                        <div className="border-t pt-2 mt-2">
                          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">
                            Current {fieldName === 'fines' ? 'Fines' : fieldName === 'salik' ? 'SALIK' : 'Maintenance'} Deducted
                          </div>
                          <div className="text-base font-mono font-bold text-rose-700">
                            AED {currentValue.toFixed(2)}
                          </div>
                          {manualAdjustmentDialog.recovered_loading ? (
                            <div className="text-xs text-slate-500 py-1 flex items-center">
                              <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> Loading items…
                            </div>
                          ) : manualAdjustmentDialog.recovered_items.length > 0 ? (
                            <div className="mt-1 max-h-32 overflow-y-auto border rounded bg-white">
                              {manualAdjustmentDialog.recovered_items.map((it) => (
                                <div key={it.id} className="px-2 py-1 text-[11px] border-b last:border-b-0 flex justify-between gap-2">
                                  <div className="min-w-0">
                                    <span className="font-medium text-slate-700">{it.label}</span>
                                    {it.date && <span className="text-slate-500 ml-1">{String(it.date).slice(0, 10)}</span>}
                                    {it.detail && <span className="text-slate-500 ml-1 truncate">— {it.detail}</span>}
                                  </div>
                                  <span className="font-mono text-rose-700 shrink-0">AED {Number(it.amount || 0).toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-[11px] text-slate-500 py-1">
                              No itemised records found (line-item total still applies).
                            </div>
                          )}
                          {amt > 0 && amt > currentValue && (
                            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-1.5 mt-1">
                              <AlertCircle className="w-3 h-3 inline mr-1" />
                              Refund amount (AED {amt.toFixed(2)}) exceeds the current {fieldName} deduction (AED {currentValue.toFixed(2)}).
                            </div>
                          )}
                        </div>
                      )}

                      {amt > 0 && (
                        <div className="border-t pt-2 mt-2 bg-emerald-50 -mx-3 -mb-3 px-3 pb-3 rounded-b-lg">
                          <div className="text-xs font-semibold text-emerald-800 uppercase tracking-wide mb-1">
                            Revised Calculation
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            {fieldName && (
                              <div>
                                <div className="text-emerald-700">
                                  {fieldName === 'fines' ? 'Fines' : fieldName === 'salik' ? 'SALIK' : 'Maintenance'}: New
                                </div>
                                <div className="font-mono font-bold">
                                  AED {newLineValue.toFixed(2)}
                                  <span className="text-emerald-600 ml-1 text-[10px]">
                                    (was AED {currentValue.toFixed(2)})
                                  </span>
                                </div>
                              </div>
                            )}
                            <div className={fieldName ? '' : 'col-span-2'}>
                              <div className="text-emerald-700">New Net Salary</div>
                              <div className="font-mono font-bold text-emerald-800 text-base">
                                AED {newNet.toFixed(2)}
                                <span className="text-emerald-600 ml-1 text-[10px]">
                                  (was AED {Number(selPayroll.net_salary || 0).toFixed(2)}, {dir === 'credit' ? '+' : '−'} AED {amt.toFixed(2)})
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div>
                  <Label className="text-sm">Reason (min 5 characters)</Label>
                  <Textarea
                    rows={2}
                    placeholder="e.g., Refund for incorrectly deducted Salik trip"
                    value={manualAdjustmentDialog.reason}
                    onChange={(e) => setManualAdjustmentDialog(prev => ({ ...prev, reason: e.target.value }))}
                    data-testid="manual-adj-reason"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setManualAdjustmentDialog({
                    open: false, rider_id: '', rider_search: '', rider_results: [],
                    payrolls: [], payrolls_loading: false, selected_payroll_id: '',
                    amount: '', direction: 'credit', category: 'add_to_gross', reason: '',
                    recovered_items: [], recovered_loading: false, saving: false,
                  })}
                  disabled={manualAdjustmentDialog.saving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={submitManualAdjustment}
                  disabled={
                    manualAdjustmentDialog.saving ||
                    !manualAdjustmentDialog.rider_id ||
                    !manualAdjustmentDialog.selected_payroll_id ||
                    !manualAdjustmentDialog.amount ||
                    !manualAdjustmentDialog.category ||
                    !manualAdjustmentDialog.reason ||
                    manualAdjustmentDialog.reason.trim().length < 5
                  }
                  data-testid="manual-adj-save-btn"
                >
                  {manualAdjustmentDialog.saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying…</>
                  ) : (
                    'Apply Adjustment'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}


      {/* ============ ADMIN SUBPAGE ============ */}
      {activeSubPage === 'admin' && userCan(user, 'adminTools', 'view', 'pmsAdmin') && (
        <div className="space-y-6">
          {/* Admin Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-600">Pending Batches</p>
                    <p className="text-2xl font-bold text-amber-800">{pendingBatches.length}</p>
                  </div>
                  <Clock className="w-8 h-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600">Active Platforms</p>
                    <p className="text-2xl font-bold text-blue-800">{platforms.filter(p => p.is_active).length}</p>
                  </div>
                  <Building2 className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600">Pending Loans</p>
                    <p className="text-2xl font-bold text-purple-800">{pendingApprovals.summary?.loan_count || 0}</p>
                  </div>
                  <CreditCard className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">Loan Amount</p>
                    <p className="text-2xl font-bold text-green-800"><CurrencyDisplay amount={pendingApprovals.summary?.loan_total || 0} /></p>
                  </div>
                  <Wallet className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Pending Payroll Batch Approvals */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileCheck className="w-5 h-5" />
                    Approve Payroll
                  </CardTitle>
                  <CardDescription>Review and approve payroll batches submitted by users</CardDescription>
                </div>
                <Button variant="outline" onClick={fetchPendingBatches} className="gap-2">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {pendingBatches.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-300" />
                  <p>No pending payroll batches</p>
                  <p className="text-sm mt-1">All payroll batches have been processed</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingBatches.map(batch => (
                    <Card key={batch.id} className="border-l-4 border-l-amber-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <Badge variant="outline" className={batch.payroll_type === 'rider' ? 'bg-green-50 text-green-700' : 'bg-purple-50 text-purple-700'}>
                                {batch.payroll_type === 'rider' ? 'Rider' : 'Staff'}
                              </Badge>
                              {batch.platform && <Badge variant="secondary">{batch.platform}</Badge>}
                              <span className="text-sm text-slate-500">
                                {['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][batch.month]} {batch.year}
                              </span>
                              <Badge className="bg-amber-100 text-amber-700">Pending Approval</Badge>
                            </div>
                            <div className="grid grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-slate-500">Employees:</span>
                                <span className="font-semibold ml-1">{batch.total_employees}</span>
                              </div>
                              <div>
                                <span className="text-slate-500">Gross:</span>
                                <span className="font-semibold ml-1 text-green-600"><CurrencyDisplay amount={batch.total_gross || 0} /></span>
                              </div>
                              <div>
                                <span className="text-slate-500">Deductions:</span>
                                <span className="font-semibold ml-1 text-red-600"><CurrencyDisplay amount={batch.total_deductions || 0} /></span>
                              </div>
                              <div>
                                <span className="text-slate-500">Net:</span>
                                <span className="font-semibold ml-1 text-blue-600"><CurrencyDisplay amount={batch.total_net || 0} /></span>
                              </div>
                            </div>
                            {batch.created_by_name && (
                              <p className="text-xs text-slate-400 mt-2">Submitted by: {batch.created_by_name}</p>
                            )}
                          </div>
                          <div className="flex gap-2 ml-4">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="sm" variant="outline"
                                  className="gap-1"
                                  data-testid={`pms-admin-actions-${batch.id}`}
                                >
                                  <MoreVertical className="w-4 h-4" />
                                  Actions
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-52">
                                <DropdownMenuLabel className="text-xs">Review</DropdownMenuLabel>
                                <DropdownMenuItem
                                  onClick={() => handleOpenWizardReadOnly(batch)}
                                  data-testid={`view-batch-${batch.id}`}
                                >
                                  <Eye className="w-3.5 h-3.5 mr-2 text-blue-600" /> View Wizard
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDownloadPayroll(batch.id, 'pdf')}
                                  data-testid={`pending-pdf-${batch.id}`}
                                >
                                  <FileText className="w-3.5 h-3.5 mr-2 text-blue-600" /> Download PDF
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDownloadPayroll(batch.id, 'excel')}
                                  data-testid={`pending-excel-${batch.id}`}
                                >
                                  <Download className="w-3.5 h-3.5 mr-2 text-emerald-600" /> Download Excel
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel className="text-xs">Decision</DropdownMenuLabel>
                                <DropdownMenuItem
                                  onClick={() => handleApproveBatch(batch.id)}
                                  className="text-green-700 focus:text-green-800 focus:bg-green-50"
                                  data-testid={`approve-batch-${batch.id}`}
                                >
                                  <Check className="w-3.5 h-3.5 mr-2" /> Approve
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    const reason = window.prompt('Enter rejection reason (optional):');
                                    handleRejectBatch(batch.id, reason || '');
                                  }}
                                  className="text-red-600 focus:text-red-700 focus:bg-red-50"
                                  data-testid={`reject-batch-${batch.id}`}
                                >
                                  <X className="w-3.5 h-3.5 mr-2" /> Reject
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                        
                        {/* Expandable Payroll Details */}
                        {expandedBatchId === batch.id && batch.payroll_details?.length > 0 && (
                          <div className="mt-4 border-t pt-4">
                            <div className="max-h-60 overflow-y-auto border rounded-lg">
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-slate-50 text-xs">
                                    <TableHead>Employee</TableHead>
                                    <TableHead className="text-right">Gross</TableHead>
                                    <TableHead className="text-right">Fines</TableHead>
                                    <TableHead className="text-right">Salik</TableHead>
                                    <TableHead className="text-right">Maint.</TableHead>
                                    <TableHead className="text-right">Loan</TableHead>
                                    <TableHead className="text-right">Clawback</TableHead>
                                    <TableHead className="text-right">Misc.</TableHead>
                                    <TableHead className="text-right">Net</TableHead>
                                    <TableHead className="text-center w-12">View</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {batch.payroll_details.map((p, i) => {
                                    const ded = p.deductions_breakdown || {};
                                    return (
                                      <TableRow key={i} className="text-sm">
                                        <TableCell>
                                          <p className="font-medium">{p.employee_name}</p>
                                          <p className="text-xs text-slate-500">{p.employee_unique_id}</p>
                                        </TableCell>
                                        <TableCell className="text-right text-green-600"><CurrencyDisplay amount={p.gross_earnings || 0} /></TableCell>
                                        <TableCell className="text-right text-red-600">{ded.fines || 0}</TableCell>
                                        <TableCell className="text-right text-red-600">{ded.salik || 0}</TableCell>
                                        <TableCell className="text-right text-red-600">{ded.maintenance || 0}</TableCell>
                                        <TableCell className="text-right text-red-600">{ded.loan_emi || 0}</TableCell>
                                        <TableCell className="text-right text-orange-700">{ded.clawback_deduction || p.clawback_deduction || 0}</TableCell>
                                        <TableCell className="text-right text-pink-600">
                                          {(ded.misc_deduction || p.misc_deduction || 0) > 0 ? (
                                            <div>
                                              <span>{ded.misc_deduction || p.misc_deduction || 0}</span>
                                              {(ded.misc_deduction_desc || p.misc_deduction_desc) && (
                                                <p className="text-[9px] text-pink-500">{ded.misc_deduction_desc || p.misc_deduction_desc}</p>
                                              )}
                                            </div>
                                          ) : 0}
                                        </TableCell>
                                        <TableCell className="text-right font-semibold"><CurrencyDisplay amount={p.net_salary || 0} /></TableCell>
                                        <TableCell className="text-center">
                                          <Button
                                            size="icon"
                                            variant="ghost"
                                            className="h-7 w-7 text-blue-700 hover:bg-blue-50"
                                            onClick={(e) => { e.stopPropagation(); setCalculationDialog({
                                              open: true,
                                              data: p.calculation_breakdown,
                                              riderName: p.employee_name,
                                              riderData: p,
                                            }); }}
                                            data-testid={`view-calc-${batch.id}-${i}`}
                                            title="View Salary Calculation (same as Payroll Wizard)"
                                          >
                                            <Eye className="w-3.5 h-3.5" />
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Held Salaries */}
          {heldSalaries.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-amber-600" />
                      Held Salaries
                    </CardTitle>
                    <CardDescription>{heldSalaries.length} salary(s) currently on hold</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchHeldSalaries}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-amber-50">
                      <TableHead>Employee</TableHead>
                      <TableHead>Month/Year</TableHead>
                      <TableHead>Platform</TableHead>
                      <TableHead className="text-right">Net Salary</TableHead>
                      <TableHead>Held On</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {heldSalaries.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell>
                          <p className="font-medium text-sm">{s.employee_name}</p>
                          <p className="text-xs text-slate-500">{s.employee_unique_id}</p>
                        </TableCell>
                        <TableCell className="text-sm">
                          {['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][s.month]} {s.year}
                        </TableCell>
                        <TableCell className="text-sm">{s.platform || '-'}</TableCell>
                        <TableCell className="text-right font-semibold"><CurrencyDisplay amount={s.net_salary || 0} /></TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {s.salary_held_at ? new Date(s.salary_held_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-7 px-2 text-xs bg-green-50 text-green-700 border-green-200"
                            onClick={() => handleReleaseSalary(s.id)}
                            data-testid={`release-salary-${s.id}`}
                          >
                            Release
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* ── Formula-Driven Gross-Salary Parser toggle ──────────────
              Flips the payroll parser between legacy hardcoded branches
              and the new formula-driven path. DB-persisted, effective
              immediately for every subsequent payroll parse. Env var
              PAYROLL_FORMULA_FALLBACK_ENABLED still overrides. */}
          <Card className={formulaFallback?.enabled ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200'}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Zap className={`w-5 h-5 ${formulaFallback?.enabled ? 'text-emerald-600' : 'text-slate-400'}`} />
                Formula-Driven Gross-Salary Parser
                <span className={`ml-2 text-xs font-medium px-2 py-0.5 rounded-full ${formulaFallback?.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
                  {formulaFallback?.enabled ? 'ACTIVE' : 'LEGACY BRANCHES ACTIVE'}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start justify-between gap-6">
                <div className="text-xs text-slate-700 space-y-1 max-w-3xl">
                  <p>
                    When <strong>ON</strong>, platforms whose AI Instructions match the legacy keyword sets — Careem App-Earnings, Careem Eligible-Split, Noon Minutes, Noon (MGV), Talabat — auto-migrate to the new formula-driven parser. Every subsequent payroll upload uses the inferred formula instead of the hardcoded elif branch.
                  </p>
                  <p>
                    Explicitly-declared <code className="text-[10px] bg-white px-1 rounded border border-slate-200">Gross_Formula = …</code> lines in AI Instructions always win over both. Downstream deductions (fines, salik, loan EMI, salary advances, maintenance, past dues) are unaffected — this toggle only changes how <strong>gross</strong> is computed.
                  </p>
                  <p className="text-slate-500">
                    Source: <span className="font-mono">{formulaFallback?.source || '—'}</span>
                    {formulaFallback?.updated_at ? ` · last flipped ${new Date(formulaFallback.updated_at).toLocaleString()} by ${formulaFallback.updated_by || '?'}` : ''}
                    {formulaFallback?.source === 'env' ? ' · pinned ON by env var — DB toggle disabled' : ''}
                  </p>
                </div>
                <div className="flex flex-col items-center gap-2 shrink-0 pt-1">
                  <Switch
                    checked={!!formulaFallback?.enabled}
                    onCheckedChange={async (nextValue) => {
                      if (formulaFallback?.source === 'env' && nextValue === false) {
                        toast.error('This deployment forces the flag ON via the PAYROLL_FORMULA_FALLBACK_ENABLED env var. Unset it in production .env to allow disabling.');
                        return;
                      }
                      setTogglingFormulaFallback(true);
                      try {
                        const r = await axios.put(
                          `${API}/talent/admin/payroll-settings/formula-fallback`,
                          { enabled: nextValue },
                          { headers: getAuthHeader() },
                        );
                        const refreshed = await axios.get(`${API}/talent/admin/payroll-settings`, { headers: getAuthHeader() });
                        setFormulaFallback(refreshed.data?.formula_fallback || null);
                        toast.success(r.data?.enabled ? 'Formula-driven parser is now ON for every subsequent payroll parse' : 'Reverted to legacy hardcoded branches');
                      } catch (e) {
                        toast.error(e.response?.data?.detail || e.message || 'Failed to toggle');
                      } finally {
                        setTogglingFormulaFallback(false);
                      }
                    }}
                    disabled={togglingFormulaFallback || !formulaFallback || formulaFallback.source === 'env'}
                    data-testid="formula-fallback-toggle"
                  />
                  <span className="text-[10px] text-slate-500">
                    {togglingFormulaFallback ? 'Saving…' : (formulaFallback?.enabled ? 'ON' : 'OFF')}
                  </span>
                </div>
              </div>
              {formulaFallback?.enabled && (
                <div className="mt-3 flex items-start gap-2 text-[11px] p-2 rounded border border-emerald-200 bg-emerald-50 text-emerald-800">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  <span>
                    Formula-driven parsing is <strong>live</strong>. Run any new payroll upload and compare the resulting gross against a legacy-parsed batch of the same period. If numbers diverge, flip this OFF to instantly revert — no redeploy needed.
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Add Payroll Type Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Payroll Type Configuration
              </CardTitle>
              <CardDescription>Configure payroll settings for Staff and Rider employees</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="staff" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="staff" className="gap-2">
                    <Briefcase className="w-4 h-4" />
                    Staff Payroll
                  </TabsTrigger>
                  <TabsTrigger value="rider" className="gap-2">
                    <Users className="w-4 h-4" />
                    Rider Payroll
                  </TabsTrigger>
                </TabsList>

                {/* Staff Payroll Configuration */}
                <TabsContent value="staff" className="space-y-4">
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <h4 className="font-semibold text-purple-800 mb-3">Pro-rata Salary Calculation</h4>
                    <p className="text-sm text-purple-600 mb-4">
                      Staff salary is calculated on a pro-rata basis based on present days plus overtime.
                    </p>
                    <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                      <div className="space-y-2">
                        <Label>Working Days per Month</Label>
                        <Input
                          type="number"
                          value={staffConfig.working_days_per_month}
                          onChange={(e) => setStaffConfig({...staffConfig, working_days_per_month: parseInt(e.target.value)})}
                          data-testid="staff-config-working-days-input"
                        />
                        <p className="text-xs text-slate-500">Used for absence pro-rata (e.g., 26)</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Overtime Base Divisor</Label>
                        <Input
                          type="number"
                          value={staffConfig.overtime_base_divisor ?? 30}
                          onChange={(e) => setStaffConfig({...staffConfig, overtime_base_divisor: parseInt(e.target.value)})}
                          data-testid="staff-config-ot-divisor-input"
                        />
                        <p className="text-xs text-slate-500">Days used to convert monthly salary → daily rate for OT. UAE Federal Decree-Law 33/2021 uses 30 (calendar days).</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Overtime Rate Multiplier</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={staffConfig.overtime_rate_multiplier}
                          onChange={(e) => setStaffConfig({...staffConfig, overtime_rate_multiplier: parseFloat(e.target.value)})}
                          data-testid="staff-config-ot-multiplier-input"
                        />
                        <p className="text-xs text-slate-500">e.g., 1.0× straight time, 1.25× day OT, 1.5× night/holiday OT (UAE Art. 19)</p>
                      </div>
                    </div>
                    <Button onClick={handleUpdateStaffConfig} className="mt-4 gap-2" data-testid="staff-config-save-btn">
                      <Check className="w-4 h-4" />
                      Save Staff Configuration
                    </Button>
                  </div>
                </TabsContent>

                {/* Rider Payroll Configuration - Platform Management */}
                <TabsContent value="rider" className="space-y-4">
                  {/* SLX Fixed Deduction Config */}
                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mb-4">
                    <h4 className="font-semibold text-amber-800 mb-3">SLX Fixed Deduction</h4>
                    <p className="text-sm text-amber-600 mb-3">Fixed monthly charge applied to all rider payrolls</p>
                    <div className="flex items-center gap-4">
                      <div className="space-y-1">
                        <Label>Amount (AED)</Label>
                        <Input type="number" className="w-32" value={slxConfig.slx_amount || 80} onChange={(e) => setSlxConfig({...slxConfig, slx_amount: parseFloat(e.target.value) || 0})} />
                      </div>
                      <div className="flex items-center gap-2 pt-5">
                        <input type="checkbox" checked={slxConfig.slx_enabled !== false} onChange={(e) => setSlxConfig({...slxConfig, slx_enabled: e.target.checked})} className="w-4 h-4" />
                        <Label>Enabled</Label>
                      </div>
                      <Button className="mt-5 gap-1" size="sm" onClick={async () => {
                        const fd = new FormData();
                        fd.append('slx_amount', slxConfig.slx_amount);
                        fd.append('slx_enabled', slxConfig.slx_enabled);
                        try {
                          await axios.put(`${API}/talent/admin/rider-deduction-config`, fd, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
                          toast.success('SLX config updated');
                        } catch (e) { toast.error('Failed to update'); }
                      }}>
                        <Check className="w-3 h-3" /> Save
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mb-4">
                    <p className="text-sm text-slate-600">Configure platforms with salary type, rates, and payout formula</p>
                    <Button onClick={() => { setPlatformForm({ name: '', salary_type: 'per_order', pickup_rate: 0, dropoff_rate: 0, fixed_salary_config: null }); setPlatformDialogOpen(true); }} className="gap-2">
                      <Plus className="w-4 h-4" />
                      Add Platform
                    </Button>
                  </div>

                  {platforms.length === 0 ? (
                    <div className="text-center py-8 text-slate-500 border rounded-lg">
                      <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>No platforms configured</p>
                      <p className="text-sm mt-1">Add a platform to start configuring rider payroll</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {platforms.map(platform => (
                        <Card key={platform.id} className={`${platform.is_active ? '' : 'opacity-60'}`}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h4 className="font-semibold text-lg">{platform.name}</h4>
                                  <Badge className={platform.salary_type === 'fixed' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}>
                                    {platform.salary_type === 'fixed' ? 'Fixed Salary' : 'Per Order'}
                                  </Badge>
                                  {!platform.is_active && <Badge variant="secondary">Inactive</Badge>}
                                </div>
                                
                                {platform.salary_type === 'per_order' ? (
                                  <div className="flex gap-6 text-sm">
                                    <div>
                                      <span className="text-slate-500">Pickup Rate:</span>
                                      <span className="font-semibold ml-1"><CurrencyDisplay amount={platform.pickup_rate} /></span>
                                    </div>
                                    <div>
                                      <span className="text-slate-500">Dropoff Rate:</span>
                                      <span className="font-semibold ml-1"><CurrencyDisplay amount={platform.dropoff_rate} /></span>
                                    </div>
                                    <div>
                                      <span className="text-slate-500">Total/Order:</span>
                                      <span className="font-semibold ml-1 text-green-600"><CurrencyDisplay amount={platform.pickup_rate + platform.dropoff_rate} /></span>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-sm">
                                    <span className="text-slate-500">Monthly Salary:</span>
                                    <span className="font-semibold ml-1"><CurrencyDisplay amount={platform.fixed_salary_config?.monthly_salary || 0} /></span>
                                    {platform.fixed_salary_config?.eligibility && (
                                      <div className="mt-1 text-xs text-slate-500">
                                        Eligibility: Min {platform.fixed_salary_config.eligibility.min_attendance_days} days, 
                                        {platform.fixed_salary_config.eligibility.min_working_hours} hrs, 
                                        {platform.fixed_salary_config.eligibility.min_orders} orders
                                      </div>
                                    )}
                                  </div>
                                )}

                                {platform.formula_config && (
                                  <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
                                    <CheckCircle2 className="w-3 h-3" />
                                    Formula configured
                                    {platform.formula_config.formula && (
                                      <span className="text-slate-500 ml-1">({platform.formula_config.mapped_columns?.length || 0} columns mapped)</span>
                                    )}
                                  </div>
                                )}

                                {/* AI Instructions Status */}
                                {platform.ai_instructions && (
                                  <div className="mt-1 text-xs text-purple-600 flex items-center gap-1">
                                    <Bot className="w-3 h-3" />
                                    AI instructions configured
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleOpenAIInstructions(platform)}
                                  className="gap-1 text-purple-600 hover:bg-purple-50"
                                  title="Configure AI Processing Instructions"
                                >
                                  <Brain className="w-3 h-3" />
                                  AI
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedPlatform(platform);
                                    setFormulaConfig(platform.formula_config || { 
                                      rider_id_column: '', 
                                      selected_columns: [], 
                                      formula: '',
                                      net_formula: '',
                                      detected_columns: platform.sample_format?.columns || []
                                    });
                                    setFormulaDialogOpen(true);
                                  }}
                                  className="gap-1"
                                >
                                  <Calculator className="w-3 h-3" />
                                  Formula
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    setPlatformForm(platform);
                                    setPlatformDialogOpen(true);
                                  }}
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="text-red-600 hover:bg-red-50"
                                  onClick={() => handleDeletePlatform(platform.id)}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* ── Payroll Reconciliation (moved from /talent/reconcile) ──
              Detect + fix phantom over-deductions / missed fines / orphan
              totals across historical payrolls. Runs a dry-run preview
              first, then optionally applies + stamps `payroll_corrections`. */}
          {userCan(user, 'adminTools', 'view', 'payrollReconcile') && (
          <Card className="border-amber-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-amber-700" />
                Payroll Reconciliation
              </CardTitle>
              <CardDescription>
                Detect and fix phantom over-deductions, missed fines, and orphan totals across historical payrolls. Admin-only.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label>Month</Label>
                  <Select value={String(reconMonth)} onValueChange={(v) => setReconMonth(Number(v))}>
                    <SelectTrigger data-testid="recon-month"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[
                        {v:1,l:'January'},{v:2,l:'February'},{v:3,l:'March'},{v:4,l:'April'},
                        {v:5,l:'May'},{v:6,l:'June'},{v:7,l:'July'},{v:8,l:'August'},
                        {v:9,l:'September'},{v:10,l:'October'},{v:11,l:'November'},{v:12,l:'December'},
                      ].map((m) => <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Year</Label>
                  <Input type="number" value={reconYear} onChange={(e) => setReconYear(Number(e.target.value))} data-testid="recon-year" />
                </div>
                <div>
                  <Label>Platform (blank = all)</Label>
                  <Select value={reconPlatform} onValueChange={setReconPlatform}>
                    <SelectTrigger data-testid="recon-platform"><SelectValue placeholder="All platforms" /></SelectTrigger>
                    <SelectContent>
                      {['__all__','Keeta','Talabat','Careem','Noon','Deliveroo','Instashop'].map((p) => (
                        <SelectItem key={p} value={p}>{p === '__all__' ? 'All Platforms' : p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <p className="text-xs text-slate-500">Leave month/year blank in the audit URL to scan every historical month.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-blue-200">
                  <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><Download className="w-4 h-4" /> 1. Download Audit Excel</CardTitle></CardHeader>
                  <CardContent>
                    <p className="text-xs text-slate-600 mb-3">Read-only. Lists every rider with a discrepancy for the selected period.</p>
                    <Button
                      className="w-full"
                      disabled={reconBusy === 'audit'}
                      onClick={async () => {
                        setReconBusy('audit');
                        try {
                          const q = new URLSearchParams();
                          if (reconMonth) q.set('month', reconMonth);
                          if (reconYear) q.set('year', reconYear);
                          if (reconPlatform && reconPlatform !== '__all__') q.set('platform', reconPlatform);
                          const r = await fetch(`${API}/talent/admin/audit-excel?${q.toString()}`, { headers: getAuthHeader() });
                          if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
                          const blob = await r.blob();
                          const a = document.createElement('a');
                          a.href = URL.createObjectURL(blob);
                          a.download = `Payroll_Audit_${reconMonth || 'All'}_${reconYear || 'All'}${(reconPlatform && reconPlatform !== '__all__') ? '_' + reconPlatform : ''}.xlsx`;
                          a.click();
                          toast.success('Audit Excel downloaded');
                        } catch (e) { toast.error(e.message || 'Audit download failed'); }
                        finally { setReconBusy(''); }
                      }}
                      data-testid="recon-download"
                    >
                      {reconBusy === 'audit' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Download Excel
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border-amber-200">
                  <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><PlayCircle className="w-4 h-4" /> 2. Dry-Run Preview</CardTitle></CardHeader>
                  <CardContent>
                    <p className="text-xs text-slate-600 mb-3">Computes corrections without writing. Review below before applying.</p>
                    <Button
                      variant="outline"
                      className="w-full"
                      disabled={reconBusy === 'dry'}
                      onClick={async () => {
                        if (!reconMonth || !reconYear) return toast.error('Month and year required');
                        setReconBusy('dry');
                        try {
                          const r = await axios.post(`${API}/talent/admin/reconcile-month`,
                            { month: reconMonth, year: reconYear, platform: (reconPlatform && reconPlatform !== '__all__') ? reconPlatform : null, dry_run: true },
                            { headers: getAuthHeader() });
                          setReconDryRunResult(r.data);
                          toast.success(`Preview: matched ${r.data.matched_rows}, changed ${r.data.changed_rows}`);
                        } catch (e) { toast.error(e.response?.data?.detail || e.message || 'Reconcile failed'); }
                        finally { setReconBusy(''); }
                      }}
                      data-testid="recon-dry"
                    >
                      {reconBusy === 'dry' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />} Run Dry-Run
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border-red-200">
                  <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2 text-red-700"><ShieldCheck className="w-4 h-4" /> 3. Apply Reconciliation</CardTitle></CardHeader>
                  <CardContent>
                    <p className="text-xs text-slate-600 mb-3">Writes corrections + stamps audit trail. Cannot be undone.</p>
                    <Button
                      variant="destructive"
                      className="w-full"
                      disabled={reconBusy === 'apply' || !reconDryRunResult}
                      onClick={async () => {
                        if (!reconDryRunResult || reconDryRunResult.dry_run !== true) {
                          return toast.error('Run Dry-Run first to preview changes');
                        }
                        if (!window.confirm(`Apply corrections to ${reconDryRunResult.changed_rows} rows? This writes to production and cannot be undone.`)) return;
                        setReconBusy('apply');
                        try {
                          const r = await axios.post(`${API}/talent/admin/reconcile-month`,
                            { month: reconMonth, year: reconYear, platform: (reconPlatform && reconPlatform !== '__all__') ? reconPlatform : null, dry_run: false },
                            { headers: getAuthHeader() });
                          setReconDryRunResult(r.data);
                          toast.success(`Applied: matched ${r.data.matched_rows}, changed ${r.data.changed_rows}`);
                        } catch (e) { toast.error(e.response?.data?.detail || e.message || 'Reconcile failed'); }
                        finally { setReconBusy(''); }
                      }}
                      data-testid="recon-apply"
                    >
                      {reconBusy === 'apply' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />} Apply Now
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {reconDryRunResult && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-base">
                      {reconDryRunResult.dry_run ? 'Preview' : 'Applied'}: {reconDryRunResult.changed_rows} of {reconDryRunResult.matched_rows} rows changed
                    </CardTitle>
                    {!reconDryRunResult.dry_run && <span className="text-xs text-green-700 font-medium flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> Written + audit-logged</span>}
                  </CardHeader>
                  <CardContent>
                    {reconDryRunResult.changed_rows === 0 ? (
                      <p className="text-sm text-slate-600">No discrepancies found for this period ✓</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="text-left px-2 py-1">Rider</th>
                              <th className="text-right px-2 py-1">Gross</th>
                              <th className="text-right px-2 py-1">Stored Total</th>
                              <th className="text-right px-2 py-1">Corrected Total</th>
                              <th className="text-right px-2 py-1">Phantom</th>
                              <th className="text-right px-2 py-1">Missed</th>
                              <th className="text-right px-2 py-1">Net Owed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(reconDryRunResult.changes || []).map((c) => (
                              <tr key={c.payroll_id} className="border-t border-slate-200">
                                <td className="px-2 py-1">{c.rider_name}</td>
                                <td className="text-right px-2 py-1 font-mono">{c.gross.toFixed(2)}</td>
                                <td className="text-right px-2 py-1 font-mono text-red-600">{c.stored_total.toFixed(2)}</td>
                                <td className="text-right px-2 py-1 font-mono text-green-700">{c.corrected_total.toFixed(2)}</td>
                                <td className="text-right px-2 py-1 font-mono text-amber-700">{c.phantom_over_deducted.toFixed(2)}</td>
                                <td className="text-right px-2 py-1 font-mono text-amber-700">{c.missed_deductions.toFixed(2)}</td>
                                <td className={`text-right px-2 py-1 font-mono font-bold ${c.net_owed_to_rider > 0 ? 'text-green-700' : c.net_owed_to_rider < 0 ? 'text-red-700' : 'text-slate-600'}`}>
                                  {c.net_owed_to_rider > 0 ? '+' : ''}{c.net_owed_to_rider.toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <p className="text-xs text-slate-500 mt-3 flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> Net Owed &gt; 0 = refund owed to rider. &lt; 0 = deduction to recover on next payroll. Push these into next-month past_dues after Apply.</p>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
          )}

          {/* Pending Loan Requests */}
          <Card>
            <CardHeader>
              <CardTitle>Pending Loan Requests</CardTitle>
              <CardDescription>Review and approve employee loan requests</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingApprovals.loans?.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-300" />
                  <p>No pending loan requests</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Employee</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Requested Amount</TableHead>
                      <TableHead className="text-right">Proposed EMI</TableHead>
                      <TableHead>Purpose</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingApprovals.loans?.map(loan => (
                      <TableRow key={loan.id}>
                        <TableCell className="font-medium">{loan.employee_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{loan.employee_type}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold"><CurrencyDisplay amount={loan.requested_amount || 0} /></TableCell>
                        <TableCell className="text-right"><CurrencyDisplay amount={loan.proposed_emi || 0} /></TableCell>
                        <TableCell className="text-sm max-w-[150px] truncate">{loan.purpose || '-'}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              className="h-7 px-2 bg-green-600 hover:bg-green-700"
                              onClick={() => handleApproveLoan(loan)}
                            >
                              <Check className="w-3 h-3" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-7 px-2 text-red-600 hover:bg-red-50"
                              onClick={() => handleRejectLoan(loan)}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Salary Slips Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Salary Slips</CardTitle>
                  <CardDescription>Generate and manage salary slips</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleBatchGenerateSalarySlips} variant="outline" className="gap-2">
                    <FileDown className="w-4 h-4" />
                    Generate All
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {salarySlips.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Receipt className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No salary slips generated yet</p>
                  <p className="text-sm mt-1">Generate salary slips for approved payrolls</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Reference</TableHead>
                      <TableHead>Employee</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead className="text-right">Net Salary</TableHead>
                      <TableHead>Email Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salarySlips.map(slip => (
                      <TableRow key={slip.id}>
                        <TableCell className="font-mono text-sm">{slip.reference_number}</TableCell>
                        <TableCell className="font-medium">{slip.employee_name}</TableCell>
                        <TableCell>{slip.month}/{slip.year}</TableCell>
                        <TableCell className="text-right font-semibold"><CurrencyDisplay amount={slip.net_salary || 0} /></TableCell>
                        <TableCell>
                          {slip.email_sent ? (
                            <Badge className="bg-green-100 text-green-800">Sent</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-800">Not Sent</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="h-7 px-2"
                              onClick={() => viewSalarySlip(slip)}
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                            {!slip.email_sent && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="h-7 px-2"
                                onClick={() => handleEmailSalarySlip(slip.id)}
                              >
                                <Send className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Platform Add/Edit Dialog */}
          <Dialog open={platformDialogOpen} onOpenChange={setPlatformDialogOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{platformForm.id ? 'Edit Platform' : 'Add New Platform'}</DialogTitle>
                <DialogDescription>Configure platform settings for rider payroll</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Platform Name</Label>
                  <Input
                    value={platformForm.name}
                    onChange={(e) => setPlatformForm({...platformForm, name: e.target.value})}
                    placeholder="e.g., Talabat, Careem, Noon"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Salary Type</Label>
                  <Select 
                    value={platformForm.salary_type} 
                    onValueChange={(v) => setPlatformForm({...platformForm, salary_type: v})}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_order">Per Order (Pickup + Dropoff)</SelectItem>
                      <SelectItem value="fixed">Fixed Monthly Salary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {platformForm.salary_type === 'per_order' ? (
                  <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                    <div className="space-y-2">
                      <Label>Pickup Rate</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={platformForm.pickup_rate}
                        onChange={(e) => setPlatformForm({...platformForm, pickup_rate: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Dropoff Rate</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={platformForm.dropoff_rate}
                        onChange={(e) => setPlatformForm({...platformForm, dropoff_rate: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-medium text-blue-800">Fixed Salary Configuration</h4>
                    <div className="space-y-2">
                      <Label>Monthly Salary</Label>
                      <Input
                        type="number"
                        value={platformForm.fixed_salary_config?.monthly_salary || ''}
                        onChange={(e) => setPlatformForm({
                          ...platformForm, 
                          fixed_salary_config: {
                            ...platformForm.fixed_salary_config,
                            monthly_salary: parseFloat(e.target.value) || 0
                          }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Eligibility Criteria</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-slate-500">Min Attendance Days</Label>
                          <Input
                            type="number"
                            value={platformForm.fixed_salary_config?.eligibility?.min_attendance_days || 26}
                            onChange={(e) => setPlatformForm({
                              ...platformForm,
                              fixed_salary_config: {
                                ...platformForm.fixed_salary_config,
                                eligibility: {
                                  ...platformForm.fixed_salary_config?.eligibility,
                                  min_attendance_days: parseInt(e.target.value) || 26
                                }
                              }
                            })}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Min Working Hours</Label>
                          <Input
                            type="number"
                            value={platformForm.fixed_salary_config?.eligibility?.min_working_hours || 260}
                            onChange={(e) => setPlatformForm({
                              ...platformForm,
                              fixed_salary_config: {
                                ...platformForm.fixed_salary_config,
                                eligibility: {
                                  ...platformForm.fixed_salary_config?.eligibility,
                                  min_working_hours: parseInt(e.target.value) || 260
                                }
                              }
                            })}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Min Orders</Label>
                          <Input
                            type="number"
                            value={platformForm.fixed_salary_config?.eligibility?.min_orders || 400}
                            onChange={(e) => setPlatformForm({
                              ...platformForm,
                              fixed_salary_config: {
                                ...platformForm.fixed_salary_config,
                                eligibility: {
                                  ...platformForm.fixed_salary_config?.eligibility,
                                  min_orders: parseInt(e.target.value) || 400
                                }
                              }
                            })}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={platformForm.fixed_salary_config?.eligibility?.last_3_days_mandatory ?? true}
                            onChange={(e) => setPlatformForm({
                              ...platformForm,
                              fixed_salary_config: {
                                ...platformForm.fixed_salary_config,
                                eligibility: {
                                  ...platformForm.fixed_salary_config?.eligibility,
                                  last_3_days_mandatory: e.target.checked
                                }
                              }
                            })}
                          />
                          <Label className="text-xs text-slate-500">Last 3 Days Mandatory</Label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPlatformDialogOpen(false)}>Cancel</Button>
                <Button onClick={platformForm.id ? () => handleUpdatePlatform(platformForm.id, platformForm) : handleCreatePlatform}>
                  {platformForm.id ? 'Update' : 'Create'} Platform
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Formula Builder Dialog */}
          <FormulaBuilderDialog
            open={formulaDialogOpen}
            onOpenChange={setFormulaDialogOpen}
            selectedPlatform={selectedPlatform}
            formulaConfig={formulaConfig}
            setFormulaConfig={setFormulaConfig}
            onUploadSample={handleUploadSampleFormat}
            onSave={handleSaveFormulaConfig}
          />
        </div>
      )}

      {/* Post-Approval Success Dialog */}
      <Dialog open={approvalSuccessDialog} onOpenChange={setApprovalSuccessDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="w-6 h-6" />
              Payroll Approved Successfully
            </DialogTitle>
            <DialogDescription>
              The payroll batch has been approved. You can now generate and email salary slips.
            </DialogDescription>
          </DialogHeader>
          
          {approvedBatchData && (
            <div className="py-4 space-y-4">
              {/* Batch Summary */}
              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Period:</span>
                  <span className="font-medium">
                    {['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][approvedBatchData.month]} {approvedBatchData.year}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Type:</span>
                  <Badge variant="outline" className={approvedBatchData.payroll_type === 'rider' ? 'bg-green-50 text-green-700' : 'bg-purple-50 text-purple-700'}>
                    {approvedBatchData.payroll_type === 'rider' ? 'Rider' : 'Staff'}
                  </Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Employees:</span>
                  <span className="font-medium">{approvedBatchData.total_employees}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Net Payable:</span>
                  <span className="font-semibold text-green-600"><CurrencyDisplay amount={approvedBatchData.total_net || 0} /></span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button 
                  className="w-full gap-2 bg-blue-600 hover:bg-blue-700"
                  onClick={() => handleGenerateBatchSalarySlips(approvedBatchData.id)}
                  disabled={generatingSlips}
                >
                  {generatingSlips ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4" />
                  )}
                  Generate Salary Slips
                </Button>
                <Button 
                  variant="outline"
                  className="w-full gap-2"
                  onClick={() => handleEmailBatchSalarySlips(approvedBatchData.id)}
                  disabled={emailingSlips}
                >
                  {emailingSlips ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                  Email Salary Slips to Employees
                </Button>
              </div>

              <p className="text-xs text-slate-500 text-center">
                Salary slips will be available in each employee&apos;s account.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalSuccessDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ DIALOGS ============ */}
      
      {/* Document Upload Dialog */}
      <Dialog open={documentDialogOpen} onOpenChange={setDocumentDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload Documents</DialogTitle>
            <DialogDescription>
              {selectedEmployee?.name} - Upload required documents
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedEmployee?.employee_type === 'rider' ? (
              <>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {selectedEmployee?.employment_agreement_url ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                    )}
                    <span>Employment Agreement</span>
                  </div>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="w-40"
                    onChange={(e) => e.target.files[0] && handleUploadDocument(selectedEmployee.id, 'employment_agreement', e.target.files[0])}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {selectedEmployee?.loan_agreement_url ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                    )}
                    <span>Loan Agreement</span>
                  </div>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="w-40"
                    onChange={(e) => e.target.files[0] && handleUploadDocument(selectedEmployee.id, 'loan_agreement', e.target.files[0])}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {selectedEmployee?.asset_handover_url ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                    )}
                    <span>Asset Handover Form</span>
                  </div>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="w-40"
                    onChange={(e) => e.target.files[0] && handleUploadDocument(selectedEmployee.id, 'asset_handover', e.target.files[0])}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {selectedEmployee?.cash_receipt_url ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                    )}
                    <span>Receipt of Cash Paid</span>
                  </div>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="w-40"
                    onChange={(e) => e.target.files[0] && handleUploadDocument(selectedEmployee.id, 'cash_receipt', e.target.files[0])}
                  />
                </div>
                
                {/* Other Documents Section */}
                <div className="border rounded-lg p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Other Documents</span>
                    <Badge variant="outline">{selectedEmployee?.other_documents?.length || 0} uploaded</Badge>
                  </div>
                  
                  {/* List existing other documents */}
                  {selectedEmployee?.other_documents && selectedEmployee.other_documents.length > 0 && (
                    <div className="space-y-2">
                      {selectedEmployee.other_documents.map((doc, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm bg-slate-50 p-2 rounded">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            <span>{doc.name || `Document ${idx + 1}`}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => viewDocFile(doc.url)}
                            className="text-blue-600 hover:underline text-xs"
                            data-testid={`view-rider-other-doc-${idx}`}
                          >
                            View
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Upload new other document */}
                  <div className="flex items-center gap-2">
                    <Input
                      type="text"
                      placeholder="Document name (optional)"
                      className="flex-1"
                      id="otherDocName"
                    />
                    <Input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="w-40"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          const docName = document.getElementById('otherDocName')?.value || `Document ${(selectedEmployee?.other_documents?.length || 0) + 1}`;
                          handleUploadDocument(selectedEmployee.id, 'other_document', e.target.files[0], docName);
                          if (document.getElementById('otherDocName')) {
                            document.getElementById('otherDocName').value = '';
                          }
                        }
                      }}
                    />
                  </div>
                  <p className="text-xs text-slate-500">Add additional documents as needed</p>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {selectedEmployee?.emirates_id_front_url ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                    )}
                    <span>Emirates ID (Front)</span>
                    {selectedEmployee?.emirates_id_front_url && (
                      <button
                        type="button"
                        onClick={() => viewDocFile(selectedEmployee.emirates_id_front_url)}
                        className="text-xs text-blue-600 hover:underline"
                        data-testid="view-uploaded-emirates-id-front"
                      >View</button>
                    )}
                  </div>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="w-40"
                    title={selectedEmployee?.emirates_id_front_url ? 'Replace uploaded file' : 'Upload file'}
                    onChange={(e) => e.target.files[0] && handleUploadDocument(selectedEmployee.id, 'emirates_id_front', e.target.files[0])}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {selectedEmployee?.passport_url ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                    )}
                    <span>Passport</span>
                    {selectedEmployee?.passport_url && (
                      <button
                        type="button"
                        onClick={() => viewDocFile(selectedEmployee.passport_url)}
                        className="text-xs text-blue-600 hover:underline"
                        data-testid="view-uploaded-passport"
                      >View</button>
                    )}
                  </div>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="w-40"
                    title={selectedEmployee?.passport_url ? 'Replace uploaded file' : 'Upload file'}
                    onChange={(e) => e.target.files[0] && handleUploadDocument(selectedEmployee.id, 'passport', e.target.files[0])}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg" data-testid="staff-driving-license-row">
                  <div className="flex items-center gap-3">
                    {selectedEmployee?.driving_license_url ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                    )}
                    <span>Driving License</span>
                    {selectedEmployee?.driving_license_url && (
                      <button
                        type="button"
                        onClick={() => viewDocFile(selectedEmployee.driving_license_url)}
                        className="text-xs text-blue-600 hover:underline"
                        data-testid="view-uploaded-driving-license"
                      >View</button>
                    )}
                  </div>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="w-40"
                    data-testid="staff-driving-license-input"
                    title={selectedEmployee?.driving_license_url ? 'Replace uploaded file' : 'Upload file'}
                    onChange={(e) => e.target.files[0] && handleUploadDocument(selectedEmployee.id, 'driving_license', e.target.files[0])}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg" data-testid="staff-photo-row">
                  <div className="flex items-center gap-3">
                    {selectedEmployee?.photo_url ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                    )}
                    <span>Photo</span>
                    {selectedEmployee?.photo_url && (
                      <button
                        type="button"
                        onClick={() => viewDocFile(selectedEmployee.photo_url)}
                        className="text-xs text-blue-600 hover:underline"
                        data-testid="view-uploaded-photo"
                      >View</button>
                    )}
                  </div>
                  <Input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    className="w-40"
                    data-testid="staff-photo-input"
                    title={selectedEmployee?.photo_url ? 'Replace uploaded file' : 'Upload file'}
                    onChange={(e) => e.target.files[0] && handleUploadDocument(selectedEmployee.id, 'photo', e.target.files[0])}
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg" data-testid="staff-labour-card-row">
                  <div className="flex items-center gap-3">
                    {selectedEmployee?.labour_card_url ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500" />
                    )}
                    <span>Labour Card</span>
                    {selectedEmployee?.labour_card_url && (
                      <button
                        type="button"
                        onClick={() => viewDocFile(selectedEmployee.labour_card_url)}
                        className="text-xs text-blue-600 hover:underline"
                        data-testid="view-uploaded-labour-card"
                      >View</button>
                    )}
                  </div>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="w-40"
                    data-testid="staff-labour-card-input"
                    title={selectedEmployee?.labour_card_url ? 'Replace uploaded file' : 'Upload file'}
                    onChange={(e) => e.target.files[0] && handleUploadDocument(selectedEmployee.id, 'labour_card', e.target.files[0])}
                  />
                </div>

                {/* Miscellaneous Documents Section (staff) */}
                <div className="border rounded-lg p-3 space-y-3" data-testid="staff-misc-docs-section">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Miscellaneous Documents</span>
                    <Badge variant="outline">{selectedEmployee?.other_documents?.length || 0} uploaded</Badge>
                  </div>

                  {selectedEmployee?.other_documents && selectedEmployee.other_documents.length > 0 && (
                    <div className="space-y-2">
                      {selectedEmployee.other_documents.map((doc, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm bg-slate-50 p-2 rounded">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            <span>{doc.name || `Document ${idx + 1}`}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => viewDocFile(doc.url)}
                            className="text-blue-600 hover:underline text-xs"
                            data-testid={`view-uploaded-misc-${idx}`}
                          >
                            View
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Input
                      type="text"
                      placeholder="Document name (optional)"
                      className="flex-1"
                      id="staffMiscDocName"
                    />
                    <Input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="w-40"
                      data-testid="staff-misc-doc-input"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          const docName = document.getElementById('staffMiscDocName')?.value || `Document ${(selectedEmployee?.other_documents?.length || 0) + 1}`;
                          handleUploadDocument(selectedEmployee.id, 'other_document', e.target.files[0], docName);
                          if (document.getElementById('staffMiscDocName')) {
                            document.getElementById('staffMiscDocName').value = '';
                          }
                        }
                      }}
                    />
                  </div>
                  <p className="text-xs text-slate-500">Add any additional supporting documents</p>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDocumentDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Details Dialog */}
      <Dialog open={employeeDetailsDialogOpen} onOpenChange={setEmployeeDetailsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Employee Details</DialogTitle>
          </DialogHeader>

          {/* Skeleton spinner while a new employee is being fetched.
              Prevents the previous employee's data flashing for 5-10s. */}
          {loadingEmployeeId && !employeeDetails && (
            <div className="flex flex-col items-center justify-center py-16 gap-3" data-testid="employee-details-loading">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              <p className="text-sm text-slate-500">Loading employee details…</p>
              <p className="text-xs text-slate-400">Loan account, expenses, payroll and salary slips</p>
            </div>
          )}

          {employeeDetails && (
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Employee Information
                    {employeeDetails.employee?.tap_candidate_id && (
                      <Badge className="bg-purple-100 text-purple-700 ml-2">From TAP</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-3'}`}>
                    <div>
                      <Label className="text-slate-500 text-xs">Name</Label>
                      <p className="font-medium">{employeeDetails.employee?.rider_name || employeeDetails.employee?.name}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Employee ID</Label>
                      <p className="font-mono text-blue-600">{employeeDetails.employee?.rider_unique_id || employeeDetails.employee?.employee_unique_id || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Type</Label>
                      <Badge variant="outline" className={employeeDetails.employee_type === 'rider' ? 'bg-green-50 text-green-700' : 'bg-purple-50 text-purple-700'}>
                        {employeeDetails.employee_type === 'rider' ? 'Rider' : 'Staff'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Identity Documents */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Identity Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-3'}`}>
                    <div>
                      <Label className="text-slate-500 text-xs">Emirates ID</Label>
                      <p className="font-mono">{employeeDetails.employee?.emirates_id || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Emirates ID Expiry</Label>
                      <p>{employeeDetails.full_rider_info?.emirates_id_expiry || employeeDetails.tap_candidate_info?.emirates_id_expiry || employeeDetails.employee?.emirates_id_expiry || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Passport Number</Label>
                      <p className="font-mono">{employeeDetails.full_rider_info?.passport_number || employeeDetails.tap_candidate_info?.passport_no || employeeDetails.employee?.passport_number || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Passport Expiry</Label>
                      <p>{employeeDetails.full_rider_info?.passport_expiry || employeeDetails.tap_candidate_info?.passport_expiry || employeeDetails.employee?.passport_expiry || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">License Number</Label>
                      <p className="font-mono">{employeeDetails.full_rider_info?.license_number || employeeDetails.tap_candidate_info?.home_license_no || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">License Expiry</Label>
                      <p>{employeeDetails.full_rider_info?.license_expiry || employeeDetails.tap_candidate_info?.home_license_expiry || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Contact Information */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Contact Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-3'}`}>
                    <div>
                      <Label className="text-slate-500 text-xs">Mobile (UAE)</Label>
                      <p>{employeeDetails.full_rider_info?.mobile_uae || employeeDetails.tap_candidate_info?.mobile_uae || employeeDetails.employee?.mobile || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Mobile (Home Country)</Label>
                      <p>{employeeDetails.full_rider_info?.mobile_home_country || employeeDetails.tap_candidate_info?.home_contact || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Email</Label>
                      <p className="text-blue-600">{employeeDetails.full_rider_info?.email || employeeDetails.tap_candidate_info?.email || employeeDetails.employee?.email || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Home Address</Label>
                      <p className="text-sm">{employeeDetails.tap_candidate_info?.home_address || employeeDetails.employee?.address || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Date of Birth</Label>
                      <p>{employeeDetails.full_rider_info?.date_of_birth || employeeDetails.tap_candidate_info?.date_of_birth || employeeDetails.employee?.date_of_birth || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Employment Information */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Employment Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-3'}`}>
                    <div>
                      <Label className="text-slate-500 text-xs">Company</Label>
                      <p>{employeeDetails.employee?.lessee_name || employeeDetails.employee?.employer_name || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Platform</Label>
                      <p>{employeeDetails.employee?.platform || employeeDetails.full_rider_info?.platform || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Platform ID</Label>
                      <p className="font-mono">{employeeDetails.employee?.platform_id || employeeDetails.full_rider_info?.platform_id || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Edenred ID</Label>
                      <p className="font-mono">{employeeDetails.employee?.edenred_id || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Designation</Label>
                      <Badge className={employeeDetails.employee?.designation === 'Cyclist' ? 'bg-amber-100 text-amber-800' : 'bg-cyan-100 text-cyan-800'}>
                        {employeeDetails.employee?.designation || employeeDetails.full_rider_info?.designation || 'Motorcyclist'}
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-slate-500 text-xs">Status</Label>
                      <Badge className={getStatusBadge(employeeDetails.employee?.status, employeeDetails.employee?.tap_status)}>
                        {employeeDetails.employee?.tap_candidate_id ? formatTapStatus(employeeDetails.employee?.tap_status || employeeDetails.employee?.status) : (employeeDetails.employee?.tap_status ? formatTapStatus(employeeDetails.employee?.tap_status) : 'Ready')}
                      </Badge>
                    </div>
                    {employeeDetails.employee_type === 'staff' && (
                      <div>
                        <Label className="text-slate-500 text-xs">Assigned Location (cost analysis)</Label>
                        <p data-testid="staff-detail-location">
                          {employeeDetails.employee?.location
                            ? (String(employeeDetails.employee.location).toLowerCase() === 'head office'
                                ? <Badge className="bg-slate-100 text-slate-700">Head Office (excluded)</Badge>
                                : <Badge variant="outline">{employeeDetails.employee.location}</Badge>)
                            : <span className="text-slate-400 text-sm">— Not assigned —</span>}
                        </p>
                      </div>
                    )}
                    {employeeDetails.employee_type === 'staff' && (
                      <div>
                        <Label className="text-slate-500 text-xs">Joining Date</Label>
                        <p className="text-sm" data-testid="staff-detail-joining-date">
                          {employeeDetails.employee?.joining_date || <span className="text-slate-400">— Not set —</span>}
                        </p>
                      </div>
                    )}
                    {employeeDetails.employee_type === 'staff' && employeeDetails.employee?.exit_date && (
                      <div>
                        <Label className="text-slate-500 text-xs">Exit Date</Label>
                        <p className="text-sm text-rose-600" data-testid="staff-detail-exit-date">
                          {employeeDetails.employee.exit_date}
                        </p>
                      </div>
                    )}
                    {employeeDetails.employee_type === 'staff' && (employeeDetails.employee?.salary_history || []).length > 0 && (
                      <div className="col-span-full">
                        <Label className="text-slate-500 text-xs">Salary History</Label>
                        <div className="text-xs space-y-0.5 mt-1" data-testid="staff-detail-salary-history">
                          {(employeeDetails.employee.salary_history || []).map((row, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                              <span className="text-slate-500 tabular-nums">{row.effective_from}</span>
                              <span className="tabular-nums">AED {Number(row.monthly_salary || 0).toLocaleString()}/mo</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Salary Breakdown (Staff only) */}
              {employeeDetails.employee_type === 'staff' && (employeeDetails.employee?.basic_salary || employeeDetails.employee?.salary_breakdown) && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Banknote className="w-4 h-4" />
                      Salary Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                      <div>
                        <Label className="text-slate-500 text-xs">Basic Salary</Label>
                        <p className="font-semibold"><CurrencyDisplay amount={employeeDetails.employee?.basic_salary || 0} /></p>
                      </div>
                      <div>
                        <Label className="text-slate-500 text-xs">Housing Allowance</Label>
                        <p><CurrencyDisplay amount={employeeDetails.employee?.housing_allowance || 0} /></p>
                      </div>
                      <div>
                        <Label className="text-slate-500 text-xs">Transport Allowance</Label>
                        <p><CurrencyDisplay amount={employeeDetails.employee?.transport_allowance || 0} /></p>
                      </div>
                      <div>
                        <Label className="text-slate-500 text-xs">Telephone Allowance</Label>
                        <p><CurrencyDisplay amount={employeeDetails.employee?.telephone_allowance || 0} /></p>
                      </div>
                      {(parseFloat(employeeDetails.employee?.pension_amount) || 0) > 0 && (
                        <div>
                          <Label className="text-slate-500 text-xs">Pension (GPSSA)</Label>
                          <p><CurrencyDisplay amount={employeeDetails.employee?.pension_amount || 0} /></p>
                        </div>
                      )}
                      {(employeeDetails.employee?.custom_allowances || []).map((a, i) => (
                        <div key={i}>
                          <Label className="text-slate-500 text-xs">{a.description || 'Allowance'}</Label>
                          <p><CurrencyDisplay amount={parseFloat(a.amount) || 0} /></p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-3 border-t flex justify-between items-center">
                      <span className="text-sm font-semibold text-green-700">Total Monthly Salary</span>
                      <span className="text-lg font-bold text-green-700">
                        <CurrencyDisplay amount={
                          (parseFloat(employeeDetails.employee?.basic_salary) || 0) +
                          (parseFloat(employeeDetails.employee?.housing_allowance) || 0) +
                          (parseFloat(employeeDetails.employee?.transport_allowance) || 0) +
                          (parseFloat(employeeDetails.employee?.telephone_allowance) || 0) +
                          (parseFloat(employeeDetails.employee?.pension_amount) || 0) +
                          ((employeeDetails.employee?.custom_allowances || []).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0))
                        } />
                      </span>
                    </div>
                    {(parseFloat(employeeDetails.employee?.room_rent) || 0) > 0 && (
                      <div className="mt-3 pt-3 border-t flex justify-between items-center">
                        <span className="text-sm text-red-600">Room Rent (Monthly Deduction)</span>
                        <span className="text-sm font-semibold text-red-600">
                          <CurrencyDisplay amount={parseFloat(employeeDetails.employee?.room_rent) || 0} />
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Bike Information (if assigned) */}
              {(employeeDetails.bike_info || employeeDetails.full_rider_info?.assigned_bike_plate) && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Bike className="w-4 h-4" />
                      Assigned Vehicle
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-3'}`}>
                      <div>
                        <Label className="text-slate-500 text-xs">Plate Number</Label>
                        <p className="font-mono font-semibold text-lg">{employeeDetails.bike_info?.plate_number || employeeDetails.full_rider_info?.assigned_bike_plate || '-'}</p>
                      </div>
                      {employeeDetails.bike_info && (
                        <>
                          <div>
                            <Label className="text-slate-500 text-xs">Model</Label>
                            <p>{employeeDetails.bike_info.brand} {employeeDetails.bike_info.model}</p>
                          </div>
                          <div>
                            <Label className="text-slate-500 text-xs">Status</Label>
                            <Badge variant="outline">{employeeDetails.bike_info.status}</Badge>
                          </div>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Loan Account Status */}
              {employeeDetails.loan_account ? (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      Loan Account Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="text-center p-3 bg-slate-50 rounded-lg">
                        <Label className="text-slate-500 text-xs">Initial Loan</Label>
                        <p className="text-lg font-semibold">
                          <CurrencyDisplay amount={employeeDetails.loan_account.initial_loan_amount || 0} />
                        </p>
                      </div>
                      <div className="text-center p-3 bg-red-50 rounded-lg">
                        <Label className="text-red-500 text-xs">Outstanding Balance</Label>
                        <p className="text-lg font-semibold text-red-600">
                          <CurrencyDisplay amount={employeeDetails.loan_account.current_balance || 0} />
                        </p>
                      </div>
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <Label className="text-blue-500 text-xs">Monthly EMI</Label>
                        <p className="text-lg font-semibold text-blue-600">
                          AED {employeeDetails.loan_account.monthly_emi?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    
                    {/* Loan Progress Bar */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-slate-500">
                        <span>Loan Repayment Progress</span>
                        <span>
                          {Math.round(100 - (employeeDetails.loan_account.current_balance / employeeDetails.loan_account.initial_loan_amount * 100))}% Paid
                        </span>
                      </div>
                      <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-green-500 rounded-full transition-all"
                          style={{ 
                            width: `${Math.max(0, Math.min(100, 100 - (employeeDetails.loan_account.current_balance / employeeDetails.loan_account.initial_loan_amount * 100)))}%` 
                          }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-slate-400">
                        <span>Paid: AED {(employeeDetails.loan_account.initial_loan_amount - employeeDetails.loan_account.current_balance)?.toLocaleString()}</span>
                        <span>Remaining: <CurrencyDisplay amount={employeeDetails.loan_account.current_balance || 0} /></span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-4 text-center text-slate-500">
                    <CreditCard className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p>No active loan account</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEmployeeDetailsDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loan Request Dialog */}
      <Dialog open={loanRequestDialogOpen} onOpenChange={setLoanRequestDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Additional Loan</DialogTitle>
            <DialogDescription>
              Submit a loan request for admin approval
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Employee Info */}
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <span className="text-slate-500 text-xs block">Employee</span>
                  <span className="font-medium">{selectedEmployee?.name}</span>
                </div>
                <div>
                  <span className="text-slate-500 text-xs block">Current Outstanding</span>
                  <span className="font-medium text-red-600">
                    AED {employeeDetails?.loan_account?.current_balance?.toLocaleString() || '0'}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Loan Amount (AED) *</Label>
              <Input
                type="number"
                value={loanRequestForm.requested_amount}
                onChange={(e) => setLoanRequestForm({...loanRequestForm, requested_amount: e.target.value})}
                placeholder="Enter loan amount"
              />
            </div>
            <div className="space-y-2">
              <Label>Purpose / Reason *</Label>
              <Textarea
                value={loanRequestForm.purpose}
                onChange={(e) => setLoanRequestForm({...loanRequestForm, purpose: e.target.value})}
                placeholder="Explain the purpose of this loan..."
              />
            </div>
            <div className="space-y-2">
              <Label>Proposed Monthly EMI (AED) *</Label>
              <Input
                type="number"
                value={loanRequestForm.proposed_emi}
                onChange={(e) => setLoanRequestForm({...loanRequestForm, proposed_emi: e.target.value})}
                placeholder="Suggested monthly deduction"
              />
              {loanRequestForm.requested_amount && loanRequestForm.proposed_emi && (
                <p className="text-xs text-slate-500">
                  Estimated recovery: {Math.ceil(parseFloat(loanRequestForm.requested_amount) / parseFloat(loanRequestForm.proposed_emi))} months
                </p>
              )}
            </div>

            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                This request will be sent to Admin for approval. Once approved, the loan amount will be added to the employee&apos;s outstanding balance.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLoanRequestDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleLoanRequest} className="bg-blue-600 hover:bg-blue-700">
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loan Details Dialog - Enhanced */}
      <Dialog open={loanDialogOpen} onOpenChange={setLoanDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-purple-600" />
              Loan Account - {selectedEmployee?.name}
            </DialogTitle>
            <DialogDescription>Manage loan entries, recoveries, and EMI settings</DialogDescription>
          </DialogHeader>

          {/* Spinner while loan/employee details are being fetched —
              avoids showing the previous employee's stale data. */}
          {loadingEmployeeId && (!employeeDetails
              || employeeDetails.employee?.id !== loadingEmployeeId) && (
            <div className="flex flex-col items-center justify-center py-16 gap-3" data-testid="loan-modal-loading">
              <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
              <p className="text-sm text-slate-500">Loading loan account…</p>
              <p className="text-xs text-slate-400">Recoveries, EMIs, expenses and TAP-linked entries</p>
            </div>
          )}

          {(!loadingEmployeeId || (employeeDetails
              && employeeDetails.employee?.id === selectedEmployee?.id)) && (<>
          {/* Summary Cards — Contract Loan Amount and Outstanding are
              computed net of any `recovered=true` arrears entries so
              the math stays consistent when arrears from a previous
              payroll have been fully recovered. */}
          {(() => {
            const entries = employeeDetails?.loan_account?.contract_loan_entries || [];
            const grossLoan = entries.reduce((s, e) => s + (Number(e.amount) || 0), 0);
            const recoveredLoan = entries.reduce((s, e) => (e.recovered && e.recovered_via !== 'manual_settlement') ? s + (Number(e.amount) || 0) : s, 0);
            const netLoan = entries.length > 0
              ? grossLoan - recoveredLoan
              : (Number(employeeDetails?.loan_account?.initial_loan_amount) || 0);
            const totalRecovery = (employeeDetails?.loan_account?.recoveries || [])
              .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
            const outstanding = Math.max(0, netLoan - totalRecovery);
            const emiSchedule = employeeDetails?.loan_account?.emi_schedule || [];
            const totalScheduledEmi = emiSchedule.reduce((s, e) => s + (Number(e.amount) || 0), 0);
            const pendingEmiCount = emiSchedule.filter(e => e.status === 'pending').length;
            return (
          <div className="grid grid-cols-5 gap-2 py-3">
            <div className="text-center p-2 bg-blue-50 rounded-lg border border-blue-200">
              <Label className="text-blue-600 text-[10px]">Contract Loan Amount</Label>
              <p className="text-sm font-bold text-blue-700">
                <CurrencyDisplay amount={netLoan} />
              </p>
              {recoveredLoan > 0 && (
                <div className="text-[9px] text-slate-500 mt-0.5" title={`Gross ${grossLoan.toFixed(2)} − Recovered arrears ${recoveredLoan.toFixed(2)}`}>
                  gross <s>{grossLoan.toLocaleString()}</s>
                </div>
              )}
            </div>
            <div className="text-center p-2 bg-amber-50 rounded-lg border border-amber-200">
              <Label className="text-amber-600 text-[10px]">Total Expenses</Label>
              <p className="text-sm font-bold text-amber-700">
                <CurrencyDisplay amount={
                  (employeeDetails?.loan_account?.loan_entries || []).reduce((s, e) => s + (e.amount || 0), 0)
                } />
              </p>
            </div>
            <div className="text-center p-2 bg-green-50 rounded-lg border border-green-200">
              <Label className="text-green-600 text-[10px]">Total Recovery</Label>
              <p className="text-sm font-bold text-green-700">
                - <CurrencyDisplay amount={totalRecovery} />
              </p>
            </div>
            <div className="text-center p-2 bg-red-50 rounded-lg border border-red-200">
              <Label className="text-red-600 text-[10px]">Outstanding</Label>
              <p className="text-sm font-bold text-red-700">
                <CurrencyDisplay amount={outstanding} />
              </p>
            </div>
            <div className="text-center p-2 bg-purple-50 rounded-lg border border-purple-200" data-testid="scheduled-emis-summary-card">
              <Label className="text-purple-600 text-[10px]">Scheduled EMIs</Label>
              <p className="text-sm font-bold text-purple-700">
                <CurrencyDisplay amount={totalScheduledEmi} />
              </p>
              {emiSchedule.length > 0 && (
                <div className="text-[9px] text-slate-500 mt-0.5" title={`${emiSchedule.length} total • ${pendingEmiCount} pending`}>
                  {emiSchedule.length} EMI{emiSchedule.length === 1 ? '' : 's'}{pendingEmiCount > 0 ? ` • ${pendingEmiCount} pending` : ''}
                </div>
              )}
            </div>
          </div>
            );
          })()}

          {/* Action Buttons */}
          <div className="flex gap-2 flex-wrap">
            {canCreateContractLoan && <Button 
              size="sm" 
              className="bg-blue-600 hover:bg-blue-700 gap-1"
              data-testid="add-loan-entry-btn"
              onClick={() => {
                setContractLoanForm({
                  amount: '',
                  description: '',
                  date: new Date().toISOString().split('T')[0],
                  recovery_method: '',
                  recovery_installments: 2
                });
                setAddLoanEntryDialogOpen(true);
              }}
            >
              <Plus className="w-4 h-4" />
              Add Loan Entry
            </Button>}
            <Button 
              size="sm" 
              className="bg-amber-600 hover:bg-amber-700 gap-1"
              onClick={() => {
                setExpenseForm({
                  loan_type: '',
                  sub_type: '',
                  amount: '',
                  description: '',
                  month: '',
                  year: new Date().getFullYear(),
                  date: new Date().toISOString().split('T')[0],
                  receipt: null
                });
                setAddExpenseDialogOpen(true);
              }}
            >
              <Plus className="w-4 h-4" />
              Add Expense
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              className="gap-1 border-green-300 text-green-700 hover:bg-green-50"
              onClick={() => {
                setRecoveryForm({
                  amount: '',
                  month: '',
                  year: new Date().getFullYear(),
                  description: '',
                  date: new Date().toISOString().split('T')[0],
                  receipt: null
                });
                setAddRecoveryDialogOpen(true);
              }}
            >
              <Plus className="w-4 h-4" />
              Add Recovery
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              className="gap-1 border-purple-300 text-purple-700 hover:bg-purple-50"
              onClick={() => {
                setAgreedEmi(employeeDetails?.loan_account?.agreed_emi || employeeDetails?.loan_account?.monthly_emi || '');
                setSetEmiDialogOpen(true);
              }}
            >
              <Edit className="w-4 h-4" />
              Set EMI
            </Button>
            
            {/* Download Statement Buttons */}
            <div className="ml-auto flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={includeExpensesInStatement}
                  onChange={(e) => setIncludeExpensesInStatement(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Include expenses
              </label>
              <Button 
                size="sm" 
                variant="outline"
                className="gap-1 border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => generateLoanStatementPDF(selectedEmployee, employeeDetails?.loan_account, employeeDetails)}
              >
                <FileDown className="w-4 h-4" />
                PDF Statement
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                className="gap-1 border-green-300 text-green-700 hover:bg-green-50"
                onClick={() => generateLoanStatementExcel(selectedEmployee, employeeDetails?.loan_account)}
                data-testid="loan-statement-excel-btn"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Excel Statement
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="gap-1 border-slate-300 text-slate-700 hover:bg-slate-50"
                onClick={() => generateLoanStatementCSV(selectedEmployee, employeeDetails?.loan_account)}
                data-testid="loan-statement-csv-btn"
              >
                <FileDown className="w-4 h-4" />
                CSV
              </Button>
            </div>
          </div>

          {/* Contract Loan Entries Section */}
          <Card className="mt-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Wallet className="w-4 h-4 text-blue-600" />
                Contract Loan Entries ({(employeeDetails?.loan_account?.contract_loan_entries || []).length})
                <span className="ml-auto text-xs text-blue-600 font-normal">
                  {(() => {
                    const es = employeeDetails?.loan_account?.contract_loan_entries || [];
                    const gross = es.reduce((s, e) => s + (Number(e.amount) || 0), 0);
                    const rec = es.reduce((s, e) => (e.recovered && e.recovered_via !== 'manual_settlement') ? s + (Number(e.amount) || 0) : s, 0);
                    const net = gross - rec;
                    return rec > 0 ? (
                      <>
                        Net: <CurrencyDisplay amount={net} />
                        <span className="text-slate-400 ml-1.5" title={`Gross ${gross.toFixed(2)} − Recovered ${rec.toFixed(2)}`}>
                          (gross <s>{gross.toLocaleString()}</s>)
                        </span>
                      </>
                    ) : (
                      <>Total: <CurrencyDisplay amount={gross} /></>
                    );
                  })()}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(employeeDetails?.loan_account?.contract_loan_entries || []).length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="text-xs">
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(employeeDetails?.loan_account?.contract_loan_entries || []).map((entry) => (
                        <TableRow key={entry.id} className={`text-sm ${entry.recovered ? 'bg-emerald-50/40' : ''}`}>
                          <TableCell className="text-xs">{formatDateDisplay(entry.date)}</TableCell>
                          <TableCell className="text-xs">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span>{entry.description || '-'}</span>
                              {entry.recovered && (
                                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[9px] border" title={`Recovered${entry.recovered_in_month ? ' in ' + entry.recovered_in_month : ''}`}>
                                  ✓ Recovered
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-xs ${
                              entry.source === 'TAP' ? 'border-purple-300 text-purple-700' :
                              entry.source === 'migration' ? 'border-amber-300 text-amber-700' :
                              entry.source === 'salary_advance' ? 'border-sky-300 text-sky-700 bg-sky-50' :
                              entry.source === 'negative_salary_auto' ? 'border-rose-300 text-rose-700 bg-rose-50' :
                              'border-blue-300 text-blue-700'
                            }`}>
                              {entry.source === 'TAP' ? 'TAP'
                                : entry.source === 'migration' ? 'Migrated'
                                : entry.source === 'salary_advance' ? 'Salary Advance'
                                : entry.source === 'negative_salary_auto' ? 'Arrears'
                                : 'Manual'}
                            </Badge>
                          </TableCell>
                          <TableCell className={`text-right font-medium ${entry.recovered ? 'text-slate-400 line-through' : 'text-blue-700'}`}>
                            <CurrencyDisplay amount={entry.amount || 0} />
                          </TableCell>
                          <TableCell>
                            {entry.source === 'TAP' ? (
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-xs text-slate-400 cursor-help italic">TAP linked</span>
                                  </TooltipTrigger>
                                  <TooltipContent>Go to TAP page to edit this entry</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : entry.source === 'salary_advance' ? (
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-xs text-slate-400 cursor-help italic">SA linked</span>
                                  </TooltipTrigger>
                                  <TooltipContent>Manage this from Talent PMS → Salary Advance</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <div className="flex items-center gap-1">
                                {canEditContractLoan && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-6 w-6 p-0 text-blue-500 hover:text-blue-700"
                                    data-testid={`edit-contract-entry-${entry.id}`}
                                    onClick={() => {
                                      setEditContractLoanForm({
                                        id: entry.id,
                                        amount: entry.amount || '',
                                        description: entry.description || '',
                                        date: entry.date || ''
                                      });
                                      setEditContractLoanDialogOpen(true);
                                    }}
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </Button>
                                )}
                                {canDeleteContractLoan && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                    data-testid={`delete-contract-entry-${entry.id}`}
                                    onClick={async () => {
                                      // Resolve rider_id with explicit guard so a stale state (e.g. loan
                                      // summary 524) never silently no-ops. If anything is missing we
                                      // surface a clear toast instead of swallowing it.
                                      const riderId =
                                        employeeDetails?.loan_account?.rider_id ||
                                        selectedEmployee?.rider_id ||
                                        selectedEmployee?.id;
                                      const entryId = entry?.id;
                                      // eslint-disable-next-line no-console
                                      console.log('[CLE:delete:click]', { riderId, entryId, selectedEmployeeId: selectedEmployee?.id, hasEmployeeDetails: !!employeeDetails });
                                      if (!riderId || !entryId) {
                                        toast.error('Cannot delete: rider or entry not loaded. Please refresh and try again.');
                                        return;
                                      }
                                      if (!window.confirm('Delete this contract loan entry? This will reduce the contract loan amount.')) return;
                                      try {
                                        const resp = await axios.delete(
                                          `${API}/talent/loan-accounts/${riderId}/contract-entry/${entryId}`,
                                          { headers: getAuthHeader(), timeout: 30000 }
                                        );
                                        // eslint-disable-next-line no-console
                                        console.log('[CLE:delete:success]', resp?.status, resp?.data);
                                        toast.success('Contract loan entry deleted');
                                        if (selectedEmployee?.id) fetchEmployeeDetails(selectedEmployee.id);
                                      } catch (error) {
                                        const status = error?.response?.status;
                                        const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
                                        // eslint-disable-next-line no-console
                                        console.error('[CLE:delete:fail]', { status, detail, url: `${API}/talent/loan-accounts/${riderId}/contract-entry/${entryId}` });
                                        if (status === 520 || status === 524 || !status) {
                                          toast.error('Server unreachable (gateway timeout). Please retry in a few seconds.');
                                        } else if (status === 403) {
                                          toast.error('Permission denied: your account does not allow deleting this entry.');
                                        } else if (status === 404) {
                                          toast.error('Entry no longer exists. Refreshing…');
                                          if (selectedEmployee?.id) fetchEmployeeDetails(selectedEmployee.id);
                                        } else {
                                          toast.error(`Failed to delete entry (${status || 'network'}): ${detail}`);
                                        }
                                      }
                                    }}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">No contract loan entries yet. Click &quot;Add Loan Entry&quot; to add the agreed loan amount.</p>
              )}
            </CardContent>
          </Card>

          {/* Expense Entries Section (formerly Loan Entries) */}
          <Card className="mt-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="w-4 h-4 text-amber-600" />
                Expense Entries ({(employeeDetails?.loan_account?.loan_entries || []).length})
                <span className="ml-auto text-xs text-amber-600 font-normal">
                  Total Expenses: <CurrencyDisplay amount={(employeeDetails?.loan_account?.loan_entries || []).reduce((s, e) => s + (e.amount || 0), 0)} />
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(employeeDetails?.loan_account?.loan_entries || []).length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="text-xs">
                        <TableHead>TXN ID</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Sub-Type</TableHead>
                        <TableHead>Details</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Docs</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(employeeDetails?.loan_account?.loan_entries || []).map((entry) => (
                        <TableRow key={entry.id} className="text-sm">
                          <TableCell className="text-xs font-mono text-blue-600">
                            {entry.transaction_ref || '-'}
                          </TableCell>
                          <TableCell className="text-xs">{formatDateDisplay(entry.date)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs capitalize">
                              {(entry.loan_type || '').replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs">{entry.sub_type || '-'}</TableCell>
                          <TableCell className="text-xs max-w-[150px] truncate">
                            {entry.loan_type === 'accommodation' 
                              ? `${entry.month || ''} ${entry.year || ''}`
                              : entry.description || '-'}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            <CurrencyDisplay amount={entry.amount || 0} />
                          </TableCell>
                          <TableCell>
                            {(() => {
                              // Normalise: prefer multi-file arrays, fall back to legacy single URL.
                              const invs = Array.isArray(entry.invoice_files) && entry.invoice_files.length
                                ? entry.invoice_files
                                : (entry.invoice_url ? [{ url: entry.invoice_url, filename: '' }] : []);
                              const recs = Array.isArray(entry.receipt_files) && entry.receipt_files.length
                                ? entry.receipt_files
                                : (entry.receipt_url ? [{ url: entry.receipt_url, filename: '' }] : []);
                              if (!invs.length && !recs.length) {
                                return <span className="text-slate-400 text-xs">-</span>;
                              }
                              return (
                                <div className="flex flex-wrap items-center gap-1">
                                  {invs.map((f, i) => (
                                    <button
                                      key={`inv-${i}`}
                                      type="button"
                                      onClick={() => openLoanEntryFile(f.url, f.filename)}
                                      className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:underline"
                                      title={f.filename || `Invoice ${i + 1}`}
                                      data-testid={`loan-entry-invoice-${entry.id}-${i}`}
                                    >
                                      <FileText className="w-3 h-3" /> Inv{invs.length > 1 ? ` ${i + 1}` : ''}
                                    </button>
                                  ))}
                                  {recs.map((f, i) => (
                                    <button
                                      key={`rec-${i}`}
                                      type="button"
                                      onClick={() => openLoanEntryFile(f.url, f.filename)}
                                      className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:underline"
                                      title={f.filename || `Receipt ${i + 1}`}
                                      data-testid={`loan-entry-receipt-${entry.id}-${i}`}
                                    >
                                      <FileCheck className="w-3 h-3" /> Rcpt{recs.length > 1 ? ` ${i + 1}` : ''}
                                    </button>
                                  ))}
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                              onClick={async () => {
                                if (window.confirm('Delete this loan entry?')) {
                                  try {
                                    await axios.delete(
                                      `${API}/talent/loan-accounts/${employeeDetails?.loan_account?.rider_id || selectedEmployee?.rider_id || selectedEmployee?.id}/entry/${entry.id}`,
                                      { headers: getAuthHeader() }
                                    );
                                    toast.success('Loan entry deleted');
                                    fetchEmployeeDetails(selectedEmployee.id);
                                  } catch (error) {
                                    toast.error('Failed to delete entry');
                                  }
                                }
                              }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">No expense entries yet</p>
              )}
            </CardContent>
          </Card>

          {/* Recoveries Section */}
          <Card className="mt-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Receipt className="w-4 h-4 text-green-600" />
                Recovery Entries ({(employeeDetails?.loan_account?.recoveries || []).length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(employeeDetails?.loan_account?.recoveries || []).length > 0 ? (
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="text-xs">
                        <TableHead>TXN ID</TableHead>
                        <TableHead>Month/Year</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(employeeDetails?.loan_account?.recoveries || []).map((recovery) => (
                        <TableRow key={recovery.id} className="text-sm">
                          <TableCell className="text-xs font-mono text-green-600">
                            {recovery.transaction_ref || recovery.reference_number || '-'}
                          </TableCell>
                          <TableCell className="font-medium">{recovery.month} {recovery.year}</TableCell>
                          <TableCell className="text-xs">{formatDateDisplay(recovery.date)}</TableCell>
                          <TableCell className="text-xs">
                            {recovery.source === 'salary_slip_loan_emi' && recovery.salary_slip_id ? (
                              // Clickable link to open the underlying
                              // salary slip PDF in a new tab. Uses the
                              // blob-fetch pattern (auth-aware) — see
                              // handleDownloadSalarySlipPDF for the twin.
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    const resp = await axios.get(
                                      `${API}/talent/salary-slip/${recovery.salary_slip_id}/download-pdf`,
                                      { headers: getAuthHeader(), responseType: 'blob' },
                                    );
                                    const blobUrl = window.URL.createObjectURL(
                                      new Blob([resp.data], { type: 'application/pdf' }),
                                    );
                                    window.open(blobUrl, '_blank', 'noopener,noreferrer');
                                    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
                                  } catch (err) {
                                    toast.error('Could not open salary slip PDF');
                                  }
                                }}
                                className="text-blue-600 hover:text-blue-800 underline underline-offset-2 inline-flex items-center gap-1"
                                title="Open the salary slip PDF that generated this EMI"
                                data-testid={`open-slip-${recovery.salary_slip_id}`}
                              >
                                <FileText className="w-3 h-3" />
                                {recovery.description || 'Salary Slip EMI'}
                              </button>
                            ) : (
                              recovery.description || '-'
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-xs ${
                              recovery.source === 'TAP' ? 'border-purple-300 text-purple-700' :
                              recovery.source === 'petty_cash_loan_repayment' ? 'border-emerald-300 text-emerald-700 bg-emerald-50' :
                              recovery.source === 'salary_slip_loan_emi' ? 'border-blue-300 text-blue-700 bg-blue-50' :
                              recovery.source === 'salary_advance' ? 'border-amber-300 text-amber-700 bg-amber-50' :
                              recovery.source === 'hiring_advance' ? 'border-fuchsia-300 text-fuchsia-700 bg-fuchsia-50' :
                              'border-blue-300 text-blue-700'
                            }`}>
                              {recovery.source === 'TAP' ? 'TAP'
                                : recovery.source === 'petty_cash_loan_repayment' ? 'Petty Cash'
                                : recovery.source === 'salary_slip_loan_emi' ? 'Salary Slip'
                                : recovery.source === 'salary_advance' ? 'Salary Advance'
                                : recovery.source === 'hiring_advance' ? 'Hiring Advance'
                                : 'Manual'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium text-green-600">
                            - <CurrencyDisplay amount={recovery.amount || 0} />
                          </TableCell>
                          <TableCell>
                            {recovery.source === 'TAP' ? (
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-xs text-slate-400 cursor-help italic">TAP linked</span>
                                  </TooltipTrigger>
                                  <TooltipContent>Go to TAP page to edit this entry</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : recovery.source === 'petty_cash_loan_repayment' ? (
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-xs text-slate-400 cursor-help italic">Petty Cash linked</span>
                                  </TooltipTrigger>
                                  <TooltipContent>Manage this from Accounts → Petty Cash</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : recovery.source === 'salary_slip_loan_emi' ? (
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-xs text-slate-400 cursor-help italic">Payroll linked</span>
                                  </TooltipTrigger>
                                  <TooltipContent>Auto-generated from a posted salary slip — click the description to open the PDF.</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : recovery.source === 'salary_advance' ? (
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-xs text-slate-400 cursor-help italic">Salary Advance linked</span>
                                  </TooltipTrigger>
                                  <TooltipContent>Managed under Talent → Salary Advances.</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : recovery.source === 'hiring_advance' ? (
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="text-xs text-slate-400 cursor-help italic">TAP advance</span>
                                  </TooltipTrigger>
                                  <TooltipContent>Auto-consumed from a pre-employment TAP advance payment.</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                onClick={async () => {
                                  if (window.confirm('Delete this recovery entry?')) {
                                    try {
                                      await axios.delete(
                                        `${API}/talent/loan-accounts/${employeeDetails?.loan_account?.rider_id || selectedEmployee?.rider_id || selectedEmployee?.id}/recovery/${recovery.id}`,
                                        { headers: getAuthHeader() }
                                      );
                                      toast.success('Recovery entry deleted');
                                      fetchEmployeeDetails(selectedEmployee.id);
                                    } catch (error) {
                                      toast.error('Failed to delete entry');
                                    }
                                  }
                                }}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">No recovery entries yet</p>
              )}
            </CardContent>
          </Card>

          {/* EMI Summary */}
          {(employeeDetails?.loan_account?.current_balance > 0 && employeeDetails?.loan_account?.agreed_emi > 0) && (
            <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-purple-800">Agreed Monthly EMI</p>
                  <p className="text-xs text-purple-600">
                    Estimated completion: {Math.ceil(employeeDetails.loan_account.current_balance / employeeDetails.loan_account.agreed_emi)} months
                  </p>
                </div>
                <p className="text-xl font-bold text-purple-700">
                  <CurrencyDisplay amount={employeeDetails.loan_account.agreed_emi} /> /month
                </p>
              </div>
            </div>
          )}

          {/* EMI Repayment Schedule */}
          {employeeDetails?.loan_account && (
            <div className="mt-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
                <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  EMI Repayment Schedule
                </h4>
                <div className="flex flex-wrap gap-2 items-center">
                  {/* Status Filter */}
                  <Select value={emiStatusFilter} onValueChange={setEmiStatusFilter}>
                    <SelectTrigger className="h-8 text-xs w-[110px]">
                      <Filter className="w-3 h-3 mr-1" />
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="partial">Partial</SelectItem>
                      <SelectItem value="missed">Missed</SelectItem>
                      <SelectItem value="skipped">Skipped</SelectItem>
                      <SelectItem value="rescheduled">Rescheduled</SelectItem>
                      <SelectItem value="closed">Loan Repaid</SelectItem>
                    </SelectContent>
                  </Select>
                  {employeeDetails?.loan_account?.agreed_emi > 0 && employeeDetails?.loan_account?.current_balance > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        const currentMonth = new Date().toLocaleString('default', { month: 'long' });
                        const currentYear = new Date().getFullYear();
                        try {
                          const formData = new FormData();
                          formData.append('start_month', currentMonth);
                          formData.append('start_year', currentYear);
                          await axios.post(
                            `${API}/talent/loan-accounts/${employeeDetails?.loan_account?.rider_id || selectedEmployee?.id}/generate-emi-schedule`,
                            formData,
                            { headers: getAuthHeader() }
                          );
                          toast.success('EMI schedule generated');
                          fetchEmployeeDetails(selectedEmployee.id);
                        } catch (error) {
                          toast.error(formatErrorMessage(error, 'Failed to generate schedule'));
                        }
                      }}
                      className="text-xs"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Auto-Generate
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setAddEmiScheduleDialogOpen(true)}
                    className="text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Entry
                  </Button>
                </div>
              </div>
              
              {/* EMI Schedule Summary */}
              {employeeDetails?.loan_account?.emi_schedule?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3 text-xs">
                  <Badge variant="outline" className="bg-slate-50">
                    Total: {employeeDetails.loan_account.emi_schedule.length}
                  </Badge>
                  <Badge variant="outline" className="bg-green-50 text-green-700">
                    Paid: {employeeDetails.loan_account.emi_schedule.filter(e => e.status === 'paid').length}
                  </Badge>
                  <Badge variant="outline" className="bg-slate-50 text-slate-700">
                    Pending: {employeeDetails.loan_account.emi_schedule.filter(e => e.status === 'pending').length}
                  </Badge>
                  <Badge variant="outline" className="bg-red-50 text-red-700">
                    Missed: {employeeDetails.loan_account.emi_schedule.filter(e => e.status === 'missed' || e.status === 'skipped').length}
                  </Badge>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700">
                    Rescheduled: {employeeDetails.loan_account.emi_schedule.filter(e => e.status === 'rescheduled').length}
                  </Badge>
                  {employeeDetails.loan_account.emi_schedule.some(e => e.status === 'closed') && (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                      Loan Repaid: {employeeDetails.loan_account.emi_schedule.filter(e => e.status === 'closed').length}
                    </Badge>
                  )}
                </div>
              )}
              
              {(!employeeDetails?.loan_account?.emi_schedule || employeeDetails?.loan_account?.emi_schedule?.length === 0) ? (
                <div className="text-center py-4 text-slate-500 text-sm bg-slate-50 rounded-lg">
                  <Calendar className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  No EMI schedule entries yet
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-100">
                        <TableHead className="text-xs">Month/Year</TableHead>
                        <TableHead className="text-xs">EMI Amount</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                        <TableHead className="text-xs">Paid</TableHead>
                        <TableHead className="text-xs">Notes</TableHead>
                        <TableHead className="text-xs text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(employeeDetails?.loan_account?.emi_schedule || [])
                        .filter(emi => emiStatusFilter === 'all' || emi.status === emiStatusFilter)
                        .map((emi, idx) => (
                        <TableRow key={emi.id || idx} className="text-sm">
                          <TableCell className="font-medium">{emi.month} {emi.year}</TableCell>
                          <TableCell>
                            {emi.prepaid_amount > 0 && emi.status === 'pending' ? (
                              <div className="leading-tight">
                                <CurrencyDisplay amount={emi.amount} />
                                <div className="text-[10px] text-slate-400">
                                  <s>{Number(emi.original_amount || 0).toLocaleString()}</s>
                                  <span className="ml-1 text-emerald-700">−{Number(emi.prepaid_amount || 0).toLocaleString()} prepaid</span>
                                </div>
                              </div>
                            ) : (
                              <CurrencyDisplay amount={emi.amount} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              emi.status === 'paid' ? 'bg-green-100 text-green-700' :
                              emi.status === 'closed' && emi._auto_managed && emi.prepaid_amount > 0 ? 'bg-emerald-200 text-emerald-800 border border-emerald-300' :
                              emi.status === 'closed' ? 'bg-emerald-100 text-emerald-700' :
                              emi.status === 'partial' ? 'bg-yellow-100 text-yellow-700' :
                              emi.status === 'skipped' ? 'bg-red-100 text-red-700' :
                              emi.status === 'missed' ? 'bg-red-100 text-red-700' :
                              emi.status === 'rescheduled' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            } title={emi._auto_managed ? 'Auto-adjusted by prepayment reconciler' : ''}>
                              {emi.status === 'closed' && emi._auto_managed && emi.prepaid_amount > 0
                                ? '✂ Squared off'
                                : emi.status === 'closed'
                                  ? 'Loan Repaid'
                                  : emi.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {emi.paid_amount > 0 ? <CurrencyDisplay amount={emi.paid_amount} /> : '-'}
                          </TableCell>
                          <TableCell className="text-xs text-slate-500 max-w-[100px] truncate">
                            {emi.notes || '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0"
                                onClick={() => {
                                  setEditingEmiEntry(emi);
                                  setEmiScheduleForm({
                                    month: emi.month,
                                    year: emi.year,
                                    amount: emi.amount,
                                    status: emi.status,
                                    paid_amount: emi.paid_amount || 0,
                                    paid_date: emi.paid_date || '',
                                    notes: emi.notes || ''
                                  });
                                  setEditEmiScheduleDialogOpen(true);
                                }}
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                onClick={async () => {
                                  if (window.confirm('Delete this EMI schedule entry?')) {
                                    try {
                                      await axios.delete(
                                        `${API}/talent/loan-accounts/${employeeDetails?.loan_account?.rider_id || selectedEmployee?.id}/emi-schedule/${emi.id}`,
                                        { headers: getAuthHeader() }
                                      );
                                      toast.success('EMI entry deleted');
                                      fetchEmployeeDetails(selectedEmployee.id);
                                    } catch (error) {
                                      toast.error('Failed to delete entry');
                                    }
                                  }
                                }}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setLoanDialogOpen(false)}>Close</Button>
          </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Add Loan Entry Dialog */}
      <Dialog open={addLoanEntryDialogOpen} onOpenChange={setAddLoanEntryDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Contract Loan Entry</DialogTitle>
            <DialogDescription>Add a new contract loan amount to {selectedEmployee?.name}&apos;s account</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
              <div className="space-y-2">
                <Label>Amount (AED) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={contractLoanForm.amount}
                  onChange={(e) => setContractLoanForm({ ...contractLoanForm, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={contractLoanForm.date}
                  onChange={(e) => setContractLoanForm({ ...contractLoanForm, date: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={contractLoanForm.description}
                onChange={(e) => setContractLoanForm({ ...contractLoanForm, description: e.target.value })}
                placeholder="e.g., Initial contract loan, Top-up, Additional loan..."
              />
            </div>

            {/* Recovery Method */}
            <div className="space-y-3 border-t pt-3">
              <Label className="text-sm font-semibold">Recovery Method *</Label>
              <p className="text-xs text-slate-500">How should this loan amount be recovered from EMI?</p>
              <div className="space-y-2">
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${contractLoanForm.recovery_method === 'next_emi' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                  <input type="radio" name="contract_recovery" value="next_emi" checked={contractLoanForm.recovery_method === 'next_emi'}
                    onChange={() => setContractLoanForm({ ...contractLoanForm, recovery_method: 'next_emi' })} className="mt-0.5" />
                  <div>
                    <span className="text-sm font-medium">Full recovery in next EMI</span>
                    <p className="text-xs text-slate-500">Entire amount added to next month&apos;s EMI deduction</p>
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${contractLoanForm.recovery_method === 'installments' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                  <input type="radio" name="contract_recovery" value="installments" checked={contractLoanForm.recovery_method === 'installments'}
                    onChange={() => setContractLoanForm({ ...contractLoanForm, recovery_method: 'installments' })} className="mt-0.5" />
                  <div className="flex-1">
                    <span className="text-sm font-medium">Equal installments</span>
                    <p className="text-xs text-slate-500">Split into equal parts added to existing EMI</p>
                    {contractLoanForm.recovery_method === 'installments' && (
                      <div className="flex items-center gap-2 mt-2">
                        <Input type="number" min="2" className="w-20 h-8 text-sm" value={contractLoanForm.recovery_installments}
                          onChange={(e) => setContractLoanForm({ ...contractLoanForm, recovery_installments: parseInt(e.target.value) || 2 })} />
                        <span className="text-xs text-slate-500">months ({contractLoanForm.amount && contractLoanForm.recovery_installments ? `AED ${(parseFloat(contractLoanForm.amount) / contractLoanForm.recovery_installments).toFixed(2)}/mo` : ''})</span>
                      </div>
                    )}
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${contractLoanForm.recovery_method === 'extend' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                  <input type="radio" name="contract_recovery" value="extend" checked={contractLoanForm.recovery_method === 'extend'}
                    onChange={() => setContractLoanForm({ ...contractLoanForm, recovery_method: 'extend' })} className="mt-0.5" />
                  <div>
                    <span className="text-sm font-medium">Extend EMI tenure</span>
                    <p className="text-xs text-slate-500">Keep same EMI amount, add extra months to recover</p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddLoanEntryDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={async () => {
                if (!contractLoanForm.amount || parseFloat(contractLoanForm.amount) <= 0) {
                  toast.error('Please enter a valid amount');
                  return;
                }
                if (!contractLoanForm.recovery_method) {
                  toast.error('Please select a recovery method');
                  return;
                }
                
                try {
                  setSavingContractLoan(true);
                  const formData = new FormData();
                  formData.append('amount', contractLoanForm.amount);
                  if (contractLoanForm.description) formData.append('description', contractLoanForm.description);
                  formData.append('date', contractLoanForm.date);
                  formData.append('recovery_method', contractLoanForm.recovery_method);
                  if (contractLoanForm.recovery_installments) formData.append('recovery_installments', contractLoanForm.recovery_installments);
                  
                  await axios.post(
                    `${API}/talent/loan-accounts/${employeeDetails?.loan_account?.rider_id || selectedEmployee?.rider_id || selectedEmployee?.id}/add-contract-loan`,
                    formData,
                    { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
                  );
                  
                  toast.success('Contract loan entry added successfully');
                  setAddLoanEntryDialogOpen(false);
                  setContractLoanForm({ amount: '', description: '', date: new Date().toISOString().split('T')[0], recovery_method: '', recovery_installments: 2 });
                  fetchEmployeeDetails(selectedEmployee.id);
                } catch (error) {
                  toast.error(formatErrorMessage(error, 'Failed to add contract loan entry'));
                } finally {
                  setSavingContractLoan(false);
                }
              }}
              disabled={savingContractLoan}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {savingContractLoan ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Contract Loan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Contract Loan Entry Dialog */}
      <Dialog open={editContractLoanDialogOpen} onOpenChange={setEditContractLoanDialogOpen}>
        <DialogContent className="max-w-md" data-testid="edit-contract-loan-dialog">
          <DialogHeader>
            <DialogTitle>Edit Contract Loan Entry</DialogTitle>
            <DialogDescription>Update the contract loan entry for {selectedEmployee?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Amount (AED) *</Label>
              <Input
                type="number"
                data-testid="edit-contract-loan-amount"
                value={editContractLoanForm.amount}
                onChange={(e) => setEditContractLoanForm({ ...editContractLoanForm, amount: e.target.value })}
                placeholder="Enter amount"
              />
            </div>
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                data-testid="edit-contract-loan-date"
                value={editContractLoanForm.date}
                onChange={(e) => setEditContractLoanForm({ ...editContractLoanForm, date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                data-testid="edit-contract-loan-description"
                value={editContractLoanForm.description}
                onChange={(e) => setEditContractLoanForm({ ...editContractLoanForm, description: e.target.value })}
                placeholder="e.g., Contract loan, Top-up..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditContractLoanDialogOpen(false)}>Cancel</Button>
            <Button
              data-testid="save-edit-contract-loan-btn"
              disabled={savingEditContractLoan || !editContractLoanForm.amount || parseFloat(editContractLoanForm.amount) <= 0}
              className="bg-blue-600 hover:bg-blue-700"
              onClick={async () => {
                try {
                  setSavingEditContractLoan(true);
                  const formData = new FormData();
                  formData.append('amount', editContractLoanForm.amount);
                  formData.append('description', editContractLoanForm.description);
                  formData.append('date', editContractLoanForm.date);
                  
                  await axios.put(
                    `${API}/talent/loan-accounts/${employeeDetails?.loan_account?.rider_id || selectedEmployee?.rider_id || selectedEmployee?.id}/contract-entry/${editContractLoanForm.id}`,
                    formData,
                    { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
                  );
                  
                  toast.success('Contract loan entry updated');
                  setEditContractLoanDialogOpen(false);
                  fetchEmployeeDetails(selectedEmployee.id);
                } catch (error) {
                  toast.error(formatErrorMessage(error, 'Failed to update entry'));
                } finally {
                  setSavingEditContractLoan(false);
                }
              }}
            >
              {savingEditContractLoan ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Expense Entry Dialog */}
      <Dialog open={addExpenseDialogOpen} onOpenChange={setAddExpenseDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Expense Entry</DialogTitle>
            <DialogDescription>Add an expense record to {selectedEmployee?.name}&apos;s account (informational only)</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
              <div className="space-y-2">
                <Label>Expense Type *</Label>
                <Select
                  value={expenseForm.loan_type}
                  onValueChange={(value) => setExpenseForm({ ...expenseForm, loan_type: value, sub_type: '' })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(loanTypes).map(([key, value]) => (
                      <SelectItem key={key} value={key}>{value.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {expenseForm.loan_type && loanTypes[expenseForm.loan_type]?.sub_types?.length > 0 && (
                <div className="space-y-2">
                  <Label>Sub-Type</Label>
                  <Select
                    value={expenseForm.sub_type}
                    onValueChange={(value) => setExpenseForm({ ...expenseForm, sub_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select sub-type" />
                    </SelectTrigger>
                    <SelectContent>
                      {loanTypes[expenseForm.loan_type]?.sub_types?.map((st) => (
                        <SelectItem key={st} value={st}>{st}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {expenseForm.loan_type === 'accommodation' && (
              <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <div className="space-y-2">
                  <Label>Month</Label>
                  <Select
                    value={expenseForm.month}
                    onValueChange={(value) => setExpenseForm({ ...expenseForm, month: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select month" />
                    </SelectTrigger>
                    <SelectContent>
                      {['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'].map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Year</Label>
                  <Input
                    type="number"
                    value={expenseForm.year}
                    onChange={(e) => setExpenseForm({ ...expenseForm, year: parseInt(e.target.value) })}
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                placeholder="Describe the expense..."
              />
            </div>

            <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
              <div className="space-y-2">
                <Label>Amount (AED) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={expenseForm.date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Receipt (Optional)</Label>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setExpenseForm({ ...expenseForm, receipt: e.target.files[0] })}
              />
              <p className="text-xs text-slate-500">Upload supporting receipt (PDF, JPG, PNG)</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddExpenseDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={async () => {
                if (!expenseForm.loan_type || !expenseForm.amount) {
                  toast.error('Please fill required fields');
                  return;
                }
                
                try {
                  setSavingExpense(true);
                  const formData = new FormData();
                  formData.append('loan_type', expenseForm.loan_type);
                  if (expenseForm.sub_type) formData.append('sub_type', expenseForm.sub_type);
                  formData.append('amount', expenseForm.amount);
                  if (expenseForm.description) formData.append('description', expenseForm.description);
                  if (expenseForm.month) formData.append('month', expenseForm.month);
                  if (expenseForm.year) formData.append('year', expenseForm.year);
                  formData.append('date', expenseForm.date);
                  if (expenseForm.receipt) formData.append('receipt', expenseForm.receipt);
                  
                  await axios.post(
                    `${API}/talent/loan-accounts/${employeeDetails?.loan_account?.rider_id || selectedEmployee?.rider_id || selectedEmployee?.id}/add-expense`,
                    formData,
                    { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
                  );
                  
                  toast.success('Expense entry added successfully');
                  setAddExpenseDialogOpen(false);
                  setExpenseForm({ loan_type: '', sub_type: '', amount: '', description: '', month: '', year: new Date().getFullYear(), date: new Date().toISOString().split('T')[0], receipt: null });
                  fetchEmployeeDetails(selectedEmployee.id);
                } catch (error) {
                  toast.error(formatErrorMessage(error, 'Failed to add expense entry'));
                } finally {
                  setSavingExpense(false);
                }
              }}
              disabled={savingExpense}
              className="bg-green-600 hover:bg-green-700"
            >
              {savingExpense ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* EMI Adjustment Recovery Method Dialog */}
      <Dialog open={emiAdjustmentDialogOpen} onOpenChange={(open) => {
        if (!open && !emiAdjustmentData.recoveryMethod) {
          // If closing without selecting recovery method, revert the EMI change
          const updated = [...wizardData.preview];
          if (emiAdjustmentData.idx !== null && updated[emiAdjustmentData.idx]) {
            updated[emiAdjustmentData.idx].loan_emi = emiAdjustmentData.originalEmi;
            const row = updated[emiAdjustmentData.idx];
            row.total_deductions = round2((row.fines || 0) + (row.salik || 0) + (row.maintenance || 0) + (row.loan_emi || 0) + (row.prev_arrears || 0) + (row.paid_in_wallet || 0) + (row.paid_as_tips || 0) + (row.paid_as_cod || 0) + (row.misc_deduction || 0) + (row.clawback_deduction || 0) + (row.platform_deduction || 0) + (row.food_compensation || 0) + (row.room_rent || 0) + (row.slx_deduction || 0) + (row.dynamic_deductions_total || 0) + (row.salary_advance || 0));
            row.net_salary = round2((row.gross_salary || 0) + (row.past_dues || 0) - row.total_deductions);
            setWizardData({ ...wizardData, preview: updated });
            toast.info('EMI change reverted');
          }
        }
        setEmiAdjustmentDialogOpen(open);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              EMI Adjustment
            </DialogTitle>
            <DialogDescription>
              EMI reduced for <strong>{emiAdjustmentData.riderName}</strong>. How should the skipped amount be recovered?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
              <div className="flex justify-between"><span>Original EMI:</span><span className="font-semibold">AED {emiAdjustmentData.originalEmi?.toFixed(2)}</span></div>
              <div className="flex justify-between"><span>This Month EMI:</span><span className="font-semibold text-orange-600">AED {emiAdjustmentData.newEmi?.toFixed(2)}</span></div>
              <div className="flex justify-between border-t border-orange-200 mt-1 pt-1"><span>Skipped Amount:</span><span className="font-bold text-red-600">AED {emiAdjustmentData.skippedAmount?.toFixed(2)}</span></div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold">Recovery Method</Label>
              <div className="space-y-2">
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${emiAdjustmentData.recoveryMethod === 'next_emi' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                  <input type="radio" name="emi_recovery" value="next_emi" checked={emiAdjustmentData.recoveryMethod === 'next_emi'}
                    onChange={() => setEmiAdjustmentData({ ...emiAdjustmentData, recoveryMethod: 'next_emi' })} className="mt-0.5" />
                  <div>
                    <span className="text-sm font-medium">Full recovery in next EMI</span>
                    <p className="text-xs text-slate-500">AED {emiAdjustmentData.skippedAmount?.toFixed(2)} added to next month's EMI</p>
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${emiAdjustmentData.recoveryMethod === 'installments' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                  <input type="radio" name="emi_recovery" value="installments" checked={emiAdjustmentData.recoveryMethod === 'installments'}
                    onChange={() => setEmiAdjustmentData({ ...emiAdjustmentData, recoveryMethod: 'installments' })} className="mt-0.5" />
                  <div className="flex-1">
                    <span className="text-sm font-medium">Equal installments</span>
                    <p className="text-xs text-slate-500">Split across multiple months</p>
                    {emiAdjustmentData.recoveryMethod === 'installments' && (
                      <div className="flex items-center gap-2 mt-2">
                        <Input type="number" min="2" className="w-20 h-8 text-sm" value={emiAdjustmentData.installments}
                          onChange={(e) => setEmiAdjustmentData({ ...emiAdjustmentData, installments: parseInt(e.target.value) || 2 })} />
                        <span className="text-xs text-slate-500">months (AED {(emiAdjustmentData.skippedAmount / (emiAdjustmentData.installments || 2)).toFixed(2)}/mo added to EMI)</span>
                      </div>
                    )}
                  </div>
                </label>
                <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${emiAdjustmentData.recoveryMethod === 'extend' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                  <input type="radio" name="emi_recovery" value="extend" checked={emiAdjustmentData.recoveryMethod === 'extend'}
                    onChange={() => setEmiAdjustmentData({ ...emiAdjustmentData, recoveryMethod: 'extend' })} className="mt-0.5" />
                  <div>
                    <span className="text-sm font-medium">Extend EMI tenure</span>
                    <p className="text-xs text-slate-500">Keep same EMI, add extra month(s) at the end</p>
                  </div>
                </label>
                {emiAdjustmentData.employeePlatforms?.length > 0 && (
                  <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${emiAdjustmentData.recoveryMethod === 'cross_platform' ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                    <input type="radio" name="emi_recovery" value="cross_platform" checked={emiAdjustmentData.recoveryMethod === 'cross_platform'}
                      onChange={() => setEmiAdjustmentData({ ...emiAdjustmentData, recoveryMethod: 'cross_platform' })} className="mt-0.5" />
                    <div className="flex-1">
                      <span className="text-sm font-medium">Adjust in other platform</span>
                      <p className="text-xs text-slate-500">Recover from this employee's payroll on another platform</p>
                      {emiAdjustmentData.recoveryMethod === 'cross_platform' && (
                        <div className="mt-2">
                          <Select value={emiAdjustmentData.crossPlatformTarget} onValueChange={(v) => setEmiAdjustmentData({ ...emiAdjustmentData, crossPlatformTarget: v })}>
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue placeholder="Select platform" />
                            </SelectTrigger>
                            <SelectContent>
                              {emiAdjustmentData.employeePlatforms.map(p => (
                                <SelectItem key={p} value={p}>{p}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </label>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Reason (Optional)</Label>
              <Input value={emiAdjustmentData.reason} onChange={(e) => setEmiAdjustmentData({ ...emiAdjustmentData, reason: e.target.value })}
                placeholder="e.g., Employee hardship, salary advance" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              // Revert EMI change
              const updated = [...wizardData.preview];
              if (emiAdjustmentData.idx !== null && updated[emiAdjustmentData.idx]) {
                updated[emiAdjustmentData.idx].loan_emi = emiAdjustmentData.originalEmi;
                const row = updated[emiAdjustmentData.idx];
                row.total_deductions = round2((row.fines || 0) + (row.salik || 0) + (row.maintenance || 0) + (row.loan_emi || 0) + (row.prev_arrears || 0) + (row.paid_in_wallet || 0) + (row.paid_as_tips || 0) + (row.paid_as_cod || 0) + (row.misc_deduction || 0) + (row.clawback_deduction || 0) + (row.platform_deduction || 0) + (row.food_compensation || 0) + (row.room_rent || 0) + (row.slx_deduction || 0) + (row.dynamic_deductions_total || 0) + (row.salary_advance || 0));
                row.net_salary = round2((row.gross_salary || 0) + (row.past_dues || 0) - row.total_deductions);
                setWizardData({ ...wizardData, preview: updated });
              }
              setEmiAdjustmentDialogOpen(false);
            }}>Cancel & Revert</Button>
            <Button onClick={handleConfirmEmiAdjustment} className="bg-blue-600 hover:bg-blue-700">
              Confirm Recovery Method
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Room Rent Adjustment Dialog (Staff Payroll Wizard) */}
      <Dialog
        open={roomRentAdjustmentDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            // Closing without saving — revert to original
            const updated = [...wizardData.preview];
            if (roomRentAdjustmentData.idx !== null && updated[roomRentAdjustmentData.idx]) {
              const row = updated[roomRentAdjustmentData.idx];
              row.room_rent = roomRentAdjustmentData.originalRoomRent;
              row.room_rent_was_adjusted = false;
              row.room_rent_adjustment_reason = '';
              row.total_deductions = round2((row.fines || 0) + (row.salik || 0) + (row.maintenance || 0) + (row.loan_emi || 0) + (row.prev_arrears || 0) + (row.paid_in_wallet || 0) + (row.paid_as_tips || 0) + (row.paid_as_cod || 0) + (row.misc_deduction || 0) + (row.clawback_deduction || 0) + (row.platform_deduction || 0) + (row.food_compensation || 0) + (row.room_rent || 0) + (row.slx_deduction || 0) + (row.dynamic_deductions_total || 0) + (row.salary_advance || 0));
              row.net_salary = round2((row.gross_salary || 0) + (row.past_dues || 0) - row.total_deductions);
              setWizardData({ ...wizardData, preview: updated });
              toast.info('Room rent change reverted');
            }
          }
          setRoomRentAdjustmentDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-md" data-testid="room-rent-adjustment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              Room Rent Adjustment
            </DialogTitle>
            <DialogDescription>
              Room rent changed for <strong>{roomRentAdjustmentData.employeeName}</strong>. Please provide a reason — this will be saved on the payroll and surfaced on the Recovery Report.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
              <div className="flex justify-between">
                <span>Configured Room Rent:</span>
                <span className="font-semibold">AED {Number(roomRentAdjustmentData.originalRoomRent || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>This Month Deduction:</span>
                <span className="font-semibold text-orange-600">AED {Number(roomRentAdjustmentData.newRoomRent || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between border-t border-orange-200 mt-1 pt-1">
                <span>Difference:</span>
                <span className={`font-bold ${(roomRentAdjustmentData.newRoomRent - roomRentAdjustmentData.originalRoomRent) >= 0 ? 'text-red-600' : 'text-green-700'}`}>
                  {(roomRentAdjustmentData.newRoomRent - roomRentAdjustmentData.originalRoomRent) >= 0 ? '+' : ''}
                  AED {(Number(roomRentAdjustmentData.newRoomRent || 0) - Number(roomRentAdjustmentData.originalRoomRent || 0)).toFixed(2)}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="room-rent-reason" className="text-sm font-semibold">
                Reason for change <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="room-rent-reason"
                rows={3}
                placeholder="e.g. Tenant moved out on 15th, prorated for half month / Rent revised after new agreement / Damage charge added"
                value={roomRentAdjustmentData.reason}
                onChange={(e) => setRoomRentAdjustmentData({ ...roomRentAdjustmentData, reason: e.target.value })}
                data-testid="room-rent-reason-input"
              />
              <p className="text-xs text-slate-500">
                Visible on the recovery report. Minimum 5 characters.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                // Revert change
                const updated = [...wizardData.preview];
                if (roomRentAdjustmentData.idx !== null && updated[roomRentAdjustmentData.idx]) {
                  const row = updated[roomRentAdjustmentData.idx];
                  row.room_rent = roomRentAdjustmentData.originalRoomRent;
                  row.room_rent_was_adjusted = false;
                  row.room_rent_adjustment_reason = '';
                  row.total_deductions = round2((row.fines || 0) + (row.salik || 0) + (row.maintenance || 0) + (row.loan_emi || 0) + (row.prev_arrears || 0) + (row.paid_in_wallet || 0) + (row.paid_as_tips || 0) + (row.paid_as_cod || 0) + (row.misc_deduction || 0) + (row.clawback_deduction || 0) + (row.platform_deduction || 0) + (row.food_compensation || 0) + (row.room_rent || 0) + (row.slx_deduction || 0) + (row.dynamic_deductions_total || 0) + (row.salary_advance || 0));
                  row.net_salary = round2((row.gross_salary || 0) + (row.past_dues || 0) - row.total_deductions);
                  setWizardData({ ...wizardData, preview: updated });
                  toast.info('Room rent change reverted');
                }
                setRoomRentAdjustmentDialogOpen(false);
              }}
              data-testid="room-rent-cancel-btn"
            >
              Cancel & Revert
            </Button>
            <Button
              onClick={() => {
                const reason = (roomRentAdjustmentData.reason || '').trim();
                if (reason.length < 5) {
                  toast.error('Please enter a reason (at least 5 characters)');
                  return;
                }
                const updated = [...wizardData.preview];
                const i = roomRentAdjustmentData.idx;
                if (i === null || !updated[i]) {
                  setRoomRentAdjustmentDialogOpen(false);
                  return;
                }
                const row = updated[i];
                row.room_rent = Number(roomRentAdjustmentData.newRoomRent) || 0;
                row.original_room_rent = Number(roomRentAdjustmentData.originalRoomRent) || 0;
                row.room_rent_was_adjusted = true;
                row.room_rent_adjustment_reason = reason;
                row.room_rent_adjusted_by = user?.email || user?.name || '';
                row.room_rent_adjusted_at = new Date().toISOString();
                row.total_deductions = round2((row.fines || 0) + (row.salik || 0) + (row.maintenance || 0) + (row.loan_emi || 0) + (row.prev_arrears || 0) + (row.paid_in_wallet || 0) + (row.paid_as_tips || 0) + (row.paid_as_cod || 0) + (row.misc_deduction || 0) + (row.clawback_deduction || 0) + (row.platform_deduction || 0) + (row.food_compensation || 0) + (row.room_rent || 0) + (row.slx_deduction || 0) + (row.dynamic_deductions_total || 0) + (row.salary_advance || 0));
                row.net_salary = round2((row.gross_salary || 0) + (row.past_dues || 0) - row.total_deductions);
                setWizardData({ ...wizardData, preview: updated });
                setRoomRentAdjustmentDialogOpen(false);
                toast.success('Room rent updated. Reason saved.');
              }}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="room-rent-confirm-btn"
            >
              Save Adjustment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Salary Slips Viewer Dialog */}
      <Dialog open={slipsDialog.open} onOpenChange={(open) => setSlipsDialog(prev => ({ ...prev, open, search: '' }))}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-600" />
              Salary Slips
            </DialogTitle>
            <DialogDescription>
              {slipsDialog.slips.length} salary slip(s) generated
            </DialogDescription>
          </DialogHeader>
          
          {slipsDialog.loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-green-600" />
              <span className="ml-2 text-sm text-slate-500">Loading salary slips...</span>
            </div>
          ) : slipsDialog.slips.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-500">No salary slips found. Generate slips first.</p>
              <Button 
                className="mt-3 bg-blue-600 hover:bg-blue-700" 
                size="sm"
                onClick={() => { setSlipsDialog(prev => ({ ...prev, open: false })); handleGenerateBatchSalarySlips(slipsDialog.batchId); }}
              >
                Generate Salary Slips
              </Button>
            </div>
          ) : (
            <div>
              {/* Search + Bulk actions */}
              <div className="flex items-center justify-between mb-3 pb-3 border-b gap-3">
                <div className="flex items-center gap-3 flex-1">
                  <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search employee..."
                      className="w-full h-8 pl-8 pr-3 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                      value={slipsDialog.search || ''}
                      onChange={(e) => setSlipsDialog(prev => ({ ...prev, search: e.target.value }))}
                      data-testid="slip-search-input"
                    />
                  </div>
                  <span className="text-sm text-slate-600 whitespace-nowrap">
                    {(() => {
                      const q = (slipsDialog.search || '').toLowerCase();
                      const filtered = q ? slipsDialog.slips.filter(s => (s.employee_name || '').toLowerCase().includes(q) || (s.employee_unique_id || '').toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q)) : slipsDialog.slips;
                      return q ? `${filtered.length} of ${slipsDialog.slips.length}` : `${slipsDialog.slips.length} employees`;
                    })()}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="h-7 text-xs gap-1"
                    disabled={!!slipsDialog.actionLoading}
                    onClick={async () => {
                      setSlipsDialog(prev => ({ ...prev, actionLoading: 'regenerate' }));
                      document.body.classList.add('loading-cursor');
                      try {
                        const res = await axios.post(`${API}/talent/pms/batch/${slipsDialog.batchId}/generate-slips?regenerate=true`, {}, { headers: getAuthHeader() });
                        toast.success(res.data.message || 'Slips regenerated');
                        handleViewBatchSlips(slipsDialog.batchId);
                      } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                      finally { setSlipsDialog(prev => ({ ...prev, actionLoading: '' })); document.body.classList.remove('loading-cursor'); }
                    }}
                    data-testid="regenerate-slips"
                  >
                    {slipsDialog.actionLoading === 'regenerate' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    {slipsDialog.actionLoading === 'regenerate' ? 'Regenerating...' : 'Regenerate'}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="h-7 text-xs gap-1"
                    disabled={!!slipsDialog.actionLoading}
                    onClick={async () => {
                      setSlipsDialog(prev => ({ ...prev, actionLoading: 'email' }));
                      document.body.classList.add('loading-cursor');
                      try {
                        await handleEmailBatchSalarySlips(slipsDialog.batchId);
                      } finally { setSlipsDialog(prev => ({ ...prev, actionLoading: '' })); document.body.classList.remove('loading-cursor'); }
                    }}
                    data-testid="email-all-slips"
                  >
                    {slipsDialog.actionLoading === 'email' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                    {slipsDialog.actionLoading === 'email' ? 'Sending...' : 'Email All'}
                  </Button>
                </div>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Employee</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Deductions</TableHead>
                    <TableHead className="text-right">Net Salary</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="text-center whitespace-nowrap">
                      <div className="flex flex-col items-center leading-tight">
                        <span>Actions</span>
                        <span className="text-[10px] font-normal text-slate-400">Salary Slip&nbsp;·&nbsp;Loan Statement</span>
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {slipsDialog.slips
                    .filter(slip => {
                      const q = (slipsDialog.search || '').toLowerCase();
                      if (!q) return true;
                      return (slip.employee_name || '').toLowerCase().includes(q) || (slip.employee_unique_id || '').toLowerCase().includes(q) || (slip.email || '').toLowerCase().includes(q);
                    })
                    .map((slip) => (
                    <TableRow 
                      key={slip.id} 
                      className="text-sm cursor-pointer hover:bg-slate-50"
                      onClick={() => handleDownloadIndividualSlip(slip.id, slip.employee_name)}
                      data-testid={`slip-row-${slip.id}`}
                    >
                      <TableCell>
                        <p className="font-medium text-sm">{slip.employee_name}</p>
                        <p className="text-xs text-slate-500">{slip.employee_unique_id}</p>
                      </TableCell>
                      <TableCell className="text-xs font-mono text-slate-500">{slip.reference_number}</TableCell>
                      <TableCell className="text-right text-green-700"><CurrencyDisplay amount={slip.gross_earnings || 0} /></TableCell>
                      <TableCell className="text-right text-red-600"><CurrencyDisplay amount={slip.total_deductions || 0} /></TableCell>
                      <TableCell className="text-right font-semibold"><CurrencyDisplay amount={slip.net_salary || 0} /></TableCell>
                      <TableCell className="text-center">
                        {slip.salary_held ? (
                          <Button
                            size="sm" variant="outline"
                            className="h-6 px-2 text-[10px] bg-red-50 text-red-700 border-red-200 hover:bg-green-50 hover:text-green-700 hover:border-green-200"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await axios.post(`${API}/talent/pms/release-salary/${slip.payroll_record_id}`, {}, { headers: getAuthHeader() });
                                toast.success(`Salary released for ${slip.employee_name}`);
                                handleViewBatchSlips(slipsDialog.batchId);
                              } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                            }}
                            data-testid={`toggle-hold-${slip.id}`}
                          >
                            HOLD — Release
                          </Button>
                        ) : (
                          <Button
                            size="sm" variant="ghost"
                            className="h-6 px-2 text-[10px] text-slate-400 hover:bg-amber-50 hover:text-amber-700"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await axios.post(`${API}/talent/pms/hold-salary/${slip.payroll_record_id}`, {}, { headers: getAuthHeader() });
                                toast.success(`Salary held for ${slip.employee_name}`);
                                handleViewBatchSlips(slipsDialog.batchId);
                              } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                            }}
                            data-testid={`toggle-hold-${slip.id}`}
                          >
                            Hold
                          </Button>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500 max-w-[120px] truncate" title={slip.email || 'No email'}>
                        {slip.email || <span className="text-amber-500">No email</span>}
                      </TableCell>
                      <TableCell>
                        <div
                          className="flex items-stretch justify-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* ────── SALARY SLIP group ────── */}
                          <div
                            className="flex flex-col items-center rounded-md border border-green-100 bg-green-50/60 px-1.5 py-1"
                            data-testid={`slip-actions-${slip.id}`}
                          >
                            <div className="text-[9px] font-semibold uppercase tracking-wide text-green-700 leading-none mb-1">
                              Salary Slip
                            </div>
                            <div className="flex items-center gap-0.5">
                              <Button
                                size="sm" variant="ghost"
                                className="h-6 w-6 p-0 text-slate-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => handleViewIndividualSlip(slip.id)}
                                title="View Salary Slip PDF (opens in a new tab)"
                                data-testid={`view-slip-${slip.id}`}
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                size="sm" variant="ghost"
                                className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                onClick={() => handleDownloadIndividualSlip(slip.id, slip.employee_name)}
                                title="Download Salary Slip PDF"
                                data-testid={`download-slip-${slip.id}`}
                              >
                                <Download className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                size="sm" variant="ghost"
                                className="h-6 w-6 p-0 text-green-600 hover:text-green-800 hover:bg-green-100"
                                onClick={() => handleEmailIndividualSlip(slip.id)}
                                title={!slip.email ? 'No email configured' : (slip.net_salary || 0) <= 0 ? 'Cannot email: zero/negative salary' : `Email Salary Slip to ${slip.email}`}
                                disabled={!slip.email || (slip.net_salary || 0) <= 0}
                                data-testid={`email-slip-${slip.id}`}
                              >
                                <Mail className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                size="sm" variant="ghost"
                                className="h-6 w-6 p-0 hover:bg-green-100"
                                onClick={() => handleWhatsAppSalarySlip(slip.id, slip.employee_name, slip.employee_id || slip.rider_id)}
                                title="Send Salary Slip via WhatsApp"
                                disabled={(slip.net_salary || 0) <= 0}
                                data-testid={`wa-slip-${slip.id}`}
                              >
                                <WhatsAppIcon className="w-3.5 h-3.5 text-[#25D366]" />
                              </Button>
                            </div>
                          </div>

                          {/* ────── LOAN STATEMENT group ────── */}
                          <div
                            className="flex flex-col items-center rounded-md border border-amber-100 bg-amber-50/60 px-1.5 py-1"
                            data-testid={`loan-actions-${slip.id}`}
                          >
                            <div className="text-[9px] font-semibold uppercase tracking-wide text-amber-700 leading-none mb-1">
                              Loan Statement
                            </div>
                            <div className="flex items-center gap-0.5">
                              <Button
                                size="sm" variant="ghost"
                                className="h-6 w-6 p-0 text-slate-600 hover:text-amber-700 hover:bg-amber-50"
                                onClick={() => handleViewLoanStatement(slip)}
                                title="View Loan Account Statement PDF (opens in a new tab)"
                                data-testid={`view-loan-statement-${slip.id}`}
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                size="sm" variant="ghost"
                                className="h-6 w-6 p-0 text-amber-600 hover:text-amber-800 hover:bg-amber-100"
                                onClick={() => handleDownloadLoanStatement(slip)}
                                title="Download Loan Account Statement PDF (as of this month)"
                                data-testid={`loan-statement-${slip.id}`}
                              >
                                <Banknote className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                size="sm" variant="ghost"
                                className="h-6 w-6 p-0 hover:bg-amber-100"
                                onClick={() => handleWhatsAppLoanStatement(slip)}
                                title="Send Loan Statement via WhatsApp"
                                data-testid={`wa-loan-${slip.id}`}
                              >
                                <WhatsAppIcon className="w-3.5 h-3.5 text-[#25D366]" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Maintenance Detail Dialog */}
      <Dialog open={maintDetailDialog.open} onOpenChange={(open) => setMaintDetailDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-red-600" />
              Maintenance Records
            </DialogTitle>
            <DialogDescription>
              {maintDetailDialog.records.length} maintenance record(s) found for this employee
            </DialogDescription>
          </DialogHeader>
          {maintDetailDialog.loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="ml-2 text-sm text-slate-500">Loading...</span>
            </div>
          ) : maintDetailDialog.records.length === 0 ? (
            <p className="text-center text-slate-500 py-4">No maintenance records found for this payroll period.</p>
          ) : (
            <div className="space-y-4">
              {maintDetailDialog.records.map((rec) => {
                const subtotal = (rec.repair_items || []).reduce((s, it) => s + (Number(it.rate) || 0) * (Number(it.quantity) || 1), 0);
                const amcDiscount = (rec.repair_items || [])
                  .filter(it => it.amc_covered)
                  .reduce((s, it) => s + (Number(it.rate) || 0) * (Number(it.quantity) || 1), 0);
                const adminDiscount = parseFloat(rec.admin_discount_amount || 0);
                return (
                <div key={rec.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-800">{rec.description || rec.repair_type || 'Maintenance'}</p>
                      <p className="text-xs text-slate-500 mt-1">{rec.maintenance_date || rec.date || '-'} · Bike: {rec.bike_plate_number || rec.bike_plate || rec.motorcycle_plate || '-'}</p>
                      {rec.technician && <p className="text-xs text-slate-500">Technician: {rec.technician}</p>}
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <p className="text-base font-bold text-red-600">AED {parseFloat(rec.total_cost || 0).toFixed(2)}</p>
                      {adminDiscount > 0 && (
                        <p className="text-xs text-green-600">Discount applied: AED {adminDiscount.toFixed(2)}</p>
                      )}
                    </div>
                  </div>

                  {/* Repair Items & Scope */}
                  {rec.repair_items && rec.repair_items.length > 0 && (
                    <div className="bg-slate-50 rounded-md p-3 border border-slate-200">
                      <p className="text-xs font-semibold text-slate-700 mb-2">Repair Items & Scope</p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-slate-300 text-slate-500">
                              <th className="text-left py-1.5 pr-2 font-medium">Code</th>
                              <th className="text-left py-1.5 pr-2 font-medium">Item</th>
                              <th className="text-left py-1.5 pr-2 font-medium">Category</th>
                              <th className="text-center py-1.5 px-2 font-medium">Qty</th>
                              <th className="text-right py-1.5 px-2 font-medium">Rate (Dh)</th>
                              <th className="text-right py-1.5 pl-2 font-medium">Total (Dh)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rec.repair_items.map((it, ix) => (
                              <tr key={ix} className="border-b border-slate-100 last:border-0">
                                <td className="py-1.5 pr-2 text-slate-500">{it.item_code || '-'}</td>
                                <td className="py-1.5 pr-2 font-medium text-slate-800">
                                  {it.item_name}
                                  {it.amc_covered && <span className="ml-1.5 text-[10px] bg-green-100 text-green-700 px-1 py-0.5 rounded">AMC FREE</span>}
                                </td>
                                <td className="py-1.5 pr-2"><Badge variant="outline" className="text-[10px]">{it.category || '-'}</Badge></td>
                                <td className="py-1.5 px-2 text-center">{it.quantity || 1}</td>
                                <td className="py-1.5 px-2 text-right">{Number(it.rate || 0).toFixed(2)}</td>
                                <td className="py-1.5 pl-2 text-right font-semibold">
                                  {it.amc_covered ? (
                                    <span className="text-green-600 line-through">{((Number(it.rate) || 0) * (Number(it.quantity) || 1)).toFixed(2)}</span>
                                  ) : (
                                    <span className="text-slate-800">{((Number(it.rate) || 0) * (Number(it.quantity) || 1)).toFixed(2)}</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex flex-col items-end gap-0.5 text-xs mt-2 pt-2 border-t border-slate-200">
                        <div className="flex gap-6">
                          <span className="text-slate-500">Subtotal:</span>
                          <span className="font-semibold w-20 text-right">AED {subtotal.toFixed(2)}</span>
                        </div>
                        {amcDiscount > 0 && (
                          <div className="flex gap-6 text-green-600">
                            <span>AMC Free Discount:</span>
                            <span className="font-semibold w-20 text-right">− AED {amcDiscount.toFixed(2)}</span>
                          </div>
                        )}
                        {adminDiscount > 0 && (
                          <div className="flex gap-6 text-blue-600">
                            <span>Admin Discount:</span>
                            <span className="font-semibold w-20 text-right">− AED {adminDiscount.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex gap-6 pt-1 border-t border-slate-200 mt-1">
                          <span className="text-slate-700 font-semibold">Total Cost:</span>
                          <span className="font-bold w-20 text-right text-green-700">AED {parseFloat(rec.total_cost || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {rec.admin_discount_reason && (
                    <p className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded">Previous discount reason: {rec.admin_discount_reason}</p>
                  )}

                  {/* Admin Discount Controls (preset reasons + custom) */}
                  <div className="border-t pt-3 mt-1 space-y-2">
                    <p className="text-xs font-semibold text-slate-700">Apply Admin Discount</p>
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        type="number"
                        placeholder="Discount amount (Dh)"
                        className="h-9 text-sm w-44"
                        id={`maint-disc-${rec.id}`}
                        defaultValue={rec.admin_discount_amount || ''}
                      />
                      <select
                        className="h-9 text-sm rounded-md border border-slate-200 px-2 bg-white min-w-[260px]"
                        id={`maint-reason-select-${rec.id}`}
                        defaultValue={(() => {
                          const presets = [
                            'Factory defect, to be covered under warranty',
                            'Assembly defect, to be covered by Company',
                            'Defect due to non availability of spares',
                            'Maintenance caused not attributed to Rider',
                          ];
                          const cur = rec.admin_discount_reason || '';
                          if (presets.includes(cur)) return cur;
                          if (cur) return '__other__';
                          return '';
                        })()}
                        onChange={(e) => {
                          const otherInp = document.getElementById(`maint-reason-other-${rec.id}`);
                          if (otherInp) otherInp.style.display = e.target.value === '__other__' ? '' : 'none';
                        }}
                      >
                        <option value="">Select a reason…</option>
                        <option value="Factory defect, to be covered under warranty">Factory defect, to be covered under warranty</option>
                        <option value="Assembly defect, to be covered by Company">Assembly defect, to be covered by Company</option>
                        <option value="Defect due to non availability of spares">Defect due to non availability of spares</option>
                        <option value="Maintenance caused not attributed to Rider">Maintenance caused not attributed to Rider</option>
                        <option value="__other__">Other (custom reason)</option>
                      </select>
                      <Input
                        type="text"
                        placeholder="Type custom reason…"
                        className="h-9 text-sm flex-1 min-w-[200px]"
                        id={`maint-reason-other-${rec.id}`}
                        defaultValue={(() => {
                          const presets = [
                            'Factory defect, to be covered under warranty',
                            'Assembly defect, to be covered by Company',
                            'Defect due to non availability of spares',
                            'Maintenance caused not attributed to Rider',
                          ];
                          const cur = rec.admin_discount_reason || '';
                          return presets.includes(cur) ? '' : cur;
                        })()}
                        style={{
                          display: (() => {
                            const presets = [
                              'Factory defect, to be covered under warranty',
                              'Assembly defect, to be covered by Company',
                              'Defect due to non availability of spares',
                              'Maintenance caused not attributed to Rider',
                            ];
                            const cur = rec.admin_discount_reason || '';
                            return (cur && !presets.includes(cur)) ? '' : 'none';
                          })()
                        }}
                      />
                      <Button
                        size="sm"
                        className="h-9 text-xs bg-green-600 hover:bg-green-700 ml-auto"
                        onClick={async () => {
                          const discAmt = parseFloat(document.getElementById(`maint-disc-${rec.id}`).value) || 0;
                          const selVal = document.getElementById(`maint-reason-select-${rec.id}`).value;
                          const otherVal = document.getElementById(`maint-reason-other-${rec.id}`).value || '';
                          const reason = selVal === '__other__' ? otherVal : selVal;
                          if (discAmt > 0 && !reason) {
                            toast.error('Please select or type a reason for the discount');
                            return;
                          }
                          try {
                            const formData = new FormData();
                            formData.append('discount_amount', discAmt);
                            formData.append('discount_reason', reason);
                            const res = await axios.post(`${API}/talent/pms/maintenance-admin-discount/${rec.id}`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
                            toast.success(res.data.message);
                            const refreshRes = await axios.get(`${API}/talent/pms/rider-maintenance-details/${maintDetailDialog.employeeId}?month=${maintDetailDialog.month}&year=${maintDetailDialog.year}`, { headers: getAuthHeader() });
                            setMaintDetailDialog(prev => ({ ...prev, records: refreshRes.data.records || [] }));
                            const newTotal = (refreshRes.data.records || []).reduce((sum, r) => sum + parseFloat(r.total_cost || 0), 0);
                            const updated = [...wizardData.preview];
                            if (updated[maintDetailDialog.idx]) {
                              updated[maintDetailDialog.idx].maintenance = newTotal;
                              const row = updated[maintDetailDialog.idx];
                              row.total_deductions = round2((row.fines || 0) + (row.salik || 0) + (row.maintenance || 0) + (row.loan_emi || 0) + (row.prev_arrears || 0) + (row.paid_in_wallet || 0) + (row.paid_as_tips || 0) + (row.paid_as_cod || 0) + (row.misc_deduction || 0) + (row.clawback_deduction || 0) + (row.platform_deduction || 0) + (row.food_compensation || 0) + (row.room_rent || 0) + (row.slx_deduction || 0) + (row.dynamic_deductions_total || 0) + (row.salary_advance || 0));
                              row.net_salary = round2((row.gross_salary || 0) + (row.past_dues || 0) - row.total_deductions);
                              const totalGross = updated.reduce((s, r) => s + (r.gross_salary || 0), 0);
                              const totalDed = updated.reduce((s, r) => s + (r.total_deductions || 0), 0);
                              setWizardData({ ...wizardData, preview: updated, summary: { ...wizardData.summary, total_gross: totalGross, total_deductions: totalDed, total_net: totalGross - totalDed } });
                            }
                          } catch (e) { toast.error(e.response?.data?.detail || 'Failed to apply discount'); }
                        }}
                        data-testid={`apply-maint-disc-${rec.id}`}
                      >
                        Apply Discount
                      </Button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Cancel-Wizard confirmation — reverts any "Rider Pay" tags
          applied during the in-progress payroll wizard session. */}
      <Dialog open={cancelWizardConfirm} onOpenChange={setCancelWizardConfirm}>
        <DialogContent className="max-w-md" data-testid="cancel-wizard-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-700">Cancel payroll?</DialogTitle>
            <DialogDescription>
              Are you sure to cancel the payroll? All the fines shall be reverted to original status.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded p-3 space-y-1">
            {wizardPendingRiderPayFineIds.size > 0 && (
              <div>
                <strong>{wizardPendingRiderPayFineIds.size}</strong> fine(s) marked as "Rider Pays Directly" in this session will be restored to their original status.
              </div>
            )}
            {wizardPendingInstalmentPlanIds.size > 0 && (
              <div>
                <strong>{wizardPendingInstalmentPlanIds.size}</strong> instalment plan(s) created in this session will be cancelled and the parent fine(s) restored to their original status.
              </div>
            )}
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setCancelWizardConfirm(false)} data-testid="cancel-wizard-keep-btn">
              Continue payroll
            </Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700"
              onClick={confirmCancelWizard}
              data-testid="cancel-wizard-confirm-btn"
            >
              Yes, cancel & revert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Fines Detail + Split-into-Instalments Dialog */}
      <Dialog open={finesDetailDialog.open} onOpenChange={(open) => setFinesDetailDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileWarning className="w-5 h-5 text-red-600" />
              Fines — {finesDetailDialog.employeeName || 'Rider'}
              <Badge variant="outline" className="ml-2 text-xs">
                {String(finesDetailDialog.month).padStart(2, '0')}/{finesDetailDialog.year}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              {finesDetailDialog.fines.length} fine(s) for this rider in this payroll period.
              Click <b>Split into instalments</b> to convert a fine into multiple monthly recoveries.
            </DialogDescription>
          </DialogHeader>
          {finesDetailDialog.loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="ml-2 text-sm text-slate-500">Loading fines...</span>
            </div>
          ) : finesDetailDialog.fines.length === 0 ? (
            <p className="text-center text-slate-500 py-4">No fines for this rider in this period.</p>
          ) : (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left p-2">Fine No.</th>
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Reason</th>
                    <th className="text-left p-2">Bike</th>
                    <th className="text-right p-2">Amount</th>
                    <th className="text-right p-2">Admin Fee</th>
                    <th className="text-right p-2">Total</th>
                    <th className="text-center p-2">Status</th>
                    <th className="text-center p-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {finesDetailDialog.fines.map((f) => {
                    const plan = f.instalment_plan;
                    return (
                      <tr key={f.id} className="border-t">
                        <td className="p-2 font-mono text-xs">{f.fine_number || '-'}</td>
                        <td className="p-2 text-xs">{f.fine_date || '-'}</td>
                        <td className="p-2 text-xs">{f.reason || '-'}</td>
                        <td className="p-2 text-xs">{f.bike_plate_number || '-'}</td>
                        <td className="p-2 text-right">{f.amount.toFixed(2)}</td>
                        <td className="p-2 text-right">{f.admin_fees.toFixed(2)}</td>
                        <td className="p-2 text-right font-semibold">{f.total.toFixed(2)}</td>
                        <td className="p-2 text-center">
                          {plan ? (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">
                              {plan.num_instalments} instalments
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px]">{f.status || 'pending'}</Badge>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          {plan ? (
                            <Button
                              size="sm" variant="outline"
                              className="h-6 text-[10px] border-red-300 text-red-600 hover:bg-red-50"
                              onClick={async () => {
                                if (!window.confirm('Cancel this instalment plan? The fine will revert to fully due in the current month.')) return;
                                try {
                                  await axios.delete(`${API}/fines/instalments/${plan.id}`, { headers: getAuthHeader() });
                                  toast.success('Instalment plan cancelled');
                                  // refetch
                                  const res = await axios.get(`${API}/fines/by-rider-month`, {
                                    headers: getAuthHeader(),
                                    params: { rider_id: finesDetailDialog.employeeId, month: finesDetailDialog.month, year: finesDetailDialog.year, bike_plate: finesDetailDialog.bikePlate },
                                  });
                                  setFinesDetailDialog(prev => ({ ...prev, fines: res.data.fines || [] }));
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || 'Failed to cancel plan');
                                }
                              }}
                              data-testid={`cancel-fine-instalment-${f.id}`}
                            >
                              Cancel Plan
                            </Button>
                          ) : f.status === 'rider_pay' ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700 border border-blue-200"
                              title={f.rider_pay_remark || 'Rider allowed to pay by himself'}
                              data-testid={`rider-pay-badge-${f.id}`}
                            >
                              Rider Pay
                            </span>
                          ) : (
                            <div className="flex gap-1 flex-wrap justify-end">
                              <Button
                                size="sm"
                                className="h-6 text-[10px] bg-amber-500 hover:bg-amber-600"
                                onClick={() => setSplitFineDialog({ open: true, fine: f, numInstalments: 3, submitting: false })}
                                data-testid={`split-fine-${f.id}`}
                              >
                                Split into instalments
                              </Button>
                              <Button
                                size="sm"
                                className="h-6 text-[10px] bg-blue-600 hover:bg-blue-700"
                                onClick={() => setRiderPayDialog({ open: true, fine: f, remark: 'Rider allowed to pay by himself', submitting: false })}
                                data-testid={`rider-pay-${f.id}`}
                                title="Allow rider to pay this fine outside payroll"
                              >
                                Rider Pays Directly
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFinesDetailDialog(prev => ({ ...prev, open: false }))}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split-Fine Sub-dialog: pick number of instalments */}
      <Dialog open={splitFineDialog.open} onOpenChange={(open) => setSplitFineDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Split Fine into Instalments</DialogTitle>
            <DialogDescription>
              The fine will be marked as <b>Partial Recovered</b> and a monthly instalment plan will be created.
              The first instalment is deducted in <b>{String(finesDetailDialog.month).padStart(2,'0')}/{finesDetailDialog.year}</b>.
            </DialogDescription>
          </DialogHeader>
          {splitFineDialog.fine && (
            <div className="space-y-3 text-sm">
              <div className="bg-slate-50 rounded p-3 space-y-1">
                <p><span className="text-slate-500">Fine No:</span> <b className="font-mono">{splitFineDialog.fine.fine_number || '-'}</b></p>
                <p><span className="text-slate-500">Total Amount:</span> <b>AED {splitFineDialog.fine.total.toFixed(2)}</b></p>
                <p className="text-xs text-slate-500">{splitFineDialog.fine.reason || '-'}</p>
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Number of instalments (2 to 12)</label>
                <input
                  type="number" min="2" max="12"
                  className="w-full border rounded px-3 py-2"
                  value={splitFineDialog.numInstalments}
                  onChange={(e) => setSplitFineDialog(prev => ({ ...prev, numInstalments: Math.max(2, Math.min(12, parseInt(e.target.value) || 2)) }))}
                  data-testid="split-fine-num-instalments"
                />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs">
                <p className="text-blue-700">
                  <b>Preview:</b> {splitFineDialog.numInstalments} monthly instalments of <b>AED {(splitFineDialog.fine.total / splitFineDialog.numInstalments).toFixed(2)}</b> each
                  (last instalment may adjust by a few fils to absorb rounding).
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSplitFineDialog({ open: false, fine: null, numInstalments: 3, submitting: false })}>Cancel</Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600"
              disabled={splitFineDialog.submitting || !splitFineDialog.fine}
              onClick={async () => {
                if (!splitFineDialog.fine) return;
                setSplitFineDialog(prev => ({ ...prev, submitting: true }));
                try {
                  const splitRes = await axios.post(`${API}/fines/${splitFineDialog.fine.id}/split-instalments`, {
                    fine_id: splitFineDialog.fine.id,
                    num_instalments: splitFineDialog.numInstalments,
                    start_month: finesDetailDialog.month,
                    start_year: finesDetailDialog.year,
                  }, { headers: getAuthHeader() });
                  // Track this plan so it can be reverted if the user
                  // cancels the wizard before approving the payroll.
                  const newPlanId = splitRes?.data?.plan?.id;
                  if (newPlanId) {
                    setWizardPendingInstalmentPlanIds((prev) => {
                      const next = new Set(prev);
                      next.add(newPlanId);
                      return next;
                    });
                  }
                  toast.success('Fine split into instalments. Re-running preview...');
                  // Close sub-dialog and refetch the fines list inline
                  setSplitFineDialog({ open: false, fine: null, numInstalments: 3, submitting: false });
                  const res = await axios.get(`${API}/fines/by-rider-month`, {
                    headers: getAuthHeader(),
                    params: { rider_id: finesDetailDialog.employeeId, month: finesDetailDialog.month, year: finesDetailDialog.year, bike_plate: finesDetailDialog.bikePlate },
                  });
                  setFinesDetailDialog(prev => ({ ...prev, fines: res.data.fines || [] }));
                  // Hint user that wizard preview needs to be regenerated
                  toast.info('Re-run the payroll preview to see Fine Arrears reflected in the wizard.');
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Failed to split fine');
                  setSplitFineDialog(prev => ({ ...prev, submitting: false }));
                }
              }}
              data-testid="split-fine-confirm"
            >
              {splitFineDialog.submitting ? 'Saving...' : 'Create Instalment Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* "Rider Pays Directly" Sub-dialog */}
      <Dialog
        open={riderPayDialog.open}
        onOpenChange={(open) => setRiderPayDialog((prev) => ({ ...prev, open }))}
      >
        <DialogContent className="max-w-md" data-testid="rider-pay-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              Allow Rider to Pay Directly
            </DialogTitle>
            <DialogDescription>
              The rider will settle this fine outside payroll. Once marked, the fine amount
              will <strong>not</strong> be deducted from this payroll. This can only be
              reverted by a Fines-page admin (and only before the rider submits payment proof).
            </DialogDescription>
          </DialogHeader>
          {riderPayDialog.fine && (
            <div className="space-y-3 text-sm">
              <div className="bg-blue-50 border border-blue-200 rounded p-3 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-600">Fine No:</span>
                  <span className="font-mono">{riderPayDialog.fine.fine_number || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Fine Date:</span>
                  <span>{riderPayDialog.fine.fine_date || riderPayDialog.fine.date || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Amount:</span>
                  <span className="font-bold text-blue-700">
                    AED {Number(riderPayDialog.fine.amount || 0).toFixed(2)}
                  </span>
                </div>
              </div>
              <div>
                <Label htmlFor="rider-pay-remark" className="text-xs font-semibold">
                  Remark <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="rider-pay-remark"
                  rows={2}
                  value={riderPayDialog.remark}
                  onChange={(e) =>
                    setRiderPayDialog((prev) => ({ ...prev, remark: e.target.value }))
                  }
                  placeholder="Reason for allowing rider to pay directly"
                  data-testid="rider-pay-remark-input"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Stored on the fine record and shown on the salary slip remark.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setRiderPayDialog({
                  open: false,
                  fine: null,
                  remark: 'Rider allowed to pay by himself',
                  submitting: false,
                })
              }
              data-testid="rider-pay-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              disabled={riderPayDialog.submitting}
              onClick={async () => {
                if (!riderPayDialog.fine) return;
                if (!(riderPayDialog.remark || '').trim()) {
                  toast.error('Remark is required');
                  return;
                }
                try {
                  setRiderPayDialog((prev) => ({ ...prev, submitting: true }));
                  await axios.post(
                    `${API}/fines/${riderPayDialog.fine.id}/mark-rider-pay`,
                    { remark: riderPayDialog.remark.trim() },
                    { headers: getAuthHeader() },
                  );
                  // Track this fine so we can revert it if the user
                  // cancels the wizard before approving the payroll.
                  setWizardPendingRiderPayFineIds((prev) => {
                    const next = new Set(prev);
                    next.add(riderPayDialog.fine.id);
                    return next;
                  });
                  // Subtract this fine from the affected rider's row in
                  // the wizard preview so the deduction reflects
                  // immediately — no need to re-upload payout.
                  const fineAmt = (riderPayDialog.fine.amount || 0) + (riderPayDialog.fine.admin_fees || 0);
                  const targetRiderId = finesDetailDialog.employeeId;
                  if (targetRiderId && fineAmt > 0) {
                    const updated = wizardData.preview.map((p) => {
                      if ((p.rider_id || p.employee_id) !== targetRiderId) return p;
                      const newFines = round2(Math.max(0, (p.fines || 0) - fineAmt));
                      const newRiderPay = round2((p.rider_pay_fines_total || 0) + fineAmt);
                      const out = { ...p, fines: newFines, rider_pay_fines_total: newRiderPay };
                      out.total_deductions = round2((out.fines || 0) + (out.salik || 0) + (out.maintenance || 0) + (out.loan_emi || 0) + (out.prev_arrears || 0) + (out.paid_in_wallet || 0) + (out.paid_as_tips || 0) + (out.paid_as_cod || 0) + (out.misc_deduction || 0) + (out.clawback_deduction || 0) + (out.platform_deduction || 0) + (out.food_compensation || 0) + (out.room_rent || 0) + (out.slx_deduction || 0) + (out.dynamic_deductions_total || 0) + (out.salary_advance || 0));
                      out.net_salary = round2((out.gross_salary || 0) + (out.past_dues || 0) - out.total_deductions);
                      return out;
                    });
                    const totalGross = round2(updated.reduce((s, p) => s + (p.gross_salary || 0), 0));
                    const totalPastDues = round2(updated.reduce((s, p) => s + (p.past_dues || 0), 0));
                    const totalDeductions = round2(updated.reduce((s, p) => s + (p.total_deductions || 0), 0));
                    setWizardData({
                      ...wizardData,
                      preview: updated,
                      summary: {
                        ...(wizardData.summary || {}),
                        total_gross: totalGross,
                        total_past_dues: totalPastDues,
                        total_deductions: totalDeductions,
                        total_net: round2(totalGross + totalPastDues - totalDeductions),
                      },
                    });
                  }
                  toast.success('Fine tagged as Rider Pay. Deduction removed from this payroll.');
                  setRiderPayDialog({
                    open: false,
                    fine: null,
                    remark: 'Rider allowed to pay by himself',
                    submitting: false,
                  });
                  // Refetch fines so the badge updates inline
                  if (finesDetailDialog.open) {
                    const res = await axios.get(`${API}/fines/by-rider-month`, {
                      headers: getAuthHeader(),
                      params: {
                        rider_id: finesDetailDialog.employeeId,
                        month: finesDetailDialog.month,
                        year: finesDetailDialog.year,
                        bike_plate: finesDetailDialog.bikePlate,
                      },
                    });
                    setFinesDetailDialog((prev) => ({ ...prev, fines: res.data.fines || [] }));
                  }
                } catch (err) {
                  toast.error(formatErrorMessage(err, 'Failed to tag as Rider Pay'));
                  setRiderPayDialog((prev) => ({ ...prev, submitting: false }));
                }
              }}
              data-testid="rider-pay-confirm"
            >
              {riderPayDialog.submitting ? 'Saving...' : 'Confirm — Rider Pays Directly'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bike History popover — opened by clicking the Bike No. cell in the wizard */}
      <Dialog
        open={bikeHistoryDialog.open}
        onOpenChange={(open) => setBikeHistoryDialog((d) => ({ ...d, open }))}
      >
        <DialogContent className="max-w-2xl" data-testid="bike-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bike className="w-5 h-5 text-blue-600" />
              Bike Possession History
            </DialogTitle>
            <DialogDescription>
              {bikeHistoryDialog.riderName}{' '}
              <span className="text-slate-500">
                · {String(bikeHistoryDialog.month || '').padStart(2, '0')}/{bikeHistoryDialog.year}
              </span>
            </DialogDescription>
          </DialogHeader>

          {bikeHistoryDialog.loading ? (
            <div className="py-8 text-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
            </div>
          ) : bikeHistoryDialog.history.length === 0 ? (
            <div className="py-6 px-4 text-sm rounded-md bg-blue-50 border border-blue-200 text-blue-900" data-testid="bike-history-empty">
              <div className="font-semibold mb-1">
                Bike in current possession is{' '}
                <span className="font-mono">
                  {bikeHistoryDialog.currentBikePlate || '—'}
                </span>
                .
              </div>
              <div className="text-xs text-blue-800">
                No handover or takeover recorded in this month.
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="bike-history-table">
                <thead>
                  <tr className="bg-slate-50 text-slate-600 text-xs">
                    <th className="text-left py-2 px-2">#</th>
                    <th className="text-left py-2 px-2">Bike No.</th>
                    <th className="text-left py-2 px-2">Handover Date</th>
                    <th className="text-left py-2 px-2">Takeover Date</th>
                    <th className="text-right py-2 px-2">Days in Month</th>
                    <th className="text-left py-2 px-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bikeHistoryDialog.history.map((row, i) => (
                    <tr key={i} className="border-b border-slate-100" data-testid={`bike-history-row-${i}`}>
                      <td className="py-2 px-2 text-slate-500">{i + 1}</td>
                      <td className="py-2 px-2 font-mono font-semibold text-slate-800">{row.bike_plate_number}</td>
                      <td className="py-2 px-2">
                        <div>{row.handover_date || '-'}</div>
                        {row.handover_time && (
                          <div className="text-[10px] text-slate-400">{row.handover_time}</div>
                        )}
                      </td>
                      <td className="py-2 px-2">
                        {row.takeover_date ? (
                          <>
                            <div>{row.takeover_date}</div>
                            {row.takeover_time && (
                              <div className="text-[10px] text-slate-400">{row.takeover_time}</div>
                            )}
                            {row.takeover_type && (
                              <div className="text-[10px] text-amber-700 italic">{row.takeover_type}</div>
                            )}
                          </>
                        ) : (
                          <span className="text-slate-400 italic">In possession</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right font-mono">{row.days_in_month}</td>
                      <td className="py-2 px-2">
                        {row.status === 'current' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700 border border-green-200">
                            Current
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                            Returned
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setBikeHistoryDialog((d) => ({ ...d, open: false }))}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Miscellaneous Deduction Description Dialog */}
      <Dialog open={miscDeductionDialog.open} onOpenChange={(open) => {
        if (!open && miscDeductionDialog.idx !== null) {
          // Save description when closing
          const updated = [...wizardData.preview];
          if (updated[miscDeductionDialog.idx]) {
            updated[miscDeductionDialog.idx].misc_deduction_desc = miscDeductionDialog.description;
            setWizardData({ ...wizardData, preview: updated });
          }
        }
        setMiscDeductionDialog(prev => ({ ...prev, open }));
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-pink-600" />
              Miscellaneous Deduction
            </DialogTitle>
            <DialogDescription>
              Describe this deduction for {miscDeductionDialog.idx !== null ? wizardData.preview[miscDeductionDialog.idx]?.employee_name : ''} — AED {(miscDeductionDialog.amount || 0).toFixed(2)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label className="text-sm">Description *</Label>
              <Textarea
                value={miscDeductionDialog.description}
                onChange={(e) => setMiscDeductionDialog(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Uniform cost, ID replacement fee, accommodation deposit..."
                rows={3}
                data-testid="misc-deduction-desc-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              if (miscDeductionDialog.idx !== null) {
                const updated = [...wizardData.preview];
                if (updated[miscDeductionDialog.idx]) {
                  updated[miscDeductionDialog.idx].misc_deduction = 0;
                  updated[miscDeductionDialog.idx].misc_deduction_desc = '';
                  const row = updated[miscDeductionDialog.idx];
                  row.total_deductions = round2((row.fines || 0) + (row.salik || 0) + (row.maintenance || 0) + (row.loan_emi || 0) + (row.prev_arrears || 0) + (row.paid_in_wallet || 0) + (row.paid_as_tips || 0) + (row.paid_as_cod || 0) + (row.misc_deduction || 0) + (row.clawback_deduction || 0) + (row.platform_deduction || 0) + (row.food_compensation || 0) + (row.room_rent || 0) + (row.slx_deduction || 0) + (row.dynamic_deductions_total || 0) + (row.salary_advance || 0));
                  row.net_salary = round2((row.gross_salary || 0) + (row.past_dues || 0) - row.total_deductions);
                  const totalGross = updated.reduce((s, r) => s + (r.gross_salary || 0), 0);
                  const totalDed = updated.reduce((s, r) => s + (r.total_deductions || 0), 0);
                  setWizardData({ ...wizardData, preview: updated, summary: { ...wizardData.summary, total_gross: totalGross, total_deductions: totalDed, total_net: totalGross - totalDed } });
                }
              }
              setMiscDeductionDialog({ open: false, idx: null, amount: 0, description: '' });
            }} data-testid="misc-deduction-remove">Remove Deduction</Button>
            <Button onClick={() => {
              if (miscDeductionDialog.idx !== null) {
                const updated = [...wizardData.preview];
                if (updated[miscDeductionDialog.idx]) {
                  updated[miscDeductionDialog.idx].misc_deduction_desc = miscDeductionDialog.description;
                  setWizardData({ ...wizardData, preview: updated });
                }
              }
              setMiscDeductionDialog(prev => ({ ...prev, open: false }));
            }} className="bg-pink-600 hover:bg-pink-700" data-testid="misc-deduction-save">Save Description</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Past Dues Description Dialog (income side) */}
      <Dialog open={pastDuesDialog.open} onOpenChange={(open) => {
        if (!open && pastDuesDialog.idx !== null) {
          const updated = [...wizardData.preview];
          if (updated[pastDuesDialog.idx]) {
            updated[pastDuesDialog.idx].past_dues_desc = pastDuesDialog.description;
            setWizardData({ ...wizardData, preview: updated });
          }
        }
        setPastDuesDialog(prev => ({ ...prev, open }));
      }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Past Dues — Description
            </DialogTitle>
            <DialogDescription>
              Add a brief explanation for this Past Dues amount being paid to {pastDuesDialog.idx !== null ? wizardData.preview[pastDuesDialog.idx]?.employee_name : ''} — AED {(pastDuesDialog.amount || 0).toFixed(2)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label className="text-sm">Description *</Label>
              <Textarea
                value={pastDuesDialog.description}
                onChange={(e) => setPastDuesDialog(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Missed bonus from March 2026, unpaid order incentive, owed allowance..."
                rows={3}
                data-testid="past-dues-desc-input"
              />
              <p className="text-[11px] text-slate-500">
                This appears on the salary slip as a line item under earnings, with this description shown to the rider.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              if (pastDuesDialog.idx !== null) {
                const updated = [...wizardData.preview];
                if (updated[pastDuesDialog.idx]) {
                  updated[pastDuesDialog.idx].past_dues = 0;
                  updated[pastDuesDialog.idx].past_dues_desc = '';
                  const row = updated[pastDuesDialog.idx];
                  row.net_salary = round2((row.gross_salary || 0) + (row.past_dues || 0) - row.total_deductions);
                  const totalGross = updated.reduce((s, r) => s + (r.gross_salary || 0), 0);
                  const totalPastDues = updated.reduce((s, r) => s + (r.past_dues || 0), 0);
                  const totalDed = updated.reduce((s, r) => s + (r.total_deductions || 0), 0);
                  setWizardData({
                    ...wizardData,
                    preview: updated,
                    summary: { ...wizardData.summary, total_gross: totalGross, total_past_dues: totalPastDues, total_deductions: totalDed, total_net: totalGross + totalPastDues - totalDed }
                  });
                }
              }
              setPastDuesDialog({ open: false, idx: null, amount: 0, description: '' });
            }} data-testid="past-dues-remove">Remove Past Dues</Button>
            <Button
              onClick={() => {
                if (pastDuesDialog.idx !== null) {
                  const updated = [...wizardData.preview];
                  if (updated[pastDuesDialog.idx]) {
                    updated[pastDuesDialog.idx].past_dues_desc = pastDuesDialog.description;
                    setWizardData({ ...wizardData, preview: updated });
                  }
                }
                setPastDuesDialog(prev => ({ ...prev, open: false }));
              }}
              disabled={!pastDuesDialog.description.trim()}
              className="bg-green-600 hover:bg-green-700"
              data-testid="past-dues-save"
            >
              Save Description
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Recovery Dialog */}
      <Dialog open={addRecoveryDialogOpen} onOpenChange={setAddRecoveryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Recovery Entry</DialogTitle>
            <DialogDescription>Record a loan recovery for {selectedEmployee?.name}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
              <div className="space-y-2">
                <Label>Month *</Label>
                <Select
                  value={recoveryForm.month}
                  onValueChange={(value) => setRecoveryForm({ ...recoveryForm, month: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    {['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'].map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Year *</Label>
                <Input
                  type="number"
                  value={recoveryForm.year}
                  onChange={(e) => setRecoveryForm({ ...recoveryForm, year: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
              <div className="space-y-2">
                <Label>Amount (AED) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={recoveryForm.amount}
                  onChange={(e) => setRecoveryForm({ ...recoveryForm, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={recoveryForm.date}
                  onChange={(e) => setRecoveryForm({ ...recoveryForm, date: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Input
                value={recoveryForm.description}
                onChange={(e) => setRecoveryForm({ ...recoveryForm, description: e.target.value })}
                placeholder="e.g., Monthly EMI deduction"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddRecoveryDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={async () => {
                if (!recoveryForm.month || !recoveryForm.year || !recoveryForm.amount) {
                  toast.error('Please fill required fields');
                  return;
                }
                
                try {
                  setSavingRecovery(true);
                  const formData = new FormData();
                  formData.append('amount', recoveryForm.amount);
                  formData.append('month', recoveryForm.month);
                  formData.append('year', recoveryForm.year);
                  if (recoveryForm.description) formData.append('description', recoveryForm.description);
                  formData.append('date', recoveryForm.date);
                  
                  await axios.post(
                    `${API}/talent/loan-accounts/${employeeDetails?.loan_account?.rider_id || selectedEmployee?.rider_id || selectedEmployee?.id}/add-recovery`,
                    formData,
                    { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
                  );
                  
                  toast.success('Recovery entry added successfully');
                  setAddRecoveryDialogOpen(false);
                  fetchEmployeeDetails(selectedEmployee.id);
                } catch (error) {
                  toast.error(formatErrorMessage(error, 'Failed to add recovery'));
                } finally {
                  setSavingRecovery(false);
                }
              }}
              disabled={savingRecovery}
              className="bg-green-600 hover:bg-green-700"
            >
              {savingRecovery ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Recovery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set EMI Dialog */}
      <Dialog open={setEmiDialogOpen} onOpenChange={setSetEmiDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Set Agreed EMI</DialogTitle>
            <DialogDescription>Set monthly EMI for outstanding loan amount</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              <p className="text-slate-600">Outstanding Balance:</p>
              <p className="text-lg font-bold text-red-600">
                <CurrencyDisplay amount={employeeDetails?.loan_account?.current_balance || 0} />
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Monthly EMI Amount (AED)</Label>
              <Input
                type="number"
                step="0.01"
                value={agreedEmi}
                onChange={(e) => setAgreedEmi(e.target.value)}
                placeholder="0.00"
              />
            </div>
            
            {agreedEmi > 0 && employeeDetails?.loan_account?.current_balance > 0 && (
              <p className="text-sm text-purple-600">
                Estimated completion: {Math.ceil(employeeDetails.loan_account.current_balance / agreedEmi)} months
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSetEmiDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={async () => {
                try {
                  const formData = new FormData();
                  formData.append('agreed_emi', agreedEmi);
                  
                  await axios.put(
                    `${API}/talent/loan-accounts/${employeeDetails?.loan_account?.rider_id || selectedEmployee?.rider_id || selectedEmployee?.id}/set-emi`,
                    formData,
                    { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
                  );
                  
                  toast.success('EMI updated successfully');
                  setSetEmiDialogOpen(false);
                  fetchEmployeeDetails(selectedEmployee.id);
                } catch (error) {
                  toast.error('Failed to update EMI');
                }
              }}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Save EMI
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add EMI Schedule Entry Dialog */}
      <Dialog open={addEmiScheduleDialogOpen} onOpenChange={setAddEmiScheduleDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add EMI Schedule Entry</DialogTitle>
            <DialogDescription>Add a scheduled EMI payment entry</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Month *</Label>
                <Select
                  value={emiScheduleForm.month}
                  onValueChange={(value) => setEmiScheduleForm({ ...emiScheduleForm, month: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    {['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'].map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Year *</Label>
                <Input
                  type="number"
                  value={emiScheduleForm.year}
                  onChange={(e) => setEmiScheduleForm({ ...emiScheduleForm, year: parseInt(e.target.value) })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>EMI Amount *</Label>
              <Input
                type="number"
                placeholder="Enter EMI amount"
                value={emiScheduleForm.amount}
                onChange={(e) => setEmiScheduleForm({ ...emiScheduleForm, amount: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={emiScheduleForm.status}
                onValueChange={(value) => setEmiScheduleForm({ ...emiScheduleForm, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="missed">Missed</SelectItem>
                  <SelectItem value="skipped">Skipped</SelectItem>
                  <SelectItem value="rescheduled">Rescheduled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                placeholder="Optional notes"
                value={emiScheduleForm.notes}
                onChange={(e) => setEmiScheduleForm({ ...emiScheduleForm, notes: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddEmiScheduleDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={async () => {
                if (!emiScheduleForm.month || !emiScheduleForm.amount) {
                  toast.error('Please fill required fields');
                  return;
                }
                try {
                  setSavingEmiSchedule(true);
                  const formData = new FormData();
                  formData.append('month', emiScheduleForm.month);
                  formData.append('year', emiScheduleForm.year);
                  formData.append('amount', emiScheduleForm.amount);
                  formData.append('status', emiScheduleForm.status);
                  if (emiScheduleForm.notes) formData.append('notes', emiScheduleForm.notes);
                  
                  await axios.post(
                    `${API}/talent/loan-accounts/${employeeDetails?.loan_account?.rider_id || selectedEmployee?.id}/emi-schedule`,
                    formData,
                    { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
                  );
                  
                  toast.success('EMI schedule entry added');
                  setAddEmiScheduleDialogOpen(false);
                  setEmiScheduleForm({ month: '', year: new Date().getFullYear(), amount: '', status: 'pending', paid_amount: 0, paid_date: '', notes: '' });
                  fetchEmployeeDetails(selectedEmployee.id);
                } catch (error) {
                  toast.error(formatErrorMessage(error, 'Failed to add entry'));
                } finally {
                  setSavingEmiSchedule(false);
                }
              }}
              disabled={savingEmiSchedule}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {savingEmiSchedule ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit EMI Schedule Entry Dialog */}
      <Dialog open={editEmiScheduleDialogOpen} onOpenChange={setEditEmiScheduleDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit EMI Schedule Entry</DialogTitle>
            <DialogDescription>Update EMI payment details for {emiScheduleForm.month} {emiScheduleForm.year}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Month</Label>
                <Input value={emiScheduleForm.month} disabled className="bg-slate-50" />
              </div>
              <div className="space-y-2">
                <Label>Year</Label>
                <Input value={emiScheduleForm.year} disabled className="bg-slate-50" />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>EMI Amount</Label>
              <Input
                type="number"
                value={emiScheduleForm.amount}
                onChange={(e) => setEmiScheduleForm({ ...emiScheduleForm, amount: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={emiScheduleForm.status}
                onValueChange={(value) => setEmiScheduleForm({ ...emiScheduleForm, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="missed">Missed</SelectItem>
                  <SelectItem value="skipped">Skipped</SelectItem>
                  <SelectItem value="rescheduled">Rescheduled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Paid Amount</Label>
                <Input
                  type="number"
                  value={emiScheduleForm.paid_amount}
                  onChange={(e) => setEmiScheduleForm({ ...emiScheduleForm, paid_amount: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Paid Date</Label>
                <Input
                  type="date"
                  value={emiScheduleForm.paid_date}
                  onChange={(e) => setEmiScheduleForm({ ...emiScheduleForm, paid_date: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                placeholder="Optional notes"
                value={emiScheduleForm.notes}
                onChange={(e) => setEmiScheduleForm({ ...emiScheduleForm, notes: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEmiScheduleDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={async () => {
                try {
                  setSavingEmiSchedule(true);
                  const formData = new FormData();
                  formData.append('amount', emiScheduleForm.amount);
                  formData.append('status', emiScheduleForm.status);
                  formData.append('paid_amount', emiScheduleForm.paid_amount || 0);
                  if (emiScheduleForm.paid_date) formData.append('paid_date', emiScheduleForm.paid_date);
                  if (emiScheduleForm.notes) formData.append('notes', emiScheduleForm.notes);
                  
                  await axios.put(
                    `${API}/talent/loan-accounts/${employeeDetails?.loan_account?.rider_id || selectedEmployee?.id}/emi-schedule/${editingEmiEntry?.id}`,
                    formData,
                    { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
                  );
                  
                  toast.success('EMI schedule entry updated');
                  setEditEmiScheduleDialogOpen(false);
                  setEditingEmiEntry(null);
                  fetchEmployeeDetails(selectedEmployee.id);
                } catch (error) {
                  toast.error(formatErrorMessage(error, 'Failed to update entry'));
                } finally {
                  setSavingEmiSchedule(false);
                }
              }}
              disabled={savingEmiSchedule}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {savingEmiSchedule ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loan Bulk Upload Dialog */}
      <Dialog open={loanBulkUploadDialogOpen} onOpenChange={(open) => {
        setLoanBulkUploadDialogOpen(open);
        if (!open) {
          setBulkUploadPreview(null);
          setLoanBulkFile(null);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Upload Loan & Recovery Entries</DialogTitle>
            <DialogDescription>
              {bulkUploadPreview ? 'Review entries before importing' : 'Upload an Excel file to add loan and recovery entries for multiple employees'}
            </DialogDescription>
          </DialogHeader>
          
          {!bulkUploadPreview ? (
            // Step 1: File Selection
            <div className="space-y-4 py-4">
              {/* Download Template Button */}
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  className="gap-2 border-green-300 text-green-700 hover:bg-green-50"
                  onClick={async () => {
                    try {
                      const response = await axios.get(
                        `${API}/talent/loan-accounts/bulk-upload/template`,
                        { 
                          headers: getAuthHeader(),
                          responseType: 'blob'
                        }
                      );
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', 'loan_recovery_bulk_upload_template.xlsx');
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success('Template downloaded');
                    } catch (error) {
                      toast.error('Failed to download template');
                    }
                  }}
                >
                  <Download className="w-4 h-4" />
                  Download Template
                </Button>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-800 mb-2">Required Columns:</h4>
                <ul className="text-sm text-blue-700 list-disc list-inside space-y-1">
                  <li><strong>Passport No.</strong> OR <strong>Emirates ID No.</strong> - At least one required</li>
                  <li><strong>Entry Type</strong> - &quot;Loan&quot; or &quot;Recovery&quot;</li>
                  <li><strong>Loan Type</strong> - visa, insurance, driving_license, accommodation, miscellaneous (for Loan entries)</li>
                  <li><strong>Sub Type</strong> - Sub-category (auto-set to EMI for Recovery)</li>
                  <li><strong>Amount</strong> - Amount in AED</li>
                  <li><strong>Description</strong> - Additional notes (optional)</li>
                  <li><strong>Month / Year</strong> - For accommodation loans (optional)</li>
                  <li><strong>Date</strong> - Entry date (optional)</li>
                </ul>
              </div>

              <div className="space-y-2">
                <Label>Upload File</Label>
                <Input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setLoanBulkFile(e.target.files[0])}
                />
                <p className="text-xs text-slate-500">Supported formats: .xlsx, .xls, .csv</p>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setLoanBulkUploadDialogOpen(false)}>Cancel</Button>
                <Button 
                  onClick={async () => {
                    if (!loanBulkFile) {
                      toast.error('Please select a file');
                      return;
                    }
                    
                    try {
                      setPreviewingBulkUpload(true);
                      const formData = new FormData();
                      formData.append('file', loanBulkFile);
                      
                      const response = await axios.post(
                        `${API}/talent/loan-accounts/bulk-upload/preview`,
                        formData,
                        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
                      );
                      
                      setBulkUploadPreview(response.data);
                    } catch (error) {
                      toast.error(formatErrorMessage(error, 'Failed to process file'));
                    } finally {
                      setPreviewingBulkUpload(false);
                    }
                  }}
                  disabled={previewingBulkUpload || !loanBulkFile}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {previewingBulkUpload ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                  Preview Entries
                </Button>
              </DialogFooter>
            </div>
          ) : (
            // Step 2: Preview Results
            <div className="space-y-4 py-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="border bg-slate-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-slate-700">{bulkUploadPreview.total_rows}</p>
                    <p className="text-sm text-slate-500">Total Rows</p>
                  </CardContent>
                </Card>
                <Card className="border bg-green-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-green-700">{bulkUploadPreview.matched_count}</p>
                    <p className="text-sm text-green-600">Matched (Will Import)</p>
                  </CardContent>
                </Card>
                <Card className="border bg-red-50">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-red-700">{bulkUploadPreview.unmatched_count}</p>
                    <p className="text-sm text-red-600">Unmatched (Will Skip)</p>
                  </CardContent>
                </Card>
              </div>

              {/* Matched Entries */}
              {bulkUploadPreview.matched_entries.length > 0 && (
                <div>
                  <h4 className="font-medium text-green-700 mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Matched Entries ({bulkUploadPreview.matched_entries.length})
                  </h4>
                  <div className="border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-green-50">
                          <TableHead className="text-xs">Row</TableHead>
                          <TableHead className="text-xs">Employee</TableHead>
                          <TableHead className="text-xs">Passport/EID</TableHead>
                          <TableHead className="text-xs">Type</TableHead>
                          <TableHead className="text-xs">Loan Type</TableHead>
                          <TableHead className="text-xs text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bulkUploadPreview.matched_entries.map((entry, idx) => (
                          <TableRow key={idx} className="text-sm">
                            <TableCell className="py-1">{entry.row_number}</TableCell>
                            <TableCell className="py-1 font-medium">{entry.employee_name}</TableCell>
                            <TableCell className="py-1 text-xs text-slate-500">
                              {entry.passport_no || entry.emirates_id}
                            </TableCell>
                            <TableCell className="py-1">
                              <Badge className={entry.entry_type?.toLowerCase() === 'recovery' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}>
                                {entry.entry_type || 'Loan'}
                              </Badge>
                            </TableCell>
                            <TableCell className="py-1 text-xs">{entry.loan_type || '-'}</TableCell>
                            <TableCell className="py-1 text-right font-medium">AED {entry.amount?.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Unmatched Entries */}
              {bulkUploadPreview.unmatched_entries.length > 0 && (
                <div>
                  <h4 className="font-medium text-red-700 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Unmatched Entries - Will Be Skipped ({bulkUploadPreview.unmatched_entries.length})
                  </h4>
                  <div className="border border-red-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-red-50">
                          <TableHead className="text-xs">Row</TableHead>
                          <TableHead className="text-xs">Passport/EID</TableHead>
                          <TableHead className="text-xs">Type</TableHead>
                          <TableHead className="text-xs text-right">Amount</TableHead>
                          <TableHead className="text-xs">Reason</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bulkUploadPreview.unmatched_entries.map((entry, idx) => (
                          <TableRow key={idx} className="text-sm bg-red-50/50">
                            <TableCell className="py-1">{entry.row_number}</TableCell>
                            <TableCell className="py-1 text-xs">
                              {entry.passport_no || entry.emirates_id || 'N/A'}
                            </TableCell>
                            <TableCell className="py-1">
                              <Badge variant="outline">{entry.entry_type || 'Loan'}</Badge>
                            </TableCell>
                            <TableCell className="py-1 text-right">AED {entry.amount?.toLocaleString() || 0}</TableCell>
                            <TableCell className="py-1 text-xs text-red-600">{entry.error}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => {
                  setBulkUploadPreview(null);
                  setLoanBulkFile(null);
                }}>
                  Back to Upload
                </Button>
                <Button 
                  onClick={async () => {
                    try {
                      setUploadingLoanBulk(true);
                      const formData = new FormData();
                      formData.append('file', loanBulkFile);
                      
                      const response = await axios.post(
                        `${API}/talent/loan-accounts/bulk-upload`,
                        formData,
                        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
                      );
                      
                      const { success_count, failed_count, loan_entries_created, recovery_entries_created } = response.data;
                      
                      if (success_count > 0) {
                        toast.success(`Successfully imported: ${loan_entries_created || 0} loan entries, ${recovery_entries_created || 0} recovery entries`);
                      }
                      if (failed_count > 0) {
                        toast.warning(`${failed_count} entries were skipped`);
                      }
                      
                      setLoanBulkUploadDialogOpen(false);
                      setBulkUploadPreview(null);
                      setLoanBulkFile(null);
                      fetchEmployees();
                      fetchLoanSummary(); // Refresh the summary dashboard
                    } catch (error) {
                      toast.error(formatErrorMessage(error, 'Failed to upload file'));
                    } finally {
                      setUploadingLoanBulk(false);
                    }
                  }}
                  disabled={uploadingLoanBulk || !bulkUploadPreview?.can_proceed}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {uploadingLoanBulk ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Confirm Import ({bulkUploadPreview.matched_count} entries)
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Loan Summary Detail Dialog */}
      <Dialog open={loanSummaryDialogOpen} onOpenChange={setLoanSummaryDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {loanSummaryDialogType === 'loans' && <><DollarSign className="w-5 h-5 text-blue-600" /> All Loan Transactions</>}
              {loanSummaryDialogType === 'recoveries' && <><CheckCircle2 className="w-5 h-5 text-green-600" /> All Recovery Transactions</>}
              {loanSummaryDialogType === 'outstanding' && <><AlertCircle className="w-5 h-5 text-red-600" /> Outstanding by Employee</>}
              {loanSummaryDialogType === 'emi' && <><Calendar className="w-5 h-5 text-purple-600" /> Scheduled EMIs (Month-wise)</>}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {/* Loan Transactions */}
            {loanSummaryDialogType === 'loans' && loanSummary && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-slate-600">
                    Total: <span className="font-bold text-blue-600"><CurrencyDisplay amount={loanSummary.summary?.total_loan_issued || 0} /></span>
                    {' '}({loanSummary.loan_entries?.length || 0} entries)
                  </p>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-blue-50">
                        <TableHead className="font-bold">Date</TableHead>
                        <TableHead className="font-bold">Employee</TableHead>
                        <TableHead className="font-bold">Type</TableHead>
                        <TableHead className="font-bold">Sub Type</TableHead>
                        <TableHead className="font-bold text-right">Amount</TableHead>
                        <TableHead className="font-bold">Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(loanSummary.loan_entries || []).map((entry, idx) => (
                        <TableRow key={entry.id || idx} className="hover:bg-blue-50/50">
                          <TableCell className="text-sm">{formatDateDisplay(entry.date) || '-'}</TableCell>
                          <TableCell className="font-medium">{entry.employee_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{entry.loan_type?.replace('_', ' ')}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">{entry.sub_type || '-'}</TableCell>
                          <TableCell className="text-right font-semibold text-blue-600">
                            <CurrencyDisplay amount={entry.amount || 0} />
                          </TableCell>
                          <TableCell className="text-sm text-slate-500 max-w-[200px] truncate">{entry.description || '-'}</TableCell>
                        </TableRow>
                      ))}
                      {(!loanSummary.loan_entries || loanSummary.loan_entries.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-slate-500 py-8">No loan entries found</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            
            {/* Recovery Transactions */}
            {loanSummaryDialogType === 'recoveries' && loanSummary && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-slate-600">
                    Total: <span className="font-bold text-green-600"><CurrencyDisplay amount={loanSummary.summary?.total_recovery || 0} /></span>
                    {' '}({loanSummary.recovery_entries?.length || 0} entries)
                  </p>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-green-50">
                        <TableHead className="font-bold">Date</TableHead>
                        <TableHead className="font-bold">Employee</TableHead>
                        <TableHead className="font-bold text-right">Amount</TableHead>
                        <TableHead className="font-bold">Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(loanSummary.recovery_entries || []).map((entry, idx) => (
                        <TableRow key={entry.id || idx} className="hover:bg-green-50/50">
                          <TableCell className="text-sm">{formatDateDisplay(entry.date) || '-'}</TableCell>
                          <TableCell className="font-medium">{entry.employee_name}</TableCell>
                          <TableCell className="text-right font-semibold text-green-600">
                            <CurrencyDisplay amount={entry.amount || 0} />
                          </TableCell>
                          <TableCell className="text-sm text-slate-500 max-w-[300px] truncate">{entry.description || '-'}</TableCell>
                        </TableRow>
                      ))}
                      {(!loanSummary.recovery_entries || loanSummary.recovery_entries.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-slate-500 py-8">No recovery entries found</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            
            {/* Outstanding by Employee */}
            {loanSummaryDialogType === 'outstanding' && loanSummary && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-slate-600">
                    Total Outstanding: <span className="font-bold text-red-600"><CurrencyDisplay amount={loanSummary.summary?.total_outstanding || 0} /></span>
                    {' '}({loanSummary.employees_with_outstanding?.length || 0} employees)
                  </p>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-red-50">
                        <TableHead className="font-bold">Employee</TableHead>
                        <TableHead className="font-bold text-right">Total Loan</TableHead>
                        <TableHead className="font-bold text-right">Total Recovery</TableHead>
                        <TableHead className="font-bold text-right">Outstanding</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(loanSummary.employees_with_outstanding || []).map((emp, idx) => (
                        <TableRow key={emp.employee_id || idx} className="hover:bg-red-50/50">
                          <TableCell className="font-medium">{emp.employee_name}</TableCell>
                          <TableCell className="text-right text-slate-600">
                            <CurrencyDisplay amount={emp.total_loan || 0} />
                          </TableCell>
                          <TableCell className="text-right text-green-600">
                            <CurrencyDisplay amount={emp.total_recovery || 0} />
                          </TableCell>
                          <TableCell className="text-right font-bold text-red-600">
                            <CurrencyDisplay amount={emp.outstanding || 0} />
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!loanSummary.employees_with_outstanding || loanSummary.employees_with_outstanding.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-slate-500 py-8">No outstanding balances</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            
            {/* Scheduled EMIs by Month */}
            {loanSummaryDialogType === 'emi' && loanSummary && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-slate-600">
                    Total Monthly EMI: <span className="font-bold text-purple-600"><CurrencyDisplay amount={loanSummary.summary?.total_agreed_emi || 0} /></span>
                    {' '}({loanSummary.employees_with_emi?.length || 0} employees)
                  </p>
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-purple-50">
                        <TableHead className="font-bold">Month</TableHead>
                        <TableHead className="font-bold text-right">Total EMI Due</TableHead>
                        <TableHead className="font-bold text-right">Employees</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(loanSummary.monthly_emi_schedule || []).map((month, idx) => (
                        <TableRow key={month.month_key || idx} className={`hover:bg-purple-50/50 ${idx === 0 ? 'bg-purple-100/50' : ''}`}>
                          <TableCell className="font-medium">
                            {month.month_label}
                            {idx === 0 && <Badge className="ml-2 bg-purple-600">Current</Badge>}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-purple-600">
                            <CurrencyDisplay amount={month.total_emi || 0} />
                          </TableCell>
                          <TableCell className="text-right text-slate-600">
                            {month.employee_count || 0}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!loanSummary.monthly_emi_schedule || loanSummary.monthly_emi_schedule.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-slate-500 py-8">No scheduled EMIs</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Summary note */}
                <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
                  <p><strong>Note:</strong> This schedule projects EMI payments based on current agreed EMIs and outstanding balances. 
                  EMI amounts decrease as balances are paid off.</p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoanSummaryDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rate Settings Dialog */}
      <Dialog open={ratesDialogOpen} onOpenChange={setRatesDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Platform Rate Settings</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Talabat Rates</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rider Type</TableHead>
                      <TableHead>Pickup Rate</TableHead>
                      <TableHead>Dropoff Rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {['EV', 'Motorbike', 'Bicyclist'].map(type => {
                      const rate = platformRates.find(r => r.platform === 'Talabat' && r.rider_type === type) || {};
                      return (
                        <TableRow key={type}>
                          <TableCell className="font-medium">{type}</TableCell>
                          <TableCell>{rate.pickup_rate || (type === 'Bicyclist' ? 3 : 2.5)}</TableCell>
                          <TableCell>{rate.dropoff_rate || (type === 'Bicyclist' ? 2 : 5)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRatesDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payroll Detail Dialog */}
      <Dialog open={payrollDetailDialogOpen} onOpenChange={setPayrollDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payroll Details - {selectedPayroll?.rider_name}</DialogTitle>
          </DialogHeader>
          
          {selectedPayroll && (
            <div className="space-y-6 py-4">
              <Card className="bg-green-50 border-green-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-green-800">
                    Total Earnings: AED {selectedPayroll.gross_earnings?.toLocaleString()}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>Base Salary: AED {selectedPayroll.base_salary?.toLocaleString()}</div>
                    <div>Incentives: AED {selectedPayroll.incentives_distance_total?.toLocaleString()}</div>
                    <div>Wallet: AED {selectedPayroll.paid_in_wallet_total?.toLocaleString()}</div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-red-50 border-red-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-red-800">
                    Total Deductions: AED {selectedPayroll.total_deductions?.toLocaleString()}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>EMI: AED {selectedPayroll.emi_deduction?.toLocaleString()}</div>
                    <div>Fines: AED {selectedPayroll.fine_deduction?.toLocaleString()}</div>
                    <div>SALIK: AED {selectedPayroll.salik_deduction?.toLocaleString()}</div>
                    <div>Maintenance: AED {selectedPayroll.maintenance_deduction?.toLocaleString()}</div>
                    <div>Platform: AED {selectedPayroll.platform_deductions_total?.toLocaleString()}</div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4 text-center">
                  <p className="text-slate-600 mb-1">Net Salary</p>
                  <p className="text-3xl font-bold text-blue-800">
                    AED {selectedPayroll.net_salary?.toLocaleString()}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPayrollDetailDialogOpen(false)}>Close</Button>
            {selectedPayroll?.status === 'approved' && (
              <Button onClick={() => handleGenerateSalarySlip(selectedPayroll.id)} className="gap-2">
                <Receipt className="w-4 h-4" />
                Generate Salary Slip
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff Payroll Generation Dialog */}
      <Dialog open={generateStaffPayrollDialogOpen} onOpenChange={setGenerateStaffPayrollDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generate Staff Payroll</DialogTitle>
            <DialogDescription>
              Generate monthly payroll for all active staff based on attendance
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
              <div className="space-y-2">
                <Label>Month</Label>
                <Select 
                  value={payrollFilter.month.toString()} 
                  onValueChange={(v) => setPayrollFilter({...payrollFilter, month: parseInt(v)})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[...Array(12)].map((_, i) => (
                      <SelectItem key={i+1} value={(i+1).toString()}>
                        {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Year</Label>
                <Select 
                  value={payrollFilter.year.toString()} 
                  onValueChange={(v) => setPayrollFilter({...payrollFilter, year: parseInt(v)})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-sm text-slate-500">
              This will calculate pro-rata salary for all active staff based on their attendance records.
              Working days are calculated excluding Fridays.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateStaffPayrollDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleGenerateStaffPayroll} disabled={staffPayrollLoading}>
              {staffPayrollLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Employee — warning dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(o) => { if (!o) { setDeleteDialogOpen(false); setDeleteImpact(null); } }}>
        <DialogContent className="max-w-lg" data-testid="delete-employee-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" /> Delete {deleteImpact?.employee?.name || 'Employee'}?
            </DialogTitle>
            <DialogDescription>
              {deleteImpact?.employee?.employee_unique_id} · {deleteImpact?.employee?.platform || '—'} · {deleteImpact?.employee?.lessee_name || '—'}
            </DialogDescription>
          </DialogHeader>

          {deleteImpact && (
            <div className="space-y-3 py-2 text-sm">
              {/* Loan link callout */}
              {deleteImpact.impact?.loan?.loan_account_id ? (
                <div className={`rounded p-3 border ${deleteImpact.impact.loan.this_entry_owns_loan_link ? 'bg-red-50 border-red-200 text-red-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
                  <p className="font-semibold mb-1">
                    {deleteImpact.impact.loan.this_entry_owns_loan_link
                      ? "⚠ This entry holds the rider's loan account link."
                      : 'ℹ This entry is NOT linked to the loan account.'}
                  </p>
                  <p className="text-xs">
                    {deleteImpact.impact.loan.this_entry_owns_loan_link
                      ? <>Outstanding: AED {Number(deleteImpact.impact.loan.outstanding).toLocaleString()} · Disbursed: AED {Number(deleteImpact.impact.loan.total_disbursed).toLocaleString()} · Recovered: AED {Number(deleteImpact.impact.loan.total_recovered).toLocaleString()}</>
                      : <>Loan stays attached to the sibling entry · Outstanding there: AED {Number(deleteImpact.impact.loan.outstanding).toLocaleString()} (not affected by this delete)</>
                    }
                  </p>
                  {deleteImpact.impact.loan.this_entry_owns_loan_link && (
                    <p className="text-xs mt-1">
                      Deleting this row will detach the loan link. If there&apos;s a duplicate entry for the same person, prefer deleting the one without the loan link first.
                    </p>
                  )}
                </div>
              ) : null}

              {/* Outstanding items grid */}
              <div className="border rounded">
                <div className="px-3 py-2 bg-slate-50 border-b text-xs font-semibold text-slate-700">Outstanding balances</div>
                <table className="w-full text-xs">
                  <tbody>
                    <tr className="border-b">
                      <td className="px-3 py-2">Open fines</td>
                      <td className={`px-3 py-2 text-right font-mono ${deleteImpact.impact.fines.outstanding > 0 ? 'text-red-700 font-bold' : 'text-slate-500'}`}>
                        {deleteImpact.impact.fines.open_count} · AED {Number(deleteImpact.impact.fines.outstanding).toLocaleString()}
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-3 py-2">Unpaid SALIK</td>
                      <td className={`px-3 py-2 text-right font-mono ${deleteImpact.impact.salik.outstanding > 0 ? 'text-red-700 font-bold' : 'text-slate-500'}`}>
                        {deleteImpact.impact.salik.open_count} · AED {Number(deleteImpact.impact.salik.outstanding).toLocaleString()}
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-3 py-2">Open maintenance work orders</td>
                      <td className={`px-3 py-2 text-right font-mono ${deleteImpact.impact.maintenance.open_count > 0 ? 'text-red-700 font-bold' : 'text-slate-500'}`}>
                        {deleteImpact.impact.maintenance.open_count}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-3 py-2">Petty cash net payable</td>
                      <td className={`px-3 py-2 text-right font-mono ${deleteImpact.impact.petty_cash.net > 0 ? 'text-red-700 font-bold' : 'text-slate-500'}`}>
                        AED {Number(deleteImpact.impact.petty_cash.net).toLocaleString()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {!deleteImpact.impact.can_delete_safely && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
                  <p className="font-semibold">This employee has outstanding items.</p>
                  <ul className="list-disc list-inside mt-1 space-y-0.5">
                    {deleteImpact.impact.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                  <p className="mt-2">Clear or transfer these records before deleting, or use <strong>Force Delete</strong> if you accept the risk (an audit log entry is recorded).</p>
                </div>
              )}
              {deleteImpact.impact.can_delete_safely && (
                <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded p-2">
                  ✓ No outstanding records — safe to delete.
                </p>
              )}
            </div>
          )}

          {mergeCandidates.length > 0 && (
            <div className="border-t pt-3 mt-2 space-y-2" data-testid="merge-section">
              <p className="text-xs font-semibold text-slate-700">Or merge into an existing entry for the same person</p>
              <p className="text-[11px] text-slate-500">
                Moves linked Motorcycles, Fines, SALIK, Maintenance, Petty Cash, Payrolls and Salary Slips to the target,
                then deletes this duplicate. Loan account is handed over only if the target doesn&apos;t already have one.
              </p>
              <div className="flex gap-2">
                <select
                  value={mergeTargetId}
                  onChange={(e) => setMergeTargetId(e.target.value)}
                  className="flex-1 h-9 rounded border border-slate-300 px-2 text-xs"
                  data-testid="merge-target-select"
                >
                  <option value="">Choose target entry…</option>
                  {mergeCandidates.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.rider_unique_id || c.employee_unique_id || c.id} · {c.name || c.rider_name} · {c.platform || '—'}
                      {c.loan_account_id ? ' · (has loan)' : ''}
                    </option>
                  ))}
                </select>
                <Button
                  onClick={confirmMergeEmployee}
                  disabled={!mergeTargetId || mergeInFlight}
                  data-testid="merge-employee-confirm"
                >
                  {mergeInFlight ? 'Merging…' : 'Merge & Delete'}
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteDialogOpen(false); setDeleteImpact(null); setMergeCandidates([]); setMergeTargetId(''); }}>Cancel</Button>
            {deleteImpact?.impact?.can_delete_safely ? (
              <Button variant="destructive" onClick={() => confirmDeleteEmployee(false)} data-testid="delete-employee-confirm">
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete
              </Button>
            ) : (
              <Button variant="destructive" onClick={() => confirmDeleteEmployee(true)} data-testid="delete-employee-force">
                <Trash2 className="w-4 h-4 mr-1.5" /> Force Delete
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Account Statement Dialog */}
      <Dialog open={accountStatementDialogOpen} onOpenChange={setAccountStatementDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Account Statement - {selectedEmployee?.name}</DialogTitle>
            <DialogDescription>Complete financial summary and transaction history</DialogDescription>
          </DialogHeader>
          
          {accountStatement && (
            <div className="space-y-6 py-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-3">
                    <p className="text-xs text-green-600">Total Earnings</p>
                    <p className="text-lg font-bold text-green-800">
                      AED {accountStatement.summary?.total_earnings?.toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-3">
                    <p className="text-xs text-red-600">Total Deductions</p>
                    <p className="text-lg font-bold text-red-800">
                      AED {accountStatement.summary?.total_deductions?.toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-3">
                    <p className="text-xs text-blue-600">Net Paid</p>
                    <p className="text-lg font-bold text-blue-800">
                      AED {accountStatement.summary?.total_net_paid?.toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-3">
                    <p className="text-xs text-amber-600">Loan Outstanding</p>
                    <p className="text-lg font-bold text-amber-800">
                      AED {accountStatement.summary?.loan_outstanding?.toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Loan Progress */}
              {accountStatement.loan_account && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      Loan Account
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span>Contract Loan: AED {accountStatement.loan_account.initial_loan_amount?.toLocaleString()}</span>
                        <span>Outstanding: AED {accountStatement.loan_account.current_balance?.toLocaleString()}</span>
                      </div>
                      <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-green-500 rounded-full transition-all"
                          style={{ 
                            width: `${Math.max(0, Math.min(100, 100 - (accountStatement.loan_account.current_balance / accountStatement.loan_account.initial_loan_amount * 100)))}%` 
                          }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-slate-500">
                        <span>Paid: AED {(accountStatement.loan_account.initial_loan_amount - accountStatement.loan_account.current_balance)?.toLocaleString()}</span>
                        <span>Monthly EMI: AED {accountStatement.loan_account.monthly_emi?.toLocaleString()}</span>
                      </div>

                      {/* Effective EMI with active adjustments */}
                      {accountStatement.loan_account.emi_adjustments?.filter(a => a.remaining_months > 0).length > 0 && (
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mt-2">
                          <p className="text-xs font-semibold text-orange-700 mb-1">Active EMI Adjustments</p>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Base EMI:</span>
                            <span>AED {accountStatement.loan_account.monthly_emi?.toLocaleString()}</span>
                          </div>
                          {accountStatement.loan_account.emi_adjustments.filter(a => a.remaining_months > 0).map((adj, i) => (
                            <div key={adj.id || i} className="flex justify-between text-sm">
                              <span className="text-orange-600">
                                + {adj.type === 'emi_pause' ? 'Paused EMI' : adj.type === 'emi_partial' ? 'Partial EMI' : 'Loan Top-up'} recovery
                                {adj.recovery_method === 'installments' ? ` (${adj.remaining_months}/${adj.installment_count} left)` : ''}
                              </span>
                              <span className="text-orange-700 font-medium">AED {adj.monthly_addon?.toFixed(2)}</span>
                            </div>
                          ))}
                          <div className="flex justify-between text-sm font-bold border-t border-orange-200 mt-1 pt-1">
                            <span>Effective EMI (next month):</span>
                            <span className="text-orange-800">AED {(
                              (accountStatement.loan_account.monthly_emi || 0) + 
                              accountStatement.loan_account.emi_adjustments.filter(a => a.remaining_months > 0).reduce((s, a) => s + (a.monthly_addon || 0), 0)
                            ).toLocaleString()}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* EMI Adjustment History */}
              {accountStatement.loan_account?.emi_adjustments?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-orange-500" />
                      EMI Adjustments History
                    </CardTitle>
                    <CardDescription className="text-xs">Paused EMIs, partial recoveries, and loan top-up adjustments</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Type</TableHead>
                          <TableHead>Reason / Detail</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Recovery</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {accountStatement.loan_account.emi_adjustments.map((adj, i) => (
                          <TableRow key={adj.id || i}>
                            <TableCell>
                              <Badge className={adj.type === 'loan_topup' ? 'bg-blue-100 text-blue-800' : adj.type === 'emi_pause' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'}>
                                {adj.type === 'emi_pause' ? 'EMI Paused' : adj.type === 'emi_partial' ? 'Partial EMI' : 'Loan Top-up'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">
                              {adj.reason || adj.payroll_month ? `${adj.reason || ''} ${adj.payroll_month ? `(${adj.payroll_month})` : ''}`.trim() : '-'}
                            </TableCell>
                            <TableCell className="text-right font-medium">AED {adj.total_amount?.toFixed(2)}</TableCell>
                            <TableCell className="text-sm">
                              {adj.recovery_method === 'next_emi' && 'Full in next EMI'}
                              {adj.recovery_method === 'installments' && `${adj.installment_count} installments (AED ${adj.monthly_addon?.toFixed(2)}/mo)`}
                              {adj.recovery_method === 'extend' && `Extended ${adj.extended_months || adj.installment_count} month(s)`}
                            </TableCell>
                            <TableCell>
                              {adj.remaining_months > 0 ? (
                                <Badge className="bg-amber-100 text-amber-800">{adj.remaining_months} month(s) left</Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-800">Completed</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-slate-500">{adj.created_at ? new Date(adj.created_at).toLocaleDateString('en-GB') : '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Expense Entries */}
              {accountStatement.loan_account?.loan_entries?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="w-4 h-4 text-amber-500" />
                      Expense Entries
                    </CardTitle>
                    <CardDescription className="text-xs">All expenses incurred for this employee</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {accountStatement.loan_account.loan_entries.map((entry, i) => (
                          <TableRow key={entry.id || i}>
                            <TableCell className="text-sm">{entry.date || '-'}</TableCell>
                            <TableCell className="text-sm">{entry.description || entry.sub_type || '-'}</TableCell>
                            <TableCell><Badge variant="outline">{entry.loan_type || entry.type || 'Expense'}</Badge></TableCell>
                            <TableCell className="text-right font-medium text-amber-600">AED {(entry.amount || 0).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-slate-50 font-bold">
                          <TableCell colSpan={3} className="text-right">Total Expenses</TableCell>
                          <TableCell className="text-right text-amber-700">
                            AED {accountStatement.loan_account.loan_entries.reduce((s, e) => s + (e.amount || 0), 0).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Recovery Entries */}
              {accountStatement.loan_account?.recoveries?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileCheck className="w-4 h-4 text-green-500" />
                      Recovery Entries
                    </CardTitle>
                    <CardDescription className="text-xs">EMI deductions and manual recoveries</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Period</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {accountStatement.loan_account.recoveries.map((rec, i) => (
                          <TableRow key={rec.id || i}>
                            <TableCell className="text-sm">{rec.date || '-'}</TableCell>
                            <TableCell className="text-sm">{rec.month ? `${rec.month}/${rec.year}` : '-'}</TableCell>
                            <TableCell className="text-sm">{rec.description || 'EMI Recovery'}</TableCell>
                            <TableCell className="text-right font-medium text-green-600">AED {(rec.amount || 0).toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-slate-50 font-bold">
                          <TableCell colSpan={3} className="text-right">Total Recovered</TableCell>
                          <TableCell className="text-right text-green-700">
                            AED {accountStatement.loan_account.recoveries.reduce((s, r) => s + (r.amount || 0), 0).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Petty Cash Transactions (Paid to / Received from this rider/staff) */}
              {accountStatement.petty_cash?.length > 0 && (
                <Card data-testid="petty-cash-section">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-indigo-500" />
                      Petty Cash Transactions
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Cash paid to / received from this employee via the Accounts → Petty Cash ledger.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Entry #</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead>Remarks</TableHead>
                          <TableHead className="text-right">Received</TableHead>
                          <TableHead className="text-right">Paid</TableHead>
                          <TableHead className="text-center">Receipt</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {accountStatement.petty_cash.map(p => (
                          <TableRow key={p.id} className="text-sm" data-testid={`petty-cash-row-${p.id}`}>
                            <TableCell>{p.entry_date || '-'}</TableCell>
                            <TableCell className="font-mono text-xs">{p.entry_no || '-'}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={p.entry_type === 'paid' ? 'bg-red-50 text-red-700 border-red-300' : 'bg-green-50 text-green-700 border-green-300'}>
                                {p.entry_type === 'paid' ? 'Paid' : 'Received'}
                              </Badge>
                            </TableCell>
                            <TableCell>{p.reason_label || p.reason}</TableCell>
                            <TableCell className="max-w-[260px] truncate text-slate-600" title={p.remarks || ''}>{p.remarks || '—'}</TableCell>
                            <TableCell className="text-right text-green-700 font-medium">{p.entry_type === 'received' ? `AED ${Number(p.amount || 0).toLocaleString()}` : '—'}</TableCell>
                            <TableCell className="text-right text-red-600 font-medium">{p.entry_type === 'paid' ? `AED ${Number(p.amount || 0).toLocaleString()}` : '—'}</TableCell>
                            <TableCell className="text-center">
                              {p.receipt_url ? (
                                <a href={p.receipt_url} target="_blank" rel="noreferrer" className="text-blue-600 underline text-xs">View</a>
                              ) : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-slate-50 font-bold">
                          <TableCell colSpan={5} className="text-right">Petty Cash Net (Paid − Received)</TableCell>
                          <TableCell className="text-right text-green-700">AED {(accountStatement.summary?.petty_cash_received || 0).toLocaleString()}</TableCell>
                          <TableCell className="text-right text-red-700">AED {(accountStatement.summary?.petty_cash_paid || 0).toLocaleString()}</TableCell>
                          <TableCell className="text-center text-slate-700">
                            Net: AED {(accountStatement.summary?.petty_cash_net || 0).toLocaleString()}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Recent Payrolls */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Payroll History</CardTitle>
                  <CardDescription className="text-xs">Click on any row to view and download salary slip</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Period</TableHead>
                        <TableHead className="text-right">Gross</TableHead>
                        <TableHead className="text-right">Deductions</TableHead>
                        <TableHead className="text-right">Net</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-center">Salary Slip</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {accountStatement.payrolls?.map(payroll => {
                        const slip = accountStatement.salary_slips?.find(s => s.payroll_id === payroll.id);
                        return (
                          <TableRow 
                            key={payroll.id}
                            className="hover:bg-blue-50 cursor-pointer transition-colors"
                            onClick={() => {
                              if (slip) {
                                setSelectedSalarySlip(slip);
                                setSalarySlipDialogOpen(true);
                              } else {
                                // Create a temporary slip from payroll data
                                setSelectedSalarySlip({
                                  reference_number: `Payroll-${payroll.month}/${payroll.year}`,
                                  employee_name: payroll.rider_name,
                                  employee_unique_id: payroll.rider_unique_id,
                                  emirates_id: selectedEmployee?.emirates_id || '',
                                  period_start: payroll.period_start,
                                  period_end: payroll.period_end,
                                  month: payroll.month,
                                  year: payroll.year,
                                  basic_salary: payroll.base_salary,
                                  overtime_pay: payroll.overtime_pay || 0,
                                  incentives: payroll.incentives_distance_total,
                                  other_earnings: payroll.tips || 0,
                                  gross_earnings: payroll.gross_earnings,
                                  loan_emi: payroll.emi_deduction,
                                  fines: payroll.fine_deduction,
                                  salik: payroll.salik_deduction,
                                  maintenance: payroll.maintenance_deduction,
                                  other_deductions: payroll.miscellaneous_deduction || 0,
                                  total_deductions: payroll.total_deductions,
                                  net_salary: payroll.net_salary,
                                  payroll_id: payroll.id
                                });
                                setSalarySlipDialogOpen(true);
                              }
                            }}
                          >
                            <TableCell className="font-medium">
                              {payroll.month}/{payroll.year}
                              {payroll.emi_was_adjusted && (
                                <span className="ml-1 text-[10px] text-orange-600 bg-orange-50 px-1 py-0.5 rounded" title={`EMI adjusted: ${payroll.emi_recovery_method === 'next_emi' ? 'Recovery next month' : payroll.emi_recovery_method === 'installments' ? 'Installments' : 'Extended'}`}>
                                  EMI Adjusted
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right text-green-600">AED {payroll.gross_earnings?.toLocaleString()}</TableCell>
                            <TableCell className="text-right text-red-600">AED {payroll.total_deductions?.toLocaleString()}</TableCell>
                            <TableCell className="text-right font-bold">AED {payroll.net_salary?.toLocaleString()}</TableCell>
                            <TableCell><Badge className={getStatusBadge(payroll.status)}>{payroll.status}</Badge></TableCell>
                            <TableCell className="text-center">
                              {slip ? (
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="h-7 px-2 text-xs gap-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedSalarySlip(slip);
                                    setSalarySlipDialogOpen(true);
                                  }}
                                >
                                  <Download className="w-3 h-3" />
                                  View
                                </Button>
                              ) : (
                                <span className="text-xs text-slate-400">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAccountStatementDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Salary Slip View Dialog */}
      <Dialog open={salarySlipDialogOpen} onOpenChange={setSalarySlipDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {/* Header with Logo */}
          <div className="border-b-4 pb-4 mb-4" style={{ borderColor: '#4CAF50' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img 
                  src="/customer-assets/Ezy_Mobility_Logo_New.png" 
                  alt="EZY Mobility" 
                  className="h-16 w-auto"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div>
                  <h2 className="text-xl font-bold" style={{ color: '#2E7D32' }}>
                    {selectedEmployee?.lessee_name || selectedEmployee?.employer_name || 'EZY Mobility FZ LLC'}
                  </h2>
                  <p className="text-sm" style={{ color: '#4CAF50' }}>Powered by EZY Mobility</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold" style={{ color: '#2E7D32' }}>{selectedSalarySlip?.reference_number}</p>
                <p className="text-xs text-slate-500">Dubai, UAE</p>
              </div>
            </div>
          </div>

          {/* Title Banner */}
          <div className="text-white text-center py-3 rounded-lg mb-4 shadow-md" style={{ background: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)' }}>
            <h3 className="text-lg font-semibold tracking-wide">
              SALARY SLIP — {selectedSalarySlip?.month && 
                ['', 'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'][selectedSalarySlip.month]
              } {selectedSalarySlip?.year}
            </h3>
          </div>
          
          {selectedSalarySlip && (
            <div className="space-y-4">
              {/* Employee Details */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#E8F5E9', borderLeft: '4px solid #4CAF50' }}>
                  <Label className="text-xs uppercase font-semibold" style={{ color: '#2E7D32' }}>Employee Name</Label>
                  <p className="font-medium text-slate-800">{selectedSalarySlip.employee_name}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#E8F5E9', borderLeft: '4px solid #4CAF50' }}>
                  <Label className="text-xs uppercase font-semibold" style={{ color: '#2E7D32' }}>Employee ID</Label>
                  <p className="font-medium text-slate-800">{selectedSalarySlip.employee_unique_id || 'N/A'}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#E8F5E9', borderLeft: '4px solid #4CAF50' }}>
                  <Label className="text-xs uppercase font-semibold" style={{ color: '#2E7D32' }}>Emirates ID</Label>
                  <p className="font-medium text-slate-800">{selectedSalarySlip.emirates_id || 'N/A'}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#E8F5E9', borderLeft: '4px solid #4CAF50' }}>
                  <Label className="text-xs uppercase font-semibold" style={{ color: '#2E7D32' }}>Pay Period</Label>
                  <p className="font-medium text-slate-800">{selectedSalarySlip.period_start} to {selectedSalarySlip.period_end}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#E8F5E9', borderLeft: '4px solid #4CAF50' }}>
                  <Label className="text-xs uppercase font-semibold" style={{ color: '#2E7D32' }}>Employee Type</Label>
                  <p className="font-medium text-slate-800 capitalize">{selectedSalarySlip.employee_type || 'Rider'}</p>
                </div>
                <div className="p-3 rounded-lg" style={{ backgroundColor: '#E8F5E9', borderLeft: '4px solid #4CAF50' }}>
                  <Label className="text-xs uppercase font-semibold" style={{ color: '#2E7D32' }}>Company</Label>
                  <p className="font-medium text-slate-800 text-sm">{selectedEmployee?.lessee_name || selectedEmployee?.employer_name || 'EZY Mobility FZ LLC'}</p>
                </div>
              </div>

              {/* Earnings & Deductions */}
              <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <Card className="shadow-sm" style={{ backgroundColor: '#E8F5E9', borderColor: '#4CAF50' }}>
                  <CardContent className="p-4">
                    <h4 className="font-bold mb-3 text-center border-b pb-2" style={{ color: '#2E7D32', borderColor: '#4CAF50' }}>EARNINGS</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between py-1">
                        <span className="text-slate-600">Basic Salary</span>
                        <span className="font-medium">AED {selectedSalarySlip.basic_salary?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span className="text-slate-600">Overtime Pay</span>
                        <span className="font-medium">AED {selectedSalarySlip.overtime_pay?.toLocaleString() || 0}</span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span className="text-slate-600">Incentives</span>
                        <span className="font-medium">AED {selectedSalarySlip.incentives?.toLocaleString() || 0}</span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span className="text-slate-600">Other Earnings</span>
                        <span className="font-medium">AED {selectedSalarySlip.other_earnings?.toLocaleString() || 0}</span>
                      </div>
                      {(selectedSalarySlip.pension_amount > 0) && (
                        <div className="flex justify-between py-1">
                          <span className="text-slate-600">Pension (GPSSA)</span>
                          <span className="font-medium">AED {selectedSalarySlip.pension_amount?.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold pt-2 mt-2" style={{ borderTop: '2px solid #4CAF50', color: '#2E7D32' }}>
                        <span>GROSS EARNINGS</span>
                        <span>AED {selectedSalarySlip.gross_earnings?.toLocaleString()}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-sm" style={{ backgroundColor: '#FFF3E0', borderColor: '#FF9800' }}>
                  <CardContent className="p-4">
                    <h4 className="font-bold mb-3 text-center border-b pb-2" style={{ color: '#E65100', borderColor: '#FF9800' }}>DEDUCTIONS</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between py-1">
                        <span className="text-slate-600">Loan EMI</span>
                        <span className="font-medium">AED {selectedSalarySlip.loan_emi?.toLocaleString() || 0}</span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span className="text-slate-600">Fines</span>
                        <span className="font-medium">AED {selectedSalarySlip.fines?.toLocaleString() || 0}</span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span className="text-slate-600">Salik</span>
                        <span className="font-medium">AED {selectedSalarySlip.salik?.toLocaleString() || 0}</span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span className="text-slate-600">Maintenance</span>
                        <span className="font-medium">AED {selectedSalarySlip.maintenance?.toLocaleString() || 0}</span>
                      </div>
                      {(selectedSalarySlip.pension_amount > 0) && (
                        <div className="py-1">
                          <div className="flex justify-between">
                            <span className="text-slate-600">Pension (GPSSA)</span>
                            <span className="font-medium">AED {selectedSalarySlip.pension_amount?.toLocaleString()}</span>
                          </div>
                          <p className="text-[10px] text-slate-500 italic">Paid at GPSSA</p>
                        </div>
                      )}
                      <div className="flex justify-between font-bold pt-2 mt-2" style={{ borderTop: '2px solid #FF9800', color: '#E65100' }}>
                        <span>TOTAL DEDUCTIONS</span>
                        <span>AED {selectedSalarySlip.total_deductions?.toLocaleString()}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Net Salary */}
              <div className="text-white p-5 rounded-xl text-center shadow-lg" style={{ background: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)' }}>
                <p className="text-sm uppercase tracking-widest opacity-90 mb-1">Net Salary Payable</p>
                <p className="text-4xl font-bold">
                  AED {selectedSalarySlip.net_salary?.toLocaleString()}
                </p>
              </div>

              {/* Footer */}
              <div className="text-center text-xs text-slate-500 pt-2" style={{ borderTop: '2px solid #E8F5E9' }}>
                <p>This is a computer-generated document and does not require a signature.</p>
                <p className="font-medium mt-1" style={{ color: '#4CAF50' }}>EZY Mobility Payroll System</p>
              </div>
            </div>
          )}

          <DialogFooter className="mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setSalarySlipDialogOpen(false)}>Close</Button>
            {selectedSalarySlip && (
              <Button 
                onClick={() => handleDownloadSalarySlipPDF(selectedSalarySlip.id)}
                className="gap-2"
                style={{ backgroundColor: '#2E7D32' }}
              >
                <Download className="w-4 h-4" />
                Download PDF
              </Button>
            )}
            {selectedSalarySlip && !selectedSalarySlip.email_sent && (
              <Button onClick={() => handleEmailSalarySlip(selectedSalarySlip.id)} className="gap-2 bg-blue-600 hover:bg-blue-700">
                <Send className="w-4 h-4" />
                Email to Employee
              </Button>
            )}
            {selectedSalarySlip && (
              <Button
                onClick={() => handleWhatsAppSalarySlip(selectedSalarySlip.id, selectedSalarySlip.employee_name)}
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                data-testid="wa-slip-detail-btn"
              >
                <WhatsAppIcon className="w-4 h-4 text-[#25D366]" />
                Send via WhatsApp
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* View Documents Dialog */}
      <Dialog open={viewDocumentsDialogOpen} onOpenChange={(open) => {
        setViewDocumentsDialogOpen(open);
        if (!open) { setViewDocuments([]); setViewDocumentsEmployee(null); }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Documents - {viewDocumentsEmployee?.name}
            </DialogTitle>
            <DialogDescription>View and download employee documents</DialogDescription>
          </DialogHeader>
          
          {viewDocumentsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              <span className="ml-2 text-slate-500">Loading documents...</span>
            </div>
          ) : viewDocuments.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p>No documents available</p>
            </div>
          ) : (
            <>
              {/* TAP Documents */}
              {viewDocuments.filter(d => d.source === 'tap').length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs">TAP</Badge>
                    Enrollment Documents
                    <span className="text-xs text-slate-400 font-normal">({viewDocuments.filter(d => d.source === 'tap').length} files)</span>
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {viewDocuments.filter(d => d.source === 'tap').map((doc) => (
                      <div key={doc.type} className="border rounded-lg p-3 space-y-2 hover:border-blue-200 transition-colors">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-700 truncate">{doc.name}</span>
                          <Badge variant="outline" className="bg-green-50 text-green-700 text-xs shrink-0 ml-1">Available</Badge>
                        </div>
                        {doc.url && (doc.url.startsWith('data:image') || /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(doc.url)) && (
                          <img src={doc.url} alt={doc.name} className="w-16 h-20 object-cover rounded border" />
                        )}
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => viewDocFile(doc.url)}
                            className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline cursor-pointer"
                            data-testid={`view-doc-${doc.type}`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                            View
                          </button>
                          <button
                            onClick={() => downloadDocFile(doc.url, `${viewDocumentsEmployee?.name}_${doc.type}${getDocFileExt(doc.url)}`)}
                            className="flex items-center gap-1.5 text-sm text-slate-600 hover:underline cursor-pointer"
                            data-testid={`download-doc-${doc.type}`}
                          >
                            <Download className="w-3.5 h-3.5" />
                            Download
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Talent PMS Documents */}
              {viewDocuments.filter(d => d.source === 'talent').length > 0 && (
                <div className="space-y-3 mt-4">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 text-xs">Talent PMS</Badge>
                    Agreements & Forms
                    <span className="text-xs text-slate-400 font-normal">({viewDocuments.filter(d => d.source === 'talent').length} files)</span>
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {viewDocuments.filter(d => d.source === 'talent').map((doc) => (
                      <div key={doc.type + '_' + (doc.name || '')} className="border rounded-lg p-3 space-y-2 hover:border-amber-200 transition-colors">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-700 truncate">{doc.name}</span>
                          <Badge variant="outline" className="bg-green-50 text-green-700 text-xs shrink-0 ml-1">Available</Badge>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => viewDocFile(doc.url)}
                            className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline cursor-pointer"
                            data-testid={`view-doc-${doc.type}`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                            View
                          </button>
                          <button
                            onClick={() => downloadDocFile(doc.url, `${viewDocumentsEmployee?.name}_${doc.type}${getDocFileExt(doc.url)}`)}
                            className="flex items-center gap-1.5 text-sm text-slate-600 hover:underline cursor-pointer"
                            data-testid={`download-doc-${doc.type}`}
                          >
                            <Download className="w-3.5 h-3.5" />
                            Download
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Documents from Hiring Page */}
              {viewDocumentsEmployee?.hiring_documents?.length > 0 && (
                <div className="space-y-3 mt-4">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 text-xs">Hiring</Badge>
                    Documents from Hiring Page
                    <span className="text-xs text-slate-400 font-normal">({viewDocumentsEmployee.hiring_documents.length} files)</span>
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {viewDocumentsEmployee.hiring_documents.map((doc, idx) => (
                      <div key={doc.type + idx} className="border rounded-lg p-3 space-y-2 hover:border-purple-200 transition-colors">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-700 truncate">{doc.label}</span>
                          <Badge variant="outline" className={doc.generated ? "bg-indigo-50 text-indigo-700 text-xs shrink-0 ml-1" : "bg-green-50 text-green-700 text-xs shrink-0 ml-1"}>
                            {doc.generated ? 'Generated' : 'Uploaded'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => {
                              const url = doc.url.startsWith('/api') ? `${API.replace('/api', '')}${doc.url}` : doc.url;
                              viewDocFile(url);
                            }}
                            className="flex items-center gap-1.5 text-sm text-purple-600 hover:underline cursor-pointer"
                            data-testid={`view-hiring-doc-${doc.type}`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                            View
                          </button>
                          <button
                            onClick={() => {
                              const url = doc.url.startsWith('/api') ? `${API.replace('/api', '')}${doc.url}` : doc.url;
                              downloadDocFile(url, `${viewDocumentsEmployee?.name}_${doc.label.replace(/\s+/g, '_')}`);
                            }}
                            className="flex items-center gap-1.5 text-sm text-slate-600 hover:underline cursor-pointer"
                            data-testid={`hiring-doc-${doc.type}`}
                          >
                            <Download className="w-3.5 h-3.5" />
                            Download
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Staff Employee Dialog */}
      <Dialog open={editStaffDialogOpen} onOpenChange={(open) => {
        setEditStaffDialogOpen(open);
        if (!open) setEditingStaff(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Staff Employee
            </DialogTitle>
            <DialogDescription>Update staff employee information</DialogDescription>
          </DialogHeader>
          
          {editingStaff && (
            <div className="space-y-4 py-4">
              {/* Personal Information */}
              <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={editingStaff.name || ''}
                    onChange={(e) => setEditingStaff({...editingStaff, name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Designation</Label>
                  <Input
                    value={editingStaff.designation || ''}
                    onChange={(e) => setEditingStaff({...editingStaff, designation: e.target.value})}
                    placeholder="e.g., Accountant, Manager"
                  />
                </div>
              </div>

              <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <div className="space-y-2">
                  <Label>Emirates ID</Label>
                  <Input 
                    value={editingStaff.emirates_id || ''} 
                    onChange={(e) => setEditingStaff({...editingStaff, emirates_id: e.target.value})}
                    disabled
                    className="bg-slate-100 cursor-not-allowed"
                    title="Locked — Emirates ID is managed in TAP"
                  />
                  <p className="text-xs text-slate-500">🔒 Locked. <a href="/tap" className="text-emerald-700 underline hover:text-emerald-800">Edit on TAP →</a></p>
                </div>
                <div className="space-y-2">
                  <Label>Emirates ID Expiry</Label>
                  <Input
                    type="date"
                    value={editingStaff.emirates_id_expiry || ''}
                    onChange={(e) => setEditingStaff({...editingStaff, emirates_id_expiry: e.target.value})}
                  />
                </div>
              </div>

              <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <div className="space-y-2">
                  <Label>Company</Label>
                  <Input
                    value={editingStaff.lessee_name || ''}
                    onChange={(e) => setEditingStaff({...editingStaff, lessee_name: e.target.value})}
                    disabled={!!editingStaff.tap_candidate_id}
                    className={editingStaff.tap_candidate_id ? "bg-slate-100" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Platform</Label>
                  <Input
                    value={editingStaff.platform || ''}
                    onChange={(e) => setEditingStaff({...editingStaff, platform: e.target.value})}
                    disabled={!!editingStaff.tap_candidate_id}
                    className={editingStaff.tap_candidate_id ? "bg-slate-100" : ""}
                  />
                </div>
              </div>

              <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <div className="space-y-2">
                  <Label>Passport Number</Label>
                  <Input
                    value={editingStaff.passport_number || ''}
                    onChange={(e) => setEditingStaff({...editingStaff, passport_number: e.target.value})}
                    disabled
                    className="bg-slate-100 cursor-not-allowed"
                    title="Locked — Passport is managed in TAP"
                  />
                  <p className="text-xs text-slate-500">🔒 Locked. <a href="/tap" className="text-emerald-700 underline hover:text-emerald-800">Edit on TAP →</a></p>
                </div>
                <div className="space-y-2">
                  <Label>Passport Expiry</Label>
                  <Input
                    type="date"
                    value={editingStaff.passport_expiry || ''}
                    onChange={(e) => setEditingStaff({...editingStaff, passport_expiry: e.target.value})}
                  />
                </div>
              </div>

              <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={editingStaff.email || ''}
                    onChange={(e) => setEditingStaff({...editingStaff, email: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mobile</Label>
                  <Input
                    value={editingStaff.mobile || ''}
                    onChange={(e) => setEditingStaff({...editingStaff, mobile: e.target.value})}
                    disabled
                    className="bg-slate-100 cursor-not-allowed"
                    title="Locked — Mobile is managed in TAP"
                  />
                  <p className="text-xs text-slate-500">🔒 Locked. <a href="/tap" className="text-emerald-700 underline hover:text-emerald-800">Edit on TAP →</a></p>
                </div>
                <div className="space-y-2">
                  <Label>Assigned Location (for cost analysis)</Label>
                  <select
                    className="w-full border rounded-md h-10 px-3 text-sm"
                    value={editingStaff.location || ''}
                    onChange={(e) => setEditingStaff({...editingStaff, location: e.target.value})}
                    data-testid="staff-edit-location"
                  >
                    <option value="">— Not assigned —</option>
                    <option value="Head Office">Head Office (excluded from swap cost analysis)</option>
                    {(swapLocations || []).map(l => (
                      <option key={l.id || l.name} value={l.name}>{l.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Employment lifecycle — drives Cost & Energy prorated salary */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-sm">Employment Lifecycle</h4>
                  <p className="text-[11px] text-slate-500">Determines when this staff's salary starts / stops contributing to Cost & Energy analysis</p>
                </div>
                <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Joining Date</Label>
                      {!editingStaff.joining_date && editingStaff._suggested_joining_date && (
                        <button
                          type="button"
                          onClick={() => setEditingStaff({ ...editingStaff, joining_date: editingStaff._suggested_joining_date })}
                          className="text-[11px] text-blue-600 hover:underline"
                          data-testid="staff-edit-use-suggested-joining"
                        >
                          Use suggested: {editingStaff._suggested_joining_date}
                        </button>
                      )}
                    </div>
                    <Input
                      type="date"
                      value={editingStaff.joining_date || ''}
                      onChange={(e) => setEditingStaff({ ...editingStaff, joining_date: e.target.value })}
                      data-testid="staff-edit-joining-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Exit Date <span className="text-slate-400 text-xs">(optional — leave blank if still active)</span></Label>
                    <Input
                      type="date"
                      value={editingStaff.exit_date || ''}
                      onChange={(e) => setEditingStaff({ ...editingStaff, exit_date: e.target.value })}
                      data-testid="staff-edit-exit-date"
                    />
                  </div>
                </div>

                {/* Salary History — track raises. Latest entry's amount is
                    mirrored to Monthly Salary. */}
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs">Salary History (past raises)</Label>
                    <button
                      type="button"
                      onClick={() => {
                        const hist = editingStaff.salary_history || [];
                        setEditingStaff({
                          ...editingStaff,
                          salary_history: [
                            ...hist,
                            { effective_from: editingStaff.joining_date || '', monthly_salary: editingStaff.monthly_salary || 0 },
                          ],
                        });
                      }}
                      className="text-[11px] text-blue-600 hover:underline"
                      data-testid="staff-edit-add-salary-history"
                    >
                      + Add raise
                    </button>
                  </div>
                  {(editingStaff.salary_history || []).length === 0 ? (
                    <p className="text-[11px] text-slate-400">
                      No history recorded — current Monthly Salary applies from Joining Date onward.
                    </p>
                  ) : (
                    <div className="space-y-1.5" data-testid="staff-edit-salary-history-list">
                      {(editingStaff.salary_history || []).map((row, idx) => (
                        <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                          <Input
                            type="date"
                            value={row.effective_from || ''}
                            onChange={(e) => {
                              const next = [...(editingStaff.salary_history || [])];
                              next[idx] = { ...next[idx], effective_from: e.target.value };
                              setEditingStaff({ ...editingStaff, salary_history: next });
                            }}
                            className="h-9"
                          />
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="AED / month"
                            value={row.monthly_salary || ''}
                            onChange={(e) => {
                              const next = [...(editingStaff.salary_history || [])];
                              next[idx] = { ...next[idx], monthly_salary: parseFloat(e.target.value) || 0 };
                              setEditingStaff({ ...editingStaff, salary_history: next });
                            }}
                            className="h-9"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const next = (editingStaff.salary_history || []).filter((_, i) => i !== idx);
                              setEditingStaff({ ...editingStaff, salary_history: next });
                            }}
                            className="text-slate-400 hover:text-rose-600 text-xs px-2"
                            title="Remove"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      <p className="text-[10px] text-slate-500 mt-1">
                        The most recent row's amount is mirrored to Monthly Salary below.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Salary Information */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-sm mb-3">Salary Breakdown</h4>
                <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  <div className="space-y-2">
                    <Label>Basic Salary</Label>
                    <Input
                      type="number"
                      value={editingStaff.basic_salary || ''}
                      onChange={(e) => {
                        const basic = parseFloat(e.target.value) || 0;
                        const housing = parseFloat(editingStaff.housing_allowance) || 0;
                        const transport = parseFloat(editingStaff.transport_allowance) || 0;
                        const telephone = parseFloat(editingStaff.telephone_allowance) || 0;
                        const pension = parseFloat(editingStaff.pension_amount) || 0;
                        const customTotal = (editingStaff.custom_allowances || []).reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
                        setEditingStaff({
                          ...editingStaff,
                          basic_salary: e.target.value,
                          monthly_salary: (basic + housing + transport + telephone + pension + customTotal).toString()
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Housing Allowance</Label>
                    <Input
                      type="number"
                      value={editingStaff.housing_allowance || ''}
                      onChange={(e) => {
                        const basic = parseFloat(editingStaff.basic_salary) || 0;
                        const housing = parseFloat(e.target.value) || 0;
                        const transport = parseFloat(editingStaff.transport_allowance) || 0;
                        const telephone = parseFloat(editingStaff.telephone_allowance) || 0;
                        const pension = parseFloat(editingStaff.pension_amount) || 0;
                        const customTotal = (editingStaff.custom_allowances || []).reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
                        setEditingStaff({
                          ...editingStaff,
                          housing_allowance: e.target.value,
                          monthly_salary: (basic + housing + transport + telephone + pension + customTotal).toString()
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Transport Allowance</Label>
                    <Input
                      type="number"
                      value={editingStaff.transport_allowance || ''}
                      onChange={(e) => {
                        const basic = parseFloat(editingStaff.basic_salary) || 0;
                        const housing = parseFloat(editingStaff.housing_allowance) || 0;
                        const transport = parseFloat(e.target.value) || 0;
                        const telephone = parseFloat(editingStaff.telephone_allowance) || 0;
                        const pension = parseFloat(editingStaff.pension_amount) || 0;
                        const customTotal = (editingStaff.custom_allowances || []).reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
                        setEditingStaff({
                          ...editingStaff,
                          transport_allowance: e.target.value,
                          monthly_salary: (basic + housing + transport + telephone + pension + customTotal).toString()
                        });
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telephone Allowance</Label>
                    <Input
                      type="number"
                      value={editingStaff.telephone_allowance || ''}
                      onChange={(e) => {
                        const basic = parseFloat(editingStaff.basic_salary) || 0;
                        const housing = parseFloat(editingStaff.housing_allowance) || 0;
                        const transport = parseFloat(editingStaff.transport_allowance) || 0;
                        const telephone = parseFloat(e.target.value) || 0;
                        const pension = parseFloat(editingStaff.pension_amount) || 0;
                        const customTotal = (editingStaff.custom_allowances || []).reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
                        setEditingStaff({
                          ...editingStaff,
                          telephone_allowance: e.target.value,
                          monthly_salary: (basic + housing + transport + telephone + pension + customTotal).toString()
                        });
                      }}
                    />
                  </div>
                  {/* Pension (GPSSA contribution) — UAE nationals' pension deduction.
                      Counts toward the gross Monthly Salary total. */}
                  <div className="space-y-2">
                    <Label>Pension (GPSSA)</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={editingStaff.pension_amount || ''}
                      onChange={(e) => {
                        const basic = parseFloat(editingStaff.basic_salary) || 0;
                        const housing = parseFloat(editingStaff.housing_allowance) || 0;
                        const transport = parseFloat(editingStaff.transport_allowance) || 0;
                        const telephone = parseFloat(editingStaff.telephone_allowance) || 0;
                        const pension = parseFloat(e.target.value) || 0;
                        const customTotal = (editingStaff.custom_allowances || []).reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
                        setEditingStaff({
                          ...editingStaff,
                          pension_amount: e.target.value,
                          monthly_salary: (basic + housing + transport + telephone + pension + customTotal).toString()
                        });
                      }}
                      data-testid="staff-edit-pension-amount"
                    />
                    <p className="text-[10px] text-slate-500">GPSSA — added to gross Monthly Salary total</p>
                  </div>
                </div>

                {/* Custom Allowances Section */}
                <div className="mt-4 border-t pt-4">
                  <div className="flex justify-between items-center mb-3">
                    <h5 className="font-medium text-sm text-slate-700">Additional Allowances</h5>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingStaff({
                          ...editingStaff,
                          custom_allowances: [...(editingStaff.custom_allowances || []), { description: '', amount: '' }]
                        });
                      }}
                      className="text-xs h-7"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add Allowance
                    </Button>
                  </div>
                  
                  {(editingStaff.custom_allowances || []).length > 0 && (
                    <div className="space-y-2">
                      {editingStaff.custom_allowances.map((allowance, index) => (
                        <div key={index} className="flex gap-2 items-center">
                          <Input
                            placeholder="Description (e.g., Food Allowance)"
                            value={allowance.description}
                            onChange={(e) => {
                              const updated = [...editingStaff.custom_allowances];
                              updated[index].description = e.target.value;
                              setEditingStaff({ ...editingStaff, custom_allowances: updated });
                            }}
                            className="flex-1 h-9 text-sm"
                          />
                          <Input
                            type="number"
                            placeholder="Amount"
                            value={allowance.amount}
                            onChange={(e) => {
                              const updated = [...editingStaff.custom_allowances];
                              updated[index].amount = e.target.value;
                              const basic = parseFloat(editingStaff.basic_salary) || 0;
                              const housing = parseFloat(editingStaff.housing_allowance) || 0;
                              const transport = parseFloat(editingStaff.transport_allowance) || 0;
                              const telephone = parseFloat(editingStaff.telephone_allowance) || 0;
                              const customTotal = updated.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
                              setEditingStaff({ 
                                ...editingStaff, 
                                custom_allowances: updated,
                                monthly_salary: (basic + housing + transport + telephone + customTotal).toString()
                              });
                            }}
                            className="w-28 h-9 text-sm"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const updated = editingStaff.custom_allowances.filter((_, i) => i !== index);
                              const basic = parseFloat(editingStaff.basic_salary) || 0;
                              const housing = parseFloat(editingStaff.housing_allowance) || 0;
                              const transport = parseFloat(editingStaff.transport_allowance) || 0;
                              const telephone = parseFloat(editingStaff.telephone_allowance) || 0;
                              const customTotal = updated.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
                              setEditingStaff({ 
                                ...editingStaff, 
                                custom_allowances: updated,
                                monthly_salary: (basic + housing + transport + telephone + customTotal).toString()
                              });
                            }}
                            className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Salary change audit — read-only timeline populated
                    by the backend on every PUT. Newest changes first. */}
                {(editingStaff.salary_audit_log || []).length > 0 && (
                  <div className="mt-4 border rounded-md bg-slate-50/60 p-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      <History className="w-3.5 h-3.5 text-slate-500" />
                      <h5 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                        Recent Salary Changes
                      </h5>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {(editingStaff.salary_audit_log || []).slice(0, 10).map((entry) => (
                        <div key={entry.id} className="text-[11px] border-l-2 border-slate-300 pl-2 py-0.5">
                          <div className="flex justify-between items-baseline">
                            <span className="text-slate-700 font-medium">
                              {(entry.changed_by_name || entry.changed_by_email || 'Unknown').replace(/@.*/, '')}
                            </span>
                            <span className="text-slate-400">
                              {entry.changed_at
                                ? new Date(entry.changed_at).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })
                                : '—'}
                            </span>
                          </div>
                          <div className="mt-0.5 space-y-0.5">
                            {(entry.changes || []).map((c, ci) => {
                              const label = ({
                                basic_salary: 'Basic',
                                housing_allowance: 'Housing',
                                transport_allowance: 'Transport',
                                telephone_allowance: 'Telephone',
                                pension_amount: 'Pension',
                                room_rent: 'Room Rent',
                                monthly_salary: 'Monthly Total',
                                custom_allowances: 'Custom Allowances',
                              }[c.field]) || c.field;
                              if (c.field === 'custom_allowances') {
                                return (
                                  <div key={ci} className="text-slate-600">
                                    <span className="font-medium">{label}:</span> updated ({((c.new)||[]).length} item{((c.new)||[]).length===1?'':'s'})
                                  </div>
                                );
                              }
                              return (
                                <div key={ci} className="text-slate-600">
                                  <span className="font-medium">{label}:</span>{' '}
                                  <span className="text-red-600 line-through">{Number(c.old||0).toLocaleString()}</span>
                                  {' → '}
                                  <span className="text-emerald-700 font-semibold">{Number(c.new||0).toLocaleString()}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-blue-800">Total Monthly Salary</span>
                    <span className="text-lg font-bold text-blue-900">
                      AED {(
                        (parseFloat(editingStaff.basic_salary) || 0) +
                        (parseFloat(editingStaff.housing_allowance) || 0) +
                        (parseFloat(editingStaff.transport_allowance) || 0) +
                        (parseFloat(editingStaff.telephone_allowance) || 0) +
                        (parseFloat(editingStaff.pension_amount) || 0) +
                        ((editingStaff.custom_allowances || []).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0))
                      ).toLocaleString()}
                    </span>
                  </div>
                </div>
                
                <div className="mt-3">
                  <Label>Room Rent (Monthly Deduction)</Label>
                  <Input
                    type="number"
                    value={editingStaff.room_rent || ''}
                    onChange={(e) => setEditingStaff({...editingStaff, room_rent: parseFloat(e.target.value) || 0})}
                    placeholder="0"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <div className="space-y-2">
                  <Label>Edenred ID</Label>
                  <Input
                    value={editingStaff.edenred_id || ''}
                    onChange={(e) => setEditingStaff({...editingStaff, edenred_id: e.target.value})}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setEditStaffDialogOpen(false);
              setEditingStaff(null);
              setOriginalStaffSalary(null);
              setSalaryDiffModal({ open: false, changes: [] });
            }}>Cancel</Button>
            <Button onClick={handleUpdateStaff} className="bg-blue-600 hover:bg-blue-700" data-testid="staff-edit-save">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Salary-change confirmation modal — pops before the PUT fires
          when any salary field has actually moved. Prevents fat-finger
          edits by making the operator eyeball the old→new deltas. */}
      <Dialog
        open={salaryDiffModal.open}
        onOpenChange={(open) => {
          if (!open) setSalaryDiffModal({ open: false, changes: [] });
        }}
      >
        <DialogContent className="max-w-md" data-testid="salary-diff-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm salary changes
            </DialogTitle>
            <DialogDescription>
              Review every change below before saving. Amounts in <span className="font-medium">AED</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2 max-h-72 overflow-y-auto">
            {(salaryDiffModal.changes || []).map((c, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 py-1.5">
                <span className="text-slate-700 font-medium">{c.label}</span>
                {c.isCustom ? (
                  <span className="text-slate-600 text-xs">updated ({(c.new || []).length} item{(c.new || []).length === 1 ? '' : 's'})</span>
                ) : (
                  <span className="text-xs">
                    <span className="text-red-600 line-through">{Number(c.old || 0).toLocaleString()}</span>
                    {' → '}
                    <span className="text-emerald-700 font-semibold">{Number(c.new || 0).toLocaleString()}</span>
                  </span>
                )}
              </div>
            ))}
            {(salaryDiffModal.changes || []).length === 0 && (
              <p className="text-slate-500 text-sm italic">No salary changes detected.</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSalaryDiffModal({ open: false, changes: [] })}
              data-testid="salary-diff-cancel"
            >
              Back to edit
            </Button>
            <Button
              onClick={() => {
                // Close the diff modal first, then fire the writer
                // directly — bypasses the guard in handleUpdateStaff so
                // stale-state closures can't re-pop the modal.
                setSalaryDiffModal({ open: false, changes: [] });
                _persistStaffUpdate();
              }}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="salary-diff-confirm"
            >
              Confirm & Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rider/Cyclist Redirect Dialog */}
      <Dialog open={riderRedirectDialogOpen} onOpenChange={setRiderRedirectDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Edit {selectedEmployee?.designation === 'Cyclist' ? 'Cyclist' : 'Rider'} Details
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-slate-600">
              To edit <strong>{selectedEmployee?.rider_name || selectedEmployee?.name}</strong>&apos;s details, 
              please go to the <strong>{selectedEmployee?.source === 'TAP' ? 'TAP' : 'Riders'}</strong> page.
            </p>
            <p className="text-sm text-slate-500 mt-2">
              {selectedEmployee?.source === 'TAP' 
                ? 'This employee was synced from the TAP (Talent Acquisition) page. Edit their information there to maintain data consistency.'
                : `${selectedEmployee?.designation === 'Cyclist' ? 'Cyclist' : 'Rider'} information is managed centrally on the Riders page to ensure data consistency across the system.`
              }
            </p>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setRiderRedirectDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                setRiderRedirectDialogOpen(false);
                window.location.href = selectedEmployee?.source === 'TAP' ? '/tap' : '/riders';
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Go to {selectedEmployee?.source === 'TAP' ? 'TAP' : 'Riders'} Page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Instructions Dialog - Admin Only */}
      <Dialog open={aiInstructionsDialog} onOpenChange={setAiInstructionsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-purple-600" />
              AI Payroll Instructions - {selectedPlatformForAI?.name}
            </DialogTitle>
            <DialogDescription>
              Configure how the AI should process payroll sheets for this platform. These instructions guide the AI in matching riders and calculating salaries.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
              <h4 className="font-medium text-purple-800 text-sm flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                How AI Uses These Instructions
              </h4>
              <ul className="text-xs text-purple-700 mt-2 space-y-1">
                <li>• Explains which columns to use for rider matching</li>
                <li>• Defines how to calculate gross salary</li>
                <li>• Specifies any platform-specific rules or conditions</li>
                <li>• <b>Pre-aggregated platforms (e.g., Careem):</b> add <code className="bg-white px-1 rounded">Gross_Salary_Column = &lt;Excel column name&gt;</code> to use a single Excel column directly as gross salary (no orders × rate needed).</li>
              </ul>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Platform ID Column in Excel</Label>
              <Input
                value={platformIdColumn}
                onChange={(e) => setPlatformIdColumn(e.target.value)}
                placeholder="e.g., id_user, rider_id, driver_id"
                className="font-mono text-sm"
                data-testid="platform-id-column-input"
              />
              <p className="text-xs text-slate-500">The exact column header in the Excel file that contains the rider's Platform ID. This is used for deterministic matching.</p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Instructions for AI</Label>
              <textarea
                className="w-full h-64 p-3 border rounded-lg text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                value={aiInstructions}
                onChange={(e) => setAiInstructions(e.target.value)}
                placeholder="Enter instructions for how AI should process this platform's payroll..."
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAiInstructions(getDefaultAIInstructions(selectedPlatformForAI))}
              >
                Reset to Default
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAiInstructionsDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveAIInstructions} className="bg-purple-600 hover:bg-purple-700 gap-2">
              <Check className="w-4 h-4" />
              Save Instructions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Always-mounted dialogs — accessible from any sub-page */}
      <PayrollWizardReadOnlyDialog
        open={readOnlyWizardOpen}
        onOpenChange={(o) => { setReadOnlyWizardOpen(o); if (!o) { setReadOnlyWizardBatch(null); setReadOnlyWizardPayrolls([]); } }}
        batch={readOnlyWizardBatch}
        payrolls={readOnlyWizardPayrolls}
        onOpenCalcView={(p) => setCalculationDialog({
          open: true,
          data: p.calculation_breakdown,
          riderName: p.employee_name,
          riderData: p,
        })}
      />

      {/* Calculation Breakdown Dialog */}
      <Dialog open={calculationDialog.open} onOpenChange={(open) => setCalculationDialog({ ...calculationDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-blue-600" />
              Salary Calculation
            </DialogTitle>
            <DialogDescription>
              {calculationDialog.riderName}
            </DialogDescription>
          </DialogHeader>
          {calculationDialog.data ? (
            <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
              {/* Staff Attendance & Pro-rata (only shown for staff payrolls) */}
              {calculationDialog.data.staff_attendance && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2" data-testid="staff-attendance-breakdown">
                  <p className="text-xs text-amber-700 font-semibold mb-2">Attendance & Pro-rata</p>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <div className="flex justify-between"><span className="text-slate-600">Configured Monthly Salary</span><span className="font-mono">AED {Number(calculationDialog.data.staff_attendance.monthly_salary || 0).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Working Days</span><span className="font-mono">{calculationDialog.data.staff_attendance.working_days}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Present Days</span><span className="font-mono text-green-700">{calculationDialog.data.staff_attendance.present_days}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Absent Days</span><span className="font-mono text-red-600">{calculationDialog.data.staff_attendance.absent_days}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Overtime Days</span><span className="font-mono">{calculationDialog.data.staff_attendance.overtime_days}</span></div>
                    <div className="flex justify-between"><span className="text-slate-600">Pro-rata Ratio</span><span className="font-mono font-semibold text-amber-800">{calculationDialog.data.staff_attendance.present_days}/{calculationDialog.data.staff_attendance.working_days} = {Number(calculationDialog.data.staff_attendance.attendance_ratio || 0).toFixed(4)}</span></div>
                  </div>
                  {Number(calculationDialog.data.staff_attendance.overtime_pay || 0) > 0 && (
                    <div className="text-xs text-slate-600 pt-1 border-t border-amber-200 mt-2">
                      OT Pay = (AED {Number(calculationDialog.data.staff_attendance.monthly_salary || 0).toFixed(2)} ÷ {calculationDialog.data.staff_attendance.overtime_base_divisor || 30}) × {calculationDialog.data.staff_attendance.overtime_days} × {Number(calculationDialog.data.staff_attendance.overtime_rate_multiplier || 1.5).toFixed(2)} = <span className="font-mono font-semibold text-amber-800">AED {Number(calculationDialog.data.staff_attendance.overtime_pay || 0).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Step-by-step components (only when full-month baselines are present) */}
              {calculationDialog.data.staff_full_components && (
                <div className="bg-white border rounded-lg p-3" data-testid="staff-prorata-table">
                  <p className="text-xs text-slate-700 font-semibold mb-2">Step-by-step Earnings</p>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-slate-500 border-b">
                        <th className="text-left py-1">Component</th>
                        <th className="text-right py-1">Full Month</th>
                        <th className="text-right py-1">× Ratio</th>
                        <th className="text-right py-1">Pro-rated</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono">
                      {(() => {
                        const att = calculationDialog.data.staff_attendance || {};
                        const ratio = Number(att.attendance_ratio || 0);
                        const full = calculationDialog.data.staff_full_components || {};
                        const rows = [];
                        if (Number(full.basic || 0) > 0) rows.push(['Basic Salary', full.basic]);
                        if (Number(full.housing || 0) > 0) rows.push(['Housing Allowance', full.housing]);
                        if (Number(full.transport || 0) > 0) rows.push(['Transport Allowance', full.transport]);
                        if (Number(full.telephone || 0) > 0) rows.push(['Telephone Allowance', full.telephone]);
                        (full.custom_allowances || []).forEach(ca => {
                          if (Number(ca.full_amount || 0) > 0) rows.push([ca.description || 'Allowance', ca.full_amount]);
                        });
                        return rows.map(([label, amt], i) => (
                          <tr key={i} className="border-b border-slate-100">
                            <td className="py-1 text-slate-700">{label}</td>
                            <td className="text-right py-1">AED {Number(amt).toFixed(2)}</td>
                            <td className="text-right py-1 text-amber-700">× {ratio.toFixed(4)}</td>
                            <td className="text-right py-1">AED {(Number(amt) * ratio).toFixed(2)}</td>
                          </tr>
                        ));
                      })()}
                      {Number(calculationDialog.data.staff_attendance?.overtime_pay || 0) > 0 && (
                        <tr className="border-b border-slate-100">
                          <td className="py-1 text-slate-700">Overtime Pay ({calculationDialog.data.staff_attendance.overtime_days} day{calculationDialog.data.staff_attendance.overtime_days === 1 ? '' : 's'})</td>
                          <td className="text-right py-1">AED {Number((calculationDialog.data.staff_attendance.overtime_daily_rate || 0) * (calculationDialog.data.staff_attendance.overtime_rate_multiplier || 1.5)).toFixed(2)}</td>
                          <td className="text-right py-1 text-amber-700">× {calculationDialog.data.staff_attendance.overtime_days}</td>
                          <td className="text-right py-1">AED {Number(calculationDialog.data.staff_attendance.overtime_pay).toFixed(2)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Dynamic Base Components */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                {Array.isArray(calculationDialog.data.base_components) ? (
                  // Talabat format: array of {label, value, type}
                  calculationDialog.data.base_components.map((comp, idx) => (
                    <div key={idx} className={`flex justify-between items-center ${idx === 0 ? 'border-b pb-2' : ''} ${comp.highlight ? 'text-green-700 font-medium' : ''}`}>
                      <span className="text-sm text-slate-600">{comp.label}</span>
                      {comp.type === 'badge' ? (
                        <Badge variant="outline">{comp.value || 'N/A'}</Badge>
                      ) : comp.type === 'currency' ? (
                        <span className="font-mono text-sm">AED {comp.value}</span>
                      ) : (
                        <span className="font-mono text-sm">{comp.value}</span>
                      )}
                    </div>
                  ))
                ) : calculationDialog.data.base_components && typeof calculationDialog.data.base_components === 'object' ? (
                  // Generic/Keeta format: dict of {key: value}
                  Object.entries(calculationDialog.data.base_components).map(([key, val], idx) => (
                    <div key={idx} className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                      <span className="font-mono text-sm">{typeof val === 'number' ? `AED ${val.toFixed(2)}` : val}</span>
                    </div>
                  ))
                ) : (
                  // Legacy format
                  <>
                    {calculationDialog.data.rate_per_order !== undefined && (
                      <>
                        <div className="flex justify-between items-center border-b pb-2">
                          <span className="text-sm text-slate-600">Rate per Order</span>
                          <span className="font-mono text-sm">AED {calculationDialog.data.rate_per_order}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-600">Orders</span>
                          <span className="font-mono text-sm">{calculationDialog.data.orders}</span>
                        </div>
                        <div className="flex justify-between items-center text-green-700 font-medium">
                          <span className="text-sm">Orders Amount</span>
                          <span className="font-mono text-sm">AED {calculationDialog.data.orders_amount}</span>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Dynamic Additional Components */}
              <div className="bg-blue-50 rounded-lg p-4 space-y-2">
                <p className="text-xs text-blue-600 font-medium mb-2">Additional Components</p>
                {Array.isArray(calculationDialog.data.additional_components) ? (
                  calculationDialog.data.additional_components.map((comp, idx) => {
                    const _sign = comp.type === 'currency_sub' ? '-' : '+';
                    const _color = comp.type === 'currency_sub' ? 'text-red-600' : '';
                    return (
                      <div key={idx} className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">{comp.label}</span>
                        <span className={`font-mono text-sm ${_color}`}>{_sign} AED {Number(comp.value || 0).toFixed(2)}</span>
                      </div>
                    );
                  })
                ) : (
                  <>
                    {(calculationDialog.data.return_lc || 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">Deliveries Return (LC)</span>
                        <span className="font-mono text-sm">+ AED {calculationDialog.data.return_lc}</span>
                      </div>
                    )}
                    {(calculationDialog.data.distance_lc || 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">Distance (LC)</span>
                        <span className="font-mono text-sm">+ AED {calculationDialog.data.distance_lc}</span>
                      </div>
                    )}
                    {(calculationDialog.data.incentives || 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">Incentives</span>
                        <span className="font-mono text-sm">+ AED {calculationDialog.data.incentives}</span>
                      </div>
                    )}
                    {(calculationDialog.data.tips || 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">Tips</span>
                        <span className="font-mono text-sm text-teal-600">+ AED {calculationDialog.data.tips}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-xs text-green-600 mb-2">Formula Applied</p>
                <p className="font-mono text-xs text-slate-700 break-all">{calculationDialog.data.formula}</p>
                <div className="flex justify-between items-center mt-3 pt-2 border-t border-green-200">
                  <span className="font-semibold text-green-800">Gross Salary</span>
                  <span className="font-mono font-bold text-lg text-green-700">AED {calculationDialog.data.gross_salary}</span>
                </div>
              </div>

              {/* Deductions Section */}
              {calculationDialog.riderData && (
                <>
                  <div className="bg-red-50 rounded-lg p-4 space-y-2">
                    <p className="text-xs text-red-600 font-medium mb-2">Other Deductions</p>
                    {calculationDialog.riderData.paid_in_wallet > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">
                          {(calculationDialog.riderData?.calculation_breakdown?.formula_type === 'careem_eligible_split')
                            ? 'Advance Paid'
                            : 'Paid in Wallet'}
                        </span>
                        <span className="font-mono text-sm text-cyan-600">- AED {calculationDialog.riderData.paid_in_wallet || 0}</span>
                      </div>
                    )}
                    {calculationDialog.riderData.paid_as_tips > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">Paid as Tips</span>
                        <span className="font-mono text-sm text-teal-600">- AED {calculationDialog.riderData.paid_as_tips || 0}</span>
                      </div>
                    )}
                    {calculationDialog.riderData.paid_as_cod > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">
                          {(calculationDialog.riderData?.calculation_breakdown?.formula_type === 'careem_eligible_split')
                            ? 'COD'
                            : 'Paid as COD'}
                        </span>
                        <span className="font-mono text-sm text-indigo-600">- AED {calculationDialog.riderData.paid_as_cod || 0}</span>
                      </div>
                    )}
                    {(calculationDialog.riderData.clawback_deduction || 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">Clawback Deduction</span>
                        <span className="font-mono text-sm text-orange-700">- AED {calculationDialog.riderData.clawback_deduction || 0}</span>
                      </div>
                    )}
                    {/* Feb-2026: Keeta / Noon platform-side deductions
                        (food_compensation, platform_deduction) roll into
                        total_deductions on the backend but weren't shown
                        as inline rows — so the visible sum didn't match
                        the Total. Rendering them here closes the gap. */}
                    {(parseFloat(calculationDialog.riderData.platform_deduction || 0) > 0) && (
                      <div className="flex justify-between items-center" data-testid="calc-platform-deduction">
                        <span className="text-sm text-slate-600">Platform Deduction</span>
                        <span className="font-mono text-sm text-red-600">- AED {parseFloat(calculationDialog.riderData.platform_deduction || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {(parseFloat(calculationDialog.riderData.food_compensation || 0) > 0) && (
                      <div className="flex justify-between items-center" data-testid="calc-food-compensation">
                        <span className="text-sm text-slate-600">Food Compensation</span>
                        <span className="font-mono text-sm text-red-600">- AED {parseFloat(calculationDialog.riderData.food_compensation || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {/* Feb-2026: Salary Advance — enriched on wizard preview
                        from /salary-advance/pending/by-rider. Was rolled into
                        total_deductions but never shown as its own line, so
                        the modal Total didn't visually reconcile. */}
                    {(parseFloat(calculationDialog.riderData.salary_advance || 0) > 0) && (
                      <div className="flex justify-between items-center" data-testid="calc-salary-advance">
                        <span className="text-sm text-slate-600">Salary Advance</span>
                        <span className="font-mono text-sm text-red-600">- AED {parseFloat(calculationDialog.riderData.salary_advance || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Fines</span>
                      {calculationDialog.riderData.is_npp ? (
                        <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-100 text-slate-600 border border-slate-300" title={`NPP - Recovered on ${calculationDialog.riderData.npp_primary_platform || 'Primary'} payroll`}>NPP</span>
                      ) : (
                        <span className="font-mono text-sm text-red-600">- AED {calculationDialog.riderData.fines || 0}</span>
                      )}
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">SALIK</span>
                      {calculationDialog.riderData.is_npp ? (
                        <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-100 text-slate-600 border border-slate-300" title={`NPP - Recovered on ${calculationDialog.riderData.npp_primary_platform || 'Primary'} payroll`}>NPP</span>
                      ) : (
                        <span className="font-mono text-sm text-red-600">- AED {calculationDialog.riderData.salik || 0}</span>
                      )}
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Maintenance</span>
                      {calculationDialog.riderData.is_npp ? (
                        <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-100 text-slate-600 border border-slate-300" title={`NPP - Recovered on ${calculationDialog.riderData.npp_primary_platform || 'Primary'} payroll`}>NPP</span>
                      ) : (
                        <span className="font-mono text-sm text-red-600">- AED {calculationDialog.riderData.maintenance || 0}</span>
                      )}
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Loan EMI</span>
                      {calculationDialog.riderData.is_npp ? (
                        <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-100 text-slate-600 border border-slate-300" title={`NPP - Recovered on ${calculationDialog.riderData.npp_primary_platform || 'Primary'} payroll`}>NPP</span>
                      ) : (
                        <span className="font-mono text-sm text-red-600">- AED {calculationDialog.riderData.loan_emi || 0}</span>
                      )}
                    </div>
                    {(parseFloat(calculationDialog.riderData.prev_arrears || 0) > 0 || calculationDialog.riderData.is_npp) && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-600">
                          Previous Month Arrears
                          {Array.isArray(calculationDialog.riderData.prev_arrears_months) && calculationDialog.riderData.prev_arrears_months.length > 0 && (
                            <span className="text-[10px] text-slate-500 ml-1 italic">({calculationDialog.riderData.prev_arrears_months.join(', ')})</span>
                          )}
                        </span>
                        {calculationDialog.riderData.is_npp ? (
                          <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-100 text-slate-600 border border-slate-300" title={`NPP - Recovered on ${calculationDialog.riderData.npp_primary_platform || 'Primary'} payroll`}>NPP</span>
                        ) : (
                          <span className="font-mono text-sm text-red-600">- AED {parseFloat(calculationDialog.riderData.prev_arrears || 0).toFixed(2)}</span>
                        )}
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">SLX Charge</span>
                      {calculationDialog.riderData.is_npp ? (
                        <span
                          className="inline-flex items-center justify-center px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-100 text-slate-600 border border-slate-300"
                          title={`NPP - Non Primary Platform, hence not deducted. Recovery happens on ${calculationDialog.riderData.npp_primary_platform || 'Primary'} platform payroll.`}
                        >
                          NPP
                        </span>
                      ) : (
                        <span className="font-mono text-sm text-red-600">- AED {parseFloat(calculationDialog.riderData.slx_deduction || 0).toFixed(2)}</span>
                      )}
                    </div>
                    {(calculationDialog.riderData.misc_deduction || 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-sm text-slate-600">Misc. Deduction</span>
                          {calculationDialog.riderData.misc_deduction_desc && (
                            <p className="text-[10px] text-pink-600">{calculationDialog.riderData.misc_deduction_desc}</p>
                          )}
                        </div>
                        <span className="font-mono text-sm text-pink-600">- AED {calculationDialog.riderData.misc_deduction || 0}</span>
                      </div>
                    )}
                    {/* Feb-2026: additional_deductions from calculation_breakdown
                        (Riders Incentives paid in wallet + sign-tagged custom
                         mappings like Coach Fee | deduction). Rendered here
                         so the in-app modal matches the PDF + salary slip.
                         Feb-2026 (revised): skip items whose label already
                         corresponds to a dedicated inline row above
                         (Platform Deduction / Food Compensation / Fines /
                         SALIK / etc.) so a stale duplicate can never
                         double-count on the modal. */}
                    {Array.isArray(calculationDialog.data?.additional_deductions) && (
                      calculationDialog.data.additional_deductions
                        .filter((it) => {
                          if (!it || isNaN(parseFloat(it.value)) || parseFloat(it.value) <= 0) return false;
                          const _lbl = String(it.label || '').trim().toLowerCase();
                          const _skip = new Set([
                            'platform deduction', 'deduction',
                            'food compensation', 'food_compensation',
                            'fines', 'salik', 'maintenance', 'loan emi', 'loan_emi',
                            'slx charge', 'slx',
                            'paid in wallet', 'paid as tips', 'paid as cod',
                            'clawback deduction',
                          ]);
                          return !_skip.has(_lbl);
                        })
                        .map((it, idx) => (
                          <div key={`add-ded-${idx}`} className="flex justify-between items-center" data-testid={`calc-add-ded-${idx}`}>
                            <span className="text-sm text-slate-600">{it.label}</span>
                            <span className="font-mono text-sm text-red-600">- AED {parseFloat(it.value).toFixed(2)}</span>
                          </div>
                        ))
                    )}
                    <div className="flex justify-between items-center pt-2 border-t border-red-200 font-medium">
                      <span className="text-sm text-red-700">Total Deductions</span>
                      <span className="font-mono text-sm text-red-700">- AED {calculationDialog.riderData.total_deductions || 0}</span>
                    </div>
                  </div>

                  <div className="bg-indigo-100 border border-indigo-300 rounded-lg p-4">
                    <p className="text-xs text-indigo-600 mb-2">Net Salary Calculation</p>
                    <p className="font-mono text-xs text-slate-700">
                      {calculationDialog.data.gross_salary} - {calculationDialog.riderData.total_deductions || 0} = {((calculationDialog.data.gross_salary || 0) - (calculationDialog.riderData.total_deductions || 0)).toFixed(2)}
                    </p>
                    <div className="flex justify-between items-center mt-3 pt-2 border-t border-indigo-300">
                      <span className="font-semibold text-indigo-800">Net Salary</span>
                      <span className="font-mono font-bold text-xl text-indigo-700">AED {calculationDialog.riderData.net_salary || 0}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : calculationDialog.riderData ? (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center border-b pb-2">
                  <span className="text-sm text-slate-600">Platform ID</span>
                  <Badge variant="outline">{calculationDialog.riderData.employee_unique_id || 'N/A'}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Bike Plate</span>
                  <span className="font-mono text-sm">{calculationDialog.riderData.bike_plate || '-'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Total Orders</span>
                  <span className="font-mono text-sm">{calculationDialog.riderData.total_orders || 0}</span>
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-4 space-y-3">
                <p className="text-xs text-green-600 font-medium mb-2">Earnings</p>
                <div className="flex justify-between items-center text-green-700 font-medium">
                  <span className="text-sm">Gross Salary</span>
                  <span className="font-mono font-bold text-lg">AED {calculationDialog.riderData.gross_salary || 0}</span>
                </div>
                {parseFloat(calculationDialog.riderData.past_dues || 0) > 0 && (
                  <div className="flex justify-between items-start text-green-700">
                    <div>
                      <span className="text-sm">Past Dues</span>
                      {calculationDialog.riderData.past_dues_desc && (
                        <p className="text-[10px] text-slate-500 italic mt-0.5">{calculationDialog.riderData.past_dues_desc}</p>
                      )}
                    </div>
                    <span className="font-mono text-sm font-semibold">+ AED {parseFloat(calculationDialog.riderData.past_dues || 0).toFixed(2)}</span>
                  </div>
                )}
              </div>

              <div className="bg-red-50 rounded-lg p-4 space-y-2">
                <p className="text-xs text-red-600 font-medium mb-2">Deductions</p>
                {calculationDialog.riderData.paid_in_wallet > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">
                      {(calculationDialog.riderData?.calculation_breakdown?.formula_type === 'careem_eligible_split')
                        ? 'Advance Paid'
                        : 'Paid in Wallet'}
                    </span>
                    <span className="font-mono text-sm text-cyan-600">- AED {calculationDialog.riderData.paid_in_wallet || 0}</span>
                  </div>
                )}
                {calculationDialog.riderData.paid_as_tips > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Paid as Tips</span>
                    <span className="font-mono text-sm text-teal-600">- AED {calculationDialog.riderData.paid_as_tips || 0}</span>
                  </div>
                )}
                {calculationDialog.riderData.paid_as_cod > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">
                      {(calculationDialog.riderData?.calculation_breakdown?.formula_type === 'careem_eligible_split')
                        ? 'COD'
                        : 'Paid as COD'}
                    </span>
                    <span className="font-mono text-sm text-indigo-600">- AED {calculationDialog.riderData.paid_as_cod || 0}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Fines</span>
                  <span className="font-mono text-sm text-red-600">- AED {calculationDialog.riderData.fines || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">SALIK</span>
                  <span className="font-mono text-sm text-red-600">- AED {calculationDialog.riderData.salik || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Maintenance</span>
                  <span className="font-mono text-sm text-red-600">- AED {calculationDialog.riderData.maintenance || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Loan EMI</span>
                  <span className="font-mono text-sm text-red-600">- AED {calculationDialog.riderData.loan_emi || 0}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-red-200 font-medium">
                  <span className="text-sm text-red-700">Total Deductions</span>
                  <span className="font-mono text-sm text-red-700">- AED {calculationDialog.riderData.total_deductions || 0}</span>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-blue-800">Net Salary</span>
                  <span className="font-mono font-bold text-xl text-blue-700">AED {calculationDialog.riderData.net_salary || 0}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-slate-500">
              <Calculator className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No calculation details available</p>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                className="text-green-600 border-green-200 hover:bg-green-50"
                onClick={() => downloadSalaryCalculationExcel(
                  calculationDialog.riderName,
                  calculationDialog.riderData,
                  calculationDialog.data
                )}
                data-testid="download-excel-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-1" />
                Excel
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => downloadSalaryCalculationPDF(
                  calculationDialog.riderName,
                  calculationDialog.riderData,
                  calculationDialog.data
                )}
                data-testid="download-pdf-btn"
              >
                <FileDown className="w-4 h-4 mr-1" />
                PDF
              </Button>
            </div>
            <Button variant="outline" onClick={() => setCalculationDialog({ open: false, data: null, riderName: '', riderData: null })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
};

export default Talent;
